import { useState, useRef, useEffect, useMemo, Fragment } from "react";
import LogoMark from "./LogoMark";
import { supabase } from './supabase'
import {
  loadUsageInfo,
  startInterviewSession,
  endInterviewSession,
  extractDimensionScores,
  describeUsage,
} from './usage'
import { track, identify, resetUser } from "./posthog.js"
import AuthScreen from './AuthScreen'
import { Turnstile } from "@marsidev/react-turnstile"

// Cloudflare Turnstile site key (public, baked into the build). Used by the
// support form (and, soon, the signup form) to gate submissions behind an
// invisible captcha challenge. Server-side verification happens via
// /api/_lib/turnstile.js using TURNSTILE_SECRET_KEY.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_CATEGORIES = {
  "💻 Technology and Digital": [
    // Individual contributors
    "Software Engineer", "Frontend Developer", "Backend Developer",
    "Full Stack Developer", "Web Developer", "WordPress Developer",
    "Mobile Developer (iOS / Android)", "Game Developer",
    "Embedded Systems Engineer", "Blockchain Developer",
    "Data Scientist", "Data Analyst", "Data Engineer",
    "Analytics Engineer",
    // ── AI / ML specialist track (high-demand, 2026) ────────────────────
    "Machine Learning Engineer", "MLOps Engineer",
    "Deep Learning Engineer", "AI Research Scientist",
    "Applied Scientist", "AI Engineer", "LLM Engineer",
    "Generative AI Engineer", "Prompt Engineer",
    "NLP Engineer", "Computer Vision Engineer",
    "AI Solutions Architect", "AI Platform Engineer",
    "Conversational AI Designer", "AI Product Manager",
    "Responsible AI / AI Ethics Specialist",
    "DevOps / Cloud Engineer", "Site Reliability Engineer",
    "Cloud Architect", "Database Administrator (DBA)",
    "Network Engineer", "Cybersecurity Analyst",
    "QA / Test Engineer", "Solutions Architect",
    "Product Manager (Tech)", "Technical Program Manager",
    "IT Support / Sysadmin",
    // Management ladder
    "Engineering Manager", "Software Development Manager",
    "Director of Engineering", "AVP of Engineering",
    "VP of Engineering",
    "Chief Technology Officer (CTO)", "Chief Information Officer (CIO)",
    "Chief Information Security Officer (CISO)",
  ],
  "📈 Business and Strategy": [
    // Individual contributors
    "Business Analyst", "Operations Analyst",
    "Management Consultant", "Strategy Consultant",
    "Operations Manager", "Business Development Manager",
    "Strategic Partnerships Manager", "M&A Analyst",
    "Chief of Staff", "Corporate Strategy Associate",
    "Supply Chain Analyst", "Procurement Manager",
    "General Manager",
    // Management ladder
    "Director of Operations", "Director of Strategy",
    "VP of Operations", "VP of Strategy",
    "Chief Operating Officer (COO)", "Chief Strategy Officer",
  ],
  "👥 Human Resources": [
    // Individual contributors
    "HR Coordinator", "HR Specialist", "HR Generalist",
    "HR Business Partner", "HR Manager",
    "Payroll Specialist", "Compensation and Benefits Analyst",
    "Talent Acquisition / Recruiter", "Technical Recruiter",
    "Learning and Development Specialist",
    "Employee Relations Manager", "Diversity and Inclusion Lead",
    "People Operations Manager",
    "Organizational Development Consultant",
    // Management ladder
    "Director of Talent Acquisition", "HR Director",
    "AVP of Human Resources", "VP of Human Resources",
    "Head of People", "Chief People Officer",
    "Chief Human Resources Officer (CHRO)",
  ],
  "💰 Finance and Banking": [
    // Individual contributors
    "Financial Analyst", "FP&A Analyst",
    "Investment Banking Analyst", "Equity Research Analyst",
    "Private Equity Associate", "Venture Capital Analyst",
    "Quantitative Analyst (Quant)", "Risk Analyst",
    "Credit Analyst", "Compliance Analyst",
    "Accountant / CPA", "Bookkeeper", "Auditor",
    "Tax Consultant", "Treasury Analyst",
    "Financial Advisor", "Wealth / Portfolio Manager",
    "Actuary",
    // Management ladder
    "Finance Manager", "Controller",
    "Director of Finance",
    "Assistant Vice President (AVP)", "VP of Finance",
    "Chief Financial Officer (CFO)", "Chief Risk Officer",
  ],
  "🗂️ Project Management": [
    // Individual contributors
    "Project Coordinator", "Project Manager",
    "Senior Project Manager", "Program Manager",
    "Scrum Master", "Agile Coach", "Product Owner",
    "Construction Project Manager", "IT Project Manager",
    "Delivery Manager", "PMO Lead",
    // Management ladder
    "Director of Program Management",
    "Head of PMO", "VP of Project Management",
  ],
  "📣 Sales and Marketing": [
    // Sales — individual contributors
    "Sales Development Rep (SDR)", "Business Development Rep (BDR)",
    "Inside Sales Rep", "Sales Executive",
    "Account Executive", "Enterprise Account Executive",
    "Account Manager", "Customer Success Manager",
    // Marketing — individual contributors
    "Marketing Coordinator", "Marketing Analyst",
    "Marketing Manager", "Brand Manager",
    "Product Marketing Manager", "Demand Generation Manager",
    "Digital Marketing Specialist", "SEO / SEM Specialist",
    "Email Marketing Specialist", "Social Media Manager",
    "Content Marketer", "Growth / Performance Marketer",
    // Management ladder
    "Sales Manager", "Regional Sales Manager",
    "Director of Sales", "AVP of Sales",
    "VP of Sales", "Chief Revenue Officer (CRO)",
    "Director of Marketing", "AVP of Marketing",
    "VP of Marketing", "Chief Marketing Officer (CMO)",
  ],
  "🎨 Creative and Design": [
    // Individual contributors
    "UX Designer", "UI Designer", "Product Designer",
    "Web Designer", "Visual Designer", "Interaction Designer",
    "UX Researcher", "Graphic Designer",
    "Illustrator", "Motion Designer",
    "Animator", "3D Artist",
    "Brand Strategist", "Industrial Designer",
    "Copywriter", "Content Writer",
    "Photographer", "Videographer",
    "Art Director", "Creative Director",
    // Management ladder
    "Design Manager", "Head of Design",
    "Director of Design", "VP of Design",
    "Chief Creative Officer",
  ],
  "🔬 Science and Research": [
    // Individual contributors
    "Research Assistant", "Lab Technician",
    "Research Scientist", "Postdoctoral Researcher",
    "Biologist", "Microbiologist", "Geneticist",
    "Chemist", "Physicist", "Geologist",
    "Materials Scientist", "Bioinformatician",
    "Environmental Scientist", "Statistician",
    "Clinical Research Associate",
    // Management ladder
    "Principal Investigator", "Research Director",
    "Head of R&D", "Chief Scientific Officer",
  ],
  "🏥 Healthcare and Medicine": [
    // Individual contributors
    "Nurse / Nursing", "Midwife",
    "Medical Doctor / Physician", "Pediatrician",
    "Cardiologist", "Oncologist", "Anesthesiologist",
    "Surgeon", "Dentist", "Optometrist",
    "Pharmacist", "Veterinarian",
    "Physiotherapist", "Occupational Therapist",
    "Dietitian / Nutritionist",
    "Psychologist / Counsellor",
    "Radiologist / Imaging Tech",
    "Paramedic / EMT", "Public Health Specialist",
    "Medical Researcher",
    // Management ladder
    "Nurse Manager", "Director of Nursing",
    "Healthcare Administrator", "Medical Director",
    "Chief Medical Officer", "Chief Nursing Officer",
  ],
  "⚖️ Legal": [
    // Individual contributors
    "Paralegal", "Legal Researcher",
    "Legal Associate", "Senior Associate",
    "Lawyer / Attorney", "Litigator",
    "Corporate Counsel", "Tax Attorney",
    "Employment Lawyer", "Immigration Attorney",
    "Real Estate Attorney",
    "Intellectual Property Attorney", "Patent Agent",
    "Compliance Officer", "Contracts Manager",
    "Legal Operations Manager",
    // Management ladder
    "Partner", "Managing Partner",
    "General Counsel", "Chief Legal Officer",
    "Chief Compliance Officer",
  ],
  "🎓 Education": [
    // Individual contributors
    "Teaching Assistant", "Substitute Teacher",
    "Tutor", "Teacher (K–12)", "ESL Teacher",
    "Special Education Teacher",
    "Adjunct Professor", "University Lecturer / Professor",
    "Librarian", "School Counselor", "Career Counselor",
    "Curriculum Designer", "Instructional Designer",
    "Education Consultant", "Academic Researcher",
    // Management ladder
    "Department Chair", "School Principal / Administrator",
    "Director of Admissions", "Dean", "Provost",
    "Superintendent",
  ],
  "🔧 Engineering": [
    // Individual contributors
    "Mechanical Engineer", "Civil Engineer",
    "Structural Engineer", "Geotechnical Engineer",
    "Transportation Engineer",
    "Electrical Engineer", "Chemical Engineer",
    "Process Engineer", "Aerospace Engineer",
    "Industrial / Manufacturing Engineer",
    "Robotics Engineer", "Materials Engineer",
    "Nuclear Engineer", "Biomedical Engineer",
    "Environmental Engineer", "Petroleum Engineer",
    "Architect", "Quality Engineer",
    // Management ladder
    "Engineering Project Manager", "Construction Manager",
    "Chief Engineer",
    "Director of Engineering Operations",
    "VP of Engineering Operations",
  ],
  "🎙️ Media and Communications": [
    // Individual contributors
    "Journalist / Reporter", "Photojournalist",
    "Camera Operator", "News Editor",
    "News Anchor", "Sports Commentator",
    "PR Specialist", "Public Affairs Specialist",
    "Crisis Communications Specialist",
    "Communications Manager", "Corporate Communications Lead",
    "Investor Relations Specialist",
    "Video Producer", "Podcast Producer",
    "Radio / Broadcast Host", "Documentary Filmmaker",
    "Technical Writer", "Speechwriter",
    // Management ladder
    "Managing Editor", "Editor-in-Chief",
    "Communications Director", "Director of Public Relations",
    "VP of Communications", "Chief Communications Officer",
  ],
};

const TONE_OPTIONS = [
  { id: "encouraging", label: "Friendly", icon: "😊", desc: "Warm & supportive" },
  { id: "professional", label: "Professional", icon: "🤝", desc: "Neutral & balanced" },
  { id: "tough", label: "Tough", icon: "🔥", desc: "Hard & challenging" },
];

const EXPERIENCE_LEVELS = [
  { id: "Beginner", label: "Beginner", desc: "New to this role or just starting career" },
  { id: "Intermediate", label: "Intermediate", desc: "Some experience, looking to level up" },
  { id: "Advanced", label: "Advanced", desc: "Senior level, deep expertise" },
];

const INTERVIEWERS = [
  {
    id: "alex",
    name: "Alice",
    avatar: "👩‍💼",
    desc: "Female voice (default)",
    // Listed in priority order — high-quality neural/online voices first.
    voiceMatchers: [
      "Microsoft Aria Online", "Microsoft Jenny Online", "Microsoft Emma Online",
      "Google US English", "Google UK English Female",
      "Samantha", "Karen", "Moira", "Tessa", "Fiona",
      "Microsoft Zira", "Microsoft Aria",
    ],
    genderHint: "female",
    pitch: 1.0,
  },
  {
    id: "ryan",
    name: "Ryan",
    avatar: "👨‍💼",
    desc: "Male voice",
    voiceMatchers: [
      "Microsoft Guy Online", "Microsoft Christopher Online", "Microsoft Brian Online",
      "Google UK English Male",
      "Daniel", "Aaron", "Fred", "Rishi",
      "Microsoft David", "Microsoft Guy",
      "Alex",
    ],
    genderHint: "male",
    pitch: 0.95,
  },
];

const SKILLS_BY_CATEGORY = {
  "💻 Technology and Digital": [
    "JavaScript", "TypeScript", "Python", "Java", "C++", "Go", "SQL",
    "React", "Node.js", "REST APIs", "GraphQL", "AWS", "Azure", "GCP",
    "Docker", "Kubernetes", "Linux", "Git/GitHub", "CI/CD",
    "System Design", "Data Structures & Algorithms", "Machine Learning",
    "Cybersecurity", "Agile / Scrum",
  ],
  "📈 Business and Strategy": [
    "Excel / Spreadsheets", "PowerPoint", "Financial Modeling",
    "Market Research", "Strategic Planning", "Stakeholder Management",
    "KPIs & OKRs", "Operations", "Process Improvement", "SQL",
    "Data Analysis", "Negotiation", "Business Case Development",
  ],
  "👥 Human Resources": [
    "Recruiting / Sourcing", "ATS Tools", "Interviewing",
    "Employee Engagement", "HRIS Systems", "Compensation & Benefits",
    "Talent Management", "Onboarding", "Performance Management",
    "Conflict Resolution", "Labor Law / Compliance", "Training & L&D",
    "Diversity & Inclusion",
  ],
  "💰 Finance and Banking": [
    "Excel / VBA", "Financial Modeling", "Valuation (DCF, Comps)",
    "Accounting (GAAP / IFRS)", "Bloomberg Terminal", "SQL",
    "Risk Analysis", "M&A Analysis", "Portfolio Management",
    "Equity / Debt Markets", "Audit", "Tax", "Forecasting & Budgeting",
  ],
  "🗂️ Project Management": [
    "Agile", "Scrum", "Kanban", "Waterfall", "Jira", "Confluence",
    "MS Project", "Asana", "Risk Management", "Stakeholder Communication",
    "Budgeting", "Roadmapping", "Resource Planning", "PMP / PRINCE2",
  ],
  "📣 Sales and Marketing": [
    "CRM (Salesforce / HubSpot)", "Lead Generation", "Pipeline Management",
    "Cold Outreach", "Negotiation", "SEO", "SEM / Google Ads",
    "Email Marketing", "Content Strategy", "Social Media Marketing",
    "Brand Strategy", "Analytics (GA4)", "A/B Testing", "Copywriting",
  ],
  "🎨 Creative and Design": [
    "Figma", "Sketch", "Adobe XD", "Photoshop", "Illustrator",
    "After Effects", "Prototyping", "Wireframing", "User Research",
    "Design Systems", "Typography", "Branding", "Motion Design",
    "Storyboarding",
  ],
  "🔬 Science and Research": [
    "Lab Techniques", "Research Methodology", "Statistics",
    "Data Analysis", "Python", "R", "MATLAB", "Scientific Writing",
    "Experimental Design", "Microscopy", "Literature Review",
    "Grant Writing",
  ],
  "🏥 Healthcare and Medicine": [
    "Patient Care", "Clinical Assessment", "Diagnosis",
    "EMR / EHR Systems", "HIPAA Compliance", "Anatomy & Physiology",
    "Pharmacology", "Bedside Manner", "Critical Thinking",
    "CPR / BLS / ACLS", "Triage", "Medical Documentation",
  ],
  "⚖️ Legal": [
    "Legal Research", "Contract Drafting", "Litigation",
    "Negotiation", "Regulatory Compliance", "Case Law Analysis",
    "Westlaw / LexisNexis", "Due Diligence", "Legal Writing",
    "Client Counseling",
  ],
  "🎓 Education": [
    "Lesson Planning", "Curriculum Design", "Classroom Management",
    "Pedagogy", "Assessment & Evaluation", "EdTech Tools",
    "Differentiated Instruction", "Student Engagement",
    "Mentoring", "Special Education",
  ],
  "🔧 Engineering": [
    "AutoCAD", "SolidWorks", "MATLAB", "CAD / CAM",
    "Project Management", "Quality Control", "Mechanical Design",
    "Manufacturing Processes", "Safety Standards",
    "Problem Solving", "Technical Drawings", "Six Sigma",
  ],
  "🎙️ Media and Communications": [
    "Writing", "Editing", "AP Style", "Storytelling",
    "Video Editing (Premiere / Final Cut)", "Public Speaking",
    "Social Media", "SEO", "Press Releases", "Interviewing",
    "Adobe Audition", "Broadcast Production",
  ],
};

const GENERIC_SKILLS = [
  "Communication", "Problem Solving", "Teamwork", "Leadership",
  "Time Management", "Critical Thinking", "Adaptability",
  "Project Management", "Presentation Skills", "Analytical Thinking",
];

const ALL_ROLES = Object.values(ROLE_CATEGORIES).flat();

// Role-specific focus areas. Order matters: more specific keywords come first
// so e.g. "data engineer" matches before generic "engineer".
const SKILLS_BY_ROLE = [
  // ─── Tech ───
  { keywords: ["frontend", "front-end", "front end", "ui developer", "ui engineer", "react developer", "vue developer"], skills: [
    "JavaScript", "TypeScript", "React", "Vue / Angular", "HTML5 & CSS3", "Tailwind CSS",
    "State Management (Redux / Zustand)", "Web Performance Optimization", "Accessibility (a11y / WCAG)",
    "Testing (Jest / RTL / Cypress)", "Build Tools (Vite / Webpack)", "Cross-browser Compatibility",
  ]},
  { keywords: ["backend", "back-end", "back end", "server-side", "api developer", "api engineer"], skills: [
    "Node.js / Express", "Python (Django / Flask / FastAPI)", "Java / Spring", "Go", "REST APIs",
    "GraphQL", "SQL & Relational Databases", "NoSQL (MongoDB / Redis)", "Microservices",
    "Authentication (OAuth / JWT)", "Message Queues (Kafka / RabbitMQ)", "Caching Strategies",
  ]},
  { keywords: ["full stack", "fullstack", "full-stack"], skills: [
    "JavaScript / TypeScript", "React / Next.js", "Node.js", "REST & GraphQL APIs", "SQL Databases",
    "NoSQL Databases", "AWS / Cloud Deployment", "Docker", "Git & CI/CD", "System Design",
    "Authentication & Security", "Testing (Unit / E2E)",
  ]},
  { keywords: ["mobile developer", "mobile engineer", "ios developer", "android developer", "react native", "flutter"], skills: [
    "Swift / SwiftUI", "Kotlin / Jetpack Compose", "React Native", "Flutter / Dart",
    "iOS SDK", "Android SDK", "Mobile UI/UX Patterns", "App Store / Play Store Deployment",
    "Push Notifications", "Offline Storage", "Mobile Performance Profiling", "Native Modules",
  ]},
  { keywords: ["data scientist"], skills: [
    "Python (Pandas / NumPy)", "R", "Statistics & Hypothesis Testing", "Machine Learning",
    "Scikit-learn", "TensorFlow / PyTorch", "SQL", "Data Visualization", "Feature Engineering",
    "A/B Testing & Experimentation", "Causal Inference", "Communicating Insights to Stakeholders",
  ]},
  { keywords: ["data analyst", "business intelligence", "bi analyst"], skills: [
    "SQL", "Excel (Advanced / Pivot Tables)", "Tableau", "Power BI", "Looker",
    "Python (Pandas)", "Data Cleaning & Wrangling", "Statistics Fundamentals",
    "Dashboard Design", "Data Storytelling", "A/B Test Analysis", "Stakeholder Communication",
  ]},
  { keywords: ["data engineer"], skills: [
    "Python", "SQL", "Apache Spark", "Apache Airflow", "Apache Kafka",
    "ETL / ELT Pipelines", "Data Warehousing (Snowflake / BigQuery / Redshift)", "dbt",
    "AWS / GCP Data Services", "Data Modeling", "Data Lake Architecture", "Schema Design",
  ]},
  { keywords: ["machine learning", "ml engineer", "ml scientist"], skills: [
    "Python", "TensorFlow", "PyTorch", "MLOps", "Model Deployment & Serving",
    "Kubernetes", "Feature Engineering", "MLflow / Weights & Biases", "Distributed Training",
    "Model Evaluation & Monitoring", "Statistics & Probability", "Cloud ML Platforms (SageMaker / Vertex AI)",
  ]},
  { keywords: ["ai engineer", "prompt engineer", "llm engineer", "generative ai"], skills: [
    "LLM APIs (OpenAI / Anthropic / Google)", "Prompt Engineering", "Retrieval-Augmented Generation (RAG)",
    "Embeddings & Vector Databases (Pinecone / Weaviate)", "LangChain / LlamaIndex", "Python",
    "Fine-tuning & LoRA", "Evaluations & Guardrails", "Agent Frameworks", "Function Calling / Tool Use",
    "Token & Cost Optimization", "Model Context Protocol",
  ]},
  { keywords: ["mlops"], skills: [
    "Model Serving (TorchServe / TF Serving / KServe)", "Feature Stores (Feast / Tecton)",
    "MLflow / Weights & Biases", "Kubeflow / Vertex AI Pipelines", "Docker", "Kubernetes",
    "CI/CD for ML", "Model Monitoring & Drift Detection", "A/B Testing & Shadow Deployment",
    "Terraform", "Python", "Cost Optimization for GPU Inference",
  ]},
  { keywords: ["deep learning"], skills: [
    "PyTorch", "TensorFlow / JAX", "Transformer Architectures", "CNNs / RNNs",
    "Distributed Training (DDP / FSDP / DeepSpeed)", "Mixed-Precision & Quantization",
    "CUDA / GPU Optimization", "Hyperparameter Tuning", "Model Compression & Pruning",
    "Research Paper Implementation", "Linear Algebra & Calculus", "Experiment Tracking",
  ]},
  { keywords: ["ai research scientist", "applied scientist"], skills: [
    "PyTorch / JAX", "Transformer & Diffusion Architectures", "Reinforcement Learning (RLHF / DPO)",
    "Probability & Statistics", "Research Paper Writing & Review", "Experimental Design",
    "Benchmarking & Evaluation Methodology", "Information Theory", "Optimization Methods",
    "Reading & Reproducing State-of-the-Art Papers", "Open-Source Contribution",
  ]},
  { keywords: ["nlp engineer", "natural language"], skills: [
    "Tokenization & Subword Methods (BPE / WordPiece)", "Hugging Face Transformers",
    "Named Entity Recognition", "Sentiment Analysis", "Sequence-to-Sequence Models",
    "spaCy / NLTK", "Fine-tuning LLMs for Domain Tasks", "Embeddings & Semantic Search",
    "PyTorch", "Evaluation Metrics (BLEU / ROUGE / BERTScore)", "Multilingual Modeling",
  ]},
  { keywords: ["computer vision", "vision engineer"], skills: [
    "OpenCV", "PyTorch", "CNN Architectures (ResNet / EfficientNet / ViT)",
    "Object Detection (YOLO / DETR / Faster R-CNN)", "Image Segmentation (U-Net / Mask R-CNN)",
    "Diffusion & Generative Vision Models", "Camera Calibration & 3D Geometry",
    "Edge Deployment (CoreML / TensorRT / ONNX)", "Data Augmentation Pipelines",
    "Annotation Tooling (CVAT / Labelbox)", "Real-Time Inference Optimization",
  ]},
  { keywords: ["conversational ai", "chatbot designer", "voice ui"], skills: [
    "Dialogue Flow Design", "Intent & Entity Modeling", "LLM Prompt Design for Agents",
    "Dialogflow / Rasa / Lex", "Voice UX Patterns", "Multi-turn Context Handling",
    "Fallback & Error Recovery Strategies", "Evaluation with Human-in-the-Loop",
    "SSML & Voice Tuning", "Persona & Tone Design", "Analytics for Conversation Quality",
  ]},
  { keywords: ["responsible ai", "ai ethics", "ai safety", "trustworthy ai"], skills: [
    "Fairness Metrics & Bias Auditing", "Model Cards & Datasheets", "Privacy-Preserving ML",
    "Differential Privacy", "Explainability (SHAP / LIME / Integrated Gradients)",
    "Red-Teaming & Adversarial Testing", "EU AI Act / NIST AI RMF", "Governance Frameworks",
    "Stakeholder Risk Assessment", "Content Safety & Moderation",
    "Sociotechnical Impact Analysis", "Policy & Documentation",
  ]},
  { keywords: ["ai solutions architect", "ml solutions architect"], skills: [
    "LLM & ML System Design", "RAG Architecture Patterns", "Vector Database Selection",
    "Cloud AI Services (Bedrock / Vertex AI / Azure OpenAI)", "Latency & Throughput Tradeoffs",
    "Cost Modeling for Inference", "Security & Data Residency for AI Workloads",
    "Integration Patterns (Streaming / Batch / Agentic)", "Stakeholder Communication",
    "Reference Architectures", "Capacity Planning for GPUs", "Evaluation & Acceptance Criteria",
  ]},
  { keywords: ["ai platform engineer", "ml platform engineer"], skills: [
    "GPU Cluster Management", "Kubernetes (GPU Operators / Karpenter)", "Ray / Slurm",
    "Feature Stores (Feast / Tecton)", "Model Registry (MLflow / Vertex)", "Inference Gateways",
    "Vector DB Operations (Pinecone / Weaviate / pgvector)", "Triton / vLLM / TGI Serving",
    "CI/CD for ML Pipelines", "Observability for ML (Arize / WhyLabs)",
    "Multi-Tenant Isolation", "Cost Attribution & GPU Quota Management",
  ]},
  { keywords: ["ai product manager", "ml product manager"], skills: [
    "ML Lifecycle Fluency", "Prompt & Eval Design", "Defining Success Metrics for AI Features",
    "Data Strategy & Labeling Plans", "User Trust & Transparency Patterns",
    "Cost / Latency Tradeoffs", "Roadmapping with Model-Capability Uncertainty",
    "Stakeholder Communication", "Experimentation (A/B & Offline Eval)",
    "Privacy & Compliance Awareness", "Working with Research & ML Teams",
  ]},
  { keywords: ["analytics engineer"], skills: [
    "dbt", "SQL (Advanced)", "Data Warehousing (Snowflake / BigQuery / Redshift)",
    "Data Modeling (Dimensional / Star Schema)", "Git / Version Control",
    "Looker / Tableau / Power BI", "Python", "Data Quality Testing",
    "Pipeline Orchestration (Airflow / Dagster)", "Stakeholder Requirements Gathering",
    "Documentation & Data Catalogs",
  ]},
  { keywords: ["devops", "platform engineer", "cloud engineer", "infrastructure engineer"], skills: [
    "AWS", "Azure", "GCP", "Terraform / Pulumi", "Kubernetes", "Docker",
    "CI/CD Pipelines (GitHub Actions / Jenkins)", "Linux & Bash", "Monitoring (Prometheus / Grafana)",
    "Infrastructure as Code", "Secrets Management", "Cost Optimization",
  ]},
  { keywords: ["site reliability", "sre"], skills: [
    "Linux Internals", "Kubernetes", "Observability (Datadog / Grafana / OpenTelemetry)",
    "SLOs / SLIs / Error Budgets", "Incident Response & On-call", "Terraform", "Python / Go",
    "Bash / Scripting", "Postmortems & RCA", "Capacity Planning",
    "Distributed Systems Debugging", "Chaos Engineering",
  ]},
  { keywords: ["cybersecurity", "security analyst", "security engineer", "infosec", "appsec"], skills: [
    "Network Security", "OWASP Top 10", "SIEM Tools (Splunk / Sentinel)", "Penetration Testing",
    "Threat Modeling", "Cryptography Fundamentals", "Incident Response", "Vulnerability Management",
    "Compliance (SOC 2 / ISO 27001 / NIST)", "Cloud Security", "Linux Hardening", "Python Scripting",
  ]},
  { keywords: ["qa", "test engineer", "quality assurance", "automation engineer", "sdet"], skills: [
    "Test Automation Frameworks", "Selenium / Cypress / Playwright", "Jest / Mocha / Pytest",
    "API Testing (Postman / RestAssured)", "Regression & Smoke Testing", "Test Plan Authoring",
    "Bug Tracking (Jira)", "Performance Testing (JMeter / k6)", "Mobile Test Automation",
    "BDD (Cucumber)", "CI/CD Integration", "Exploratory Testing",
  ]},
  { keywords: ["solutions architect", "enterprise architect", "cloud architect"], skills: [
    "System Design", "Cloud Architecture (AWS / Azure / GCP)", "Microservices vs Monolith Tradeoffs",
    "API Design & Versioning", "Scalability & Reliability Patterns", "Security Architecture",
    "Cost Optimization", "Stakeholder Communication", "Architecture Decision Records",
    "Integration Patterns", "Event-Driven Architecture", "Data Architecture",
  ]},
  { keywords: ["product manager", "product owner"], skills: [
    "Product Strategy & Roadmapping", "User Research & Discovery", "Prioritization (RICE / MoSCoW)",
    "A/B Testing & Experimentation", "Analytics (Mixpanel / Amplitude / GA4)", "Wireframing & Prototyping",
    "Stakeholder Management", "PRD / Spec Writing", "Agile / Scrum Ceremonies",
    "Competitive Analysis", "Pricing & Packaging", "OKRs & Success Metrics",
  ]},
  { keywords: ["it support", "sysadmin", "helpdesk", "system administrator"], skills: [
    "Windows Server Administration", "Linux Administration", "Active Directory & Group Policy",
    "Networking (TCP/IP, DNS, DHCP, VLANs)", "Helpdesk Ticketing (ServiceNow / Zendesk)",
    "PowerShell", "Bash Scripting", "Hardware Troubleshooting", "Microsoft 365 / Google Workspace",
    "VPN & Firewall Basics", "Backup & Disaster Recovery", "Endpoint Security",
  ]},

  // ─── Business ───
  { keywords: ["business analyst"], skills: [
    "Requirements Gathering", "SQL", "Excel & Pivot Tables", "Process Mapping (BPMN)",
    "User Story Writing", "JIRA / Confluence", "Stakeholder Interviews", "Tableau / Power BI",
    "Use Case Documentation", "Gap Analysis", "Acceptance Criteria", "UAT Coordination",
  ]},
  { keywords: ["consultant", "consulting"], skills: [
    "Case Interviews", "MECE Frameworks", "Hypothesis-Driven Problem Solving", "PowerPoint Storytelling",
    "Excel Modeling", "Market Sizing", "Strategic Frameworks (Porter's / SWOT / 4Cs)",
    "Stakeholder Communication", "Workshop Facilitation", "Executive Summaries",
    "Industry Research", "Change Management",
  ]},
  { keywords: ["operations manager", "ops manager"], skills: [
    "Process Improvement", "Lean / Six Sigma", "KPIs & Dashboards", "Supply Chain Coordination",
    "Vendor Management", "Excel & SQL", "ERP Systems (SAP / Oracle / NetSuite)",
    "Team Leadership", "Cost Optimization", "Capacity Planning", "SOP Development",
    "Cross-functional Project Management",
  ]},

  // ─── HR ───
  { keywords: ["technical recruiter"], skills: [
    "Technical Sourcing", "Boolean Search & GitHub Sourcing", "ATS (Greenhouse / Lever / Workday)",
    "Tech Stack Literacy", "Interviewing & Screening", "LinkedIn Recruiter", "Offer Negotiation",
    "Pipeline Management", "Candidate Experience", "DEI Sourcing", "Calibration with Hiring Managers",
    "Compensation Benchmarking",
  ]},
  { keywords: ["recruiter", "talent acquisition", "sourcer"], skills: [
    "Sourcing (LinkedIn / Boolean Search)", "Interviewing & Screening", "ATS (Greenhouse / Lever)",
    "Offer Negotiation", "Employer Branding", "Pipeline Management", "Candidate Experience",
    "Compensation Benchmarking", "DEI Recruiting", "Stakeholder Management", "Reference Checks",
    "Workforce Planning",
  ]},
  { keywords: ["hr manager", "hrbp", "hr business partner", "hr generalist", "people operations", "chro", "hr director"], skills: [
    "Employee Relations", "HRIS (Workday / BambooHR)", "Performance Management", "Compensation & Benefits",
    "Labor Law / Compliance", "Onboarding & Offboarding", "Diversity & Inclusion", "Conflict Resolution",
    "Coaching Managers", "Engagement Surveys", "Workforce Planning", "Policy Development",
  ]},

  // ─── Finance ───
  { keywords: ["investment banking", "investment banker"], skills: [
    "Financial Modeling (DCF / LBO / Comps)", "Excel (Advanced)", "PowerPoint Pitch Books",
    "M&A Analysis", "Valuation", "Bloomberg Terminal", "Industry & Company Research",
    "Three-Statement Modeling", "Capital Structure", "Due Diligence", "Client Communication",
    "Attention to Detail under Pressure",
  ]},
  { keywords: ["equity research"], skills: [
    "Financial Modeling", "Excel", "Industry Analysis", "Earnings Calls & Transcripts",
    "Valuation Methodologies", "Bloomberg / FactSet", "Investment Thesis Writing", "Sector Knowledge",
    "Financial Statement Analysis", "Forecasting", "Client Reports", "Channel Checks",
  ]},
  { keywords: ["private equity", "venture capital", "vc analyst"], skills: [
    "Deal Sourcing", "Due Diligence", "Financial Modeling (LBO)", "Market Mapping",
    "Investment Memos", "Founder / Management Diligence", "Portfolio Support", "Valuation",
    "Network Building", "Industry Research", "Term Sheets", "Exit Planning",
  ]},
  { keywords: ["financial analyst", "fp&a"], skills: [
    "Excel & Financial Modeling", "Forecasting & Budgeting", "Variance Analysis", "GAAP / IFRS",
    "PowerPoint", "SQL", "Three-Statement Modeling", "Valuation", "FP&A Cycles",
    "Management Reporting", "Scenario Analysis", "Business Partnering",
  ]},
  { keywords: ["accountant", "cpa", "auditor"], skills: [
    "GAAP / IFRS", "Excel", "QuickBooks / NetSuite", "Audit Procedures", "Tax Preparation",
    "Financial Statements", "Reconciliation", "ERP (SAP / Oracle)", "Internal Controls",
    "Month-End Close", "Variance Analysis", "Compliance Reporting",
  ]},

  // ─── Project Management ───
  { keywords: ["scrum master"], skills: [
    "Scrum Framework", "Sprint Planning & Retrospectives", "Backlog Refinement", "Servant Leadership",
    "Coaching Teams", "JIRA", "Velocity & Burndown Tracking", "Conflict Resolution",
    "Removing Blockers", "Stakeholder Communication", "Agile Metrics", "Continuous Improvement",
  ]},
  { keywords: ["project manager", "program manager", "delivery manager", "pmo"], skills: [
    "Agile & Waterfall Methodologies", "Scrum / Kanban", "JIRA / Asana / MS Project",
    "Risk & Issue Management", "Stakeholder Communication", "Budget & Resource Planning",
    "Roadmapping", "Status Reporting", "Cross-functional Coordination", "Vendor Management",
    "Change Management", "PMP / PRINCE2 Knowledge",
  ]},

  // ─── Sales ───
  { keywords: ["sdr", "bdr", "sales development", "business development rep"], skills: [
    "Cold Outreach (Email / Phone / LinkedIn)", "Prospecting & Pipeline Building", "Salesforce",
    "Outreach.io / Salesloft", "LinkedIn Sales Navigator", "Discovery & Qualification (BANT / MEDDIC)",
    "Objection Handling", "Cadence Design", "Quota Attainment", "Account Research",
    "Personalization at Scale", "Booking Meetings",
  ]},
  { keywords: ["account executive", "sales executive", "sales rep"], skills: [
    "Discovery Calls", "Demo Skills", "Negotiation & Closing", "Pipeline Management",
    "Salesforce / HubSpot", "Account Planning", "Quota Attainment", "Objection Handling",
    "MEDDIC / Sandler / Challenger", "Forecasting", "Cross-functional Selling", "Executive Presence",
  ]},
  { keywords: ["customer success"], skills: [
    "Account Health Monitoring", "Onboarding & Adoption", "Renewals & Expansion",
    "QBRs (Quarterly Business Reviews)", "Salesforce / Gainsight", "Churn Reduction",
    "Stakeholder Management", "Product Knowledge", "NPS & CSAT", "Customer Advocacy",
    "Cross-functional Collaboration", "Escalation Management",
  ]},
  { keywords: ["account manager"], skills: [
    "Account Planning", "Renewals & Upselling", "QBRs", "CRM (Salesforce / HubSpot)",
    "Negotiation", "Stakeholder Management", "Customer Success Metrics", "Forecasting",
    "Cross-functional Coordination", "Contract Management", "Retention Strategies", "Product Knowledge",
  ]},
  { keywords: ["product marketing"], skills: [
    "Positioning & Messaging", "Go-to-Market Strategy", "Competitive Analysis", "Sales Enablement",
    "Customer Research", "Pricing & Packaging", "Launch Planning", "Win/Loss Analysis",
    "Persona Development", "Content Creation", "Analyst Relations", "Product Storytelling",
  ]},
  { keywords: ["growth marketer", "performance marketer", "growth marketing"], skills: [
    "Paid Acquisition (Meta / Google / TikTok Ads)", "A/B Testing & CRO", "CAC / LTV Analysis",
    "Funnel Optimization", "Attribution Modeling", "Cohort Analysis", "SQL", "GA4 / Mixpanel",
    "Landing Page Optimization", "Lifecycle Marketing", "Referral Programs", "Growth Experimentation",
  ]},
  { keywords: ["seo", "sem"], skills: [
    "On-page SEO", "Technical SEO Audits", "Keyword Research (Ahrefs / SEMrush)",
    "Google Ads", "Bing Ads", "Link Building", "Google Analytics", "Search Console",
    "Schema Markup", "Conversion Tracking", "Content Strategy for Search", "Local SEO",
  ]},
  { keywords: ["social media manager", "community manager"], skills: [
    "Content Calendar Planning", "Instagram / TikTok / LinkedIn / X Native Skills",
    "Community Management", "Social Analytics", "Influencer Marketing", "Copywriting",
    "Crisis Communication", "Trend Spotting", "Paid Social Basics", "Video / Reels Production",
    "Brand Voice", "UGC Strategy",
  ]},
  { keywords: ["content marketer", "content strategist"], skills: [
    "Content Strategy", "SEO Writing", "Editorial Calendar", "CMS (WordPress / Webflow)",
    "Long-form Writing", "Brand Voice", "Distribution & Promotion", "Content Analytics",
    "Interviewing SMEs", "Storytelling", "Email Newsletters", "Repurposing",
  ]},
  { keywords: ["marketing manager", "brand manager"], skills: [
    "Brand Strategy & Positioning", "Campaign Management", "GA4 & Marketing Analytics",
    "Marketing Automation (HubSpot / Marketo)", "SEO Basics", "Content Strategy",
    "Budget & ROI Management", "A/B Testing", "Cross-channel Coordination",
    "Creative Briefs", "Stakeholder Management", "Market Research",
  ]},

  // ─── Design ───
  { keywords: ["ux designer", "ux researcher"], skills: [
    "User Research & Interviews", "Usability Testing", "Wireframing & Prototyping (Figma)",
    "Information Architecture", "Personas & Journey Maps", "Accessibility (WCAG)",
    "Design Systems", "Cross-functional Collaboration", "Design Critique",
    "Heuristic Evaluation", "User Flows", "Storytelling with Data",
  ]},
  { keywords: ["ui designer"], skills: [
    "Figma", "Visual Hierarchy & Typography", "Color Theory & Contrast", "Design Systems & Tokens",
    "Prototyping & Micro-interactions", "Component Libraries", "Responsive Design",
    "Iconography & Illustration", "Motion Design Basics", "Accessibility (WCAG)",
    "Designer-Developer Handoff", "Brand Application",
  ]},
  { keywords: ["product designer"], skills: [
    "Figma & Prototyping", "User Research", "Wireframing", "Visual Design", "Design Systems",
    "Usability Testing", "Cross-functional Collaboration", "Information Architecture",
    "Interaction Design", "Accessibility", "Design Critique", "Storytelling Design Decisions",
  ]},
  { keywords: ["graphic designer", "visual designer"], skills: [
    "Photoshop", "Illustrator", "InDesign", "Branding & Identity", "Typography",
    "Print Production & Pre-press", "Layout Composition", "Color Theory",
    "Mockups & Presentation", "Asset Management", "Concept Development", "Design Critique",
  ]},
  { keywords: ["motion designer", "motion graphics"], skills: [
    "After Effects", "Cinema 4D / Blender", "Storyboarding", "Animation Principles",
    "Visual Effects", "Premiere Pro", "Lottie / SVG Animation", "Sound Design Basics",
    "Brand Animation Systems", "Compositing", "Render Optimization", "Color & Lighting",
  ]},

  // ─── Healthcare ───
  { keywords: ["doctor", "physician", "medical doctor"], skills: [
    "Clinical Diagnosis", "Differential Diagnosis", "Patient History & Examination",
    "EMR Systems (Epic / Cerner)", "Medical Documentation", "Pharmacology",
    "Bedside Manner", "Critical Thinking under Pressure", "Triage", "Medical Ethics",
    "Interdisciplinary Collaboration", "Evidence-Based Medicine",
  ]},
  { keywords: ["nurse", "nursing"], skills: [
    "Patient Assessment", "Vital Signs Monitoring", "Medication Administration",
    "Wound Care", "EMR (Epic / Cerner)", "Triage", "CPR / BLS / ACLS",
    "Patient Education", "IV Insertion & Care", "Care Planning",
    "Infection Control", "Empathy & Communication",
  ]},
  { keywords: ["surgeon"], skills: [
    "Surgical Procedures", "Surgical Anatomy", "Pre-op & Post-op Care",
    "Sterile Technique", "Surgical Decision-Making", "OR Team Leadership",
    "Suturing & Wound Closure", "Critical Thinking under Pressure",
    "Patient Communication", "Surgical Instruments", "Imaging Interpretation", "Complication Management",
  ]},
  { keywords: ["pharmacist"], skills: [
    "Medication Therapy Management", "Drug Interactions", "Pharmacology",
    "Patient Counseling", "Compounding", "Pharmacy Software", "Insurance Billing",
    "Inventory Management", "Immunizations", "Regulatory Compliance",
    "Prescription Verification", "Clinical Recommendations",
  ]},
  { keywords: ["dentist"], skills: [
    "Diagnostic Imaging", "Restorative Dentistry", "Oral Surgery Basics",
    "Patient Communication", "Sterilization Protocols", "Oral Pathology",
    "Cosmetic Procedures", "Dental Anesthesia", "Treatment Planning",
    "Insurance & Billing", "Practice Management", "Periodontics",
  ]},
  { keywords: ["physiotherapist", "physical therapist"], skills: [
    "Physical Assessment", "Treatment Planning", "Manual Therapy",
    "Exercise Prescription", "Rehabilitation Protocols", "Patient Education",
    "Modalities (Heat / Cold / TENS)", "Sports Injury Recovery",
    "Documentation (SOAP Notes)", "Anatomy & Biomechanics", "Goal Setting", "Outcome Measurement",
  ]},
  { keywords: ["psychologist", "counsellor", "counselor", "therapist"], skills: [
    "Active Listening", "Cognitive Behavioral Therapy (CBT)", "Psychological Assessment",
    "Treatment Planning", "Confidentiality (HIPAA)", "Crisis Intervention",
    "Documentation", "Empathy & Rapport Building", "DSM-5 Knowledge",
    "Trauma-Informed Care", "Group Therapy", "Cultural Competence",
  ]},

  // ─── Legal ───
  { keywords: ["lawyer", "attorney", "litigator"], skills: [
    "Legal Research (Westlaw / LexisNexis)", "Litigation Strategy", "Contract Drafting",
    "Negotiation", "Client Counseling", "Case Strategy", "Legal Writing",
    "Oral Advocacy", "Discovery & Depositions", "Statutory & Case Law Analysis",
    "Time Management & Billing", "Ethics & Professional Responsibility",
  ]},
  { keywords: ["corporate counsel", "in-house counsel", "general counsel"], skills: [
    "Contract Negotiation", "M&A Support", "Compliance & Regulatory",
    "Securities Law", "Employment Law", "Risk Management",
    "Stakeholder Management", "IP Basics", "Privacy (GDPR / CCPA)",
    "Litigation Management", "Vendor Agreements", "Board Support",
  ]},
  { keywords: ["paralegal"], skills: [
    "Legal Research", "Document Management", "Case Filing & E-filing",
    "Discovery Support", "Drafting Pleadings", "Westlaw / LexisNexis",
    "Case Management Software", "Calendar & Deadlines", "Client Intake",
    "Trial Preparation", "Confidentiality", "Notary Duties",
  ]},
  { keywords: ["compliance officer", "compliance analyst"], skills: [
    "Regulatory Compliance", "Risk Assessment", "Policy Development",
    "AML / KYC", "Auditing", "SOC 2 / ISO 27001",
    "Investigations", "Training Delivery", "Reporting to Regulators",
    "Privacy (GDPR / CCPA)", "Internal Controls", "Documentation",
  ]},

  // ─── Education ───
  { keywords: ["teacher", "k-12", "k–12"], skills: [
    "Lesson Planning", "Classroom Management", "Differentiated Instruction",
    "Assessment & Evaluation", "Student Engagement", "EdTech (Google Classroom / Seesaw)",
    "Parent Communication", "Curriculum Standards", "IEP / 504 Plans Basics",
    "Behavior Management", "Co-teaching", "Cultural Responsiveness",
  ]},
  { keywords: ["professor", "lecturer", "academic"], skills: [
    "Curriculum Design", "Research & Publication", "Grant Writing",
    "Lecturing", "Mentoring Graduate Students", "Peer Review",
    "Academic Writing", "Conference Presentations", "Course Assessment Design",
    "LMS (Canvas / Blackboard)", "Office Hours & Advising", "Departmental Service",
  ]},
  { keywords: ["instructional designer", "curriculum designer"], skills: [
    "ADDIE / SAM Models", "Articulate Storyline / Rise", "Adobe Captivate",
    "eLearning Standards (SCORM / xAPI)", "LMS (Canvas / Moodle)", "Assessment Design",
    "Adult Learning Theory", "Bloom's Taxonomy", "Storyboarding",
    "Multimedia Production", "Accessibility (WCAG)", "Learning Analytics",
  ]},

  // ─── Engineering (non-software) ───
  { keywords: ["mechanical engineer"], skills: [
    "SolidWorks", "AutoCAD", "FEA (ANSYS)", "Thermodynamics & Heat Transfer",
    "Materials Science", "GD&T", "Manufacturing Processes", "Project Management",
    "Mechanical Design", "Prototyping", "Quality Standards (ISO)", "DFM / DFA",
  ]},
  { keywords: ["civil engineer"], skills: [
    "AutoCAD", "Civil 3D", "Structural Analysis", "Site Surveying",
    "Construction Materials", "Building Codes", "Project Management",
    "Permitting & Regulations", "Geotechnical Basics", "Cost Estimation",
    "Concrete & Steel Design", "Site Inspections",
  ]},
  { keywords: ["electrical engineer"], skills: [
    "Circuit Design", "PCB Layout (Altium / KiCad)", "Microcontrollers",
    "Power Systems", "MATLAB / Simulink", "Schematic Capture",
    "Embedded Systems (C / C++)", "NEC / IEC Codes", "Signal Processing",
    "Lab Instruments (Oscilloscope / Multimeter)", "EMC / EMI Basics", "Test & Validation",
  ]},
  { keywords: ["chemical engineer"], skills: [
    "Process Design", "Aspen Plus / HYSYS", "Mass & Energy Balances",
    "Heat Transfer", "Reaction Kinetics", "Safety & HAZOP",
    "Distillation & Separations", "P&ID Reading", "Process Control",
    "Lab Skills", "Material Balances", "Plant Operations",
  ]},
  { keywords: ["aerospace engineer"], skills: [
    "Aerodynamics", "Propulsion Systems", "CATIA / NX",
    "CFD (ANSYS Fluent)", "Materials & Composites", "Flight Mechanics",
    "Structural Analysis", "FAA / EASA Standards", "Wind Tunnel Testing Basics",
    "Systems Engineering", "MATLAB", "Avionics Basics",
  ]},
  { keywords: ["biomedical engineer"], skills: [
    "Medical Device Design", "FDA Regulations (510k)", "ISO 13485",
    "MATLAB / LabVIEW", "CAD (SolidWorks)", "Biomaterials",
    "Signal Processing", "Anatomy & Physiology Basics", "Verification & Validation",
    "Risk Management (ISO 14971)", "Clinical Trials Basics", "Sterilization",
  ]},
  { keywords: ["architect"], skills: [
    "AutoCAD", "Revit / BIM", "SketchUp", "Building Codes & Zoning",
    "Sustainable / LEED Design", "Construction Documents", "Project Management",
    "Rendering (Lumion / Enscape)", "Site Analysis", "Design Concept Development",
    "Specification Writing", "Client Presentations",
  ]},

  // ─── Media ───
  { keywords: ["journalist", "reporter"], skills: [
    "AP Style", "Investigative Reporting", "Interviewing Sources",
    "Source Development", "Fact-Checking", "News Writing & Leads",
    "Multimedia Storytelling", "FOIA / Public Records", "Social Media for Reporters",
    "Beat Reporting", "Deadline Pressure", "Ethics in Journalism",
  ]},
  { keywords: ["news editor", "editor"], skills: [
    "Editorial Judgment", "AP Style", "Headline & Lede Writing",
    "Content Curation", "Fact-Checking", "Team Management",
    "CMS Tools", "Breaking News Workflow", "SEO for News",
    "Photo & Caption Editing", "Style Guides", "Audience Analytics",
  ]},
  { keywords: ["pr specialist", "public relations", "publicist"], skills: [
    "Press Release Writing", "Media Relations", "Crisis Communication",
    "Pitch Writing", "Brand Voice", "Event & Press Conference Management",
    "Media Monitoring (Cision / Meltwater)", "Spokesperson Coaching",
    "Influencer Outreach", "Media Lists", "Award Submissions", "Reporting & Analytics",
  ]},
  { keywords: ["communications manager", "corporate communications"], skills: [
    "Internal Communications", "External / Press Relations", "Executive Communications",
    "Brand Voice & Messaging", "Crisis Management", "Content Strategy",
    "Stakeholder Mapping", "Change Communications", "Editorial Planning",
    "Channel Strategy (Email / Intranet)", "Town Hall Production", "Reputation Management",
  ]},
  { keywords: ["video producer", "videographer"], skills: [
    "Premiere Pro", "Final Cut Pro", "DaVinci Resolve", "Storyboarding",
    "Lighting", "Cinematography", "Sound Design", "Color Grading",
    "Project Management", "Camera Operation", "Pre-production Planning", "Distribution & Delivery",
  ]},
  { keywords: ["podcast producer"], skills: [
    "Audio Editing (Audition / Pro Tools / Descript)", "Interview Skills",
    "Show Production & Booking", "Distribution Platforms (Spotify / Apple)",
    "Sound Design & Music Beds", "Marketing & Promotion",
    "Show Notes Writing", "Episode Structuring", "Remote Recording (Riverside)",
    "Listener Analytics", "Sponsorship Coordination", "Brand Voice",
  ]},
  { keywords: ["technical writer"], skills: [
    "Documentation Tools (Confluence / GitBook / Markdown)", "API Documentation",
    "User Guides & Tutorials", "Information Architecture", "Editing & Proofreading",
    "DITA / Structured Authoring", "Version Control (Git)", "Diagrams (Mermaid / Lucid)",
    "Style Guides", "Audience Analysis", "SME Interviewing", "Docs-as-Code",
  ]},

  // ─── Science / Research ───
  { keywords: ["research scientist", "scientific researcher"], skills: [
    "Experimental Design", "Statistical Analysis", "Lab Techniques",
    "Scientific Writing", "Grant Writing", "Literature Review",
    "Peer Review", "Data Analysis (R / Python)", "Conference Presentations",
    "Hypothesis Testing", "Reproducibility & Documentation", "Cross-disciplinary Collaboration",
  ]},
  { keywords: ["biologist"], skills: [
    "Cell Culture", "PCR & qPCR", "Microscopy",
    "Molecular Biology Techniques", "Lab Safety", "Data Analysis",
    "Scientific Writing", "Western Blot / ELISA", "DNA / RNA Extraction",
    "Sterile Technique", "Statistical Software", "Documentation",
  ]},
  { keywords: ["chemist", "chemistry"], skills: [
    "Spectroscopy (NMR / IR / MS)", "Chromatography (HPLC / GC)",
    "Synthesis Techniques", "Lab Safety (MSDS)", "Analytical Chemistry",
    "Data Analysis", "Method Development", "Quality Control",
    "GLP / GMP", "Scientific Writing", "Inventory & Reagent Management", "Instrument Calibration",
  ]},
  { keywords: ["statistician"], skills: [
    "R", "Python", "Statistical Modeling", "Hypothesis Testing",
    "Bayesian Methods", "SAS", "Experimental Design", "Data Visualization",
    "Causal Inference", "Survey Methodology", "Time Series Analysis", "Regression & GLMs",
  ]},

  // ─── Generic software-engineer fallback (must come last among tech) ───
  { keywords: ["software engineer", "software developer", "swe", "programmer"], skills: [
    "JavaScript / TypeScript", "Python", "Java", "Data Structures & Algorithms",
    "System Design", "REST APIs", "SQL Databases", "Git & Version Control",
    "Testing (Unit / Integration)", "Code Review", "Agile / Scrum", "Debugging & Problem Solving",
  ]},
];

const matchSkillsToRole = (typedRole) => {
  if (!typedRole) return null;
  const t = typedRole.trim().toLowerCase();
  if (!t) return null;
  for (const entry of SKILLS_BY_ROLE) {
    for (const kw of entry.keywords) {
      if (t.includes(kw)) return entry.skills;
    }
  }
  return null;
};

// Precomputed exact-match index: lowercased curated role → its category.
// Lets `getCategoryForRole` resolve every curated role deterministically,
// instead of falling back to first-substring-hit which mis-routed roles
// like "Director of Engineering Operations" into Tech.
const ROLE_TO_CATEGORY = (() => {
  const map = new Map();
  for (const [cat, roles] of Object.entries(ROLE_CATEGORIES)) {
    for (const r of roles) map.set(r.toLowerCase(), cat);
  }
  return map;
})();

const getCategoryForRole = (typedRole) => {
  if (!typedRole) return null;
  const t = typedRole.trim().toLowerCase();
  if (!t) return null;
  // 1. Exact match — every curated role in ROLE_CATEGORIES lands here.
  const exact = ROLE_TO_CATEGORY.get(t);
  if (exact) return exact;
  // 2. Free-typed fallback: containment match, but pick the LONGEST match
  //    instead of the first one in iteration order. Avoids "Director of
  //    Engineering" beating "Director of Engineering Operations" just
  //    because Tech happens to be iterated before Engineering.
  let bestCat = null;
  let bestLen = 0;
  for (const [cat, roles] of Object.entries(ROLE_CATEGORIES)) {
    for (const r of roles) {
      const rl = r.toLowerCase();
      if (rl.includes(t) || t.includes(rl)) {
        const len = Math.min(rl.length, t.length);
        if (len > bestLen) { bestLen = len; bestCat = cat; }
      }
    }
  }
  return bestCat;
};

const getSkillsForRole = (typedRole) => {
  // Prefer role-specific skills (e.g. Frontend gets React/CSS,
  // Data Engineer gets Spark/Airflow). Fall back to category, then generic.
  const roleSpecific = matchSkillsToRole(typedRole);
  if (roleSpecific) return roleSpecific;
  const cat = getCategoryForRole(typedRole);
  return cat ? SKILLS_BY_CATEGORY[cat] : GENERIC_SKILLS;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const readFileAsBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = () => rej(new Error("Read failed"));
    r.readAsDataURL(file);
  });

const getVoicesAsync = () => new Promise((resolve) => {
  const synth = window.speechSynthesis;
  if (!synth) return resolve([]);
  const v = synth.getVoices();
  if (v && v.length) return resolve(v);
  let done = false;
  const finish = () => { if (done) return; done = true; resolve(synth.getVoices() || []); };
  synth.addEventListener("voiceschanged", finish, { once: true });
  setTimeout(finish, 1200);
});

const QUALITY_KEYWORDS = ["Natural", "Online", "Neural", "Premium", "Enhanced"];

const pickVoice = (voices, interviewer) => {
  let best = null;
  let bestScore = -1;
  for (const v of voices) {
    if (!v.lang || !v.lang.toLowerCase().startsWith("en")) continue;
    const matchIdx = interviewer.voiceMatchers.findIndex((m) => v.name.includes(m));
    if (matchIdx === -1) continue;
    let score = 200 - matchIdx * 5;
    if (QUALITY_KEYWORDS.some((q) => v.name.includes(q))) score += 80;
    if (v.localService === false) score += 10; // remote/cloud voices are usually higher-fidelity
    if (score > bestScore) { bestScore = score; best = v; }
  }
  return best;
};

const splitIntoChunks = (text) => {
  // Split on sentence boundaries to avoid iOS truncation and mid-sentence cutoffs.
  const sentences = text.match(/[^.!?\n]+[.!?]+|\S[^.!?\n]*\S?/g) || [text];
  // Re-merge tiny fragments and break overly long ones (>180 chars) at commas.
  const chunks = [];
  for (let s of sentences) {
    s = s.trim();
    if (!s) continue;
    if (s.length <= 180) {
      chunks.push(s);
    } else {
      const parts = s.split(/,\s+/);
      let buf = "";
      for (const p of parts) {
        if ((buf + ", " + p).length > 180 && buf) {
          chunks.push(buf);
          buf = p;
        } else {
          buf = buf ? buf + ", " + p : p;
        }
      }
      if (buf) chunks.push(buf);
    }
  }
  return chunks;
};

// Module-level cancellation token. Each speak() call captures the current
// generation; stopAllSpeech() bumps it so any in-flight chunked utterance
// stops queueing the next chunk. Without this, synth.cancel() fires onend
// on the current utterance, which then queues the next one and resumes.
let __speakGen = 0;
const stopAllSpeech = () => {
  __speakGen++;
  try { window.speechSynthesis?.cancel(); } catch {}
};

const speak = async (text, onEnd, interviewerId) => {
  if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) {
    if (onEnd) onEnd();
    return;
  }
  const synth = window.speechSynthesis;
  const interviewer = INTERVIEWERS.find((i) => i.id === interviewerId) || INTERVIEWERS[0];
  try { synth.cancel(); } catch {}

  const voices = await getVoicesAsync();
  const voice = pickVoice(voices, interviewer);
  const chunks = splitIntoChunks(text);

  const myGen = ++__speakGen; // claim this generation
  const isStillCurrent = () => myGen === __speakGen;

  // Workaround for Chrome 15-second cutoff: nudge the synthesizer periodically.
  const keepAlive = setInterval(() => {
    if (!isStillCurrent()) { clearInterval(keepAlive); return; }
    try { if (synth.speaking) { synth.pause(); synth.resume(); } } catch {}
  }, 9000);

  let cancelled = false;
  const cleanup = () => {
    if (cancelled) return;
    cancelled = true;
    clearInterval(keepAlive);
    // CRITICAL for iOS: explicitly release the SpeechSynthesis audio
    // session ASAP after a natural TTS completion. Without this, iOS
    // leaves the audio session category in playback-only mode for an
    // observed 1-2 MINUTES before it times out on its own — which
    // completely blocks any subsequent getUserMedia call from delivering
    // mic frames. Real-device testing showed that between-answers
    // Record taps fail for 20+ seconds (even after a fresh lock+wait)
    // when the previous AI question was played via speak(), because
    // the session is stuck in playback mode and no amount of mic-side
    // recovery can override it. Calling cancel() here releases the
    // session immediately so iOS can return to play-and-record mode
    // by the time the user finishes reading the question and taps
    // Record. Safe on Chrome/Firefox (no-op when nothing is pending).
    //
    // Guarded by isStillCurrent(): a stale-generation cleanup (e.g.,
    // user tapped Replay while this generation was winding down, so
    // a newer speak() call has already bumped __speakGen) must NOT
    // call the global synth.cancel() — that would kill the newer
    // generation's in-flight utterance. Only release the session when
    // we're the current owner.
    if (isStillCurrent()) {
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch {}
    }
    if (onEnd) onEnd();
  };

  let i = 0;
  const speakNext = () => {
    if (cancelled) return;
    if (!isStillCurrent()) { cleanup(); return; } // someone called stopAllSpeech
    if (i >= chunks.length) { cleanup(); return; }
    const utt = new SpeechSynthesisUtterance(chunks[i++]);
    utt.lang = "en-US";
    utt.rate = 1.0;
    utt.pitch = interviewer.pitch;
    utt.volume = 1;
    if (voice) utt.voice = voice;
    utt.onend = speakNext;
    utt.onerror = cleanup;
    try { synth.speak(utt); } catch { cleanup(); }
  };
  speakNext();
};

const unlockSpeech = () => {
  if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) return;
  try {
    window.speechSynthesis.resume();
    const utt = new SpeechSynthesisUtterance(".");
    utt.volume = 0.01;
    utt.rate = 10;
    window.speechSynthesis.speak(utt);
  } catch {}
};

// ─── System Prompt ────────────────────────────────────────────────────────────

const buildSystemPrompt = ({ role, experience, tone, resumeText, resumeInsights, interviewerName, skills, jobDescription }) => {
  const toneMap = {
    encouraging: "warm, supportive and encouraging — celebrate good answers, gently correct mistakes, motivate the candidate",
    professional: "professional and neutral — like a senior corporate interviewer, fair but demanding",
    tough: "challenging and direct — push back on vague answers, ask difficult follow-ups, simulate high-pressure interviews",
  };

  const name = interviewerName || "Alice";

  const resumeSection = resumeText
    ? `The candidate has uploaded their resume:\n---\n${resumeText}\n---\n${resumeInsights ? `Key observations: ${resumeInsights}` : ""}\nUse the resume to ask highly specific questions about their actual projects, skills, gaps, and career transitions.`
    : `No resume uploaded. Ask general questions appropriate for a ${role || "general"} role at ${experience || "mid"} level.`;

  const skillsSection = skills && skills.length
    ? `\nThe candidate wants to be tested specifically on these skills/areas: ${skills.join(", ")}. Make sure several of your role-specific questions probe these directly.`
    : "";

  const cleanJd = (jobDescription || "").trim();
  const jobDescriptionSection = cleanJd
    ? `\nThe candidate is interviewing for THIS specific position. Job description below:\n---\n${cleanJd.slice(0, 5000)}\n---\nWeave the job description into your questions: probe the exact responsibilities, skills, and qualifications it lists. ${resumeText ? "Identify gaps between the candidate's resume and what this JD asks for, then ask how they would bridge them. Where their resume strongly matches the JD, ask for concrete examples that prove the match." : "Anchor your questions to the specific responsibilities and skills the JD lists."}`
    : "";

  return `You are an expert AI interview coach named "${name}" conducting a realistic mock interview.
Tone: ${toneMap[tone] || toneMap.professional}.
Role: ${role || "General / Open Role"}
Experience level: ${experience || "Not specified"}
${skillsSection}
${resumeSection}
${jobDescriptionSection}

INTERVIEW FLOW (follow strictly in this order):
1. FIRST MESSAGE: Briefly introduce yourself as ${name} (1 sentence), mention the ${role || "role"} interview${cleanJd ? " and that you've reviewed the job description" : ""}, then ask ONLY a warm opening question to get to know the candidate — e.g. "To start, could you tell me a little about yourself and what brought you to this point in your career?". Do NOT ask any skills, technical, or behavioral questions yet.
2. SECOND MESSAGE: Acknowledge their intro in one short sentence, then ask one follow-up about their background, motivation, or what excites them about this role.
3. FROM THE THIRD MESSAGE ONWARDS: Begin role-specific questions. Mix skills/technical, behavioral (STAR), situational, and career-goal questions. ${resumeText && cleanJd ? "Anchor questions to BOTH the candidate's resume AND the job description — favor questions that test the overlap or expose meaningful gaps." : resumeText ? "Anchor questions to specific items from their resume (projects, employers, skills, gaps) wherever possible." : cleanJd ? "Anchor questions to the responsibilities and qualifications listed in the job description." : "Tailor difficulty to their stated experience level."}
4. After each user answer, decide whether a targeted follow-up is needed. If vague or interesting, ask ONE follow-up. If sufficient, move on.

RULES:
- Ask exactly ONE question per message.
- Keep each response to 2–4 short sentences.
- Stay in character as "${name}" throughout. Never break character or mention you are an AI.
- Do NOT give a final summary or rating yourself — the app handles end-of-session evaluation separately.`;
};

// ─── Evaluation Prompts (parallelised) ───────────────────────────────────────
// We split the old single mega-prompt into 5 smaller prompts that run in
// parallel. Each call returns a much smaller JSON, the model spends less time
// generating tokens per call, and the wall-clock time drops to roughly the
// slowest single call rather than the sum of everything. Quality is preserved
// because each prompt keeps the same depth requirements (specific quotes,
// 2–4-sentence dimension feedback, weighted scoring), it's just narrower.

const CATEGORIES_FOR_EVAL = [
  { name: "Communication Quality", weight: 1.0, dims: ["Verbal Clarity", "Answer Relevance", "Conciseness"] },
  { name: "Thinking Quality", weight: 1.2, dims: ["Critical Thinking", "STAR Structure", "Examples and Metrics"] },
  { name: "Professional Presence", weight: 1.0, dims: ["Confidence and Composure", "Enthusiasm and Motivation", "Professional Language"] },
  { name: "Role Specific Performance", weight: 1.2, dims: ["Technical Depth", "Role-specific Knowledge", "Situational Judgement"] },
];

// ─── Time-pacing instruction injected into the live interview prompt ───────
// The interviewer should be aware of how much time is left and behave like a
// real interviewer would: in a 60-min slot you don't open a brand-new
// problem at minute 58. We append a one-line guidance to the system prompt
// on every candidate-turn so the model has live awareness, not stale advice
// from the start of the session.
//
// Returns "" when there's plenty of time so we don't pollute the prompt or
// nudge the model toward closure too early.
const buildTimeAwarenessNote = (secondsRemaining) => {
  const s = Number(secondsRemaining);
  if (!isFinite(s) || s == null || s > 5 * 60) return "";

  // ≤ 30s: hard "no more questions" — give a short closing instead.
  if (s <= 30) {
    return `

LIVE TIMING (read carefully): only ~${Math.max(0, Math.round(s))} seconds remain in this interview. DO NOT ask a new question. DO NOT start a new topic. THIS OVERRIDES THE NORMAL "ASK ONE QUESTION PER MESSAGE" RULE FOR THIS TURN ONLY. Instead, give a brief warm closing remark — under 25 words spoken — that:
- thanks the candidate for the conversation,
- names ONE specific thing that stood out (positive or area to grow), and
- mentions their detailed feedback will be ready momentarily.
End with a friendly sign-off. That's it. No follow-up.`;
  }

  // 30s – 2 min: this should be the LAST question. Make it small + closing.
  if (s <= 2 * 60) {
    return `

LIVE TIMING: only ~${Math.round(s / 30) * 30 / 60 < 1 ? `${Math.round(s)} seconds` : `${Math.ceil(s / 60)} minute(s)`} remain. Ask AT MOST one more short, focused question — something the candidate can realistically answer in 60–90 seconds. Don't open a new deep topic. After this answer you'll be wrapping up.`;
  }

  // 2 – 5 min: start steering toward a close.
  return `

LIVE TIMING: ~${Math.ceil(s / 60)} minutes remain. Begin steering toward a close — ask only 1–2 more questions, ideally tying back to themes you've already covered rather than opening brand-new areas.`;
};

// ─── Duration-aware scoring policy ──────────────────────────────────────────
// Real interviewers weight signal by how much the candidate said over how
// long. No experienced interviewer makes a hire/no-hire call in under 5
// minutes, and even a 10-minute exchange can only sketch a candidate's
// shape — so short sessions are hard-capped, otherwise the app produces
// falsely confident numbers.
//
// Bands (closed-open intervals):
//   <  5min → "Insufficient signal"  — hard cap 2.0/10
//   < 10min → "Very brief"           — hard cap 4.0/10
//   < 15min → "Brief"                — hard cap 6.5/10
//   < 25min → "Standard"             — no cap
//   ≥ 25min → "Deep practice"        — no cap, may credit stamina/range
const getDurationBand = (durationSeconds) => {
  const d = Number(durationSeconds) || 0;
  if (d < 300)       return { key: 'insufficient', label: 'Insufficient signal', cap: 2.0,  minutes: '< 5 min',   reason: 'no real interviewer can assess a candidate in under 5 minutes — the transcript is simply too short to demonstrate range, depth, or follow-up handling' };
  if (d < 600)       return { key: 'very_brief',   label: 'Very brief',          cap: 4.0,  minutes: '5–10 min',  reason: 'a 5–10 minute exchange shows only a sketch of the candidate — not enough sustained evidence to support higher scores' };
  if (d < 900)       return { key: 'brief',        label: 'Brief',               cap: 6.5,  minutes: '10–15 min', reason: 'a handful of answers with some depth, but limited breadth across topics' };
  if (d < 1500)      return { key: 'standard',     label: 'Standard',            cap: null, minutes: '15–25 min', reason: 'enough transcript to show range and depth' };
  return                     { key: 'deep',        label: 'Deep practice',       cap: null, minutes: '25+ min',   reason: 'extended practice — credit stamina and consistency' };
};

// Renders the duration policy block injected into both eval prompts so Sonnet
// applies the cap itself instead of us doing it post-hoc.
const buildDurationPolicyBlock = (durationSeconds) => {
  const band = getDurationBand(durationSeconds);
  const minutes = Math.floor((Number(durationSeconds) || 0) / 60);
  const seconds = Math.floor((Number(durationSeconds) || 0) % 60);
  const human = `${minutes}m ${seconds}s (~${band.minutes})`;
  const capLine = band.cap == null
    ? (band.key === 'deep'
        ? `No score cap — this is a deep practice session (25+ min). If the candidate sustained quality throughout, you may credit stamina and breadth.`
        : `No score cap — this session length is sufficient for a full evaluation.`)
    : `HARD CAP: every dimension score MUST be ≤ ${band.cap.toFixed(1)} regardless of how good the individual answers were. Reason: ${band.reason}. A handful of strong answers in a short session is still only a sketch of the candidate — real interviewers do not make hire/no-hire calls on this little signal.`;
  return `DURATION & SIGNAL POLICY (apply BEFORE finalising any score):
- This session ran for ${human} — band: "${band.label}".
- ${capLine}
- When applying a cap, also reflect this honestly in feedback strings — say something like "with only a few minutes of conversation we can't really see how you handle X" rather than pretending you have full evidence.`;
};

const buildCategoryEvalPrompt = (category, role, experience, interviewerName, durationSeconds) => `You are an expert interview evaluator. Review the entire conversation between "${interviewerName || "Alice"}" (interviewer) and the candidate for a ${role || "general"} role at ${experience || "unspecified"} level.

You are scoring ONLY the "${category.name}" category right now, which has these 3 dimensions:
${category.dims.map(d => `- ${d}`).join("\n")}

Return ONLY valid JSON (no markdown, no commentary, no code fences) in this exact shape:
{
  "name": "${category.name}",
  "dimensions": [
${category.dims.map(d => `    { "name": "${d}", "score": <0.0-10.0>, "feedback": "<2-4 sentence detailed assessment citing specific moments from the transcript>" }`).join(",\n")}
  ]
}

SCORING RULES:
- Scores are on a 0.0–10.0 scale. Use ONE decimal where it adds nuance (e.g. 8.5, 6.5, 4.5) and integers where they fit naturally (e.g. 7, 9). Do NOT clamp everything to integers — the half-point granularity matters.
- Be honest. If the candidate barely engaged on a dimension, score it low (1–3) and say why kindly.

${buildDurationPolicyBlock(durationSeconds)}

CONTENT RULES:
- Every "feedback" string must reference SPECIFIC moments from the transcript — quote phrases the candidate actually said when possible. Generic advice is forbidden.
- If the candidate said almost nothing relevant to a dimension, score it low and the feedback should kindly note the lack of evidence and suggest what to demonstrate next time.
- Tone: friendly, warm, motivating, honest. Treat the candidate as someone you want to help grow.`;

const buildMetaEvalPrompt = ({ role, experience, interviewerName, durationSeconds }) => `You are an expert interview evaluator. Review the entire conversation between "${interviewerName || "Alice"}" (interviewer) and the candidate for a ${role || "general"} role at ${experience || "unspecified"} level.

${buildDurationPolicyBlock(durationSeconds)}

Return ONLY valid JSON (no markdown, no commentary, no code fences) in this exact shape:
{
  "headline": "<one warm, motivating sentence summing up the session>",
  "fillerWords": {
    "total": <integer count of all filler words detected in the candidate's transcript>,
    "breakdown": [
      { "word": "um", "count": <integer> },
      { "word": "uh", "count": <integer> },
      { "word": "like", "count": <integer> },
      { "word": "you know", "count": <integer> },
      { "word": "basically", "count": <integer> }
    ],
    "comment": "<1-2 sentences interpreting the count for this candidate — e.g. minimal and natural, or distracting>"
  },
  "bestAnswer": {
    "questionSummary": "<short paraphrase of the question they answered best>",
    "why": "<2-3 sentences explaining what made this answer stand out — be specific>"
  },
  "weakestAnswer": {
    "questionSummary": "<short paraphrase of the question they struggled with most>",
    "why": "<2-3 sentences on exactly what was missing or wrong — direct, actionable, kind>"
  },
  "topPriorities": [
    "<priority #1 — the single most impactful thing to work on before the next interview, concrete and specific>",
    "<priority #2 — second most impactful, concrete>",
    "<priority #3 — third, concrete>"
  ],
  "readinessVerdict": {
    "readyFor": "<the level/scope of role this candidate could realistically interview for today, e.g. 'Junior Software Engineer roles at small-to-mid companies'>",
    "notReadyFor": "<the level/scope they are NOT yet ready for, e.g. 'Senior or Staff Engineer roles, or any role requiring system design depth'>",
    "keyBlocker": "<the single biggest gap holding them back from the next level, in one specific sentence>"
  }
}

CONTENT RULES:
- For fillerWords.breakdown: count ONLY occurrences in the candidate's lines, not the interviewer's. If a word never appears, set its count to 0 (do not omit the entry).
- bestAnswer and weakestAnswer must reference real questions from this transcript. If only one substantive answer exists, use it for bestAnswer and explain that the candidate didn't engage enough elsewhere in weakestAnswer.
- topPriorities is a RANKED list of 3 — most impactful first. Each must be concrete (e.g. "Practice 5 STAR-format answers about cross-team conflict using your real Acme project as the situation") not generic ("improve your communication").
- readinessVerdict.keyBlocker is the ONE specific gap most worth fixing — name it precisely.
- Tone: friendly, warm, motivating, honest. Treat the candidate as someone you want to help grow.`;

// Legacy single-prompt evaluator — preserved only as a reference; no longer
// used at runtime. Kept for one release in case we need to A/B compare quality
// against the parallel implementation.
const buildEvaluationPrompt = ({ role, experience, interviewerName }) => `You are an expert interview evaluator. Review the entire conversation between "${interviewerName || "Alice"}" (interviewer) and the candidate for a ${role || "general"} role at ${experience || "unspecified"} level.

Return ONLY valid JSON (no markdown, no commentary, no code fences) in this exact shape:
{
  "overallScore": <number 0.0-10.0, one decimal place allowed>,
  "readiness": "<one of: Beginner | Developing | Interview Ready | Strong Candidate>",
  "headline": "<one warm, motivating sentence summing up the session>",
  "categories": [
    {
      "name": "Communication Quality",
      "dimensions": [
        { "name": "Verbal Clarity", "score": <0.0-10.0>, "feedback": "<2-4 sentence detailed assessment citing specific moments from the transcript>" },
        { "name": "Answer Relevance", "score": <0.0-10.0>, "feedback": "<...>" },
        { "name": "Conciseness", "score": <0.0-10.0>, "feedback": "<...>" }
      ]
    },
    {
      "name": "Thinking Quality",
      "dimensions": [
        { "name": "Critical Thinking", "score": <0.0-10.0>, "feedback": "<...>" },
        { "name": "STAR Structure", "score": <0.0-10.0>, "feedback": "<...>" },
        { "name": "Examples and Metrics", "score": <0.0-10.0>, "feedback": "<...>" }
      ]
    },
    {
      "name": "Professional Presence",
      "dimensions": [
        { "name": "Confidence and Composure", "score": <0.0-10.0>, "feedback": "<...>" },
        { "name": "Enthusiasm and Motivation", "score": <0.0-10.0>, "feedback": "<...>" },
        { "name": "Professional Language", "score": <0.0-10.0>, "feedback": "<...>" }
      ]
    },
    {
      "name": "Role Specific Performance",
      "dimensions": [
        { "name": "Technical Depth", "score": <0.0-10.0>, "feedback": "<...>" },
        { "name": "Role-specific Knowledge", "score": <0.0-10.0>, "feedback": "<...>" },
        { "name": "Situational Judgement", "score": <0.0-10.0>, "feedback": "<...>" }
      ]
    }
  ],
  "fillerWords": {
    "total": <integer count of all filler words detected in the candidate's transcript>,
    "breakdown": [
      { "word": "um", "count": <integer> },
      { "word": "uh", "count": <integer> },
      { "word": "like", "count": <integer> },
      { "word": "you know", "count": <integer> },
      { "word": "basically", "count": <integer> }
    ],
    "comment": "<1-2 sentences interpreting the count for this candidate — e.g. minimal and natural, or distracting>"
  },
  "bestAnswer": {
    "questionSummary": "<short paraphrase of the question they answered best>",
    "why": "<2-3 sentences explaining what made this answer stand out — be specific>"
  },
  "weakestAnswer": {
    "questionSummary": "<short paraphrase of the question they struggled with most>",
    "why": "<2-3 sentences on exactly what was missing or wrong — direct, actionable, kind>"
  },
  "topPriorities": [
    "<priority #1 — the single most impactful thing to work on before the next interview, concrete and specific>",
    "<priority #2 — second most impactful, concrete>",
    "<priority #3 — third, concrete>"
  ],
  "readinessVerdict": {
    "readyFor": "<the level/scope of role this candidate could realistically interview for today, e.g. 'Junior Software Engineer roles at small-to-mid companies'>",
    "notReadyFor": "<the level/scope they are NOT yet ready for, e.g. 'Senior or Staff Engineer roles, or any role requiring system design depth'>",
    "keyBlocker": "<the single biggest gap holding them back from the next level, in one specific sentence>"
  }
}

SCORING RULES:
- All 12 dimension scores are on a 0.0–10.0 scale. Use ONE decimal where it adds nuance (e.g. 8.5, 6.5, 4.5) and integers where they fit naturally (e.g. 7, 9). Do NOT clamp everything to integers — the half-point granularity matters.
- Be honest. If the candidate barely engaged on a dimension, score it low (1–3) and say why kindly.
- The overallScore is a weighted feel of all 12 dimensions (one decimal). Role Specific Performance and Thinking Quality should weigh slightly more than Communication and Presence for technical roles.
- Map readiness honestly: Beginner (0–3), Developing (3.1–5.5), Interview Ready (5.6–7.9), Strong Candidate (8.0–10).

CONTENT RULES:
- Every "feedback" string must reference SPECIFIC moments from the transcript — quote phrases the candidate actually said when possible. Generic advice is forbidden.
- For fillerWords.breakdown: count ONLY occurrences in the candidate's lines, not the interviewer's. If a word never appears, set its count to 0 (do not omit the entry).
- bestAnswer and weakestAnswer must reference real questions from this transcript. If only one substantive answer exists, use it for bestAnswer and explain that the candidate didn't engage enough elsewhere in weakestAnswer.
- topPriorities is a RANKED list of 3 — most impactful first. Each must be concrete (e.g. "Practice 5 STAR-format answers about cross-team conflict using your real Acme project as the situation") not generic ("improve your communication").
- readinessVerdict.keyBlocker is the ONE specific gap most worth fixing — name it precisely.
- Tone: friendly, warm, motivating, honest. Treat the candidate as someone you want to help grow.
- If the candidate said almost nothing in a dimension, score it low, and the feedback should kindly note the lack of evidence and suggest what to demonstrate next time.`;

// ─── AI API (calls our secure backend — key never exposed) ───────────────────

const API_BASE = process.env.REACT_APP_API_URL || "";

// Pull user_id from the in-memory Supabase session (no network). Returns null
// if the caller is signed-out. Used purely for cost attribution — never for
// authorization. Best-effort: any failure swallows so chat keeps working.
const getCurrentUserIdForCostAttribution = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id || null;
  } catch {
    return null;
  }
};

// callClaude takes an optional `opts` for cost attribution:
//   { sessionId?: uuid|null, callKind?: 'chat'|'eval'|'resume_parse'|'weekly_note'|... }
// These are forwarded to /api/chat which logs the response's token usage into
// public.claude_token_usage. They have no effect on the AI behaviour.
const callClaude = async (messages, systemPrompt, maxTokens, opts = {}) => {
  const userId = await getCurrentUserIdForCostAttribution();
  const res = await fetch(`${API_BASE}/api/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      messages,
      system: systemPrompt,
      // Optional stable prefix for Anthropic prompt caching. When provided,
      // chat.js sends `system` as a two-block array with cache_control on
      // this prefix. Used by the per-turn chat call where the base system
      // prompt is immutable but a small pacing note is appended each turn.
      system_cacheable: opts.cacheableSystem || undefined,
      max_tokens: maxTokens,
      session_id: opts.sessionId || null,
      user_id: userId,
      call_kind: opts.callKind || 'chat',
    }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  return data.content.map((b) => b.text || "").join("");
};

// ─── Global Styles ────────────────────────────────────────────────────────────

function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;0,700;1,400&display=swap');
      /* Reset is scoped to our React tree (#root) so it can't clobber
         third-party widgets that inject their own DOM into <body>
         (e.g. the Termly consent banner). body keeps a margin:0 reset
         to override the browser default 8px gutter, and #root carries
         the horizontal-overflow guard that prevents the landing page
         from spilling past mobile viewports. */
      body, html { margin: 0; padding: 0; }
      #root, #root * { box-sizing: border-box; margin: 0; padding: 0; min-width: 0; }
      #root { max-width: 100vw; overflow-x: hidden; }

      /* Native <select> dropdown options can't be styled per-element across
         browsers — only via the parent <select>'s color/background, which
         most browsers inherit into the open list. Setting it on <option>
         too keeps Firefox/Safari aligned with Chrome and stops the
         white-on-white in dark mode for the support form's Topic dropdown. */
      select, option {
        background-color: var(--bg);
        color: var(--text-strong);
      }

      :root[data-theme="dark"] {
        --bg: #070c18;
        --header-bg: rgba(7,12,24,0.96);
        --input-bg: rgba(7,12,24,0.55);
        --card: rgba(255,255,255,0.032);
        --surface: rgba(255,255,255,0.035);
        --surface-2: rgba(255,255,255,0.06);
        --surface-3: rgba(255,255,255,0.09);
        --surface-strong: rgba(255,255,255,0.12);
        --border-soft: rgba(255,255,255,0.07);
        --border: rgba(255,255,255,0.12);
        --text-strong: #f8fafc;
        --text: #e2e8f0;
        --text-muted: #cbd5e1;
        --text-faint: #b6c2d6;
        --text-subtle: #a3afc4;
        --text-dim: #94a3b8;
        --text-dimmer: #8493aa;
        --text-darker: #74849c;
        --scroll-thumb: #1e293b;
      }
      :root[data-theme="light"] {
        --bg: #f8fafc;
        --header-bg: rgba(248,250,252,0.96);
        --input-bg: rgba(255,255,255,0.85);
        --card: rgba(255,255,255,0.85);
        --surface: rgba(15,23,42,0.04);
        --surface-2: rgba(15,23,42,0.06);
        --surface-3: rgba(15,23,42,0.08);
        --surface-strong: rgba(15,23,42,0.12);
        --border-soft: rgba(15,23,42,0.09);
        --border: rgba(15,23,42,0.14);
        --text-strong: #0f172a;
        --text: #1e293b;
        --text-muted: #1e293b;
        --text-faint: #1e293b;
        --text-subtle: #334155;
        --text-dim: #334155;
        --text-dimmer: #475569;
        --text-darker: #475569;
        --scroll-thumb: #cbd5e1;
      }

      body { background: var(--bg); position: relative; }
      /* Background is intentionally a single solid color (var(--bg)) in
         both themes — no grid, no dots, no radial glow. Per-phase wrappers
         already set background: var(--bg) inline, so they paint correctly
         before JS hydrates. */
      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: var(--scroll-thumb); border-radius: 99px; }
      @keyframes fadeUp {
        from { opacity: 0; transform: translateY(14px); }
        to   { opacity: 1; transform: none; }
      }
      @keyframes wave { from { height: 4px; } to { height: 38px; } }
      @keyframes blink { 0%,100%{ opacity:1 } 50%{ opacity:0.25 } }
      @keyframes ripple {
        0%   { transform: scale(1); opacity: 0.6; }
        100% { transform: scale(2); opacity: 0; }
      }
      @keyframes glow {
        0%,100% { box-shadow: 0 0 28px rgba(249,115,22,0.45); }
        50%     { box-shadow: 0 0 48px rgba(249,115,22,0.75); }
      }
    `}</style>
  );
}

// ─── Waveform ─────────────────────────────────────────────────────────────────

const Waveform = ({ isActive }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 3, height: 44 }}>
    {Array.from({ length: 32 }).map((_, i) => (
      <div key={i} style={{
        width: 3, borderRadius: 99,
        background: isActive ? `hsl(${18 + i * 1.5}, 88%, 58%)` : "var(--surface-3)",
        animation: isActive ? `wave ${0.45 + (i % 6) * 0.12}s ease-in-out infinite alternate` : "none",
        animationDelay: `${(i * 55) % 580}ms`,
        minHeight: 4, maxHeight: 40,
        height: isActive ? undefined : 5,
        transition: "background 0.4s",
      }} />
    ))}
  </div>
);

// ─── Chat Bubble ──────────────────────────────────────────────────────────────

const Bubble = ({ msg, isNew, avatar }) => {
  const isAI = msg.role === "assistant";
  return (
    <div style={{
      display: "flex", justifyContent: isAI ? "flex-start" : "flex-end",
      marginBottom: 18,
      animation: isNew ? "fadeUp 0.35s cubic-bezier(0.16,1,0.3,1) both" : "none",
    }}>
      {isAI && (
        <div style={{
          width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
          background: "linear-gradient(135deg,#f97316,#dc2626)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, color: "#fff",
          marginRight: 10, marginTop: 2,
          boxShadow: "0 4px 14px rgba(249,115,22,0.4)",
        }}>{avatar || "👩‍💼"}</div>
      )}
      <div style={{
        maxWidth: "74%", padding: "13px 18px", lineHeight: 1.68, fontSize: 14.5,
        borderRadius: isAI ? "4px 20px 20px 20px" : "20px 4px 20px 20px",
        background: isAI
          ? "var(--surface-2)"
          : "linear-gradient(135deg,#f97316,#dc2626)",
        color: "var(--text-strong)",
        border: isAI ? "1px solid var(--surface-3)" : "none",
        backdropFilter: isAI ? "blur(10px)" : "none",
        boxShadow: isAI ? "none" : "0 4px 22px rgba(249,115,22,0.32)",
        fontFamily: isAI ? "'Lora', serif" : "sans-serif",
      }}>
        {msg.content}
      </div>
    </div>
  );
};

// ─── Progress Dots ────────────────────────────────────────────────────────────

const ProgressDots = ({ current, total }) => (
  <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} style={{
        height: 6, borderRadius: 99,
        width: i === current ? 22 : 6,
        background: i <= current ? "#f97316" : "var(--surface-strong)",
        transition: "all 0.35s ease",
      }} />
    ))}
  </div>
);

// ─── Atom components ──────────────────────────────────────────────────────────

const Label = ({ children }) => (
  <p style={{
    color: "var(--text-subtle)", fontSize: 11, fontWeight: 700, letterSpacing: 1.3,
    textTransform: "uppercase", margin: "0 0 10px", fontFamily: "sans-serif",
  }}>{children}</p>
);

const Chip = ({ label, active, onClick, small }) => (
  <button onClick={onClick} style={{
    padding: small ? "5px 11px" : "7px 15px", borderRadius: 99,
    cursor: "pointer", transition: "all 0.15s", fontFamily: "sans-serif",
    border: `1.5px solid ${active ? "#f97316" : "var(--surface-3)"}`,
    background: active ? "rgba(249,115,22,0.14)" : "transparent",
    color: active ? "#f97316" : "var(--text-subtle)",
    fontSize: small ? 12 : 13,
  }}>{label}</button>
);

const NextBtn = ({ onClick, disabled, label }) => (
  <button onClick={onClick} disabled={disabled} style={{
    width: "100%", padding: "14px 0", borderRadius: 12, border: "none",
    background: disabled ? "var(--surface-2)" : "linear-gradient(135deg,#f97316,#dc2626)",
    color: disabled ? "var(--text-dimmer)" : "#fff",
    fontSize: 15, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "0 4px 24px rgba(249,115,22,0.3)",
    transition: "all 0.2s", fontFamily: "sans-serif",
    animation: disabled ? "none" : "glow 2.5s infinite",
  }}>{label}</button>
);

const StepShell = ({ icon, title, subtitle, children }) => (
  <div style={{ animation: "fadeUp 0.4s cubic-bezier(0.16,1,0.3,1) both" }}>
    <div style={{ textAlign: "center", marginBottom: 26 }}>
      <div style={{ fontSize: 38, marginBottom: 10 }}>{icon}</div>
      <h2 style={{ color: "var(--text-strong)", fontSize: 21, margin: "0 0 6px", fontWeight: 700, fontFamily: "'Lora', serif" }}>
        {title}
      </h2>
      <p style={{ color: "var(--text-dim)", fontSize: 13, margin: 0, fontFamily: "sans-serif" }}>{subtitle}</p>
    </div>
    {children}
  </div>
);

// Bottom action row: secondary Back on the left, primary Continue/Start on the right.
const StepActions = ({ onBack, onNext, nextLabel = "Continue →", nextDisabled = false, backLabel = "← Back" }) => (
  <div style={{ display: "flex", gap: 10, alignItems: "stretch", marginTop: 4 }}>
    {onBack && (
      <button onClick={onBack} style={{
        flex: "0 0 auto", padding: "14px 18px", borderRadius: 12,
        background: "var(--surface-2)", border: "1px solid var(--border-soft)",
        color: "var(--text-muted)", fontSize: 14, fontWeight: 600,
        cursor: "pointer", fontFamily: "sans-serif",
      }}>{backLabel}</button>
    )}
    <button
      onClick={onNext}
      disabled={nextDisabled}
      style={{
        flex: 1, padding: "14px 24px", borderRadius: 12,
        background: nextDisabled ? "var(--surface-2)" : "linear-gradient(135deg,#f97316,#dc2626)",
        border: "none", color: nextDisabled ? "var(--text-dimmer)" : "#fff",
        fontSize: 15, fontWeight: 700, cursor: nextDisabled ? "not-allowed" : "pointer",
        boxShadow: nextDisabled ? "none" : "0 4px 24px rgba(249,115,22,0.3)",
        transition: "all 0.2s", fontFamily: "sans-serif",
        animation: nextDisabled ? "none" : "glow 2.5s infinite",
      }}
    >{nextLabel}</button>
  </div>
);

// ─── Setup Steps ──────────────────────────────────────────────────────────────

const StepApiKey = ({ value, onChange, onNext }) => (
  <StepShell icon="🔑" title="Connect your API key" subtitle="Your key powers the interview engine — it stays in your browser only">
    <input
      type="password" placeholder="sk-ant-api03-…"
      value={value} onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%", padding: "13px 15px", borderRadius: 11, fontSize: 14,
        background: "var(--border-soft)", border: "1px solid var(--surface-strong)",
        color: "var(--text-strong)", outline: "none", letterSpacing: 2, marginBottom: 8,
        fontFamily: "monospace",
      }}
    />
    <p style={{ color: "var(--text-dimmer)", fontSize: 12, marginBottom: 20, fontFamily: "sans-serif", lineHeight: 1.6 }}>
      Get a free key at{" "}
      <a href="https://console.anthropic.com" target="_blank" rel="noreferrer"
        style={{ color: "#f97316", textDecoration: "none" }}>
        console.anthropic.com
      </a>
    </p>
    <NextBtn onClick={onNext} disabled={!value.startsWith("sk-")} label="Continue →" />
  </StepShell>
);

const StepRole = ({ role, setRole, experience, setExperience, skills, setSkills, jobDescription, setJobDescription, onNext, onBack }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSkills, setShowSkills] = useState(false);

  const suggestions = role.trim().length >= 1
    ? ALL_ROLES.filter((r) => r.toLowerCase().includes(role.trim().toLowerCase())).slice(0, 8)
    : [];

  const availableSkills = getSkillsForRole(role);
  const allSelected = availableSkills.length > 0 && availableSkills.every((s) => skills.includes(s));

  const toggleSkill = (s) => {
    setSkills(skills.includes(s) ? skills.filter((x) => x !== s) : [...skills, s]);
  };
  const toggleAll = () => {
    setSkills(allSelected ? [] : [...availableSkills]);
  };

  return (
    <StepShell icon="🎯" title="Target Role" subtitle="We'll tailor every question to this position" onBack={onBack}>
      <div style={{ marginBottom: 18 }}>
        <Label>Experience level</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {EXPERIENCE_LEVELS.map((lvl) => (
            <button key={lvl.id} onClick={() => setExperience(lvl.id)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "11px 14px",
              borderRadius: 11, cursor: "pointer", textAlign: "left", transition: "all 0.2s",
              background: experience === lvl.id ? "rgba(249,115,22,0.1)" : "var(--surface)",
              border: `1.5px solid ${experience === lvl.id ? "#f97316" : "var(--surface-3)"}`,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: experience === lvl.id ? "#f97316" : "var(--text-strong)", fontWeight: 600, fontSize: 14, fontFamily: "'Lora', serif" }}>
                  {lvl.label}
                </div>
                <div style={{ color: "var(--text-subtle)", fontSize: 12, fontFamily: "sans-serif", marginTop: 2 }}>{lvl.desc}</div>
              </div>
              {experience === lvl.id && <span style={{ color: "#f97316", fontSize: 16 }}>✓</span>}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 20, position: "relative" }}>
        <Label>Type the Role *</Label>
        <input
          type="text" placeholder="Start typing… e.g. Product Designer, Data Analyst, Nurse"
          value={role}
          onChange={(e) => { setRole(e.target.value); setShowSuggestions(true); setSkills([]); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          style={{
            width: "100%", padding: "11px 13px", borderRadius: 10, fontSize: 14,
            background: "var(--border-soft)", border: "1px solid var(--surface-strong)",
            color: "var(--text-strong)", outline: "none", fontFamily: "sans-serif",
            boxSizing: "border-box",
          }}
        />
        {showSuggestions && suggestions.length > 0 && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
            background: "var(--bg)", border: "1px solid var(--surface-strong)",
            borderRadius: 10, maxHeight: 240, overflowY: "auto", zIndex: 10,
            boxShadow: "0 12px 28px rgba(0,0,0,0.5)",
          }}>
            {suggestions.map((s) => (
              <button
                key={s}
                onMouseDown={(e) => { e.preventDefault(); setRole(s); setShowSuggestions(false); setSkills([]); }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "10px 13px", background: "transparent", border: "none",
                  color: "var(--text-strong)", fontSize: 13.5, cursor: "pointer", fontFamily: "sans-serif",
                  borderBottom: "1px solid var(--surface)",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(249,115,22,0.1)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >{s}</button>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 22 }}>
        <button
          type="button"
          onClick={() => setShowSkills(!showSkills)}
          style={{
            width: "100%", textAlign: "left", padding: "11px 14px", borderRadius: 10,
            background: "var(--surface)", border: "1px solid var(--surface-3)",
            color: "var(--text-muted)", fontSize: 13.5, cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "space-between", fontFamily: "sans-serif",
          }}
        >
          <span>
            🎯 Focus Areas <span style={{ color: "var(--text-subtle)", fontSize: 12, marginLeft: 6 }}>(optional)</span>
            {skills.length > 0 && (
              <span style={{
                marginLeft: 10, padding: "1px 8px", borderRadius: 99,
                background: "rgba(249,115,22,0.15)", color: "#f97316", fontSize: 11,
              }}>{skills.length} picked</span>
            )}
          </span>
          <span style={{ opacity: 0.55 }}>{showSkills ? "▲" : "▼"}</span>
        </button>
        {showSkills && (
          <div style={{ marginTop: 10, padding: "12px 4px 4px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ color: "var(--text-subtle)", fontSize: 11.5, fontFamily: "sans-serif" }}>
                {role.trim() ? "Suggested for your role" : "General focus areas"}
              </span>
              <button
                onClick={toggleAll}
                style={{
                  background: allSelected ? "rgba(249,115,22,0.18)" : "var(--surface-2)",
                  border: `1px solid ${allSelected ? "#f97316" : "var(--surface-strong)"}`,
                  color: allSelected ? "#f97316" : "var(--text-faint)", padding: "4px 11px",
                  borderRadius: 99, fontSize: 11.5, cursor: "pointer", fontWeight: 600,
                  fontFamily: "sans-serif",
                }}
              >{allSelected ? "✓ All selected" : "Select All"}</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {availableSkills.map((s) => (
                <Chip key={s} label={s} active={skills.includes(s)} onClick={() => toggleSkill(s)} small />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Optional Job Description ─────────────────────────────────── */}
      <div style={{ marginBottom: 22 }}>
        <Label>
          Job description
          <span style={{ color: "var(--text-subtle)", fontSize: 12, marginLeft: 6, fontWeight: 400 }}>(optional)</span>
        </Label>
        <div style={{ color: "var(--text-subtle)", fontSize: 12, fontFamily: "sans-serif", marginBottom: 8 }}>
          Paste the JD and the interviewer will tailor questions to it — probing required skills, surfacing resume gaps, and asking for matching examples.
        </div>
        <textarea
          value={jobDescription || ""}
          onChange={(e) => setJobDescription(e.target.value.slice(0, 5000))}
          placeholder="Paste the full job description here…"
          rows={6}
          style={{
            width: "100%", padding: "11px 13px", borderRadius: 10, fontSize: 13.5,
            background: "var(--border-soft)", border: "1px solid var(--surface-strong)",
            color: "var(--text-strong)", outline: "none", fontFamily: "sans-serif",
            boxSizing: "border-box", resize: "vertical", minHeight: 110, lineHeight: 1.5,
          }}
        />
        <div style={{
          display: "flex", justifyContent: "space-between", marginTop: 6,
          color: (jobDescription || "").length >= 4500 ? "#fbbf24" : "var(--text-subtle)",
          fontSize: 11.5, fontFamily: "sans-serif",
        }}>
          <span>
            {(jobDescription || "").length >= 5000
              ? "Maximum length reached — additional text will be ignored."
              : (jobDescription || "").length >= 4500
                ? "Approaching 5,000 character limit."
                : ""}
          </span>
          <span>{(jobDescription || "").length} / 5,000</span>
        </div>
      </div>

      <StepActions onBack={onBack} onNext={onNext} nextDisabled={!role.trim() || !experience} nextLabel="Continue →" />
    </StepShell>
  );
};

const StepTone = ({ tone, setTone, interviewer, setInterviewer, onNext, onBack }) => (
  <StepShell icon="🎭" title="Interviewer Style" subtitle="Pick your interviewer and how they should approach the session" onBack={onBack}>
    <div style={{ marginBottom: 20 }}>
      <Label>Choose your interviewer</Label>
      <div style={{ display: "flex", gap: 10 }}>
        {INTERVIEWERS.map((iv) => (
          <button
            key={iv.id}
            onClick={() => setInterviewer(iv.id)}
            style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
              gap: 6, padding: "16px 10px", borderRadius: 14, cursor: "pointer",
              background: interviewer === iv.id ? "rgba(249,115,22,0.1)" : "var(--surface)",
              border: `1.5px solid ${interviewer === iv.id ? "#f97316" : "var(--surface-3)"}`,
              transition: "all 0.2s",
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: "linear-gradient(135deg,#f97316,#dc2626)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 30,
              boxShadow: interviewer === iv.id ? "0 0 22px rgba(249,115,22,0.45)" : "none",
            }}>{iv.avatar}</div>
            <div style={{
              color: interviewer === iv.id ? "#f97316" : "var(--text-strong)",
              fontWeight: 700, fontSize: 14, fontFamily: "'Lora', serif", marginTop: 2,
            }}>{iv.name}</div>
            <div style={{ color: "var(--text-subtle)", fontSize: 11, fontFamily: "sans-serif", textAlign: "center" }}>
              {iv.desc}
            </div>
          </button>
        ))}
      </div>
    </div>

    <Label>Interviewer style</Label>
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 22 }}>
      {TONE_OPTIONS.map((t) => (
        <button key={t.id} onClick={() => setTone(t.id)} style={{
          display: "flex", alignItems: "center", gap: 14, padding: "14px 18px",
          borderRadius: 12, cursor: "pointer", transition: "all 0.2s", textAlign: "left",
          background: tone === t.id ? "rgba(249,115,22,0.1)" : "var(--surface)",
          border: `1.5px solid ${tone === t.id ? "#f97316" : "var(--surface-3)"}`,
        }}>
          <span style={{ fontSize: 26 }}>{t.icon}</span>
          <div>
            <div style={{ color: tone === t.id ? "#f97316" : "var(--text-strong)", fontWeight: 600, fontSize: 15, fontFamily: "'Lora', serif" }}>
              {t.label}
            </div>
            <div style={{ color: "var(--text-dim)", fontSize: 12, fontFamily: "sans-serif" }}>{t.desc}</div>
          </div>
          {tone === t.id && <span style={{ marginLeft: "auto", color: "#f97316", fontSize: 18 }}>✓</span>}
        </button>
      ))}
    </div>
    <StepActions onBack={onBack} onNext={onNext} nextDisabled={!tone} nextLabel="Continue →" />
  </StepShell>
);

const StepResume = ({ resumeName, hasResume, uploadStatus, resumeInsights, onUpload, onClear, onNext, onBack }) => (
  <StepShell icon="📄" title="Upload Your Resume" subtitle="Your interviewer reads it and asks questions from your real experience" onBack={onBack}>
    <label style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      border: `2px dashed ${hasResume ? "#f97316" : "var(--surface-strong)"}`,
      borderRadius: 14, padding: "26px 18px", cursor: "pointer", marginBottom: 14,
      background: hasResume ? "rgba(249,115,22,0.05)" : "transparent",
      transition: "all 0.3s", gap: 8,
    }}>
      <span style={{ fontSize: 34 }}>{hasResume ? "✅" : "📁"}</span>
      <span style={{ color: hasResume ? "#f97316" : "var(--text-dim)", fontSize: 14, textAlign: "center", fontFamily: "sans-serif" }}>
        {hasResume ? (resumeName || "Resume ready") : "Tap to upload PDF resume"}
      </span>
      <span style={{ color: "var(--text-dim)", fontSize: 11, fontFamily: "sans-serif" }}>
        PDF only · max 5 MB
      </span>
      {uploadStatus && (
        <span style={{ color: "var(--text-faint)", fontSize: 12, fontFamily: "sans-serif", textAlign: "center" }}>{uploadStatus}</span>
      )}
      <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
        onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
    </label>

    {resumeInsights && (
      <div style={{
        padding: "11px 14px", borderRadius: 10, marginBottom: 16,
        background: "rgba(34,197,94,0.07)", border: "1px solid rgba(34,197,94,0.18)",
        color: "#86efac", fontSize: 13, lineHeight: 1.6, fontFamily: "sans-serif",
      }}>
        <strong>Detected:</strong> {resumeInsights}
      </div>
    )}

    {hasResume && (
      <button
        type="button"
        onClick={onClear}
        style={{
          background: "transparent", border: "1px solid var(--surface-strong)",
          color: "var(--text-faint)", padding: "8px 14px", borderRadius: 999, fontSize: 12,
          cursor: "pointer", marginBottom: 14, fontFamily: "sans-serif",
        }}
      >
        Remove saved resume
      </button>
    )}

    <StepActions onBack={onBack} onNext={onNext} nextLabel={hasResume ? "Start Interview →" : "Skip & Start →"} />
  </StepShell>
);

// ─── Speech-to-Text correction ───────────────────────────────────────────────
// The Web Speech API mishears tech terms a lot. We bias the transcript toward
// the user's actual role + selected skills (passed in as `vocab`) plus a base
// list of common tech vocabulary, fix well-known phrase mishearings, and run a
// fuzzy single-word fix for words that are within a small edit-distance of any
// vocabulary term.

const BASE_TECH_VOCAB = [
  "React","Angular","Vue","Svelte","JavaScript","TypeScript","Python","Java","Kotlin","Swift",
  "Go","Golang","Rust","Ruby","PHP","Scala","Perl","Bash","PowerShell",
  "Node.js","Express","Django","Flask","FastAPI","Spring","Rails","Laravel",
  "Next.js","Nuxt","Redux","MobX","Zustand","jQuery","RxJS",
  "GraphQL","REST","gRPC","WebSocket","HTTP","HTTPS","TCP","UDP","DNS","CDN",
  "Webpack","Vite","Rollup","Babel","ESLint","Prettier","npm","pnpm","yarn",
  "Jest","Mocha","Vitest","Cypress","Playwright","Selenium","JUnit","pytest",
  "Docker","Kubernetes","Terraform","Ansible","Helm","Jenkins","CircleCI","Argo",
  "GitHub","GitLab","Bitbucket","Git",
  "AWS","Azure","GCP","Lambda","S3","EC2","RDS","DynamoDB","CloudFront","CloudFormation",
  "Firebase","Supabase","Vercel","Netlify","Heroku",
  "MongoDB","PostgreSQL","Postgres","MySQL","SQLite","Redis","Cassandra",
  "Kafka","RabbitMQ","Elasticsearch","Snowflake","Databricks","Airflow",
  "Tailwind","Bootstrap","Sass","SCSS","HTML","CSS","SQL","NoSQL",
  "JSON","YAML","XML","API","SDK","UI","UX","CI/CD","TDD","BDD","CRUD",
  "SaaS","PaaS","IaaS","MVC","MVVM","OOP","DRY","SOLID",
  "Agile","Scrum","Kanban","Jira","Confluence","Figma",
  "JWT","OAuth","SAML","TLS","SSL",
  "ML","AI","LLM","NLP","RAG","TensorFlow","PyTorch","NumPy","Pandas","scikit-learn",
  "Claude","ChatGPT","OpenAI","Anthropic","Gemini",
];

// Multi-word phrase fixes (case-insensitive).
const TECH_PHRASE_FIXES = [
  [/\bjava\s*script\b/gi, "JavaScript"],
  [/\btype\s*script\b/gi, "TypeScript"],
  [/\bno\s*sql\b/gi, "NoSQL"],
  [/\bpost\s*gres(?:s|ql)?\b/gi, "PostgreSQL"],
  [/\bpost\s*gras(?:s|ql)?\b/gi, "PostgreSQL"],
  [/\bmy\s*sql\b/gi, "MySQL"],
  [/\bmy\s*squeal\b/gi, "MySQL"],
  [/\bmongo\s*db\b/gi, "MongoDB"],
  [/\bnode\s*\.?\s*js\b/gi, "Node.js"],
  [/\breact\s*\.?\s*js\b/gi, "React"],
  [/\bnext\s*\.?\s*js\b/gi, "Next.js"],
  [/\bnuxt\s*\.?\s*js\b/gi, "Nuxt"],
  [/\bvue\s*\.?\s*js\b/gi, "Vue"],
  [/\bgraph\s*ql\b/gi, "GraphQL"],
  [/\brest\s*ful\b/gi, "RESTful"],
  [/\bweb\s*socket\b/gi, "WebSocket"],
  [/\bweb\s*pack\b/gi, "Webpack"],
  [/\bes\s*lint\b/gi, "ESLint"],
  [/\bc\s*sharp\b/gi, "C#"],
  [/\bc\s*plus\s*plus\b/gi, "C++"],
  [/\bdot\s*net\b/gi, ".NET"],
  [/\bk\s*8\s*s\b/gi, "Kubernetes"],
  [/\bcube\s*r?net(?:e|i)s?\b/gi, "Kubernetes"],
  [/\bdocker\s*file\b/gi, "Dockerfile"],
  [/\bgit\s*hub\b/gi, "GitHub"],
  [/\bgit\s*lab\b/gi, "GitLab"],
  [/\bbit\s*bucket\b/gi, "Bitbucket"],
  [/\bvs\s*code\b/gi, "VS Code"],
  [/\btail\s*wind\b/gi, "Tailwind"],
  [/\bma?ch(?:ine)?\s*learning\b/gi, "machine learning"],
  [/\bci\s*\/?\s*cd\b/gi, "CI/CD"],
  [/\bopen\s*ai\b/gi, "OpenAI"],
  [/\bchat\s*g\s*p\s*t\b/gi, "ChatGPT"],
];

// Single-word mishearings that browsers commonly emit.
const WORD_FIXES = {
  "wrecked": "React",
  "reactor": "React",
  "kuberneties": "Kubernetes",
  "kubernets": "Kubernetes",
  "anglerr": "Angular",
  "angler": "Angular",
  "tipescript": "TypeScript",
  "javascripts": "JavaScript",
  "noeds": "Node.js",
  "redacks": "Redux",
  "postgres": "Postgres",
  "postgress": "Postgres",
  "monogo": "Mongo",
  "kafkah": "Kafka",
  "doker": "Docker",
};

// Words we must never auto-correct (would destroy normal English speech).
const STT_SAFE_WORDS = new Set((
  "the a an and or but if then is are was were be been being have has had do does did " +
  "i you he she we they me my your our their this that these those for to of in on at by " +
  "with from as it its will would can could should may might not no yes so very really " +
  "what when where why how which who whom about into over under again still just only " +
  "good great well bad like love know think feel make made build built work worked " +
  "first second third last next previous"
).split(/\s+/));

// Vocab terms that are ALSO common English words/names. These should only be
// normalized to their tech-form casing when the user explicitly listed them in
// their skills/role (i.e. they're in the user-skills set), never from the base
// vocab alone. Otherwise "I need to rest" → "I need to REST", "took the swift
// path" → "took the Swift path", etc.
const AMBIGUOUS_VOCAB = new Set([
  "rest","restful","spring","swift","rails","go","ruby","rust","scrum","git",
  "react","vue","next","node","scale","scala","perl","kotlin","helm","argo",
  "lambda","figma","claude","gemini","ai","ml","ui","ux","api","sdk","jwt",
  "tls","ssl","http","https","tcp","udp","dns","cdn","crud","tdd","bdd","oop",
  "mvc","saas","paas","iaas","sql","html","css","xml","json","yaml","npm",
  "yarn","jest","mocha","selenium","docker","redis","kafka","jenkins","jira",
  "azure","heroku","vercel","netlify","firebase","supabase","databricks",
  "snowflake","airflow","s3","ec2","rds",
]);

function buildSpeechVocab(role, skills) {
  const split = (s) => String(s || "").split(/[\s,/|]+/).filter(Boolean);
  const fromSkills = (skills || []).flatMap(split);
  const fromRole = split(role);
  const userTerms = [...fromSkills, ...fromRole].filter((w) => w && w.length >= 2);
  const userSet = new Set(userTerms.map((w) => w.toLowerCase()));
  const all = Array.from(new Set([...BASE_TECH_VOCAB, ...userTerms].filter((w) => w && w.length >= 2)));
  // Attach the user-set so downstream helpers can decide whether an ambiguous
  // term should be normalized (only if the user explicitly listed it).
  all.userSet = userSet;
  return all;
}

function _editDist(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const m = a.length, n = b.length;
  const dp = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[n];
}

function _fuzzyFixWord(word, vocab) {
  const m = word.match(/^([^a-zA-Z0-9.+#]*)([a-zA-Z0-9.+#'-]+?)([^a-zA-Z0-9]*)$/);
  if (!m) return word;
  const [, lead, core, trail] = m;
  const lc = core.toLowerCase();
  if (lc.length < 4) return word;
  if (STT_SAFE_WORDS.has(lc)) return word;
  const userSet = vocab.userSet || new Set();
  // Already an exact vocab term? Normalize casing to vocab form — but only if
  // the term isn't ambiguous with normal English, OR the user explicitly
  // selected it as one of their skills.
  for (const v of vocab) {
    if (v.toLowerCase() === lc) {
      if (AMBIGUOUS_VOCAB.has(lc) && !userSet.has(lc)) return word;
      return lead + v + trail;
    }
  }
  // Fuzzy match: ONLY against user-selected terms (high signal). Base vocab is
  // not used here to avoid silent rewrites of natural words.
  let best = null, bestD = Infinity;
  for (const v of vocab) {
    const vlc = v.toLowerCase();
    if (!userSet.has(vlc)) continue;
    if (Math.abs(vlc.length - lc.length) > 2) continue;
    const d = _editDist(lc, vlc);
    if (d < bestD) { bestD = d; best = v; }
    if (bestD === 0) break;
  }
  // Tight threshold: 1 edit for short words, 2 only when the word is long.
  const allowed = lc.length >= 8 ? 2 : 1;
  if (best && bestD > 0 && bestD <= allowed) return lead + best + trail;
  return word;
}

function correctTranscript(text, vocab) {
  if (!text) return text;
  let out = text;
  for (const [re, repl] of TECH_PHRASE_FIXES) out = out.replace(re, repl);
  for (const bad of Object.keys(WORD_FIXES)) {
    const re = new RegExp(`\\b${bad}\\b`, "gi");
    out = out.replace(re, WORD_FIXES[bad]);
  }
  out = out.split(/(\s+)/).map((tok) => /\s/.test(tok) ? tok : _fuzzyFixWord(tok, vocab)).join("");
  return out;
}

function scoreVocabMatches(text, vocab) {
  if (!text || !vocab || !vocab.length) return 0;
  const lc = text.toLowerCase();
  const userSet = vocab.userSet || new Set();
  let n = 0;
  for (const v of vocab) {
    const vlc = v.toLowerCase();
    if (vlc.length < 3) continue;
    // Skip ambiguous English words unless the user explicitly listed them — we
    // don't want "rest"/"swift"/etc. to bias alternative selection.
    if (AMBIGUOUS_VOCAB.has(vlc) && !userSet.has(vlc)) continue;
    const re = new RegExp(`\\b${vlc.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
    const matches = lc.match(re);
    if (matches) {
      // Weight user-selected skills more heavily than base vocab.
      n += matches.length * (userSet.has(vlc) ? 3 : 1);
    }
  }
  return n;
}

// ─── Landing Page ─────────────────────────────────────────────────────────────
// Public homepage. Designed so future Sign In / Sign Up / Pricing can drop into
// the existing slots without restructuring. Uses the same CSS variables as the
// rest of the app so light/dark theming flows through automatically.

// useIsMobile — single source of truth for viewport-based sizing.
// Used by Wordmark + HomeButton to scale the brand mark on small screens
// so it fits comfortably alongside nav links / theme toggle on phones.
const useIsMobile = (breakpoint = 640) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
};

const Logo = ({ size = 76 }) => <LogoMark size={size} />;

// HomeButton: brand mark + "Home" label — acts as a navigate-home button.
// Auto-shrinks on small viewports so it doesn't crowd the rest of the chrome.
const HomeButton = ({ onClick, style = {}, circleSize = 50, fontSize = 13 }) => {
  const isMobile = useIsMobile();
  const cs = isMobile ? Math.round(circleSize * 0.72) : circleSize;
  const fs = isMobile ? Math.max(11, fontSize - 1) : fontSize;
  return (
    <button
      onClick={onClick}
      title="Back to homepage"
      aria-label="Home"
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "4px 14px 4px 4px", borderRadius: 99,
        background: "var(--surface-2)", border: "1px solid var(--border-soft)",
        color: "var(--text-muted)", fontSize: fs, cursor: "pointer",
        fontFamily: "sans-serif", lineHeight: 1,
        transition: "transform 0.12s ease, border-color 0.18s ease",
        ...style,
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.97)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      <LogoMark size={cs} />
      <span style={{ fontWeight: 700 }}>Home</span>
    </button>
  );
};

// Brand wordmark: [LogoMark] that interview — both words bold mono lowercase,
// gradient-filled to match the disc. Auto-scales down on phones.
const Wordmark = ({ logoSize = 76, fontSize = 18, gap = 6 }) => {
  const isMobile = useIsMobile();
  const ls = isMobile ? Math.round(logoSize * 0.72) : logoSize;
  const fs = isMobile ? Math.max(13, Math.round(fontSize * 0.82)) : fontSize;
  const gp = isMobile ? Math.max(4, gap - 1) : gap;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: gp }}>
      <LogoMark size={ls} />
      <span style={{
        fontSize: fs,
        letterSpacing: 0, lineHeight: 1,
        fontFamily: "'JetBrains Mono', 'Courier New', Courier, monospace",
        textTransform: "lowercase",
        fontWeight: 700,
        background: "linear-gradient(135deg,#f97316,#dc2626)",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent",
        color: "transparent",
      }}>
        <span style={{ marginRight: 6 }}>that</span>
        <span>interview</span>
      </span>
    </div>
  );
};

// Lightweight click-to-play YouTube embed. Renders only the thumbnail until
// the user clicks — no YouTube iframe, no third-party JS, no cookies fire on
// page load. Uses youtube-nocookie.com so even after click no tracking cookies
// are set until the video actually plays. Keeps homepage Lighthouse score and
// our Termly/GDPR posture clean.
const DemoVideo = ({ videoId, title }) => {
  const [playing, setPlaying] = useState(false);
  const thumb = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const embedSrc = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;

  const handlePlay = () => {
    setPlaying(true);
    try {
      if (window.posthog && typeof window.posthog.capture === "function") {
        window.posthog.capture("demo_video_play", { video_id: videoId });
      }
    } catch (_) { /* analytics is best-effort */ }
  };

  return (
    <div style={{
      position: "relative", width: "100%", paddingBottom: "56.25%",
      borderRadius: 16, overflow: "hidden",
      border: "1px solid var(--border-soft)",
      background: "var(--card)",
      boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
    }}>
      {playing ? (
        <iframe
          src={embedSrc}
          title={title || "Product demo"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            border: 0,
          }}
        />
      ) : (
        <button
          type="button"
          onClick={handlePlay}
          aria-label={`Play ${title || "product demo"}`}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            padding: 0, border: "none", cursor: "pointer",
            background: `linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.55)), url("${thumb}") center/cover no-repeat`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <span style={{
            width: 78, height: 56, borderRadius: 14,
            background: "rgba(220,38,38,0.92)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 8px 28px rgba(0,0,0,0.55)",
            transition: "transform 0.18s ease",
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
          <noscript>
            <a href={watchUrl} style={{ position: "absolute", bottom: 12, color: "#fff" }}>
              Watch on YouTube
            </a>
          </noscript>
        </button>
      )}
    </div>
  );
};

const HowStep = ({ n, title, body }) => (
  <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
    <div style={{
      width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
      background: "linear-gradient(135deg,#f97316,#dc2626)",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", fontWeight: 800, fontSize: 15,
      boxShadow: "0 0 18px rgba(249,115,22,0.35)",
    }}>{n}</div>
    <div>
      <h4 style={{ color: "var(--text-strong)", fontSize: 16, margin: "6px 0 4px", fontWeight: 700 }}>{title}</h4>
      <p style={{ color: "var(--text-dim)", fontSize: 13.5, margin: 0, lineHeight: 1.55, fontFamily: "sans-serif" }}>{body}</p>
    </div>
  </div>
);

// ─── Animated landing-page demos ──────────────────────────────────────────────
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const useDemoCycle = (intervalMs) => {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const id = setInterval(() => setCycle((c) => c + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return cycle;
};

const DemoPoint = ({ icon, title, body }) => (
  <li style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
    <div style={{
      width: 36, height: 36, flexShrink: 0,
      borderRadius: 10, background: "rgba(249,115,22,0.12)",
      border: "1px solid rgba(249,115,22,0.35)",
      display: "grid", placeItems: "center", fontSize: 17,
    }}>{icon}</div>
    <div>
      <div style={{ color: "var(--text-strong)", fontWeight: 700, fontSize: 14.5, marginBottom: 3 }}>{title}</div>
      <p style={{ color: "var(--text-dim)", fontSize: 13.5, margin: 0, lineHeight: 1.55, fontFamily: "sans-serif" }}>{body}</p>
    </div>
  </li>
);

const DemoBubble = ({ side, delay, children }) => (
  <div style={{
    alignSelf: side === "alex" ? "flex-start" : "flex-end",
    maxWidth: "84%",
    background: side === "alex" ? "rgba(249,115,22,0.10)" : "var(--surface-2)",
    border: `1px solid ${side === "alex" ? "rgba(249,115,22,0.40)" : "var(--border-soft)"}`,
    color: "var(--text-muted)",
    padding: "10px 14px", borderRadius: 14,
    fontSize: 13, lineHeight: 1.5, fontFamily: "sans-serif",
    opacity: 0, transform: "translateY(8px)",
    animation: `nti-bubble-in 0.45s cubic-bezier(.2,.8,.2,1) ${delay}s forwards`,
  }}>{children}</div>
);

const DemoWave = ({ delay, duration }) => (
  <div style={{
    alignSelf: "flex-start",
    display: "flex", gap: 3, alignItems: "center",
    height: 22, padding: "0 6px",
    opacity: 0,
    animation: `nti-fade-in 0.3s ease ${delay}s forwards, nti-fade-out 0.3s ease ${delay + duration}s forwards`,
  }}>
    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
      <span key={i} style={{
        width: 3, background: "#f97316", borderRadius: 2, height: 6,
        animation: `nti-wave 0.8s ease-in-out ${i * 0.07}s infinite alternate`,
      }} />
    ))}
  </div>
);

const DemoTyping = ({ side = "user", delay, duration }) => (
  <div style={{
    alignSelf: side === "alex" ? "flex-start" : "flex-end",
    display: "flex", gap: 4, alignItems: "center",
    padding: "9px 13px",
    background: side === "alex" ? "rgba(249,115,22,0.10)" : "var(--surface-2)",
    border: `1px solid ${side === "alex" ? "rgba(249,115,22,0.40)" : "var(--border-soft)"}`,
    borderRadius: 14,
    opacity: 0,
    animation: `nti-fade-in 0.3s ease ${delay}s forwards, nti-fade-out 0.3s ease ${delay + duration}s forwards`,
  }}>
    {[0, 1, 2].map((i) => (
      <span key={i} style={{
        width: 6, height: 6, borderRadius: "50%",
        background: side === "alex" ? "#f97316" : "var(--text-dim)",
        animation: `nti-bounce 1.0s ease-in-out ${i * 0.15}s infinite`,
      }} />
    ))}
  </div>
);

const InterviewDemo = () => {
  const cycle = useDemoCycle(13000);
  return (
    <div key={cycle} style={{
      background: "var(--card)",
      border: "1px solid var(--border-soft)",
      borderRadius: 18, padding: 20,
      maxWidth: 480, margin: "0 auto", width: "100%",
      boxShadow: "0 30px 60px -30px rgba(0,0,0,0.45)",
      backdropFilter: "blur(14px)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        paddingBottom: 14, borderBottom: "1px solid var(--border-soft)",
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "linear-gradient(135deg,#f97316,#dc2626)",
          display: "grid", placeItems: "center",
          color: "#fff", fontWeight: 800, fontSize: 14,
          boxShadow: "0 0 14px rgba(249,115,22,0.45)",
        }}>A</div>
        <div style={{ flex: 1 }}>
          <div style={{ color: "var(--text-strong)", fontWeight: 700, fontSize: 14 }}>AI</div>
          <div style={{ color: "var(--text-dim)", fontSize: 11, display: "flex", alignItems: "center", gap: 6, fontFamily: "sans-serif" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", animation: "nti-pulse 1.5s ease-in-out infinite" }} />
            Live mock interview
          </div>
        </div>
        <div style={{ color: "var(--text-dim)", fontSize: 11, fontFamily: "sans-serif", textAlign: "right", lineHeight: 1.3 }}>
          Backend Engineer<br /><span style={{ opacity: 0.7 }}>Mid-level</span>
        </div>
      </div>

      <div style={{
        minHeight: 280, paddingTop: 16,
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <DemoBubble side="alex" delay={0.4}>
          Walk me through a backend system you've designed end-to-end — ideally one where you owned the architecture decisions, not just the implementation.
        </DemoBubble>
        <DemoWave delay={0.5} duration={1.8} />
        <DemoTyping side="user" delay={3.2} duration={1.1} />
        <DemoBubble side="user" delay={4.5}>
          Last quarter I led a migration from a monolith to event-driven services for our payments pipeline.
        </DemoBubble>
        <DemoTyping side="alex" delay={5.4} duration={2.2} />
        <DemoBubble side="alex" delay={7.8}>
          That's helpful — could you unpack what "led" looked like in practice? Did you design the event schema yourself, or coordinate the engineers who built it? And how did the production cutover land?
        </DemoBubble>
        <DemoWave delay={8.0} duration={2.2} />
      </div>
    </div>
  );
};

const DemoScoreBar = ({ label, value, delay }) => {
  const pct = Math.max(0, Math.min(100, value * 10));
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "minmax(90px, 130px) 1fr 32px",
      alignItems: "center", gap: 10,
    }}>
      <div style={{ color: "var(--text-muted)", fontSize: 12, fontFamily: "sans-serif" }}>{label}</div>
      <div style={{ height: 6, background: "var(--border-soft)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: "linear-gradient(90deg,#f97316,#dc2626)",
          borderRadius: 3,
          transformOrigin: "left", transform: "scaleX(0)",
          animation: `nti-bar-fill 0.9s cubic-bezier(.2,.8,.2,1) ${delay}s forwards`,
        }} />
      </div>
      <div style={{
        color: "var(--text-strong)", fontSize: 12, fontWeight: 700,
        textAlign: "right", fontFamily: "sans-serif",
        opacity: 0,
        animation: `nti-fade-in 0.3s ease ${delay + 0.6}s forwards`,
      }}>{value.toFixed(1)}</div>
    </div>
  );
};

const FeedbackDemo = () => {
  const cycle = useDemoCycle(14000);
  const categories = [
    { icon: "🗣️", name: "Communication", scores: [
      { label: "Clarity", value: 8.5 },
      { label: "Conciseness", value: 7.5 },
      { label: "Confidence", value: 8.0 },
    ]},
    { icon: "🧠", name: "Technical depth", scores: [
      { label: "Accuracy", value: 9.0 },
      { label: "Reasoning", value: 8.5 },
      { label: "Tradeoffs", value: 7.5 },
    ]},
    { icon: "🧩", name: "Structure", scores: [
      { label: "STAR usage", value: 8.0 },
      { label: "Story arc", value: 7.5 },
      { label: "Relevance", value: 9.0 },
    ]},
    { icon: "🎯", name: "Role fit", scores: [
      { label: "Seniority signal", value: 8.5 },
      { label: "Domain depth", value: 8.0 },
      { label: "Ownership", value: 9.0 },
    ]},
  ];

  return (
    <div key={cycle} style={{
      background: "var(--card)",
      border: "1px solid var(--border-soft)",
      borderRadius: 18, padding: 22,
      maxWidth: 480, margin: "0 auto", width: "100%",
      boxShadow: "0 30px 60px -30px rgba(0,0,0,0.45)",
      backdropFilter: "blur(14px)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        paddingBottom: 14, borderBottom: "1px solid var(--border-soft)",
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: "var(--text-strong)", fontWeight: 700, fontSize: 14 }}>Performance breakdown</div>
          <div style={{ color: "var(--text-dim)", fontSize: 11, display: "flex", alignItems: "center", gap: 6, fontFamily: "sans-serif" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f97316", animation: "nti-pulse 1.5s ease-in-out infinite" }} />
            Generating feedback…
          </div>
        </div>
        <div style={{ color: "var(--text-dim)", fontSize: 11, fontFamily: "sans-serif", textAlign: "right", lineHeight: 1.3 }}>
          12 dimensions<br /><span style={{ opacity: 0.7 }}>4 categories</span>
        </div>
      </div>

      <div style={{ paddingTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>
        {categories.map((cat, ci) => {
          const baseDelay = 0.3 + ci * 1.0;
          return (
            <div key={ci} style={{
              opacity: 0, transform: "translateY(6px)",
              animation: `nti-bubble-in 0.45s cubic-bezier(.2,.8,.2,1) ${baseDelay}s forwards`,
            }}>
              <div style={{ color: "var(--text-strong)", fontSize: 12.5, fontWeight: 700, marginBottom: 8, fontFamily: "sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 14 }}>{cat.icon}</span> {cat.name}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {cat.scores.map((s, si) => (
                  <DemoScoreBar key={si} label={s.label} value={s.value} delay={baseDelay + 0.1 + si * 0.12} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-soft)",
        opacity: 0, animation: "nti-fade-in 0.5s ease 5.5s forwards",
        fontSize: 12.5, color: "var(--text-muted)", fontFamily: "sans-serif", lineHeight: 1.5,
      }}>
        Readiness verdict: <strong style={{ color: "var(--text-strong)" }}>Ready for Mid-level Backend roles</strong>, with two weeks of focused prep on system-design tradeoffs.
      </div>
    </div>
  );
};

// ─── Lemon Squeezy hosted-checkout config ───────────────────────────────────
// Variant UUIDs come from each product's "Buy now" / share link in the Lemon
// dashboard. Test-mode and live-mode UUIDs are different — flip these when we
// move from test to live. Store subdomain is the prefix on every checkout URL.
const LEMON_STORE_SUBDOMAIN = "nailthatinterview";

// Variant UUIDs come from per-environment env vars — dev (Replit) carries
// Lemon test-mode UUIDs, prod (Vercel) carries live-mode UUIDs, with the
// same code on both branches. Companion server-side env vars (without the
// REACT_APP_ prefix) are read in api/lemon-webhook.js — both halves must
// be set on every environment. CRA inlines REACT_APP_* values at build
// time, so changing them on Vercel requires a redeploy.
//
// Loud-fail policy: a missing variant aborts module load with a clear
// error rather than silently falling back. A silent fallback to the
// wrong UUID in prod would route real-money checkouts at the wrong
// product — far worse than a failed deploy.
function requireLemonVariant(key) {
  const v = process.env[key];
  if (!v) {
    throw new Error(
      `Missing env var ${key}. Set the six REACT_APP_LEMON_VARIANT_* keys ` +
      `(STARTER, PRO, POWER, STARTER_PASS, PRO_PASS, POWER_PASS) on every ` +
      `environment that builds this bundle (Replit for dev, Vercel for prod).`
    );
  }
  return v;
}

const LEMON_VARIANT_IDS = {
  starter: requireLemonVariant("REACT_APP_LEMON_VARIANT_STARTER"),
  pro:     requireLemonVariant("REACT_APP_LEMON_VARIANT_PRO"),
  power:   requireLemonVariant("REACT_APP_LEMON_VARIANT_POWER"),
};

// One-time 3-month pass variants. Each pass is a Lemon Squeezy "Single
// Payment" product (NOT a subscription) — buyers pay once and get 3
// months of access at the matching tier's monthly cap (non-pooling: cap
// resets each month, unused interviews do NOT roll over).
const LEMON_PASS_VARIANT_IDS = {
  starter_pass: requireLemonVariant("REACT_APP_LEMON_VARIANT_STARTER_PASS"),
  pro_pass:     requireLemonVariant("REACT_APP_LEMON_VARIANT_PRO_PASS"),
  power_pass:   requireLemonVariant("REACT_APP_LEMON_VARIANT_POWER_PASS"),
};

// Builds a Lemon Squeezy hosted-checkout URL with the user's email pre-filled
// and their Supabase user_id attached as custom data so the webhook can match
// the resulting purchase back to the right account. After payment, Lemon
// redirects the buyer to `success_url` — we send them back to the cockpit.
// Plan codes ending in `_pass` route to the one-time-pass variant map.
function buildLemonCheckoutUrl(planCode, user, opts = {}) {
  const isPass = typeof planCode === "string" && planCode.endsWith("_pass");
  const variantId = isPass
    ? LEMON_PASS_VARIANT_IDS[planCode]
    : LEMON_VARIANT_IDS[planCode];
  if (!variantId) return null;
  const base = `https://${LEMON_STORE_SUBDOMAIN}.lemonsqueezy.com/buy/${variantId}`;
  const params = new URLSearchParams();
  // Buyers should not be able to change their email at checkout — it's the
  // identity the webhook uses to find them in Supabase.
  params.set("checkout[email]", user?.email || "");
  params.set("checkout[email_disabled]", "true");
  // Custom data is echoed back to us in the webhook payload.
  params.set("checkout[custom][user_id]", user?.id || "");
  params.set("checkout[custom][plan_code]", planCode);
  // Optional promo code (auto-applied at Lemon's hosted checkout). If the
  // code is invalid, capped, or expired, Lemon still loads checkout — the
  // discount line just won't apply and the buyer sees the normal price.
  // Server-side enforcement (redemption cap, expiry, per-customer limit)
  // lives in the Lemon discount config — DO NOT rely on this client param
  // for security.
  if (opts.discountCode) {
    params.set("checkout[discount_code]", String(opts.discountCode));
  }
  // Send buyers back to the cockpit after a successful payment.
  if (typeof window !== "undefined") {
    params.set("checkout[success_url]", `${window.location.origin}/?checkout=success`);
  }
  return `${base}?${params.toString()}`;
}

// Pricing tiers (May 2026 rebalance).
//
// Each tier lists its FULL feature set with a tick mark — no "Everything in X,
// plus" inheritance, no implicit carry-over. Cards repeat the shared lines so
// users can scan a single column without cross-referencing the previous tier.
// Order the lines identically across tiers (interviews → minutes → JD → PDF →
// resume → history → feedback report) so eyes can sweep horizontally.
//
// `perHour` is the small descender under the price ($X.YY / hour). `perHourNote`
// is an optional pill ("Best value per hour" on Power) — the per-hour ladder
// makes higher tiers cheaper per minute, so we surface it explicitly.
const PRICING_TIERS = [
  {
    name: "Free",
    code: "free",
    price: "$0",
    period: "/ forever",
    badge: null,
    highlighted: false,
    perfectFor: "Trying us out before committing",
    features: [
      { text: "2 interviews per month", included: true },
      { text: "15 minutes per session", included: true },
      { text: "Downloadable feedback report (PDF)", included: true },
      { text: "12-dimension feedback report", included: true },
      { text: "Unlimited session history", included: true },
      { text: "Performance insights — Snapshot view", included: true },
    ],
    cta: { label: "Start free →", variant: "outlined", action: "start" },
  },
  {
    name: "Starter",
    code: "starter",
    price: "$9",
    cents: ".99",
    currency: "USD",
    period: "/ month",
    perHour: "$4.00 / hour",
    badge: null,
    highlighted: false,
    perfectFor: "Final prep for an interview that matters",
    features: [
      { text: "5 interviews per month", included: true },
      { text: "30 minutes per session", included: true },
      { text: "Questions tailored to your job description", included: true },
      { text: "Downloadable feedback report (PDF)", included: true },
      { text: "12-dimension feedback report", included: true },
      { text: "Unlimited session history", included: true },
      { text: "Performance insights dashboard", included: true },
    ],
    cta: { label: "Subscribe →", variant: "outlined", action: "subscribe" },
  },
  {
    name: "Pro",
    code: "pro",
    price: "$24",
    cents: ".99",
    currency: "USD",
    period: "/ month",
    perHour: "$3.33 / hour",
    badge: "Most popular",
    highlighted: true,
    perfectFor: "Mid-job-hunt, several interviews lined up",
    features: [
      { text: "15 interviews per month", included: true },
      { text: "30 minutes per session", included: true },
      { text: "Questions tailored to your job description", included: true },
      { text: "Downloadable feedback report (PDF)", included: true },
      { text: "12-dimension feedback report", included: true },
      { text: "Unlimited session history", included: true },
      { text: "Performance insights dashboard", included: true },
    ],
    cta: { label: "Subscribe →", variant: "primary", action: "subscribe" },
  },
  {
    name: "Power",
    code: "power",
    price: "$39",
    cents: ".99",
    currency: "USD",
    period: "/ month",
    perHour: "$2.67 / hour",
    perHourNote: "Best value per hour",
    badge: null,
    highlighted: false,
    perfectFor: "Going all-in on landing the dream role",
    features: [
      { text: "20 interviews per month", included: true },
      { text: "45-minute sessions (real interview length)", included: true },
      { text: "Upload your resume", included: true },
      { text: "Questions tailored to your resume + JD", included: true },
      { text: "Downloadable feedback report (PDF)", included: true },
      { text: "12-dimension feedback report", included: true },
      { text: "Unlimited session history", included: true },
      { text: "Performance insights dashboard", included: true },
    ],
    cta: { label: "Subscribe →", variant: "outlined", action: "subscribe" },
  },
];

// One-time 3-month passes (May 2026). Same monthly cap as the matching
// subscription tier, NON-POOLING — unused interviews don't roll over from
// month to month. Pass auto-expires 3 months from purchase. Per-hour math:
//   Starter Pass: 5/mo × 3 = 15 sessions × 0.5h = 7.5h  → $24.99 / 7.5  ≈ $3.33/hr
//   Pro Pass:    15/mo × 3 = 45 sessions × 0.5h = 22.5h → $64.99 / 22.5 ≈ $2.89/hr
//   Power Pass:  20/mo × 3 = 60 sessions × 0.75h = 45h  → $99.99 / 45   ≈ $2.22/hr
// Pass slider intentionally omits the Free tier — Free is already the
// baseline option on the Subscription slider, and showing it under
// "3-month pass" would imply you can "buy" a free pass, which is
// confusing. Users who want to start free pick it from the subscription
// view; the toggle remains visible so they can flip back at any time.
const PASS_TIERS = [
  {
    name: "Starter Pass",
    code: "starter_pass",
    price: "$24",
    cents: ".99",
    currency: "USD",
    period: "/ one-time",
    perHour: "3 months · ~$3.33 / hour",
    badge: null,
    highlighted: false,
    perfectFor: "Final prep without committing to monthly billing",
    savings: { amount: "$4.98", percent: 17 },
    features: [
      { text: "5 interviews per month × 3 months", included: true, hint: "Resets monthly. Unused interviews don't carry over." },
      { text: "30 minutes per session", included: true },
      { text: "Questions tailored to your job description", included: true },
      { text: "Downloadable feedback report (PDF)", included: true },
      { text: "12-dimension feedback report", included: true },
      { text: "Unlimited session history", included: true },
      { text: "Performance insights dashboard", included: true },
      { text: "One-time payment · no auto-renew", included: true },
    ],
    cta: { label: "Buy 3-month pass →", variant: "outlined", action: "subscribe" },
  },
  {
    name: "Pro Pass",
    code: "pro_pass",
    price: "$64",
    cents: ".99",
    currency: "USD",
    period: "/ one-time",
    perHour: "3 months · ~$2.89 / hour",
    badge: "Most popular",
    highlighted: true,
    perfectFor: "Active job hunt without subscription friction",
    savings: { amount: "$9.98", percent: 13 },
    features: [
      { text: "15 interviews per month × 3 months", included: true, hint: "Resets monthly. Unused interviews don't carry over." },
      { text: "30 minutes per session", included: true },
      { text: "Questions tailored to your job description", included: true },
      { text: "Downloadable feedback report (PDF)", included: true },
      { text: "12-dimension feedback report", included: true },
      { text: "Unlimited session history", included: true },
      { text: "Performance insights dashboard", included: true },
      { text: "One-time payment · no auto-renew", included: true },
    ],
    cta: { label: "Buy 3-month pass →", variant: "primary", action: "subscribe" },
  },
  {
    name: "Power Pass",
    code: "power_pass",
    price: "$99",
    cents: ".99",
    currency: "USD",
    period: "/ one-time",
    perHour: "3 months · ~$2.22 / hour",
    perHourNote: "Best value per hour",
    badge: null,
    highlighted: false,
    perfectFor: "Going all-in for a 3-month job-search sprint",
    savings: { amount: "$19.98", percent: 17 },
    features: [
      { text: "20 interviews per month × 3 months", included: true, hint: "Resets monthly. Unused interviews don't carry over." },
      { text: "45-minute sessions (real interview length)", included: true },
      { text: "Upload your resume", included: true },
      { text: "Questions tailored to your resume + JD", included: true },
      { text: "Downloadable feedback report (PDF)", included: true },
      { text: "12-dimension feedback report", included: true },
      { text: "Unlimited session history", included: true },
      { text: "Performance insights dashboard", included: true },
      { text: "One-time payment · no auto-renew", included: true },
    ],
    cta: { label: "Buy 3-month pass →", variant: "outlined", action: "subscribe" },
  },
];

const PriceCard = ({ tier, onStart, onSubscribe }) => {
  const { name, code, price, cents, currency, period, badge, features, cta, highlighted,
          perHour, perHourNote } = tier;

  const ctaStyles = {
    primary: {
      background: "linear-gradient(135deg,#f97316,#dc2626)",
      border: "none",
      color: "#fff",
      cursor: "pointer",
      boxShadow: "0 8px 22px rgba(249,115,22,0.35)",
    },
    "primary-disabled": {
      background: "linear-gradient(135deg, rgba(249,115,22,0.55), rgba(220,38,38,0.55))",
      border: "none",
      color: "#fff",
      cursor: "not-allowed",
      opacity: 0.85,
    },
    outlined: {
      background: "transparent",
      border: "1px solid rgba(249,115,22,0.55)",
      color: "#f97316",
      cursor: "pointer",
    },
    disabled: {
      background: "var(--surface-2)",
      border: "1px solid var(--border-soft)",
      color: "var(--text-dimmer)",
      cursor: "not-allowed",
    },
  };

  const isClickable = cta.variant === "primary" || cta.variant === "outlined";
  const handleClick = () => {
    if (cta.action === "start" && onStart) onStart();
    else if (cta.action === "subscribe" && onSubscribe) onSubscribe(code);
  };

  return (
    <div style={{
      background: highlighted
        ? "linear-gradient(160deg, rgba(249,115,22,0.13), var(--card))"
        : "var(--card)",
      border: `1px solid ${highlighted ? "rgba(249,115,22,0.55)" : "var(--border-soft)"}`,
      borderRadius: 20,
      padding: highlighted ? 30 : 24,
      position: "relative",
      backdropFilter: "blur(14px)",
      transform: highlighted ? "translateY(-6px)" : "none",
      boxShadow: highlighted ? "0 16px 48px rgba(249,115,22,0.18)" : "none",
      display: "flex",
      flexDirection: "column",
    }}>
      {badge && (
        <span style={{
          position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
          background: "linear-gradient(135deg,#f97316,#dc2626)",
          color: "#fff", fontSize: 10.5, fontWeight: 700,
          padding: "4px 14px", borderRadius: 99, letterSpacing: 0.6, textTransform: "uppercase",
          fontFamily: "sans-serif",
          boxShadow: "0 4px 14px rgba(249,115,22,0.45)",
          whiteSpace: "nowrap",
        }}>{badge}</span>
      )}

      <div style={{ color: "var(--text-strong)", fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
        {name}
      </div>

      {/* Savings pill — only on one-time pass cards. Green on purpose
          (universally reads as "you save money"); contrasts with the
          orange brand palette so it draws the eye on glance. */}
      {tier.savings && (
        <div style={{
          display: "inline-block",
          fontSize: 11, fontWeight: 700, fontFamily: "sans-serif",
          color: "#10b981",
          background: "rgba(16,185,129,0.10)",
          border: "1px solid rgba(16,185,129,0.40)",
          padding: "3px 9px", borderRadius: 99,
          marginBottom: 10, letterSpacing: 0.3,
        }}>
          Save {tier.savings.amount} ({tier.savings.percent}% off)
        </div>
      )}

      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{
          color: "var(--text-strong)", fontSize: 34, fontWeight: 800, letterSpacing: -0.8,
          display: "inline-flex", alignItems: "flex-start", lineHeight: 1,
        }}>
          <span>{price}</span>
          {cents && (
            <span style={{
              fontSize: 16, fontWeight: 700, marginTop: 4, marginLeft: 1, letterSpacing: -0.3,
            }}>{cents}</span>
          )}
        </span>
        {currency && (
          <span style={{
            color: "var(--text-dimmer)", fontSize: 11, fontFamily: "sans-serif",
            fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase",
          }}>{currency}</span>
        )}
        <span style={{ color: "var(--text-dimmer)", fontSize: 13, fontFamily: "sans-serif" }}>
          {period}
        </span>
      </div>

      {/* Always render this row with the WORST-CASE content shape (per-hour
          text + the rounded note chip) so every card has the same intrinsic
          height for this slot. Individual pieces are hidden when their data
          isn't present. This way the "Perfect for" line and the feature list
          start at the same Y on all four tiers regardless of viewport, font
          size, or wrapping — instead of relying on a hardcoded minHeight. */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        marginTop: -8, marginBottom: 14,
        fontFamily: "sans-serif",
      }}>
        <span style={{
          color: "var(--text-dimmer)", fontSize: 11.5, fontWeight: 600,
          visibility: perHour ? "visible" : "hidden",
        }}>
          {perHour || "$0.00 / hour"}
        </span>
        <span aria-hidden={!perHourNote} style={{
          fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
          padding: "3px 8px", borderRadius: 99,
          color: "#f97316",
          background: "rgba(249,115,22,0.10)",
          border: "1px solid rgba(249,115,22,0.35)",
          visibility: perHourNote ? "visible" : "hidden",
        }}>{perHourNote || "Placeholder"}</span>
      </div>

      {tier.perfectFor && (
        <div style={{
          fontSize: 11.5, lineHeight: 1.5, margin: "0 0 14px",
          color: "var(--text-dim)", fontFamily: "sans-serif", textAlign: "center",
        }}>
          <span style={{ color: "var(--text-dimmer)" }}>Perfect for: </span>
          {tier.perfectFor}
        </div>
      )}

      <ul style={{
        listStyle: "none", padding: 0, margin: "0 0 18px",
        fontSize: 13, fontFamily: "sans-serif", flex: 1,
      }}>
        {features.map((f) => {
          if (f.starred) {
            return (
              <li key={f.text} style={{
                padding: "10px 12px", margin: "8px 0 4px",
                display: "flex", flexDirection: "column", gap: 4,
                background: "rgba(251,191,36,0.12)",
                border: "1px solid rgba(251,191,36,0.45)",
                borderRadius: 10,
              }}>
                <span style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "var(--text-strong)", fontWeight: 700 }}>
                  <span style={{ color: "#fbbf24", minWidth: 14 }}>★</span>
                  <span>{f.text}</span>
                </span>
                {f.subtext && (
                  <span style={{
                    fontSize: 11.5, color: "var(--text-dim)", paddingLeft: 22, lineHeight: 1.4,
                  }}>{f.subtext}</span>
                )}
              </li>
            );
          }
          return (
            <li key={f.text} style={{
              padding: "5px 0", display: "flex", gap: 8, alignItems: "flex-start",
              color: f.included ? "var(--text-dim)" : "var(--text-dimmer)",
              opacity: f.included ? 1 : 0.55,
            }}>
              <span style={{
                color: f.included ? "#f97316" : "var(--text-dimmer)",
                fontWeight: 700, minWidth: 12,
              }}>{f.included ? "✓" : "✕"}</span>
              <span style={{
                display: "inline-flex", flexDirection: "column",
              }}>
                {/* Strike-through is applied only to the feature label, never
                    to the hint underneath — the hint is informative ("where
                    you'd find this feature") and shouldn't look crossed-out. */}
                <span style={{
                  textDecoration: f.included ? "none" : "line-through",
                }}>{f.text}</span>
                {!f.included && f.hint && (
                  <span style={{
                    fontSize: 11, color: "var(--text-dimmer)",
                    textDecoration: "none", marginTop: 2, fontStyle: "italic",
                    opacity: 1,
                  }}>{f.hint}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <button
        onClick={isClickable ? handleClick : undefined}
        disabled={!isClickable}
        title={isClickable ? "" : "Coming soon"}
        style={{
          marginTop: "auto",
          width: "100%",
          padding: "12px 14px",
          borderRadius: 11,
          fontSize: 13.5,
          fontWeight: 700,
          fontFamily: "sans-serif",
          ...ctaStyles[cta.variant],
        }}
      >{cta.label}</button>
    </div>
  );
};

// ─── Analytics section (Pro / Power) ─────────────────────────────────────────
// Reads from the same `sessions` array the cockpit already loaded — no extra
// network call. Renders four small panels: score trend, strength/weakness
// ranking, role comparison, and improvement delta. Custom inline SVG keeps us
// dependency-free.

const buildSparklinePath = (values, w, h, pad = 4) => {
  if (!values || values.length === 0) return '';
  if (values.length === 1) {
    const v0 = Math.max(0, Math.min(10, values[0]));
    const x = w / 2, y = h - pad - (v0 / 10) * (h - pad * 2);
    return `M ${x.toFixed(1)} ${y.toFixed(1)} L ${x.toFixed(1)} ${y.toFixed(1)}`;
  }
  const stepX = (w - pad * 2) / (values.length - 1);
  return values.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - (Math.max(0, Math.min(10, v)) / 10) * (h - pad * 2);
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
};

const PanelCard = ({ title, hint, children }) => (
  <div style={{
    background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
    borderRadius: 14, padding: 16, fontFamily: 'sans-serif',
    display: 'flex', flexDirection: 'column', gap: 10, minHeight: 180,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
      <h3 style={{
        margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: 0.6,
      }}>{title}</h3>
      {hint && <span style={{ fontSize: 11, color: 'var(--text-dimmer)' }}>{hint}</span>}
    </div>
    {children}
  </div>
);

const RankedBars = ({ rows, emptyLabel = 'Not enough data yet.' }) => {
  if (!rows || rows.length === 0) {
    return <div style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>{emptyLabel}</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {rows.map((r) => {
        const v = Math.max(0, Math.min(10, r.value));
        const pct = (v / 10) * 100;
        const color = scoreColor(v);
        return (
          <div key={r.label} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{r.label}</span>
              <span style={{ color, fontWeight: 700 }}>{v.toFixed(1)}{r.suffix || ''}</span>
            </div>
            <div style={{
              height: 6, width: '100%', borderRadius: 99,
              background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', width: `${pct}%`, background: color,
                borderRadius: 99, transition: 'width 0.3s ease',
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Insights dashboard helpers ──────────────────────────────────────────────
// Friendly labels for every dimension key, grouped under the four spec
// categories. Order within each group matches the on-screen reading order.
const DIM_LABELS = {
  verbal_clarity:        'Verbal Clarity',
  answer_relevance:      'Answer Relevance',
  conciseness:           'Conciseness',
  critical_thinking:     'Critical Thinking',
  star_structure:        'STAR Structure',
  examples_metrics:      'Examples & Metrics',
  confidence_composure:  'Confidence & Composure',
  enthusiasm:            'Enthusiasm',
  professional_language: 'Professional Language',
  technical_depth:       'Technical Depth',
  role_specific:         'Role-Specific Knowledge',
  situational_judgement: 'Situational Judgement',
};

const CATEGORY_GROUPS_RICH = [
  { label: 'Communication Quality',     icon: '📢', keys: ['verbal_clarity', 'answer_relevance', 'conciseness'] },
  { label: 'Thinking Quality',          icon: '🧠', keys: ['critical_thinking', 'star_structure', 'examples_metrics'] },
  { label: 'Professional Presence',     icon: '💼', keys: ['confidence_composure', 'enthusiasm', 'professional_language'] },
  { label: 'Role-Specific Performance', icon: '🎯', keys: ['technical_depth', 'role_specific', 'situational_judgement'] },
];

// Short, actionable tips per dimension — surfaced in the "Focus for your next
// session" card. Kept to one sentence so they read at a glance.
const DIM_TIPS = {
  verbal_clarity:        'Slow down and finish each sentence cleanly — pause briefly between ideas.',
  answer_relevance:      'Restate the question in your opening line so the answer stays anchored to what was asked.',
  conciseness:           'Aim for 60–90 second answers. Drop the warm-up; lead with the headline.',
  critical_thinking:     'Walk the interviewer through your reasoning out loud, then state the conclusion last.',
  star_structure:        'Use Situation → Task → Action → Result — only 1 sentence on Situation/Task, then drive into Action.',
  examples_metrics:      'Quantify outcomes (%, $, time saved). Replace "a lot" with the actual number.',
  confidence_composure:  'When unsure, name it ("good question, let me think for a second") instead of filler words.',
  enthusiasm:            'Land each answer on a forward-looking note — what excited you, what you would try next.',
  professional_language: 'Swap casual fillers ("kinda", "stuff") for precise nouns and verbs.',
  technical_depth:       'Go one level deeper than the surface answer — describe trade-offs, not just choices.',
  role_specific:         'Reference 2–3 specifics from this role (tools, scope, scale) in every answer you give.',
  situational_judgement: 'When given a scenario, state your priorities first, then choose actions that serve them.',
};

const READINESS_STAGES = ['Beginner', 'Developing', 'Interview Ready', 'Strong Candidate'];

// Sort the keys of `dims` by score (desc by default). Returns [{key, label, score}].
const rankDims = (dims, asc = false) => {
  if (!dims || typeof dims !== 'object') return [];
  return Object.keys(DIM_LABELS)
    .map(k => ({ key: k, label: DIM_LABELS[k], score: dims[k] }))
    .filter(r => typeof r.score === 'number' && isFinite(r.score))
    .sort((a, b) => asc ? a.score - b.score : b.score - a.score);
};

// ─── Insights presentational components ──────────────────────────────────────

// 12-axis radar chart, native SVG. Each axis is one dimension scored 0–10.
// `dims` is a dimension_scores object (typically averaged across the
// in-scope sessions; the chart itself is agnostic to provenance).
const RadarChart = ({ dims, size = 280 }) => {
  const cx = size / 2, cy = size / 2;
  const radius = size / 2 - 28;
  const labels = Object.keys(DIM_LABELS);
  const n = labels.length;
  const angleFor = (i) => (-Math.PI / 2) + (i / n) * Math.PI * 2;
  const ringValues = [2, 4, 6, 8, 10];
  // Count how many of the 12 dims actually have a score so we can show a
  // helpful empty state instead of a degenerate polygon at the centre.
  const scoredCount = labels.filter(k => typeof dims?.[k] === 'number' && isFinite(dims[k])).length;
  if (scoredCount < 4) {
    return (
      <div style={{
        padding: '32px 16px', textAlign: 'center',
        fontSize: 12.5, color: 'var(--text-dim)', fontFamily: 'sans-serif',
        lineHeight: 1.55,
      }}>
        Not enough dimension scores yet to draw the radar.
        <div style={{ marginTop: 4, color: 'var(--text-dimmer)' }}>
          Complete a full interview to see all 12 dimensions plotted.
        </div>
      </div>
    );
  }
  // Polygon points for the actual scores
  const points = labels.map((k, i) => {
    const v = typeof dims[k] === 'number' ? Math.max(0, Math.min(10, dims[k])) : 0;
    const r = (v / 10) * radius;
    const a = angleFor(i);
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });
  const polyPoints = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" height={size} aria-label="12 dimensions radar chart">
      {/* Concentric ring outlines — bumped from 0.07 to 0.22 so the web is
          actually visible against the dark panel background. */}
      {ringValues.map((rv) => {
        const ringR = (rv / 10) * radius;
        const ringPts = labels.map((_, i) => {
          const a = angleFor(i);
          return `${(cx + ringR * Math.cos(a)).toFixed(1)},${(cy + ringR * Math.sin(a)).toFixed(1)}`;
        }).join(' ');
        return (
          <polygon key={rv} points={ringPts}
            fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
        );
      })}
      {/* Radial spokes — bumped from 0.05 to 0.16 (kept slightly dimmer than
          the rings so the rings still read as the primary grid). */}
      {labels.map((_, i) => {
        const a = angleFor(i);
        return (
          <line key={`spoke-${i}`}
            x1={cx} y1={cy}
            x2={cx + radius * Math.cos(a)} y2={cy + radius * Math.sin(a)}
            stroke="rgba(255,255,255,0.16)" strokeWidth="1" />
        );
      })}
      {/* Score polygon */}
      <polygon points={polyPoints}
        fill="rgba(249,115,22,0.40)"
        stroke="#f97316" strokeWidth="2"
        strokeLinejoin="round" />
      {/* Score points */}
      {points.map(([x, y], i) => (
        <circle key={`pt-${i}`} cx={x} cy={y} r="3" fill="#f97316" stroke="#0b1220" strokeWidth="1.5" />
      ))}
      {/* Axis labels */}
      {labels.map((k, i) => {
        const a = angleFor(i);
        const lr = radius + 18;
        const lx = cx + lr * Math.cos(a);
        const ly = cy + lr * Math.sin(a);
        // Anchor based on quadrant for readability
        const anchor = Math.abs(Math.cos(a)) < 0.2 ? 'middle'
          : Math.cos(a) > 0 ? 'start' : 'end';
        // Shorten longest labels so they don't clip against the SVG edge.
        // "Technical Depth" sits at 9 o'clock (textAnchor='end') and was
        // running off the left side of the viewBox; "Tech Depth" reads
        // unambiguously and matches the abbreviation style of the others.
        const short = DIM_LABELS[k].replace('Role-Specific Knowledge', 'Role Knowledge')
          .replace('Confidence & Composure', 'Composure')
          .replace('Professional Language', 'Pro Language')
          .replace('Situational Judgement', 'Situational')
          .replace('Examples & Metrics', 'Examples')
          .replace('Technical Depth', 'Tech Depth');
        return (
          <text key={`lbl-${i}`} x={lx} y={ly}
            fill="var(--text-muted)" fontSize="10.5" fontFamily="sans-serif"
            textAnchor={anchor} dominantBaseline="middle">
            {short}
          </text>
        );
      })}
    </svg>
  );
};

// Mini 4-stage horizontal gauge for "Interview readiness". Highlights the
// current stage with an orange marker; previous stages are dimmed.
const ReadinessMeter = ({ stage, readyFor }) => {
  // If the model returns a label we don't recognise (or null), don't silently
  // light up "Beginner" — show a neutral bar with the label instead.
  const knownIdx = READINESS_STAGES.indexOf(stage);
  const known = knownIdx >= 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{
        display: 'flex', gap: 6, flexWrap: 'wrap',
      }}>
        {READINESS_STAGES.map((label, i) => {
          const active = known && i === knownIdx;
          const passed = known && i < knownIdx;
          return (
            <div key={label} style={{
              flex: '1 1 90px', minWidth: 90,
              display: 'flex', flexDirection: 'column', gap: 5,
            }}>
              <div style={{
                height: 8, borderRadius: 99,
                background: active ? '#f97316' : passed ? 'rgba(249,115,22,0.45)' : 'rgba(255,255,255,0.08)',
                boxShadow: active ? '0 0 12px rgba(249,115,22,0.55)' : 'none',
                transition: 'background 0.3s',
              }} />
              <div style={{
                fontSize: 10.5, textAlign: 'center',
                color: active ? '#f97316' : 'var(--text-dim)',
                fontWeight: active ? 700 : 500, fontFamily: 'sans-serif',
                lineHeight: 1.3,
              }}>{label}</div>
            </div>
          );
        })}
      </div>
      {!known && stage && (
        <div style={{
          color: 'var(--text-dim)', fontSize: 11.5, fontFamily: 'sans-serif',
          lineHeight: 1.5,
        }}>
          Current stage: <span style={{ color: 'var(--text-strong)' }}>{stage}</span>
        </div>
      )}
      {readyFor && (
        <div style={{
          color: 'var(--text-dim)', fontSize: 11.5, fontFamily: 'sans-serif',
          lineHeight: 1.5,
        }}>
          Ready for: <span style={{ color: 'var(--text-strong)' }}>{readyFor}</span>
        </div>
      )}
    </div>
  );
};

// Internal short labels (used by the legacy 4-category rollup and the recent
// sessions table). Kept for backward compatibility — the analytics dashboard
// uses CATEGORY_GROUPS_RICH below for friendlier names with icons.
const CATEGORY_GROUPS = [
  { label: 'Communication',    keys: ['verbal_clarity', 'answer_relevance', 'conciseness'] },
  { label: 'Technical depth',  keys: ['technical_depth', 'role_specific', 'situational_judgement'] },
  { label: 'Structure',        keys: ['critical_thinking', 'star_structure', 'examples_metrics'] },
  { label: 'Role fit',         keys: ['confidence_composure', 'enthusiasm', 'professional_language'] },
];

const avgFromKeys = (dims, keys) => {
  if (!dims || typeof dims !== 'object') return null;
  const vals = keys.map(k => dims[k]).filter(v => typeof v === 'number' && isFinite(v));
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

const scoreColor = (n) => {
  if (n == null) return 'var(--text-dim)';
  if (n >= 7) return '#22c55e';
  if (n >= 5) return '#fbbf24';
  return '#f87171';
};

// ─── Analytics / Insights section (all logged-in tiers, May 2026) ───────────
// Reads from the same `sessions` array the cockpit already loaded (top 50).
// All charts are native SVG to keep the bundle small and the styling fully
// in step with the rest of the dark theme. Sub-cards gracefully degrade with
// "Complete more sessions to unlock this insight" when there isn't enough data.
//
// Tab availability is driven by `planCode`:
//   * 'free' → Snapshot tab only (insights_level = 'snapshot' in plan_rules)
//   * everyone else → full Snapshot + Trends + Deep Dive set
// The in-product upsell to Starter+ is rendered by the call site, not here,
// so the section stays purely presentational.
const AnalyticsSection = ({ sessions, onStart, user, planCode }) => {
  const allUnfiltered = sessions || [];
  const insightsLevel =
    String(planCode || 'free').toLowerCase() === 'free' ? 'snapshot' : 'full';

  // ── Per-role filter (top of section) ──────────────────────────────────────
  // Build the dropdown options from the union of roles that appear on any
  // *scored* session — selecting an unscored-only role would leave the whole
  // dashboard blank. The "All roles" option is always available.
  const roleOptions = useMemo(() => {
    const seen = new Map(); // role → count of scored sessions
    for (const s of allUnfiltered) {
      const role = (s.role || '').trim();
      if (!role) continue;
      if (typeof s.overall_score !== 'number' || !isFinite(s.overall_score)) continue;
      seen.set(role, (seen.get(role) || 0) + 1);
    }
    return [...seen.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([role, count]) => ({ role, count }));
  }, [allUnfiltered]);
  const [selectedRole, setSelectedRole] = useState('__all__');
  // If the current selection disappears (e.g. user deletes their last session
  // for that role), fall back to "All roles" so we never render a blank page.
  useEffect(() => {
    if (selectedRole === '__all__') return;
    if (!roleOptions.some(r => r.role === selectedRole)) setSelectedRole('__all__');
  }, [roleOptions, selectedRole]);

  // Insights tabs — Snapshot (default) / Trends / Deep Dive. Persisted in
  // localStorage so a returning user lands on whichever tab they last used.
  // Free tier (insights_level === 'snapshot') is locked to Snapshot — we
  // ignore any stored 'trends'/'deep' value at init AND re-clamp at runtime
  // so a Pro→Free downgrade can't leave the user staring at a now-hidden
  // tabpanel.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const v = localStorage.getItem('analytics.activeTab');
      if (v !== 'trends' && v !== 'deep') return 'snapshot';
      return insightsLevel === 'snapshot' ? 'snapshot' : v;
    } catch { return 'snapshot'; }
  });
  useEffect(() => {
    try { localStorage.setItem('analytics.activeTab', activeTab); } catch {}
  }, [activeTab]);
  useEffect(() => {
    if (insightsLevel === 'snapshot' && activeTab !== 'snapshot') {
      setActiveTab('snapshot');
    }
  }, [insightsLevel, activeTab]);
  // Render-time clamp: useEffect above runs AFTER paint, so on a Pro→Free
  // downgrade mid-session a stale `activeTab='trends'` could flash one
  // frame of the Trends panel. effectiveActiveTab guarantees zero-flicker
  // by recomputing the visible tab synchronously every render.
  const effectiveActiveTab =
    insightsLevel === 'snapshot' ? 'snapshot' : activeTab;

  const all = selectedRole === '__all__'
    ? allUnfiltered
    : allUnfiltered.filter(s => (s.role || '').trim() === selectedRole);

  // For score-based analytics we drop abandoned / unscored rows so trends
  // and averages are honest. For time/count stats (TimeInvestment) we keep
  // every row, since "minutes spent" still counts even on early-end sessions.
  const scored = all.filter(s => typeof s.overall_score === 'number' && isFinite(s.overall_score));

  // ── Practice cadence / streak (computed from ALL sessions, ignoring role) ──
  // Streak = consecutive calendar days ending today (or yesterday) on which
  // the user practised. We use the unfiltered list so a role filter doesn't
  // hide your true habit data.
  const streakInfo = useMemo(() => {
    if (allUnfiltered.length === 0) {
      return { streak: 0, daysSinceLast: null, lastSessionDate: null };
    }
    const dayKey = (d) => {
      const x = new Date(d);
      return `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
    };
    const days = new Set();
    let mostRecent = 0;
    for (const s of allUnfiltered) {
      const t = new Date(s.started_at || s.ended_at).getTime();
      if (!isFinite(t)) continue;
      days.add(dayKey(t));
      if (t > mostRecent) mostRecent = t;
    }
    if (mostRecent === 0) return { streak: 0, daysSinceLast: null, lastSessionDate: null };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const last = new Date(mostRecent);
    last.setHours(0, 0, 0, 0);
    const dayMs = 24 * 60 * 60 * 1000;
    const daysSinceLast = Math.round((today.getTime() - last.getTime()) / dayMs);
    // Streak only counts if you practised today or yesterday — older breaks
    // it. We walk back day-by-day using setDate (not -24h in ms) so DST
    // transitions can't accidentally land us on the same calendar day twice
    // or skip a day.
    let streak = 0;
    if (daysSinceLast <= 1) {
      const cursor = new Date(last);
      while (days.has(dayKey(cursor))) {
        streak += 1;
        cursor.setDate(cursor.getDate() - 1);
      }
    }
    return { streak, daysSinceLast, lastSessionDate: last };
  }, [allUnfiltered]);

  if (scored.length === 0) {
    return (
      <section style={{
        background: 'var(--card)', border: '1px solid var(--border-soft)',
        borderRadius: 18, padding: 22, backdropFilter: 'blur(14px)',
      }}>
        <h2 style={{
          margin: '0 0 8px', fontSize: 16, fontWeight: 700,
          color: 'var(--text-strong)', letterSpacing: -0.2,
        }}>Your insights</h2>
        <p style={{
          margin: 0, color: 'var(--text-dim)', fontSize: 13,
          fontFamily: 'sans-serif', lineHeight: 1.55,
        }}>
          Complete your first interview to see your trends, strengths and growth across sessions.
        </p>
      </section>
    );
  }

  // sessions arrive newest-first → reverse to chronological for trends
  const chrono = [...scored].reverse();
  const latest = chrono[chrono.length - 1];
  const previous = chrono.length >= 2 ? chrono[chrono.length - 2] : null;
  const latestDims = latest.dimension_scores || {};
  const previousDims = previous ? (previous.dimension_scores || {}) : null;

  // ── Averaged dims for the radar (role-aware via the scored array) ─────────
  // The radar in Deep Dive is a pattern-finding view, not a "how was today"
  // view, so we average each dimension across all scored sessions in the
  // current role filter (or all roles when '__all__'). Per-dimension
  // denominator means a session missing one dim doesn't drag that dim's
  // average toward zero — matches how categoryRows handles missing data.
  const avgDims = {};
  const avgDimSessionCount = {};
  for (const k of Object.keys(DIM_LABELS)) {
    let sum = 0, count = 0;
    for (const s of scored) {
      const v = s.dimension_scores?.[k];
      if (typeof v === 'number' && isFinite(v)) { sum += v; count += 1; }
    }
    if (count > 0) { avgDims[k] = sum / count; avgDimSessionCount[k] = count; }
  }
  // For the title we report the number of scored sessions in scope (not per
  // dim) — the per-dim denominators are an internal correctness concern,
  // not something to expose in a one-line caption.
  const radarSessionCount = scored.length;

  // ── 1. Personal Best ──
  const personalBest = [...scored].sort((a, b) => Number(b.overall_score) - Number(a.overall_score))[0];
  const pbStrengths = personalBest ? rankDims(personalBest.dimension_scores).slice(0, 2) : [];

  // ── 2. Recent form by role (replaces the old 0-100 Consistency score) ──
  // For every role with 2+ scored sessions, take the LAST 5 scores (chrono),
  // compute average + swing (max-min, in points), and bucket into a verdict.
  // We intentionally walk the *role-filtered* `scored` array so the per-role
  // pills at the top still narrow this tile when active. Sort by total
  // sessions desc so the most-practised role appears first; cap at 5 rows.
  const formByRoleAll = (() => {
    const buckets = new Map();
    // `scored` is newest-first from the API.
    for (const s of scored) {
      const r = (s.role || '').trim();
      if (!r) continue;
      if (!buckets.has(r)) buckets.set(r, []);
      buckets.get(r).push(s);
    }
    const rows = [];
    for (const [role, sess] of buckets) {
      if (sess.length < 2) continue; // skip single-session roles
      const last5 = sess.slice(0, 5).reverse(); // newest 5 → chronological
      const sc = last5.map(s => Number(s.overall_score)).filter(v => isFinite(v));
      if (sc.length < 2) continue;
      const avg = sc.reduce((a, b) => a + b, 0) / sc.length;
      const swing = Math.max(...sc) - Math.min(...sc);
      let verdict;
      if (sc.length < 3)       verdict = { key: 'need_more', label: 'Need more data', color: 'var(--text-dim)', dot: '⚪' };
      else if (swing <= 0.8)   verdict = { key: 'steady',    label: 'Steady',    color: '#22c55e',         dot: '🟢' };
      else if (swing <= 1.6)   verdict = { key: 'mixed',     label: 'Mixed',     color: '#fbbf24',         dot: '🟡' };
      else                     verdict = { key: 'swingy',    label: 'Swingy',    color: '#f87171',         dot: '🔴' };
      rows.push({ role, scores: sc, avg, swing, totalCount: sess.length, verdict });
    }
    rows.sort((a, b) => b.totalCount - a.totalCount);
    return rows;
  })();
  const formRowsShown = formByRoleAll.slice(0, 5);
  const formRowsHidden = Math.max(0, formByRoleAll.length - formRowsShown.length);

  // ── 3. Time Investment ──
  const totalSessions = all.length;
  const totalSeconds = all.reduce((a, s) => a + (Number(s.duration_seconds) || 0), 0);
  const avgSeconds = totalSessions > 0 ? totalSeconds / totalSessions : 0;
  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const sessionsThisWeek = all.filter(s => {
    const t = new Date(s.started_at || s.ended_at).getTime();
    return isFinite(t) && t >= oneWeekAgo;
  }).length;
  const fmtMins = (secs) => {
    const total = Math.round(secs / 60);
    if (total < 60) return `${total} min`;
    const h = Math.floor(total / 60), m = total % 60;
    return `${h}h ${m}m`;
  };

  // ── 4. Strengths vs Gaps (across all sessions, average per dim) ──
  const dimAvg = {};
  for (const k of Object.keys(DIM_LABELS)) {
    const vals = scored
      .map(s => s.dimension_scores?.[k])
      .filter(v => typeof v === 'number' && isFinite(v));
    if (vals.length) dimAvg[k] = vals.reduce((a, b) => a + b, 0) / vals.length;
  }
  const dimAvgRanked = Object.entries(dimAvg)
    .map(([k, v]) => ({ key: k, label: DIM_LABELS[k], score: v }))
    .sort((a, b) => b.score - a.score);
  const top3Strengths = dimAvgRanked.slice(0, 3);
  const top3Gaps = [...dimAvgRanked].reverse().slice(0, 3);

  // ── 5. Most improved / declined (latest vs previous) ──
  let mostImproved = null, mostDeclined = null;
  if (previousDims) {
    const deltas = Object.keys(DIM_LABELS).map(k => {
      const a = latestDims[k], b = previousDims[k];
      if (typeof a !== 'number' || typeof b !== 'number') return null;
      return { key: k, label: DIM_LABELS[k], delta: a - b, latest: a };
    }).filter(Boolean);
    if (deltas.length) {
      mostImproved = [...deltas].sort((a, b) => b.delta - a.delta)[0];
      mostDeclined = [...deltas].sort((a, b) => a.delta - b.delta)[0];
      // Only show if the values are actually different
      if (mostImproved.delta <= 0) mostImproved = null;
      if (mostDeclined.delta >= 0) mostDeclined = null;
    }
  }

  // ── 6. Readiness data ──
  const latestReadiness = latest.feedback?.readiness || null;
  const latestReadyFor = latest.feedback?.readinessVerdict?.readyFor || null;

  // ── 7. Score trend (existing) ──
  const trendValues = chrono.map(s => Number(s.overall_score));
  const w = 260, h = 90;
  const trendPath = buildSparklinePath(trendValues, w, h);
  const latestScore = Number(latest.overall_score);

  // ── 7b. Filler-word trend (per-minute rate, chronological) ──
  // The eval already extracts `feedback.fillerWords.total` per session. We
  // normalise by duration so comparing a 10-min and a 30-min session is fair.
  // Sessions without a filler count or without a duration are dropped from
  // the trend (we don't fabricate zeros).
  const fillerSeries = chrono
    .map(s => {
      const total = s.feedback?.fillerWords?.total;
      const secs = Number(s.duration_seconds);
      if (typeof total !== 'number' || !isFinite(total)) return null;
      if (!isFinite(secs) || secs < 30) return null; // ignore <30s as noise
      return { perMin: (total / secs) * 60, total, secs };
    })
    .filter(Boolean);
  const fillerLatest = fillerSeries.length ? fillerSeries[fillerSeries.length - 1] : null;
  const fillerAvg = fillerSeries.length
    ? fillerSeries.reduce((a, x) => a + x.perMin, 0) / fillerSeries.length
    : null;
  const fillerDelta = (fillerLatest && fillerSeries.length >= 2)
    ? fillerLatest.perMin - fillerSeries[fillerSeries.length - 2].perMin
    : null;

  // ── 8. Strength / weakness rollup (existing 4-category) ──
  const catSums = {}, catCounts = {};
  for (const s of scored) {
    const dims = s.dimension_scores;
    if (!dims || typeof dims !== 'object') continue;
    for (const g of CATEGORY_GROUPS) {
      const v = avgFromKeys(dims, g.keys);
      if (v == null) continue;
      catSums[g.label] = (catSums[g.label] || 0) + v;
      catCounts[g.label] = (catCounts[g.label] || 0) + 1;
    }
  }
  const categoryRows = CATEGORY_GROUPS
    .map(g => ({ label: g.label, value: catCounts[g.label] ? catSums[g.label] / catCounts[g.label] : null }))
    .filter(r => r.value != null)
    .sort((a, b) => a.value - b.value);

  // ── 9. Role comparison (existing) ──
  const roleStats = {};
  for (const s of scored) {
    const role = (s.role || '').trim();
    if (!role) continue;
    if (!roleStats[role]) roleStats[role] = { sum: 0, count: 0 };
    roleStats[role].sum += Number(s.overall_score);
    roleStats[role].count += 1;
  }
  const roleRows = Object.entries(roleStats)
    .map(([label, v]) => ({ label, value: v.sum / v.count, count: v.count }))
    .sort((a, b) => b.count - a.count || b.value - a.value)
    .slice(0, 5)
    .map(r => ({ label: `${r.label} (${r.count})`, value: r.value }));

  // ── 10. Improvement delta (existing) ──
  const priors = chrono.slice(0, -1);
  const priorAvg = priors.length
    ? priors.reduce((a, s) => a + Number(s.overall_score), 0) / priors.length
    : null;
  const delta = priorAvg != null ? latestScore - priorAvg : null;
  const deltaPct = (priorAvg != null && priorAvg > 0) ? (delta / priorAvg) * 100 : null;

  // ── 11. Dimension trends per dimension (latest session vs previous session) ──
  // We compare the *same* two sessions for every dimension (latest, previous)
  // so the arrow + delta reflect "what changed between my last two interviews"
  // rather than "what changed between the last two times this dim was scored",
  // which would silently drift if a session is missing one dim.
  const dimDeltas = {};
  for (const k of Object.keys(DIM_LABELS)) {
    const latestVal = typeof latestDims[k] === 'number' && isFinite(latestDims[k])
      ? latestDims[k] : null;
    const prevVal = previousDims && typeof previousDims[k] === 'number' && isFinite(previousDims[k])
      ? previousDims[k] : null;
    dimDeltas[k] = { latest: latestVal, prev: prevVal };
  }

  // ── 12. Next session focus (weakest dim from latest session) ──
  const latestRanked = rankDims(latestDims, true); // ascending
  const focusDim = latestRanked[0] || null;

  // Section helpers ─────────────────────────────────────────────────────────
  const sectionStyle = {
    background: 'var(--card)', border: '1px solid var(--border-soft)',
    borderRadius: 18, padding: 22, backdropFilter: 'blur(14px)',
    display: 'flex', flexDirection: 'column', gap: 18,
  };
  const subHeading = (text) => (
    <h3 style={{
      margin: '4px 0 2px', fontSize: 12.5, fontWeight: 700,
      color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.7,
      fontFamily: 'sans-serif',
    }}>{text}</h3>
  );

  return (
    <section style={sectionStyle}>
      {/* Header */}
      <div>
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <h2 style={{
            margin: 0, fontSize: 16, fontWeight: 700,
            color: 'var(--text-strong)', letterSpacing: -0.2,
          }}>Your insights</h2>
          <span style={{
            color: 'var(--text-dimmer)', fontSize: 11.5,
            fontFamily: 'sans-serif',
          }}>
            {scored.length} {scored.length === 1 ? 'session' : 'sessions'} analysed
            {selectedRole !== '__all__' ? ` · ${selectedRole}` : ''}
          </span>
        </div>
        <div style={{
          marginTop: 4, fontSize: 12.5, color: 'var(--text-dim)',
          fontFamily: 'sans-serif', lineHeight: 1.5,
        }}>
          Where you're improving, what to focus on next.
        </div>
      </div>

      {/* ─── AI weekly coach note ─── */}
      <WeeklyCoachNote sessions={allUnfiltered} userId={user?.id || 'anon'} />

      {/* ─── Practice cadence / streak ribbon ─── */}
      <StreakRibbon info={streakInfo} totalSessions={allUnfiltered.length} />

      {/* ─── Per-role filter pills ─── */}
      {roleOptions.length >= 2 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          fontFamily: 'sans-serif',
        }}>
          <span style={{
            fontSize: 11, color: 'var(--text-dimmer)',
            textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700,
            marginRight: 4,
          }}>Showing:</span>
          {[{ role: '__all__', count: roleOptions.reduce((a, r) => a + r.count, 0), label: 'All roles' },
            ...roleOptions.map(r => ({ ...r, label: r.role }))].map(opt => {
            const active = selectedRole === opt.role;
            return (
              <button
                key={opt.role}
                type="button"
                onClick={() => setSelectedRole(opt.role)}
                aria-pressed={active}
                style={{
                  padding: '5px 11px', borderRadius: 99, cursor: 'pointer',
                  fontFamily: 'sans-serif', fontSize: 11.5, fontWeight: active ? 700 : 600,
                  background: active ? 'rgba(249,115,22,0.20)' : 'rgba(255,255,255,0.04)',
                  color: active ? '#fdba74' : 'var(--text-muted)',
                  border: `1px solid ${active ? 'rgba(249,115,22,0.55)' : 'var(--border-soft)'}`,
                  transition: 'all 0.15s',
                }}
              >
                {opt.label} <span style={{ opacity: 0.7, marginLeft: 4 }}>· {opt.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ─── Tab strip: Snapshot / Trends / Deep Dive ─── */}
      {(() => {
        // All three tabs are always rendered so Free-tier users can SEE
        // that Trends + Deep Dive exist (locked) — silent feature
        // discoverability beats hiding the buttons. Locked tabs are
        // aria-disabled, can't be activated, and scrolling/clicking them
        // jumps to the upsell card below the panel.
        const ALL_TABS = [
          { key: 'snapshot', icon: '🏠', label: 'Snapshot', sub: 'How am I doing?' },
          { key: 'trends',   icon: '📈', label: 'Trends',   sub: 'Am I improving?' },
          { key: 'deep',     icon: '🔬', label: 'Deep Dive', sub: 'Where exactly?' },
        ];
        const TABS = ALL_TABS.map(t => ({
          ...t,
          locked: insightsLevel === 'snapshot' && t.key !== 'snapshot',
        }));
        // Locked tabs are still focusable and operable — they act as
        // upsell triggers rather than disabled controls. Arrow keys
        // traverse all tabs (locked or not). Activating a locked tab
        // (Enter/Space/click) jumps to the upsell card without changing
        // the active panel.
        const onLockedActivate = () => {
          const upsell = document.getElementById('insights-upsell');
          if (upsell) upsell.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };
        const onTabKey = (e, idx) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const next = e.key === 'ArrowRight'
              ? (idx + 1) % TABS.length
              : (idx - 1 + TABS.length) % TABS.length;
            // Move focus only — don't change activeTab if we land on a
            // locked tab (manual-activation pattern).
            if (!TABS[next].locked) setActiveTab(TABS[next].key);
            document.getElementById(`insights-tab-${TABS[next].key}`)?.focus();
          } else if (e.key === 'Home') {
            e.preventDefault();
            if (!TABS[0].locked) setActiveTab(TABS[0].key);
            document.getElementById(`insights-tab-${TABS[0].key}`)?.focus();
          } else if (e.key === 'End') {
            e.preventDefault();
            const last = TABS.length - 1;
            if (!TABS[last].locked) setActiveTab(TABS[last].key);
            document.getElementById(`insights-tab-${TABS[last].key}`)?.focus();
          } else if ((e.key === 'Enter' || e.key === ' ') && TABS[idx].locked) {
            e.preventDefault();
            onLockedActivate();
          }
        };
        return (
          <div role="tablist" aria-label="Insights tabs" style={{
            display: 'flex', gap: 6, padding: 4, borderRadius: 12,
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
            fontFamily: 'sans-serif', overflowX: 'auto',
          }}>
            {TABS.map((t, idx) => {
              const active = effectiveActiveTab === t.key && !t.locked;
              const locked = t.locked;
              return (
                <button
                  key={t.key}
                  id={`insights-tab-${t.key}`}
                  role="tab"
                  aria-selected={active}
                  aria-controls={locked ? undefined : `insights-tabpanel-${t.key}`}
                  aria-label={locked ? `${t.label} — locked, available on paid plans. Activate to see upgrade options.` : undefined}
                  title={locked ? 'Upgrade to Starter to unlock' : undefined}
                  tabIndex={(active || (locked && idx === 1)) ? 0 : -1}
                  type="button"
                  onClick={locked ? onLockedActivate : () => setActiveTab(t.key)}
                  onKeyDown={(e) => onTabKey(e, idx)}
                  style={{
                    flex: '1 1 0', minWidth: 110, padding: '10px 12px',
                    borderRadius: 9,
                    cursor: 'pointer',
                    textAlign: 'center',
                    background: active ? 'rgba(249,115,22,0.18)' : 'transparent',
                    color: locked
                      ? 'var(--text-darker)'
                      : (active ? '#fdba74' : 'var(--text-muted)'),
                    border: `1px solid ${active ? 'rgba(249,115,22,0.55)' : 'transparent'}`,
                    opacity: locked ? 0.55 : 1,
                    fontFamily: 'sans-serif', transition: 'all 0.15s',
                    position: 'relative',
                  }}
                >
                  <div style={{
                    fontSize: 13, fontWeight: 700, letterSpacing: 0.2,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    justifyContent: 'center',
                  }}>
                    <span>{t.icon}</span>
                    <span>{t.label}</span>
                    {locked && (
                      <span aria-hidden style={{
                        display: 'inline-flex', alignItems: 'center',
                        fontSize: 11, lineHeight: 1,
                      }}>
                        {/* Padlock SVG — inherits currentColor so it
                            theme-matches in light + dark. */}
                        <svg width="11" height="13" viewBox="0 0 11 13" fill="none"
                             xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 2 }}>
                          <rect x="1" y="6" width="9" height="6.5" rx="1.2"
                                stroke="currentColor" strokeWidth="1.2" fill="none" />
                          <path d="M3 6V4a2.5 2.5 0 0 1 5 0v2"
                                stroke="currentColor" strokeWidth="1.2" fill="none" />
                        </svg>
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10.5, opacity: 0.75, marginTop: 2 }}>
                    {locked ? 'Paid plans' : t.sub}
                  </div>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════════
           SNAPSHOT TAB — "How am I doing right now?"
           ═══════════════════════════════════════════════════════════════════════ */}
      {effectiveActiveTab === 'snapshot' && (
        <div
          role="tabpanel"
          id="insights-tabpanel-snapshot"
          aria-labelledby="insights-tab-snapshot"
          tabIndex={0}
          style={{ display: 'flex', flexDirection: 'column', gap: 18, outline: 'none' }}
        >
          {/* Hero row — Personal Best · Time Invested */}
          <div style={{
            display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}>
            {/* Personal Best */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(249,115,22,0.14), rgba(249,115,22,0.04))',
              border: '1px solid rgba(249,115,22,0.45)',
              borderRadius: 14, padding: 16, fontFamily: 'sans-serif',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#fdba74', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                🏆 Personal Best
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 32, fontWeight: 800, color: '#f97316', lineHeight: 1 }}>
                  {Number(personalBest.overall_score).toFixed(1)}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>/ 10</span>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-strong)', fontWeight: 600 }}>
                {personalBest.role || 'Interview'}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>
                {new Date(personalBest.started_at || personalBest.ended_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
              {pbStrengths.length > 0 && (
                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-dimmer)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Strongest that day
                  </div>
                  {pbStrengths.map(d => (
                    <div key={d.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: 'var(--text-strong)' }}>{d.label}</span>
                      <span style={{ color: '#22c55e', fontWeight: 700 }}>{Number(d.score).toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Time Investment */}
            <div style={{
              background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
              borderRadius: 14, padding: 16, fontFamily: 'sans-serif',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                ⏱ Time Invested
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Stat value={totalSessions} label="Total sessions" />
                <Stat value={fmtMins(totalSeconds)} label="Total practice" />
                <Stat value={fmtMins(avgSeconds)} label="Avg length" />
                <Stat value={sessionsThisWeek} label="This week" />
              </div>
            </div>
          </div>

          {/* Recent form by role — full-width tile (replaces old Consistency) */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
            borderRadius: 14, padding: 16, fontFamily: 'sans-serif',
          }}>
            {subHeading('📊 Recent form by role')}
            {formRowsShown.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-dim)', marginTop: 10 }}>
                Complete 2+ sessions in any role to see your form per role — the dots below will plot your last 5 scores so you can spot patterns.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                {formRowsShown.map(row => (
                  <RecentFormRow key={row.role} row={row} />
                ))}
                {formRowsHidden > 0 && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-dimmer)', marginTop: 4, fontStyle: 'italic' }}>
                    +{formRowsHidden} more role{formRowsHidden === 1 ? '' : 's'} — use the role filter above to focus on one.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Readiness Meter */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
            borderRadius: 14, padding: 16,
          }}>
            {subHeading('Interview Readiness')}
            {latestReadiness ? (
              <div style={{ marginTop: 10 }}>
                <ReadinessMeter stage={latestReadiness} readyFor={latestReadyFor} />
              </div>
            ) : (
              <div style={{ fontSize: 12.5, color: 'var(--text-dim)', marginTop: 10, fontFamily: 'sans-serif' }}>
                Complete an interview with feedback to see your readiness verdict.
              </div>
            )}
          </div>

          {/* Strengths vs Gaps */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
            borderRadius: 14, padding: 16, fontFamily: 'sans-serif',
          }}>
            {subHeading('Strengths vs Gaps')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: '#86efac', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  ✦ Top 3 Strengths
                </div>
                {top3Strengths.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>—</div>
                ) : top3Strengths.map(d => (
                  <div key={d.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '4px 0' }}>
                    <span style={{ color: 'var(--text-strong)' }}>{d.label}</span>
                    <span style={{ color: '#22c55e', fontWeight: 700 }}>{d.score.toFixed(1)}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#fca5a5', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  ⚠ Top 3 Gaps
                </div>
                {top3Gaps.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>—</div>
                ) : top3Gaps.map(d => (
                  <div key={d.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '4px 0' }}>
                    <span style={{ color: 'var(--text-strong)' }}>{d.label}</span>
                    <span style={{ color: scoreColor(d.score), fontWeight: 700 }}>{d.score.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Next Session Focus (action CTA) */}
          {focusDim && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(249,115,22,0.16), rgba(249,115,22,0.04))',
              border: '1px solid rgba(249,115,22,0.45)',
              borderRadius: 14, padding: 18, fontFamily: 'sans-serif',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{
                fontSize: 12.5, fontWeight: 700, color: '#fdba74',
                textTransform: 'uppercase', letterSpacing: 0.6,
              }}>
                🎯 Focus for your next session
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-strong)' }}>
                  {focusDim.label}
                </div>
                <div style={{
                  fontSize: 13, fontWeight: 700,
                  color: scoreColor(focusDim.score),
                }}>
                  latest {focusDim.score.toFixed(1)} / 10
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.55 }}>
                {DIM_TIPS[focusDim.key] || 'Spend a session focusing specifically on this dimension.'}
              </div>
              <div>
                <button
                  onClick={() => onStart?.()}
                  style={{
                    marginTop: 4, padding: '10px 16px', borderRadius: 10,
                    background: 'linear-gradient(135deg, #f97316, #ea580c)',
                    border: 'none', color: '#fff', fontSize: 13, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'sans-serif',
                    boxShadow: '0 4px 12px rgba(249,115,22,0.35)',
                  }}
                >
                  Start focused session →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
           TRENDS TAB — "Am I improving?"
           ═══════════════════════════════════════════════════════════════════════ */}
      {insightsLevel === 'full' && effectiveActiveTab === 'trends' && (
        <div
          role="tabpanel"
          id="insights-tabpanel-trends"
          aria-labelledby="insights-tab-trends"
          tabIndex={0}
          style={{ display: 'flex', flexDirection: 'column', gap: 18, outline: 'none' }}
        >
          {/* Score trend + Improvement + Filler words */}
          <div style={{
            display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          }}>
            <PanelCard title="Score trend" hint={`last ${trendValues.length}`}>
              <div style={{ position: 'relative' }}>
                <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" aria-hidden>
                  {[5, 7].map((g, i) => {
                    const y = h - 4 - (g / 10) * (h - 8);
                    return <line key={i} x1="0" y1={y} x2={w} y2={y}
                      stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />;
                  })}
                  <path d={trendPath} fill="none" stroke="#f97316" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round" />
                  {trendValues.map((v, i) => {
                    const stepX = trendValues.length === 1 ? 0 : (w - 8) / (trendValues.length - 1);
                    const x = trendValues.length === 1 ? w / 2 : 4 + i * stepX;
                    const y = h - 4 - (Math.max(0, Math.min(10, v)) / 10) * (h - 8);
                    return <circle key={i} cx={x} cy={y} r="2.5" fill="#f97316" />;
                  })}
                </svg>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-dim)' }}>
                <span>{Math.min(...trendValues).toFixed(1)} low</span>
                <span style={{ color: scoreColor(latestScore), fontWeight: 700 }}>
                  latest {latestScore.toFixed(1)}
                </span>
                <span>{Math.max(...trendValues).toFixed(1)} high</span>
              </div>
            </PanelCard>

            <PanelCard title="Improvement" hint="latest vs average">
              {delta == null ? (
                <div style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
                  At least two sessions are needed to measure progress.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{
                    fontSize: 32, fontWeight: 800, lineHeight: 1,
                    color: delta >= 0 ? '#22c55e' : '#f87171',
                  }}>
                    {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}
                    <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 8, opacity: 0.85 }}>
                      pts
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-dim)', lineHeight: 1.5 }}>
                    Latest <strong style={{ color: 'var(--text-strong)' }}>{latestScore.toFixed(1)}</strong>
                    {' '}vs your average <strong style={{ color: 'var(--text-strong)' }}>{priorAvg.toFixed(1)}</strong>
                    {deltaPct != null && (
                      <span style={{ color: delta >= 0 ? '#22c55e' : '#f87171' }}>
                        {' '}({delta >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </PanelCard>

            <PanelCard
              title="Filler words"
              hint={fillerSeries.length ? `last ${fillerSeries.length}` : 'no data'}
            >
              {fillerSeries.length === 0 ? (
                <div style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
                  No filler-word data yet — finish a full evaluation to start tracking.
                </div>
              ) : (
                <FillerTrend
                  series={fillerSeries}
                  latest={fillerLatest}
                  avg={fillerAvg}
                  delta={fillerDelta}
                  w={w}
                  h={h}
                />
              )}
            </PanelCard>
          </div>

          {/* Latest vs Previous (most improved/declined) */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
            borderRadius: 14, padding: 16, fontFamily: 'sans-serif',
          }}>
            {subHeading('Latest vs Previous')}
            {!previousDims ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-dim)', marginTop: 10 }}>
                Complete a second session to see what improved and what slipped.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                {mostImproved ? (
                  <DeltaRow direction="up" label={mostImproved.label} value={mostImproved.latest} delta={mostImproved.delta} />
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>No dimension improved this session.</div>
                )}
                {mostDeclined ? (
                  <DeltaRow direction="down" label={mostDeclined.label} value={mostDeclined.latest} delta={mostDeclined.delta} />
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>No dimension declined this session.</div>
                )}
              </div>
            )}
          </div>

          {/* Readiness Journey */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
            borderRadius: 14, padding: 16, fontFamily: 'sans-serif',
          }}>
            {subHeading('Readiness Journey')}
            <ReadinessJourney sessions={chrono} />
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
           DEEP DIVE TAB — "Where exactly are my strengths and gaps?"
           ═══════════════════════════════════════════════════════════════════════ */}
      {insightsLevel === 'full' && effectiveActiveTab === 'deep' && (
        <div
          role="tabpanel"
          id="insights-tabpanel-deep"
          aria-labelledby="insights-tab-deep"
          tabIndex={0}
          style={{ display: 'flex', flexDirection: 'column', gap: 18, outline: 'none' }}
        >
          {/* Radar — 12 dimensions, latest session */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
            borderRadius: 14, padding: 16,
          }}>
            {subHeading(
              selectedRole === '__all__'
                ? (radarSessionCount > 1
                    ? `12 Dimensions — average of ${radarSessionCount} sessions`
                    : '12 Dimensions')
                : (radarSessionCount > 1
                    ? `${selectedRole} — 12 Dimensions (avg of ${radarSessionCount} sessions)`
                    : `${selectedRole} — 12 Dimensions`)
            )}
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
              <div style={{ maxWidth: 380, width: '100%' }}>
                <RadarChart dims={avgDims} />
              </div>
            </div>
          </div>

          {/* Where to focus + By role */}
          <div style={{
            display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          }}>
            <PanelCard title="Where to focus" hint="weakest first">
              <RankedBars rows={categoryRows} emptyLabel="No category breakdown available yet." />
            </PanelCard>

            <PanelCard title="By role" hint={`${Object.keys(roleStats).length} role${Object.keys(roleStats).length === 1 ? '' : 's'}`}>
              <RankedBars
                rows={roleRows.sort((a, b) => b.value - a.value)}
                emptyLabel="Practise more roles to compare them."
              />
            </PanelCard>
          </div>

          {/* Dimension Trends grouped by category */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)',
            borderRadius: 14, padding: 16, fontFamily: 'sans-serif',
          }}>
            {subHeading('Dimension Trends')}
            <div style={{
              display: 'grid', gap: 14, marginTop: 10,
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            }}>
              {CATEGORY_GROUPS_RICH.map((g) => (
                <div key={g.label}>
                  <div style={{
                    fontSize: 11.5, fontWeight: 700, color: 'var(--text-strong)',
                    marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span>{g.icon}</span>
                    <span>{g.label}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {g.keys.map(k => (
                      <DimensionRow
                        key={k}
                        label={DIM_LABELS[k]}
                        latest={dimDeltas[k].latest}
                        prev={dimDeltas[k].prev}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ── Recent form by role: one row per role with last-5-score dots + verdict ──
// Layout: wide (≥520px) shows role | dots | metrics in a 3-col grid; narrow
// stacks them so long role names and metric copy don't crowd each other.
const RecentFormRow = ({ row }) => {
  const { role, scores, avg, swing, totalCount, verdict } = row;
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 520 : false
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 520);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Pad with placeholders so every row has a 5-dot rail (visually steady).
  const dots = [...scores];
  while (dots.length < 5) dots.push(null);
  return (
    <div style={{
      display: 'grid', gap: narrow ? 6 : 8,
      gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 1.4fr) auto minmax(0, 1.4fr)',
      alignItems: 'center', padding: '8px 4px',
      borderTop: '1px solid var(--border-soft)',
      fontFamily: 'sans-serif',
    }}>
      {/* Role + count */}
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 700, color: 'var(--text-strong)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{role}</div>
        <div style={{ fontSize: 10.5, color: 'var(--text-dimmer)', marginTop: 2 }}>
          {totalCount} session{totalCount === 1 ? '' : 's'} · last {scores.length}
        </div>
      </div>
      {/* Dots */}
      <div style={{
        display: 'flex', gap: 5, alignItems: 'center',
        justifyContent: narrow ? 'flex-start' : 'center',
      }}>
        {dots.map((s, i) => {
          if (s == null) {
            return <span key={i} style={{
              width: 10, height: 10, borderRadius: 99,
              border: '1px dashed var(--border-soft)', opacity: 0.5,
            }} />;
          }
          const c = scoreColor(s);
          return (
            <span key={i}
              title={`Score ${s.toFixed(1)} / 10`}
              style={{
                width: 10, height: 10, borderRadius: 99,
                background: c, boxShadow: `0 0 0 1px ${c}33`,
              }} />
          );
        })}
      </div>
      {/* Avg · swing · verdict */}
      <div style={{ textAlign: narrow ? 'left' : 'right' }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>
          <strong>{avg.toFixed(1)}</strong>
          <span style={{ color: 'var(--text-dimmer)', fontWeight: 500 }}> avg · {swing.toFixed(1)} swing</span>
        </div>
        <div style={{
          fontSize: 11, fontWeight: 700, marginTop: 2, color: verdict.color,
        }}>
          {verdict.dot} {verdict.label}
        </div>
      </div>
    </div>
  );
};

// ── Small presentational helpers used only by AnalyticsSection ──
const Stat = ({ value, label }) => (
  <div>
    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-strong)', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 10.5, color: 'var(--text-dimmer)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>{label}</div>
  </div>
);

const DeltaRow = ({ direction, label, value, delta }) => {
  const up = direction === 'up';
  const color = up ? '#22c55e' : '#f87171';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 12px',
      background: up ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
      border: `1px solid ${up ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
      borderRadius: 10,
    }}>
      <div style={{ fontSize: 22, color, lineHeight: 1, fontWeight: 800 }}>{up ? '↑' : '↓'}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-dimmer)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {up ? 'Most improved' : 'Most declined'}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{label}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color }}>
          {up ? '+' : ''}{delta.toFixed(1)}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>now {value.toFixed(1)}</div>
      </div>
    </div>
  );
};

const DimensionRow = ({ label, latest, prev }) => {
  const dlt    = (latest != null && prev != null) ? latest - prev : null;
  const arrow  = dlt == null ? '→' : dlt > 0.05 ? '↑' : dlt < -0.05 ? '↓' : '→';
  const color  = dlt == null ? 'var(--text-dim)'
                : dlt > 0.05 ? '#22c55e'
                : dlt < -0.05 ? '#f87171'
                : 'var(--text-dim)';
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: 8, padding: '4px 0', fontSize: 12,
    }}>
      <span style={{ color: 'var(--text-strong)' }}>{label}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'sans-serif' }}>
        {latest != null ? (
          <span style={{ color: scoreColor(latest), fontWeight: 700 }}>{latest.toFixed(1)}</span>
        ) : (
          <span style={{ color: 'var(--text-dim)' }}>—</span>
        )}
        <span style={{ color, fontWeight: 700, minWidth: 14, textAlign: 'center' }}>{arrow}</span>
        <span style={{ color, fontSize: 11, fontWeight: 600, minWidth: 36, textAlign: 'right' }}>
          {dlt == null ? '' : `${dlt > 0 ? '+' : ''}${dlt.toFixed(1)}`}
        </span>
      </span>
    </div>
  );
};

// Horizontal timeline showing the readiness label per session in chronological
// order. Latest pill highlighted in orange. Connectors between pills.
const ReadinessJourney = ({ sessions }) => {
  const points = sessions
    .map((s) => s.feedback?.readiness ? { label: s.feedback.readiness, date: s.started_at || s.ended_at } : null)
    .filter(Boolean);
  if (points.length === 0) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--text-dim)', marginTop: 10, fontFamily: 'sans-serif' }}>
        Complete more sessions to unlock this insight.
      </div>
    );
  }
  return (
    <div style={{
      marginTop: 10, display: 'flex', alignItems: 'stretch', gap: 0,
      overflowX: 'auto', paddingBottom: 6,
    }}>
      {points.map((p, i) => {
        const isLatest = i === points.length - 1;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <div style={{
              padding: '8px 12px', borderRadius: 10, fontFamily: 'sans-serif',
              background: isLatest ? 'rgba(249,115,22,0.18)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${isLatest ? 'rgba(249,115,22,0.55)' : 'var(--border-soft)'}`,
              minWidth: 110, textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, color: 'var(--text-dimmer)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>
                #{i + 1}
              </div>
              <div style={{
                fontSize: 12, fontWeight: 700,
                color: isLatest ? '#f97316' : 'var(--text-strong)',
              }}>
                {p.label}
              </div>
            </div>
            {i < points.length - 1 && (
              <div style={{
                width: 18, height: 2,
                background: 'rgba(255,255,255,0.12)',
                margin: '0 2px',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Streak / cadence ribbon ───────────────────────────────────────────────
// Compact strip rendered just under the section header. Shows the current
// practice streak, the last-session recency, and a gentle nudge if the user
// has gone quiet for a while.
const StreakRibbon = ({ info, totalSessions }) => {
  const { streak, daysSinceLast } = info;
  let primary, color, sub;
  if (totalSessions === 0) {
    return null; // empty-state path is handled upstream
  }
  if (streak >= 2) {
    primary = `🔥 ${streak}-day streak`;
    color = '#f97316';
    sub = 'Keep it going — even one short session today extends it.';
  } else if (daysSinceLast === 0) {
    primary = '✨ Practised today';
    color = '#22c55e';
    sub = 'One more tomorrow starts a streak.';
  } else if (daysSinceLast === 1) {
    primary = '👋 Last session yesterday';
    color = '#fbbf24';
    sub = 'Practise today to begin a streak.';
  } else if (daysSinceLast != null && daysSinceLast <= 7) {
    primary = `⏳ ${daysSinceLast} days since last session`;
    color = '#fbbf24';
    sub = 'A 10-minute session today gets you back on track.';
  } else if (daysSinceLast != null) {
    primary = `💤 ${daysSinceLast} days since last session`;
    color = '#94a3b8';
    sub = 'Welcome back — even a quick session resets your rhythm.';
  } else {
    return null;
  }
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px', borderRadius: 12,
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid var(--border-soft)',
      fontFamily: 'sans-serif', flexWrap: 'wrap',
    }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color }}>{primary}</div>
      <div style={{ fontSize: 12, color: 'var(--text-dim)', flex: 1, minWidth: 0 }}>{sub}</div>
    </div>
  );
};

// ── Filler-word trend mini chart (per-minute rate) ────────────────────────
// Shares the same width/height as the score-trend sparkline so they align
// visually inside the 4/5-panel grid. Lower is better, so the colouring is
// inverted relative to the score chart (down = green, up = red).
const FillerTrend = ({ series, latest, avg, delta, w, h }) => {
  const values = series.map(x => x.perMin);
  const lo = Math.min(...values), hi = Math.max(...values);
  // y-axis scale: 0 at the floor, dynamic ceiling so spikes still fit.
  const cap = Math.max(8, hi * 1.1);
  const pad = 4;
  // Build path inline since the shared helper hardcodes 0–10 for scores.
  const xFor = (i) => values.length === 1 ? w / 2 : pad + i * ((w - pad * 2) / (values.length - 1));
  const yFor = (v) => h - pad - (Math.min(v, cap) / cap) * (h - pad * 2);
  const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`).join(' ');
  // For filler words, going DOWN is good. So invert the colour mapping.
  const deltaColor = delta == null
    ? 'var(--text-dim)'
    : delta < -0.05 ? '#22c55e'
    : delta > 0.05  ? '#f87171'
    : 'var(--text-dim)';
  const arrow = delta == null ? '' : delta < -0.05 ? '▼' : delta > 0.05 ? '▲' : '→';
  return (
    <>
      <div style={{ position: 'relative' }}>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" aria-hidden>
          {/* Reference line at 4/min (the "natural" threshold) */}
          {cap > 4 && (
            <line x1="0" y1={yFor(4)} x2={w} y2={yFor(4)}
              stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
          )}
          <path d={path} fill="none" stroke="#38bdf8" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />
          {values.map((v, i) => (
            <circle key={i} cx={xFor(i)} cy={yFor(v)} r="2.5" fill="#38bdf8" />
          ))}
        </svg>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-dim)' }}>
        <span>{lo.toFixed(1)} low</span>
        <span style={{ color: deltaColor, fontWeight: 700 }}>
          latest {latest.perMin.toFixed(1)}/min {arrow}
        </span>
        <span>{hi.toFixed(1)} high</span>
      </div>
      {avg != null && (
        <div style={{ fontSize: 11, color: 'var(--text-dimmer)', marginTop: 2 }}>
          Average across these sessions: <strong style={{ color: 'var(--text-dim)' }}>{avg.toFixed(1)}/min</strong>
          {' '}— under 4/min reads as natural; above 8/min is distracting.
        </div>
      )}
    </>
  );
};

// ── AI weekly coach note ──────────────────────────────────────────────────
// One-paragraph natural-language summary of the user's last 7 days of
// practice, generated by the LLM and cached in localStorage on a
// per-user, per-ISO-week basis so the call only runs once per user per week.
// Renders nothing when the user has zero sessions in the last 7 days.
const WeeklyCoachNote = ({ sessions, userId }) => {
  // Compact, deterministic key derived from the last 7 days of sessions, used
  // for the cache lookup. We include both the ISO-week tag and a hash of the
  // session ids so a brand-new session within the same week busts the cache
  // (the user just earned fresh data — we want a fresh note).
  const { recent, cacheKey } = useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const r = (sessions || []).filter(s => {
      const t = new Date(s.started_at || s.ended_at).getTime();
      return isFinite(t) && t >= sevenDaysAgo;
    });
    // True ISO 8601 week-year tag (YYYY-Www). This is the algorithm used by
    // the ISO standard: shift the date to the Thursday of its week, then
    // count weeks from the first Thursday of that year.
    const now = new Date();
    const dt = new Date(now);
    dt.setHours(0, 0, 0, 0);
    dt.setDate(dt.getDate() + 3 - ((dt.getDay() + 6) % 7));
    const week1 = new Date(dt.getFullYear(), 0, 4);
    const isoWeek = 1 + Math.round(
      ((dt - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7
    );
    const weekTag = `${dt.getFullYear()}-W${String(isoWeek).padStart(2, '0')}`;
    // Collision-resistant id digest: djb2-style 32-bit hash of the full
    // joined-and-sorted id list, base36 encoded. Two different session sets
    // would have to collide on this hash AND on length to share a cache entry,
    // which is vanishingly unlikely.
    const ids = r.map(s => s.id).sort().join(',');
    let h = 5381;
    for (let i = 0; i < ids.length; i++) h = ((h << 5) + h + ids.charCodeAt(i)) | 0;
    const idHash = (h >>> 0).toString(36);
    return {
      recent: r,
      cacheKey: `nti_weekly_note_${userId}_${weekTag}_${r.length}_${idHash}`,
    };
  }, [sessions, userId]);

  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (recent.length === 0) {
      setNote(null);
      setLoading(false);
      setError(null);
      return;
    }
    // Cache hit?
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        setNote(cached);
        setLoading(false);
        setError(null);
        return;
      }
    } catch { /* ignore quota / privacy-mode errors */ }

    setLoading(true);
    setError(null);

    // Build a compact, token-cheap digest — no transcripts, just numbers and
    // labels. The model gets enough to write a meaningful paragraph without
    // costing input tokens.
    const digest = recent.map((s, i) => ({
      n: i + 1,
      role: s.role || 'general',
      score: typeof s.overall_score === 'number' ? Number(s.overall_score.toFixed(1)) : null,
      readiness: s.feedback?.readiness || null,
      topPriority: Array.isArray(s.feedback?.topPriorities) ? s.feedback.topPriorities[0] : null,
      strongestDim: (() => {
        const r = rankDims(s.dimension_scores || {}).slice(0, 1)[0];
        return r ? `${r.label} (${r.score.toFixed(1)})` : null;
      })(),
      weakestDim: (() => {
        const r = rankDims(s.dimension_scores || {}, true).slice(0, 1)[0];
        return r ? `${r.label} (${r.score.toFixed(1)})` : null;
      })(),
    }));

    const userMsg = [{
      role: 'user',
      content: `Here is a JSON digest of the user's last ${recent.length} interview practice session(s) from the past 7 days:\n\n${JSON.stringify(digest, null, 2)}\n\nWrite a short, warm coach's note (3–4 sentences, max ~70 words). Mention what improved, the standout strength, the recurring gap, and one concrete suggestion for the next session. Use second person ("you"). No headings, no bullets, no markdown — plain prose only. Do not invent data not present in the digest. Do not mention "AI", "Claude", or any model name.`,
    }];

    callClaude(userMsg, 'You are a warm, concise interview coach summarising a candidate\'s recent practice for them.', 200, { callKind: 'weekly_note' })
      .then(text => {
        if (cancelled) return;
        const trimmed = (text || '').trim();
        if (!trimmed) {
          setError(true);
          return;
        }
        setNote(trimmed);
        try { localStorage.setItem(cacheKey, trimmed); } catch { /* ignore */ }
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [cacheKey, recent]);

  if (recent.length === 0) return null;

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(56,189,248,0.10), rgba(56,189,248,0.02))',
      border: '1px solid rgba(56,189,248,0.30)',
      borderRadius: 14, padding: 16, fontFamily: 'sans-serif',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{
        fontSize: 11.5, fontWeight: 700, color: '#7dd3fc',
        textTransform: 'uppercase', letterSpacing: 0.6,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span>💬 Your coach this week</span>
        <span style={{ color: 'var(--text-dimmer)', fontWeight: 500, fontSize: 10.5, textTransform: 'none', letterSpacing: 0.2 }}>
          · {recent.length} session{recent.length === 1 ? '' : 's'} in the last 7 days
        </span>
      </div>
      {loading && (
        <div style={{ fontSize: 12.5, color: 'var(--text-dim)', fontStyle: 'italic' }}>
          Reading through your week…
        </div>
      )}
      {!loading && error && (
        <div style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
          Couldn't generate a note this week — try refreshing in a minute.
        </div>
      )}
      {!loading && !error && note && (
        <div style={{
          fontSize: 13.5, color: 'var(--text-strong)',
          lineHeight: 1.6, fontFamily: 'Lora, Georgia, serif',
        }}>{note}</div>
      )}
    </div>
  );
};

// Renders the full coaching report (overall score, readiness verdict, multi-
// dimensional breakdown, filler words, best/weakest answers, top priorities,
// verdict). Used both during the live evaluation phase and inside the cockpit
// table's expandable Feedback panel — the persisted `feedback` jsonb column
// has the same shape as the live `evaluation` object, so a single component
// covers both.
const FeedbackReport = ({ evaluation }) => {
  if (!evaluation) return null;
  const overall = evaluation.overallScore ?? evaluation.score ?? 0;
  const readiness = evaluation.readiness || "";
  const categories = Array.isArray(evaluation.categories)
    ? evaluation.categories
    : (Array.isArray(evaluation.breakdown)
        ? [{ name: "Skill Breakdown", dimensions: evaluation.breakdown.map((b) => ({
            name: b.name, score: b.score,
            feedback: [...(b.wentWell || []), ...(b.improve || [])].join(" • "),
          })) }]
        : []);
  const fillerWords = evaluation.fillerWords || null;
  const bestAnswer = evaluation.bestAnswer || null;
  const weakestAnswer = evaluation.weakestAnswer || null;
  const topPriorities = Array.isArray(evaluation.topPriorities) ? evaluation.topPriorities : [];
  const verdict = evaluation.readinessVerdict || null;
  // Practice depth — derived from duration, used to be honest about how much
  // signal this score actually represents.
  const depthBand = evaluation.depthBand
    || (typeof evaluation.durationSeconds === 'number' ? getDurationBand(evaluation.durationSeconds) : null);
  const depthVisuals = depthBand ? ({
    insufficient: { bg: "rgba(248,113,113,0.15)", fg: "#fca5a5", bd: "rgba(248,113,113,0.4)", icon: "⚠️" },
    very_brief:   { bg: "rgba(251,146,60,0.15)",  fg: "#fdba74", bd: "rgba(251,146,60,0.4)",  icon: "⏱" },
    brief:        { bg: "rgba(252,211,77,0.15)",  fg: "#fcd34d", bd: "rgba(252,211,77,0.4)",  icon: "⏱" },
    standard:     { bg: "rgba(134,239,172,0.13)", fg: "#86efac", bd: "rgba(134,239,172,0.35)", icon: "✓" },
    deep:         { bg: "rgba(96,165,250,0.16)",  fg: "#93c5fd", bd: "rgba(96,165,250,0.42)",  icon: "🏆" },
  })[depthBand.key] || null : null;
  const readinessColor = {
    "Beginner": { bg: "rgba(248,113,113,0.15)", fg: "#fca5a5", bd: "rgba(248,113,113,0.4)" },
    "Developing": { bg: "rgba(252,211,77,0.15)", fg: "#fcd34d", bd: "rgba(252,211,77,0.4)" },
    "Interview Ready": { bg: "rgba(134,239,172,0.15)", fg: "#86efac", bd: "rgba(134,239,172,0.4)" },
    "Strong Candidate": { bg: "rgba(96,165,250,0.18)", fg: "#93c5fd", bd: "rgba(96,165,250,0.45)" },
  }[readiness] || { bg: "var(--surface-2)", fg: "var(--text-muted)", bd: "var(--surface-strong)" };
  const parseScore = (s) => {
    if (typeof s === "number" && isFinite(s)) return s;
    const m = String(s ?? "").match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : NaN;
  };
  const fmtScore = (s) => {
    const n = parseScore(s);
    if (!isFinite(n)) return "—";
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  };
  return (
    <div style={{ fontFamily: "sans-serif" }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 16,
        marginBottom: 8, flexWrap: "wrap",
      }}>
        <div style={{
          width: 96, height: 96, borderRadius: "50%",
          background: "linear-gradient(135deg,#f97316,#dc2626)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          boxShadow: "0 0 28px rgba(249,115,22,0.5)", flexShrink: 0,
        }}>
          <span style={{ color: "#fff", fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
            {fmtScore(overall)}
          </span>
          <span style={{ color: "var(--text)", fontSize: 10, marginTop: 3, opacity: 0.85 }}>OVERALL</span>
        </div>
        <div style={{ maxWidth: 360, textAlign: "left" }}>
          <div style={{ color: "var(--text-faint)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 4 }}>
            Overall Score
          </div>
          <p style={{ color: "var(--text-strong)", fontSize: 14.5, lineHeight: 1.55, margin: "0 0 10px" }}>
            {evaluation.headline}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {readiness && (
              <div style={{
                display: "inline-block", padding: "5px 13px", borderRadius: 99,
                background: readinessColor.bg, color: readinessColor.fg,
                border: `1px solid ${readinessColor.bd}`,
                fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
              }}>
                🎯 Readiness: {readiness}
              </div>
            )}
            {depthBand && depthVisuals && (
              <div
                title={depthBand.cap == null
                  ? `Practice depth: ${depthBand.label}. Duration was sufficient for an unrestricted score.`
                  : `Practice depth: ${depthBand.label}. Because the session was short, dimension scores are capped at ${depthBand.cap.toFixed(1)}/10 — there isn't enough transcript for a full evaluation.`}
                style={{
                  display: "inline-block", padding: "5px 13px", borderRadius: 99,
                  background: depthVisuals.bg, color: depthVisuals.fg,
                  border: `1px solid ${depthVisuals.bd}`,
                  fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
                }}
              >
                {depthVisuals.icon} {depthBand.label}
              </div>
            )}
          </div>
        </div>
      </div>

      {depthBand && depthBand.cap != null && (
        <div style={{
          marginTop: 14, padding: "10px 14px", borderRadius: 10,
          background: depthVisuals.bg, border: `1px solid ${depthVisuals.bd}`,
          color: depthVisuals.fg, fontSize: 12.5, lineHeight: 1.5,
          fontFamily: "sans-serif", textAlign: "left",
        }}>
          <strong>Heads-up — this was a short session.</strong> A real interviewer
          can't fairly judge a candidate on {(() => {
            const ds = Number(evaluation?.durationSeconds) || 0;
            if (ds < 60) return 'under a minute';
            const m = Math.round(ds / 60);
            return `about ${m} minute${m === 1 ? '' : 's'}`;
          })()} of conversation, so we've capped each dimension at <strong>{depthBand.cap.toFixed(1)}/10</strong> and the feedback below reflects what we could actually see. For a meaningful evaluation, aim for at least 15 minutes next time.
        </div>
      )}

      <div style={{
        textAlign: "center", marginTop: 12,
        color: "var(--text-faint)", fontSize: 11.5, letterSpacing: 0.4,
      }}>
        All scores are out of 10
      </div>

      {/* Categorized scoring breakdown */}
      <div style={{ marginTop: 22 }}>
        <h4 style={{
          color: "#fdba74", fontSize: 12.5, marginBottom: 14,
          letterSpacing: 0.6, textTransform: "uppercase",
        }}>
          📊 Multi-Dimensional Breakdown
        </h4>
        {categories.map((cat, ci) => {
          const dims = Array.isArray(cat.dimensions) ? cat.dimensions : [];
          const avg = dims.length
            ? dims.reduce((s, d) => {
                const n = parseScore(d.score);
                return s + (isFinite(n) ? n : 0);
              }, 0) / dims.length
            : 0;
          return (
            <div key={ci} style={{
              background: "var(--surface)", border: "1px solid var(--border-soft)",
              borderRadius: 14, padding: "14px 16px", marginBottom: 14,
            }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                marginBottom: 12, paddingBottom: 8,
                borderBottom: "1px solid var(--border-soft)",
              }}>
                <span style={{ color: "#fdba74", fontSize: 13, fontWeight: 700, letterSpacing: 0.3 }}>
                  {cat.name}
                </span>
                <span style={{ color: "var(--text-faint)", fontSize: 11, letterSpacing: 0.3 }}>
                  avg {fmtScore(avg)}
                </span>
              </div>
              {dims.map((d, di) => {
                const n = parseScore(d.score);
                const pct = Math.max(0, Math.min(100, ((isFinite(n) ? n : 0) / 10) * 100));
                return (
                  <div key={di} style={{ marginBottom: di === dims.length - 1 ? 0 : 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                      <span style={{ color: "var(--text-strong)", fontSize: 13.5, fontWeight: 600 }}>
                        {d.name}
                      </span>
                      <span style={{ color: "#f97316", fontSize: 14, fontWeight: 800, letterSpacing: 0.3 }}>
                        {fmtScore(d.score)}
                      </span>
                    </div>
                    <div style={{
                      height: 6, background: "var(--surface-2)", borderRadius: 99,
                      overflow: "hidden", marginBottom: 8,
                    }}>
                      <div style={{
                        width: `${pct}%`, height: "100%",
                        background: "linear-gradient(90deg,#f97316,#dc2626)",
                        transition: "width 0.6s ease",
                      }} />
                    </div>
                    {d.feedback && (
                      <p style={{
                        color: "var(--text-muted)", fontSize: 12.5, lineHeight: 1.55, margin: 0,
                      }}>
                        {d.feedback}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Filler words */}
      {fillerWords && (
        <div style={{ marginTop: 22 }}>
          <h4 style={{ color: "#fdba74", fontSize: 12.5, marginBottom: 12, letterSpacing: 0.6, textTransform: "uppercase" }}>
            🗣 Filler Word Count
          </h4>
          <div style={{
            background: "var(--surface)", border: "1px solid var(--border-soft)",
            borderRadius: 14, padding: "14px 16px",
          }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
              <span style={{ color: "#f97316", fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
                {fillerWords.total ?? 0}
              </span>
              <span style={{ color: "var(--text-faint)", fontSize: 12 }}>total filler words</span>
            </div>
            {Array.isArray(fillerWords.breakdown) && fillerWords.breakdown.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                {fillerWords.breakdown.map((f, i) => (
                  <span key={i} style={{
                    padding: "4px 10px", borderRadius: 99,
                    background: "var(--surface-2)", border: "1px solid var(--border-soft)",
                    color: "var(--text-muted)", fontSize: 12,
                  }}>
                    "{f.word}" × <strong style={{ color: "var(--text-strong)" }}>{f.count ?? 0}</strong>
                  </span>
                ))}
              </div>
            )}
            {fillerWords.comment && (
              <p style={{ color: "var(--text-muted)", fontSize: 12.5, lineHeight: 1.55, margin: 0 }}>
                {fillerWords.comment}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Best & weakest answer */}
      {(bestAnswer || weakestAnswer) && (
        <div style={{
          marginTop: 22, display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12,
        }}>
          {bestAnswer && (
            <div style={{
              background: "var(--surface)", border: "1px solid rgba(134,239,172,0.35)",
              borderRadius: 14, padding: "14px 16px",
            }}>
              <div style={{ color: "#86efac", fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 8 }}>
                ⭐ Best Answer
              </div>
              {bestAnswer.questionSummary && (
                <div style={{ color: "var(--text-strong)", fontSize: 13, fontWeight: 600, marginBottom: 6, lineHeight: 1.45 }}>
                  {bestAnswer.questionSummary}
                </div>
              )}
              {bestAnswer.why && (
                <p style={{ color: "var(--text-muted)", fontSize: 12.5, lineHeight: 1.55, margin: 0 }}>
                  {bestAnswer.why}
                </p>
              )}
            </div>
          )}
          {weakestAnswer && (
            <div style={{
              background: "var(--surface)", border: "1px solid rgba(252,211,77,0.35)",
              borderRadius: 14, padding: "14px 16px",
            }}>
              <div style={{ color: "#fcd34d", fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 8 }}>
                ⚠ Weakest Answer
              </div>
              {weakestAnswer.questionSummary && (
                <div style={{ color: "var(--text-strong)", fontSize: 13, fontWeight: 600, marginBottom: 6, lineHeight: 1.45 }}>
                  {weakestAnswer.questionSummary}
                </div>
              )}
              {weakestAnswer.why && (
                <p style={{ color: "var(--text-muted)", fontSize: 12.5, lineHeight: 1.55, margin: 0 }}>
                  {weakestAnswer.why}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Top 3 priorities */}
      {topPriorities.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <h4 style={{ color: "#fdba74", fontSize: 12.5, marginBottom: 12, letterSpacing: 0.6, textTransform: "uppercase" }}>
            🎯 Top 3 Priorities Before Next Interview
          </h4>
          <div style={{
            background: "var(--surface)", border: "1px solid var(--border-soft)",
            borderRadius: 14, padding: "14px 16px",
          }}>
            {topPriorities.slice(0, 3).map((p, i) => (
              <div key={i} style={{
                display: "flex", gap: 12, alignItems: "flex-start",
                padding: "10px 0",
                borderTop: i === 0 ? "none" : "1px solid var(--border-soft)",
              }}>
                <span style={{
                  flexShrink: 0, width: 26, height: 26, borderRadius: "50%",
                  background: "linear-gradient(135deg,#f97316,#dc2626)",
                  color: "#fff", fontSize: 12, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{i + 1}</span>
                <p style={{ color: "var(--text-strong)", fontSize: 13, lineHeight: 1.55, margin: 0, flex: 1 }}>
                  {p}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Readiness verdict */}
      {verdict && (verdict.readyFor || verdict.notReadyFor || verdict.keyBlocker) && (
        <div style={{ marginTop: 22 }}>
          <h4 style={{ color: "#fdba74", fontSize: 12.5, marginBottom: 12, letterSpacing: 0.6, textTransform: "uppercase" }}>
            🧭 Interview Readiness Verdict
          </h4>
          <div style={{
            background: "var(--surface)", border: "1px solid var(--border-soft)",
            borderRadius: 14, padding: "14px 16px",
          }}>
            {verdict.readyFor && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ color: "#86efac", fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>
                  ✓ READY FOR
                </div>
                <p style={{ color: "var(--text-strong)", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
                  {verdict.readyFor}
                </p>
              </div>
            )}
            {verdict.notReadyFor && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ color: "#fca5a5", fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>
                  ✕ NOT YET READY FOR
                </div>
                <p style={{ color: "var(--text-strong)", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
                  {verdict.notReadyFor}
                </p>
              </div>
            )}
            {verdict.keyBlocker && (
              <div>
                <div style={{ color: "#fcd34d", fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>
                  🔑 KEY BLOCKER
                </div>
                <p style={{ color: "var(--text-strong)", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
                  {verdict.keyBlocker}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mandatory disclaimer at the bottom of every feedback report. This is
          an AI practice tool — feedback is informational only and not
          professional career, legal, or psychological advice. Kept visible
          regardless of session depth so users always see it before acting on
          the score. */}
      <div style={{
        marginTop: 24, paddingTop: 14,
        borderTop: "1px solid var(--border-soft)",
        color: "var(--text-faint)", fontSize: 11.5, lineHeight: 1.55,
        fontStyle: "italic", textAlign: "center", opacity: 0.85,
      }}>
        Disclaimer: This feedback is generated by AI for practice purposes
        only. Scores and suggestions are not a guarantee of real interview
        performance or job outcomes, and should not be treated as professional
        career, legal, or psychological advice. Use it as one signal among
        many.
      </div>
    </div>
  );
};

// ─── Recent sessions table ───────────────────────────────────────────────────
// 13-column tabular layout for the cockpit "Recent sessions" panel.
//   Interview # | Role | Date | Start time | Duration | Overall | Communication |
//   Technical depth | Structure | Role fit | JD | Feedback | Transcript
// JD / Feedback / Transcript are expandable: clicking the cell button opens
// (or closes) a full-width detail panel directly under the row. Numbering
// shows the most recent session first (n, n-1, … 1) within the visible page.
const RecentSessionsTable = ({
  sessions, expandedRow, onToggle, onRedo, fmtDate, fmtDuration,
}) => {
  const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  };
  const fmtScore = (n) => {
    if (typeof n !== 'number' || !isFinite(n)) return '—';
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  };
  const tdStyle = {
    padding: '10px 8px', fontSize: 12.5, color: 'var(--text-strong)',
    borderTop: '1px solid var(--border-soft)', whiteSpace: 'nowrap',
    fontFamily: 'sans-serif',
  };
  const thStyle = {
    padding: '8px', fontSize: 10.5, color: 'var(--text-faint)',
    fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase',
    textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-soft)',
    fontFamily: 'sans-serif',
  };
  const detailBtn = (active) => ({
    padding: '5px 10px', borderRadius: 8,
    background: active ? 'rgba(249,115,22,0.18)' : 'transparent',
    border: `1px solid ${active ? 'rgba(249,115,22,0.55)' : 'var(--border-soft)'}`,
    color: active ? '#f97316' : 'var(--text-muted)',
    fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'sans-serif', whiteSpace: 'nowrap',
  });
  const scoreCell = (n) => {
    if (typeof n !== 'number' || !isFinite(n)) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
    return (
      <span style={{ color: scoreColor(n), fontWeight: 700 }}>{fmtScore(n)}</span>
    );
  };

  return (
    <div style={{ overflowX: 'auto', margin: '0 -6px' }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse',
        minWidth: 1100,
      }}>
        <caption style={{
          position: 'absolute', width: 1, height: 1, overflow: 'hidden',
          clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
        }}>
          Recent interview sessions with category scores and expandable details
        </caption>
        <thead>
          <tr>
            <th style={thStyle}>#</th>
            <th style={thStyle}>Role</th>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Start time</th>
            <th style={thStyle}>Duration</th>
            <th style={thStyle}>Overall</th>
            <th style={thStyle}>Communication</th>
            <th style={thStyle}>Technical&nbsp;depth</th>
            <th style={thStyle}>Structure</th>
            <th style={thStyle}>Role&nbsp;fit</th>
            <th style={thStyle}>JD</th>
            <th style={thStyle}>Feedback</th>
            <th style={thStyle}>Transcript</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s, i) => {
            const number = sessions.length - i;
            const dims = s.dimension_scores || {};
            const cat = (keys) => avgFromKeys(dims, keys);
            const comm = cat(['verbal_clarity','answer_relevance','conciseness']);
            const tech = cat(['technical_depth','role_specific','situational_judgement']);
            const struct = cat(['critical_thinking','star_structure','examples_metrics']);
            const fit = cat(['confidence_composure','enthusiasm','professional_language']);
            const isOpen = expandedRow.id === s.id;
            const hasJD = !!(s.job_description && String(s.job_description).trim());
            const hasFb = !!s.feedback;
            const hasTx = Array.isArray(s.transcript) && s.transcript.length > 0;
            return (
              <Fragment key={s.id}>
                <tr>
                  <td style={{ ...tdStyle, color: 'var(--text-faint)', fontWeight: 700 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {number}
                      {s.role && (
                        <button
                          onClick={() => onRedo(s)}
                          title="Re-do this interview (uses 1 from your quota)"
                          aria-label={`Re-do interview ${number}`}
                          style={{
                            padding: '2px 7px', borderRadius: 6,
                            background: 'transparent',
                            border: '1px solid rgba(249,115,22,0.45)',
                            color: '#f97316', fontSize: 10.5, fontWeight: 700,
                            cursor: 'pointer', fontFamily: 'sans-serif',
                            lineHeight: 1.2,
                          }}
                        >↻ Re-do</button>
                      )}
                    </span>
                  </td>
                  <td style={{
                    ...tdStyle, color: 'var(--text-strong)', fontWeight: 600,
                    maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
                  }} title={s.role || ''}>
                    {s.role || <span style={{ color: 'var(--text-dim)' }}>—</span>}
                  </td>
                  <td style={tdStyle}>{fmtDate(s.started_at || s.ended_at)}</td>
                  <td style={tdStyle}>{fmtTime(s.started_at)}</td>
                  <td style={tdStyle}>{s.duration_seconds ? fmtDuration(s.duration_seconds) : '—'}</td>
                  <td style={tdStyle}>{scoreCell(s.overall_score)}</td>
                  <td style={tdStyle}>{scoreCell(comm)}</td>
                  <td style={tdStyle}>{scoreCell(tech)}</td>
                  <td style={tdStyle}>{scoreCell(struct)}</td>
                  <td style={tdStyle}>{scoreCell(fit)}</td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => onToggle(s.id, 'jd')}
                      disabled={!hasJD}
                      title={hasJD ? 'View job description' : 'No job description was pasted for this session'}
                      aria-expanded={isOpen && expandedRow.panel === 'jd'}
                      aria-controls={`session-panel-${s.id}`}
                      style={{
                        ...detailBtn(isOpen && expandedRow.panel === 'jd'),
                        opacity: hasJD ? 1 : 0.4, cursor: hasJD ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {isOpen && expandedRow.panel === 'jd' ? '▲ JD' : '▼ JD'}
                    </button>
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => onToggle(s.id, 'feedback')}
                      disabled={!hasFb}
                      title={hasFb ? 'View full feedback report' : 'No stored feedback'}
                      aria-expanded={isOpen && expandedRow.panel === 'feedback'}
                      aria-controls={`session-panel-${s.id}`}
                      style={{
                        ...detailBtn(isOpen && expandedRow.panel === 'feedback'),
                        opacity: hasFb ? 1 : 0.4, cursor: hasFb ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {isOpen && expandedRow.panel === 'feedback' ? '▲ Feedback' : '▼ Feedback'}
                    </button>
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => onToggle(s.id, 'transcript')}
                      disabled={!hasTx}
                      title={hasTx ? 'View full transcript' : 'No transcript recorded'}
                      aria-expanded={isOpen && expandedRow.panel === 'transcript'}
                      aria-controls={`session-panel-${s.id}`}
                      style={{
                        ...detailBtn(isOpen && expandedRow.panel === 'transcript'),
                        opacity: hasTx ? 1 : 0.4, cursor: hasTx ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {isOpen && expandedRow.panel === 'transcript' ? '▲ Transcript' : '▼ Transcript'}
                    </button>
                  </td>
                </tr>
                {isOpen && expandedRow.panel && (
                  <tr>
                    <td colSpan={13} id={`session-panel-${s.id}`} style={{
                      padding: 0,
                      background: 'transparent',
                      border: 'none',
                    }}>
                      {/* The sessions table has minWidth:1100, so on phones
                          this <td> spans far beyond the viewport. Pinning the
                          inner panel sticky-left and clamping its width to
                          the viewport keeps JD/feedback readable on mobile
                          without forcing horizontal scroll, while collapsing
                          to the natural table width on desktop. */}
                      <div style={{
                        position: 'sticky',
                        left: 0,
                        // Cockpit main is maxWidth:880 with 24px padding each side,
                        // so the visible scrollport is ≤ 832px on desktop. Capping
                        // the panel here ensures it always fits the visible area
                        // (no right-edge clipping when sticky-pinned), while still
                        // scaling down to the viewport on mobile.
                        width: 'calc(100vw - 32px)',
                        maxWidth: 832,
                        boxSizing: 'border-box',
                        padding: '14px 16px 18px',
                        background: 'rgba(249,115,22,0.04)',
                        borderTop: '1px solid rgba(249,115,22,0.25)',
                        borderBottom: '1px solid rgba(249,115,22,0.25)',
                      }}>
                        <SessionExpandPanel
                          session={s}
                          panel={expandedRow.panel}
                          onClose={() => onToggle(s.id, expandedRow.panel)}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Detail panel rendered under an expanded session row. JD shows the pasted
// description (with character count). Feedback reuses <FeedbackReport/> so
// stored sessions render identically to the live evaluation. Transcript shows
// the Q&A pairs in conversational order.
const SessionExpandPanel = ({ session, panel, onClose }) => {
  const headerRow = (label) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: 12,
    }}>
      <div style={{
        color: '#fdba74', fontSize: 11, fontWeight: 700,
        letterSpacing: 0.6, textTransform: 'uppercase',
        fontFamily: 'sans-serif',
      }}>{label}</div>
      <button onClick={onClose} style={{
        background: 'transparent', border: 'none', color: 'var(--text-muted)',
        fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 4,
      }} title="Close panel">×</button>
    </div>
  );
  if (panel === 'jd') {
    const jd = String(session.job_description || '').trim();
    return (
      <div>
        {headerRow(`Job description · ${jd.length.toLocaleString()} chars`)}
        <pre style={{
          margin: 0, padding: '12px 14px',
          background: 'var(--surface)',
          border: '1px solid var(--border-soft)', borderRadius: 10,
          color: 'var(--text-strong)', fontSize: 12.5, lineHeight: 1.6,
          fontFamily: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          maxHeight: 380, overflowY: 'auto',
        }}>{jd}</pre>
      </div>
    );
  }
  if (panel === 'feedback') {
    // Old sessions stored before the duration-cap feature shipped don't have
    // depthBand on their feedback JSON, but duration_seconds is usually on
    // the row. Only splice when we actually have a real duration — defaulting
    // a NULL to 0 would falsely flag the session as "Insufficient signal".
    const fb = session.feedback || null;
    const knownDuration = typeof session.duration_seconds === 'number' && session.duration_seconds > 0
      ? session.duration_seconds
      : null;
    const enriched = !fb
      ? null
      : (fb.depthBand || fb.durationSeconds != null)
        ? fb
        : (knownDuration != null ? { ...fb, durationSeconds: knownDuration } : fb);
    return (
      <div>
        {headerRow('Feedback')}
        <FeedbackReport evaluation={enriched} />
      </div>
    );
  }
  if (panel === 'transcript') {
    const tx = Array.isArray(session.transcript) ? session.transcript : [];
    return (
      <div>
        {headerRow(`Transcript · ${tx.length} message${tx.length === 1 ? '' : 's'}`)}
        <div style={{
          maxHeight: 480, overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {tx.map((m, idx) => {
            const role = String(m?.role || '').toLowerCase();
            const isUser = role === 'user';
            const isInterviewer = role === 'assistant' || role === 'interviewer';
            const speaker = isUser ? 'You' : isInterviewer ? 'Interviewer' : (m?.role || 'System');
            const accent = isUser ? '#86efac' : '#fdba74';
            return (
              <div key={idx} style={{
                background: 'var(--surface)', border: '1px solid var(--border-soft)',
                borderRadius: 10, padding: '10px 12px',
              }}>
                <div style={{
                  color: accent, fontSize: 10.5, fontWeight: 700,
                  letterSpacing: 0.5, textTransform: 'uppercase',
                  marginBottom: 5, fontFamily: 'sans-serif',
                }}>{speaker}</div>
                <div style={{
                  color: 'var(--text-strong)', fontSize: 13, lineHeight: 1.55,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  fontFamily: 'sans-serif',
                }}>{m?.content || ''}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

const Cockpit = ({
  user,
  usage,
  onStart,
  onSeePlans,
  onSignOut,
  onViewLanding,
  theme,
  toggleTheme,
  onRepeatRole,
  onOpenAdmin,
  onManageSubscription,
  subscriptionBanner,
  onDismissSubscriptionBanner,
  sessionsRefreshKey = 0,
}) => {
  const [supportOpen, setSupportOpen] = useState(false);
  // Prefill payload for the support modal. Keyed off whichever entry
  // point opens the modal — the nav "Support" button clears it (blank
  // form), the pass-holder "Request refund" button populates it with a
  // refund-request template. Empty object = no prefill.
  const [supportPrefill, setSupportPrefill] = useState({});
  // (The previous PROMO_CLAIM_INTENT_KEY → auto-open-support useEffect
  // was removed when the promo flow was rewired to go straight to Lemon
  // checkout with the FRESHSTART discount auto-applied. supportPrefill is
  // still used by the refund-request button below.)
  const [managingSub, setManagingSub] = useState(false);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  // Tracks which session row currently has an expanded panel open. Only one
  // panel is open at a time (clicking a different button moves it; clicking
  // the same button again collapses it).
  const [expandedRow, setExpandedRow] = useState({ id: null, panel: null });
  const toggleExpand = (id, panel) =>
    setExpandedRow((prev) =>
      prev.id === id && prev.panel === panel ? { id: null, panel: null } : { id, panel }
    );

  const u = describeUsage(usage);
  const planCode = String(usage?.plan_code || 'free').toLowerCase();
  // Session history + Insights dashboard are visible to ALL logged-in
  // users (May 2026 conversion-funnel rethink). Free sees the Snapshot
  // tab only with an in-product upsell to Starter+; every paid tier
  // including Starter sees the full Snapshot/Trends/Deep Dive set. The
  // server-side `plan_rules.history_count = -1` already permits unlimited
  // history on every tier, so this is purely a UI-gate relaxation. The
  // tab restriction lives in AnalyticsSection (driven by planCode prop).
  const showHistory = !!user?.id;
  const atLimit = !!u?.atLimit;

  useEffect(() => {
    if (!showHistory || !user?.id) {
      setLoadingSessions(false);
      setRecentSessions([]);
      return;
    }
    let cancelled = false;
    setLoadingSessions(true);
    // We intentionally don't filter on `ended_at IS NOT NULL` here. We saw
    // sessions where the server-side counter (get_usage_info) listed a row
    // as used but this client query returned 0 — the prime suspect is rows
    // where end_interview marked status='completed' but for some reason
    // didn't populate ended_at. Trusting status + ended_reason is enough,
    // and we order by started_at (always present) so a missing ended_at
    // can't hide a row from the cockpit list. ended_at is still selected
    // for the table/analytics formatting.
    // The 12 dimension scores are stored as separate `score_*` columns in
    // Postgres (not a single jsonb `dimension_scores`). We select them all and
    // re-shape into a `dimension_scores` object on the client so the Recent
    // Sessions table and the Insights/Analytics computations can keep using
    // the same shape they do during a live interview.
    supabase
      .from('interview_sessions')
      .select(
        'id, role, experience, started_at, ended_at, overall_score, status, ended_reason, duration_seconds, job_description, interviewer, transcript, feedback, tone, ' +
        'score_verbal_clarity, score_answer_relevance, score_conciseness, ' +
        'score_critical_thinking, score_star_structure, score_examples_metrics, ' +
        'score_confidence_composure, score_enthusiasm, score_professional_language, ' +
        'score_technical_depth, score_role_specific, score_situational_judgement'
      )
      .eq('user_id', user.id)
      .in('status', ['completed', 'abandoned'])
      .neq('ended_reason', 'auto_cleanup')
      .order('started_at', { ascending: false })
      .limit(50)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn('[cockpit] could not load sessions:', error.message, error);
          setRecentSessions([]);
        } else {
          // Defensive: jsonb columns can come back from Supabase as either a
          // native value (object/array) or a JSON string for legacy rows that
          // were inserted with a stringify-ed payload. Normalize once here so
          // the table, expand panels, and FeedbackReport can treat them as
          // their natural shape without re-parsing.
          const safeParse = (v) => {
            if (v == null) return null;
            if (typeof v !== 'string') return v;
            try { return JSON.parse(v); } catch { return null; }
          };
          const shaped = (data || []).map(row => ({
            ...row,
            transcript: (() => {
              const t = safeParse(row.transcript);
              return Array.isArray(t) ? t : [];
            })(),
            feedback: (() => {
              const f = safeParse(row.feedback);
              return f && typeof f === 'object' && !Array.isArray(f) ? f : null;
            })(),
            dimension_scores: {
              verbal_clarity:        row.score_verbal_clarity,
              answer_relevance:      row.score_answer_relevance,
              conciseness:           row.score_conciseness,
              critical_thinking:     row.score_critical_thinking,
              star_structure:        row.score_star_structure,
              examples_metrics:      row.score_examples_metrics,
              confidence_composure:  row.score_confidence_composure,
              enthusiasm:            row.score_enthusiasm,
              professional_language: row.score_professional_language,
              technical_depth:       row.score_technical_depth,
              role_specific:         row.score_role_specific,
              situational_judgement: row.score_situational_judgement,
            },
          }));
          console.log('[cockpit] loaded sessions:', shaped.length, shaped);
          setRecentSessions(shaped);
        }
        setLoadingSessions(false);
      });
    return () => { cancelled = true; };
  }, [user?.id, showHistory, sessionsRefreshKey]);

  const displayName =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    (user?.email ? user.email.split('@')[0] : 'there');

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString(undefined, sameYear
      ? { day: 'numeric', month: 'short' }
      : { day: 'numeric', month: 'short', year: 'numeric' }
    );
  };

  const fmtDuration = (secs) => {
    if (!secs && secs !== 0) return '';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    if (m === 0) return `${s}s`;
    return `${m}m`;
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)', color: 'var(--text-strong)',
      fontFamily: "'Lora', serif", position: 'relative', overflow: 'hidden',
    }}>
      <GlobalStyles />

      {/* Ambient glow */}
      <div aria-hidden style={{
        position: 'absolute', top: -200, left: '50%', transform: 'translateX(-50%)',
        width: 900, height: 900, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(249,115,22,0.16), transparent 60%)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      {/* Top bar */}
      <header style={{
        position: 'relative', zIndex: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 24px', maxWidth: 1180, margin: '0 auto',
        flexWrap: 'wrap', gap: 12,
      }}>
        <Wordmark logoSize={56} fontSize={19} gap={6} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'sans-serif' }}>
          <button onClick={onViewLanding} style={cockpitNavBtn()}>Home</button>
          <button onClick={onSeePlans} style={cockpitNavBtn()}>Plans</button>
          <button onClick={() => { setSupportPrefill({}); setSupportOpen(true); }} style={cockpitNavBtn()}>Support</button>
          {/* Admin link — only rendered if the signed-in email is in the
              client-side allowlist (REACT_APP_ADMIN_EMAILS, baked at build
              time). The real gate is server-side in api/_lib/admin.js. */}
          {isAdminEmail(user?.email) && (
            <button onClick={onOpenAdmin} style={cockpitNavBtn()}>Admin</button>
          )}
          <button onClick={onSignOut} style={cockpitNavBtn()}>Sign out</button>
          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'var(--surface-2)', border: '1px solid var(--border-soft)',
              color: 'var(--text-muted)', fontSize: 15, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0, lineHeight: 1,
            }}
          >{theme === 'dark' ? '☀️' : '🌙'}</button>
        </div>
      </header>

      <main style={{
        position: 'relative', zIndex: 1,
        maxWidth: 880, margin: '20px auto 60px', padding: '0 24px',
        display: 'flex', flexDirection: 'column', gap: 22,
        // Isolate so the HeroWaves' z-index: -1 stays inside this main
        // and can't escape to paint behind the page background.
        isolation: 'isolate',
      }}>
        {/* Hero waves background — confined to roughly the greeting +
            plan-card area at the top of the cockpit. Pure background:
            absolute, pointer-events: none, z-index: -1, zero layout
            impact. Height is fixed (not 100%) so it doesn't stretch
            behind the entire scrolling history below. */}
        <HeroWaves height="460px" topGlow={true} />
        {/* Subscription success banner — shown once after a buyer returns
            from a successful Lemon Squeezy checkout. The plan flip happens
            asynchronously via webhook, so we tell them it can take a moment. */}
        {subscriptionBanner === 'success' && (
          <section style={{
            marginTop: 12,
            padding: '14px 18px',
            borderRadius: 14,
            background: 'linear-gradient(135deg, rgba(34,197,94,0.18), rgba(34,197,94,0.06))',
            border: '1px solid rgba(34,197,94,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{
                width: 28, height: 28, borderRadius: '50%',
                background: '#22c55e', color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 800,
              }}>✓</span>
              <div style={{ fontFamily: 'sans-serif' }}>
                <div style={{ color: 'var(--text-strong)', fontSize: 14, fontWeight: 700 }}>
                  Subscription activated — thank you!
                </div>
                <div style={{ color: 'var(--text-dim)', fontSize: 12.5, marginTop: 2 }}>
                  Your new plan should appear here within a moment. If it doesn't, try refreshing the page.
                </div>
              </div>
            </div>
            <button
              onClick={onDismissSubscriptionBanner}
              aria-label="Dismiss"
              style={{
                background: 'transparent', border: 'none',
                color: 'var(--text-dim)', cursor: 'pointer',
                fontSize: 18, lineHeight: 1, padding: 4,
              }}
            >×</button>
          </section>
        )}

        {/* Greeting */}
        <section style={{ marginTop: 12 }}>
          <h1 style={{
            margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: -0.6,
            color: 'var(--text-strong)',
          }}>
            Welcome back, <span style={{
              background: 'linear-gradient(135deg,#f97316,#dc2626)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>{displayName}</span>
          </h1>
          <p style={{
            margin: '6px 0 0', color: 'var(--text-dim)', fontSize: 14,
            fontFamily: 'sans-serif',
          }}>
            Ready for your next mock interview?
          </p>
        </section>

        {/* Lifecycle banner — surfaces a subscription that's been
            cancelled but is still in its paid grace period, OR a 3-month
            pass within 14 days of expiry. Renders nothing in the common
            "active sub / fresh pass / no paid plan" cases.

            Why this exists (May 2026): Lemon Squeezy "cancel" semantics
            keep the user on their paid plan until renews_at passes — so
            after cancelling, the cockpit would otherwise look identical
            to before, leading users to assume the cancel didn't work
            and contact support. Same idea for an imminent pass expiry.

            The fields (subStatus, subEndsAt, passEndsAt) come from
            get_usage_info() v3 (see get_usage_info_v3_may2026.sql). On a
            pre-v3 backend they're undefined and the banner stays hidden. */}
        {u && (() => {
          const now = Date.now();
          const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
          const formatDate = (iso) => {
            try {
              return new Date(iso).toLocaleDateString(undefined, {
                month: 'short', day: 'numeric', year: 'numeric',
              });
            } catch { return ''; }
          };
          // Lemon's `subscription_cancelled` webhook usually sets both
          // ends_at and renews_at to the current period-end date, but we
          // fall back to renews_at when ends_at is missing — some Lemon
          // payloads have been observed with only one set, and a
          // cancelled sub with a future renews_at is still in its paid
          // grace period. Always-show is the right default: this banner
          // exists to PREVENT the "did my cancel work?" confusion.
          const cancelEndIso = u.subEndsAt || u.subRenewsAt || null;
          const cancelEnd = cancelEndIso ? new Date(cancelEndIso).getTime() : null;
          const passEnd = u.passEndsAt ? new Date(u.passEndsAt).getTime() : null;
          const subCancelled =
            u.subStatus === 'cancelled' && cancelEnd && cancelEnd > now;
          const passSoon = passEnd && passEnd > now && (passEnd - now) <= FOURTEEN_DAYS_MS;

          if (!subCancelled && !passSoon) return null;

          let title = '';
          let body = '';
          if (subCancelled) {
            title = `Your ${u.planLabel} subscription is canceled`;
            body = `You'll keep ${u.planLabel} access through ${formatDate(cancelEndIso)}, then drop to Free (2 interviews/month, 15-min sessions). Click "Manage subscription" below to undo.`;
          } else {
            const daysLeft = Math.max(1, Math.ceil((passEnd - now) / (24 * 60 * 60 * 1000)));
            title = `Your ${u.planLabel} expires soon`;
            body = `Your pass ends on ${formatDate(u.passEndsAt)} (in ${daysLeft} day${daysLeft === 1 ? '' : 's'}). Renew or upgrade from "See plans" to keep your monthly interview allowance.`;
          }

          return (
            <section
              role="status"
              aria-live="polite"
              style={{
                marginTop: 4,
                padding: '14px 18px',
                borderRadius: 14,
                background: 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0.06))',
                border: '1px solid rgba(245,158,11,0.55)',
                display: 'flex', alignItems: 'flex-start', gap: 12,
                fontFamily: 'sans-serif',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  flex: '0 0 auto',
                  width: 28, height: 28, borderRadius: '50%',
                  background: '#f59e0b', color: '#1f1300',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 800,
                }}
              >!</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: 'var(--text-strong)', fontSize: 14, fontWeight: 700 }}>
                  {title}
                </div>
                <div style={{ color: 'var(--text-dim)', fontSize: 12.5, marginTop: 4, lineHeight: 1.45 }}>
                  {body}
                </div>
              </div>
            </section>
          );
        })()}

        {/* Plan + quota card */}
        {u && (
          <section style={{
            background: 'var(--card)', border: '1px solid var(--border-soft)',
            borderRadius: 18, padding: 22, backdropFilter: 'blur(14px)',
            display: 'flex', flexDirection: 'column', gap: 14,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  padding: '4px 11px', borderRadius: 999,
                  background: 'rgba(249,115,22,0.15)',
                  border: '1px solid rgba(249,115,22,0.45)',
                  color: '#f97316', fontSize: 11.5, fontWeight: 700,
                  letterSpacing: 0.4, textTransform: 'uppercase',
                  fontFamily: 'sans-serif',
                }}>{u.planLabel} plan</span>
                <span style={{
                  color: 'var(--text-muted)', fontSize: 13.5,
                  fontFamily: 'sans-serif',
                }}>
                  {u.label}
                </span>
              </div>
              <span style={{
                color: 'var(--text-dim)', fontSize: 12.5,
                fontFamily: 'sans-serif',
              }}>
                {u.sessionMinutes} min / interview
              </span>
            </div>

            {/* Progress bar */}
            <div style={{
              height: 8, borderRadius: 99, background: 'var(--surface-2)',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${u.total > 0 ? Math.min(100, (u.used / u.total) * 100) : 0}%`,
                background: atLimit
                  ? 'linear-gradient(90deg,#ef4444,#dc2626)'
                  : 'linear-gradient(90deg,#f97316,#dc2626)',
                transition: 'width 0.4s ease',
              }} />
            </div>

            {/* Action row */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {atLimit ? (
                <button
                  onClick={onSeePlans}
                  style={cockpitPrimaryBtn()}
                >See plans →</button>
              ) : (
                <button
                  onClick={onStart}
                  style={cockpitPrimaryBtn()}
                >Start a new interview →</button>
              )}
              {!atLimit && planCode === 'free' && (
                <button onClick={onSeePlans} style={cockpitSecondaryBtn()}>
                  See paid plans
                </button>
              )}
              {/* Pass holders see "Request refund" instead of "Manage
                  subscription" — passes are one-time orders so Lemon's
                  customer portal returns "No active subscription found"
                  for them. The refund itself is processed manually by
                  support from the Lemon dashboard; this button just
                  pre-fills the support form with the right context so
                  the user doesn't have to draft anything. */}
              {planCode.endsWith('_pass') ? (
                <button
                  onClick={() => {
                    const passLabel =
                      (PASS_TIERS.find((t) => t.code === planCode) || {}).name || 'Pass';
                    setSupportPrefill({
                      category: 'refund',
                      subject: `Refund request — ${passLabel}`,
                      message:
                        `Hi,\n\n` +
                        `I'd like to request a refund for my ${passLabel}.\n\n` +
                        `Account email: ${user?.email || ''}\n\n` +
                        `Reason for refund: \n\n` +
                        `Thanks!`,
                    });
                    setSupportOpen(true);
                  }}
                  style={cockpitSecondaryBtn()}
                >
                  Request refund
                </button>
              ) : (planCode !== 'free' && onManageSubscription && (
                <button
                  onClick={async () => {
                    if (managingSub) return;
                    setManagingSub(true);
                    try { await onManageSubscription(); }
                    finally { setManagingSub(false); }
                  }}
                  disabled={managingSub}
                  style={{
                    ...cockpitSecondaryBtn(),
                    opacity: managingSub ? 0.6 : 1,
                    cursor: managingSub ? 'wait' : 'pointer',
                  }}
                >
                  {managingSub ? 'Opening…' : 'Manage subscription'}
                </button>
              ))}
            </div>

            {atLimit && (
              <p style={{
                margin: 0, color: '#f87171', fontSize: 12.5,
                fontFamily: 'sans-serif',
              }}>
                You've used all your interviews on this plan. Upgrade to keep practising.
              </p>
            )}
          </section>
        )}

        {/* Recent sessions — visible to every logged-in tier (May 2026) */}
        {showHistory && (
          <section style={{
            background: 'var(--card)', border: '1px solid var(--border-soft)',
            borderRadius: 18, padding: 22, backdropFilter: 'blur(14px)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 14,
            }}>
              <h2 style={{
                margin: 0, fontSize: 16, fontWeight: 700,
                color: 'var(--text-strong)', letterSpacing: -0.2,
              }}>
                Recent sessions
              </h2>
              <span style={{
                color: 'var(--text-dimmer)', fontSize: 11.5,
                fontFamily: 'sans-serif',
              }}>
                Last 10
              </span>
            </div>

            {loadingSessions && (
              <div style={{
                color: 'var(--text-dim)', fontSize: 13,
                fontFamily: 'sans-serif', padding: '6px 0',
              }}>Loading…</div>
            )}

            {!loadingSessions && recentSessions.length === 0 && (
              <div style={{
                color: 'var(--text-dim)', fontSize: 13,
                fontFamily: 'sans-serif', padding: '6px 0', lineHeight: 1.55,
              }}>
                No sessions yet. Start your first interview above and it'll appear here.
              </div>
            )}

            {!loadingSessions && recentSessions.length > 0 && (
              <RecentSessionsTable
                sessions={recentSessions.slice(0, 10)}
                expandedRow={expandedRow}
                onToggle={toggleExpand}
                onRedo={(s) => onRepeatRole?.(s)}
                fmtDate={fmtDate}
                fmtDuration={fmtDuration}
              />
            )}
          </section>
        )}

        {/* Analytics / Insights — sits below recent sessions and reuses the
            same data so we don't make a second query. Shows an empty-state if
            no scored sessions exist yet. Visible to every logged-in tier;
            Free sees Snapshot only (controlled inside AnalyticsSection via
            planCode). */}
        {showHistory && (
          <AnalyticsSection
            sessions={recentSessions}
            onStart={onStart}
            user={user}
            planCode={planCode}
          />
        )}

        {/* Free-tier upsell — sits directly under the (snapshot-only)
            insights so the missing tabs feel adjacent to what they unlock.
            Starter+ users don't see this. */}
        {planCode === 'free' && (
          <section id="insights-upsell" style={{
            background: 'linear-gradient(160deg, rgba(249,115,22,0.10), var(--card))',
            border: '1px solid rgba(249,115,22,0.30)',
            borderRadius: 18, padding: 22, backdropFilter: 'blur(14px)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 14, flexWrap: 'wrap', scrollMarginTop: 80,
          }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <h3 style={{
                margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)',
              }}>
                Unlock Trends & Deep Dive
              </h3>
              <p style={{
                margin: '4px 0 0', color: 'var(--text-dim)', fontSize: 13,
                fontFamily: 'sans-serif', lineHeight: 1.55,
              }}>
                You're seeing the Snapshot view. Starter ($9.99/mo) unlocks the Trends tab to watch your score climb over time and Deep Dive for per-dimension breakdowns — plus 5 interviews and 30-minute sessions.
              </p>
            </div>
            <button onClick={onSeePlans} style={cockpitSecondaryBtn()}>
              See plans
            </button>
          </section>
        )}

        {/* Quick tips footer */}
        <section style={{
          color: 'var(--text-dimmer)', fontSize: 12, lineHeight: 1.6,
          fontFamily: 'sans-serif', textAlign: 'center', marginTop: 8,
        }}>
          Tip: a quiet space and a single tab open give the best voice-to-text accuracy.
        </section>
      </main>

      <SupportModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        user={user}
        defaultCategory={supportPrefill.category || ""}
        defaultSubject={supportPrefill.subject || ""}
        defaultMessage={supportPrefill.message || ""}
      />
    </div>
  );
};

const cockpitNavBtn = () => ({
  background: 'transparent',
  border: '1px solid var(--border-soft)',
  color: 'var(--text-muted)',
  padding: '7px 12px', borderRadius: 10,
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'sans-serif',
});

const cockpitPrimaryBtn = () => ({
  background: 'linear-gradient(135deg,#f97316,#dc2626)',
  border: 'none', color: '#fff',
  padding: '12px 22px', borderRadius: 12,
  fontSize: 14, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'sans-serif',
  boxShadow: '0 8px 22px rgba(249,115,22,0.35)',
});

const cockpitSecondaryBtn = () => ({
  background: 'transparent',
  border: '1px solid rgba(249,115,22,0.55)',
  color: '#f97316',
  padding: '11px 18px', borderRadius: 11,
  fontSize: 13, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'sans-serif',
});

// ─── Admin dashboard ─────────────────────────────────────────────────────────
// Internal-only /admin page. Gated client-side by the REACT_APP_ADMIN_EMAILS
// allowlist (cosmetic — hides the nav link for non-admins) and server-side
// by the ADMIN_EMAILS env var inside api/_lib/admin.js (the real gate).
//
// Four tabs: Overview + Users (search + click-row detail drawer),
// Subscriptions (status / win-back / renewals / passes), Revenue (new subs &
// passes per day, last 50 webhook events, conversion cohorts), and Health
// (DAU/WAU/MAU, auto-cleanup rate, integrity counters).
//
// All data is lazy-loaded per tab — the request only fires when you switch
// to that tab. Each tab caches its result in component state; the round
// "Refresh" button at the top forces a re-fetch.
const ADMIN_ALLOWLIST = (process.env.REACT_APP_ADMIN_EMAILS || "")
  .split(",")
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean);
const isAdminEmail = (email) =>
  ADMIN_ALLOWLIST.length > 0 &&
  ADMIN_ALLOWLIST.includes(String(email || "").toLowerCase());

async function adminFetch(params) {
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error("Not signed in");
  const apiBase = process.env.REACT_APP_API_URL || "";
  const qs = new URLSearchParams(params).toString();
  const resp = await fetch(`${apiBase}/api/admin?${qs}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
  return json;
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso); }
};
const fmtDay = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return String(iso); }
};
const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString());

// Styling primitives for the admin page. Reuse the app's CSS vars so dark
// & light themes work without separate stylesheets.
const adminCard = () => ({
  background: "var(--card)",
  border: "1px solid var(--border-soft)",
  borderRadius: 12,
  padding: 16,
});
const adminTableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
  fontFamily: "sans-serif",
};
const adminThStyle = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-soft)",
  color: "var(--text-muted)",
  fontWeight: 600,
  textTransform: "uppercase",
  fontSize: 11,
  letterSpacing: 0.4,
  whiteSpace: "nowrap",
};
const adminTdStyle = {
  padding: "10px",
  borderBottom: "1px solid var(--border-soft)",
  color: "var(--text-strong)",
  verticalAlign: "top",
};

const StatCard = ({ label, value, sub }) => (
  <div style={adminCard()}>
    <div style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 26, color: "var(--text-strong)", fontWeight: 700, marginTop: 6 }}>{value}</div>
    {sub != null && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{sub}</div>}
  </div>
);

const Pill = ({ children, tone = "neutral" }) => {
  const palette = {
    neutral: { bg: "var(--surface-2)", fg: "var(--text-muted)", bd: "var(--border-soft)" },
    good:    { bg: "rgba(34,197,94,0.12)",  fg: "#16a34a", bd: "rgba(34,197,94,0.35)" },
    warn:    { bg: "rgba(234,179,8,0.12)",  fg: "#a16207", bd: "rgba(234,179,8,0.35)" },
    bad:     { bg: "rgba(220,38,38,0.12)",  fg: "#dc2626", bd: "rgba(220,38,38,0.35)" },
    info:    { bg: "rgba(59,130,246,0.12)", fg: "#2563eb", bd: "rgba(59,130,246,0.35)" },
  }[tone] || {};
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      background: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`,
      fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>{children}</span>
  );
};

const subStatusTone = (status) => {
  if (status === "active") return "good";
  if (status === "cancelled") return "warn";
  if (status === "past_due" || status === "paused") return "bad";
  if (status === "on_trial") return "info";
  return "neutral";
};

const AdminPage = ({ theme, toggleTheme, user, onBack }) => {
  const [activeTab, setActiveTab] = useState("overview");
  // One bag for everything so we don't need 8 useStates. Keys: overview,
  // users, user (drawer), subscriptions, revenue, health.
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [openUserId, setOpenUserId] = useState(null);

  const load = async (tab, params = {}) => {
    setLoading((s) => ({ ...s, [tab]: true }));
    setErrors((s) => ({ ...s, [tab]: null }));
    try {
      const json = await adminFetch({ tab, ...params });
      setData((s) => ({ ...s, [tab]: json }));
    } catch (e) {
      setErrors((s) => ({ ...s, [tab]: e.message || String(e) }));
    } finally {
      setLoading((s) => ({ ...s, [tab]: false }));
    }
  };

  // Initial overview load on mount.
  useEffect(() => { load("overview"); /* eslint-disable-next-line */ }, []);

  // Lazy-load whichever tab the user lands on, only the first time.
  useEffect(() => {
    if (activeTab === "users" && !data.users && !loading.users) load("users", { search });
    if (activeTab === "subscriptions" && !data.subscriptions && !loading.subscriptions) load("subscriptions");
    if (activeTab === "revenue" && !data.revenue && !loading.revenue) load("revenue");
    if (activeTab === "health" && !data.health && !loading.health) load("health");
    if (activeTab === "usage" && !data.usage && !loading.usage) load("usage");
    // eslint-disable-next-line
  }, [activeTab]);

  // Debounced search for the users tab.
  useEffect(() => {
    if (activeTab !== "users") return;
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        load("users", { search: searchInput });
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchInput, activeTab]);

  // User-detail drawer.
  useEffect(() => {
    if (openUserId) load("user", { id: openUserId });
  }, [openUserId]);

  const refreshActive = () => {
    if (activeTab === "users") load("users", { search });
    else load(activeTab);
  };

  const TABS = [
    { id: "overview",      label: "Overview" },
    { id: "users",         label: "Users" },
    { id: "subscriptions", label: "Subscriptions" },
    { id: "revenue",       label: "Revenue" },
    { id: "usage",         label: "Usage" },
    { id: "health",        label: "Health" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text-strong)" }}>
      {/* Top bar — same shape as the cockpit header so the page feels native. */}
      <header style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 24px", maxWidth: 1280, margin: "0 auto",
        flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Wordmark logoSize={48} fontSize={17} gap={6} />
          <span style={{
            padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
            background: "rgba(249,115,22,0.15)", color: "#f97316",
            border: "1px solid rgba(249,115,22,0.4)", letterSpacing: 0.6,
          }}>ADMIN</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "sans-serif" }}>
          <button onClick={refreshActive} style={cockpitNavBtn()}>Refresh</button>
          <button onClick={onBack} style={cockpitNavBtn()}>← Back</button>
          <button
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-label="Toggle theme"
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "var(--surface-2)", border: "1px solid var(--border-soft)",
              color: "var(--text-muted)", fontSize: 15, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, lineHeight: 1,
            }}
          >{theme === "dark" ? "☀️" : "🌙"}</button>
        </div>
      </header>

      {/* Tab strip */}
      <nav style={{
        maxWidth: 1280, margin: "0 auto", padding: "0 24px",
        display: "flex", gap: 4, borderBottom: "1px solid var(--border-soft)",
        flexWrap: "wrap",
      }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: "12px 16px", border: "none", background: "transparent",
              color: activeTab === t.id ? "var(--text-strong)" : "var(--text-muted)",
              fontWeight: activeTab === t.id ? 700 : 500, cursor: "pointer",
              borderBottom: activeTab === t.id ? "2px solid #f97316" : "2px solid transparent",
              marginBottom: -1, fontSize: 14, fontFamily: "sans-serif",
            }}
          >{t.label}</button>
        ))}
      </nav>

      <main style={{ maxWidth: 1280, margin: "20px auto 60px", padding: "0 24px" }}>
        {errors[activeTab] && (
          <div style={{ ...adminCard(), borderColor: "rgba(220,38,38,0.4)", background: "rgba(220,38,38,0.08)", marginBottom: 16, color: "#dc2626" }}>
            <strong>Error loading {activeTab}:</strong> {errors[activeTab]}
          </div>
        )}
        {loading[activeTab] && !data[activeTab] && (
          <div style={{ ...adminCard(), color: "var(--text-muted)" }}>Loading {activeTab}…</div>
        )}

        {activeTab === "overview" && data.overview && (
          <OverviewTab d={data.overview} />
        )}
        {activeTab === "users" && (
          <UsersTab
            d={data.users}
            search={searchInput}
            onSearch={setSearchInput}
            loading={loading.users}
            onOpen={setOpenUserId}
          />
        )}
        {activeTab === "subscriptions" && data.subscriptions && (
          <SubscriptionsTab d={data.subscriptions} onOpen={setOpenUserId} />
        )}
        {activeTab === "revenue" && data.revenue && (
          <RevenueTab d={data.revenue} onOpen={setOpenUserId} />
        )}
        {activeTab === "usage" && data.usage && (
          <UsageTab d={data.usage} onOpen={setOpenUserId} />
        )}
        {activeTab === "health" && data.health && (
          <HealthTab d={data.health} />
        )}
      </main>

      {openUserId && (
        <UserDrawer
          d={data.user}
          loading={loading.user}
          error={errors.user}
          onClose={() => { setOpenUserId(null); setData((s) => ({ ...s, user: undefined })); }}
        />
      )}
    </div>
  );
};

// ── Tab: Overview ────────────────────────────────────────────────────────────
const OverviewTab = ({ d }) => {
  const dist = d.plan_distribution || {};
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        <StatCard label="Total users" value={fmtNum(d.total_users)} sub={`+${fmtNum(d.signups_7d)} in 7 days`} />
        <StatCard label="Active paid subs" value={fmtNum(d.active_paid_subs)} />
        <StatCard label="Active passes" value={fmtNum(d.active_passes)} />
        <StatCard label="Cancelled (in window)" value={fmtNum(d.cancelled_in_window)} sub="still have access" />
        <StatCard label="Past due / paused" value={fmtNum(d.past_due_or_paused)} />
        <StatCard label="Sessions today" value={fmtNum(d.sessions_today)} sub={`${fmtNum(d.sessions_7d)} in 7 days`} />
        <StatCard label="DAU" value={fmtNum(d.dau)} />
        <StatCard label="WAU" value={fmtNum(d.wau)} />
        <StatCard label="MAU" value={fmtNum(d.mau)} />
      </div>

      <div style={adminCard()}>
        <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: 14, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Plan distribution (effective)
        </h3>
        <table style={adminTableStyle}>
          <thead>
            <tr><th style={adminThStyle}>Plan</th><th style={adminThStyle}>Users</th></tr>
          </thead>
          <tbody>
            {Object.entries(dist).sort((a, b) => b[1] - a[1]).map(([plan, n]) => (
              <tr key={plan}>
                <td style={adminTdStyle}><Pill tone="info">{plan}</Pill></td>
                <td style={adminTdStyle}>{fmtNum(n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "right" }}>
        Snapshot: {fmtDate(d.generated_at)}
      </div>
    </div>
  );
};

// ── Tab: Users ───────────────────────────────────────────────────────────────
const UsersTab = ({ d, search, onSearch, loading, onOpen }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search by email…"
        style={{
          flex: "1 1 320px", padding: "10px 14px", borderRadius: 8,
          border: "1px solid var(--border-soft)", background: "var(--card)",
          color: "var(--text-strong)", fontSize: 14,
        }}
      />
      <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
        {loading ? "Loading…" : d ? `${fmtNum(d.total)} matching` : ""}
      </span>
    </div>
    <div style={{ ...adminCard(), padding: 0, overflowX: "auto" }}>
      <table style={adminTableStyle}>
        <thead>
          <tr>
            <th style={adminThStyle}>Email</th>
            <th style={adminThStyle}>Plan</th>
            <th style={adminThStyle}>Sub status</th>
            <th style={adminThStyle}>Pass</th>
            <th style={adminThStyle}>Sessions (30d / total)</th>
            <th style={adminThStyle} title="Sum of recorded session durations (all sessions with a duration), all time">Mins burned</th>
            <th style={adminThStyle} title="Average recorded session duration, all time">Avg min</th>
            <th style={adminThStyle} title="Real Claude token cost from public.claude_token_usage where logged; otherwise falls back to minutes × $0.0167/min (≈ $0.50 per 30-min interview) for pre-logging sessions.">Est. cost</th>
            <th style={adminThStyle}>Signed up</th>
            <th style={adminThStyle}>Last sign-in</th>
          </tr>
        </thead>
        <tbody>
          {(d?.rows || []).map((u) => (
            <tr key={u.user_id} onClick={() => onOpen(u.user_id)}
                style={{ cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <td style={adminTdStyle}>{u.email}</td>
              <td style={adminTdStyle}><Pill tone="info">{u.effective_plan}</Pill></td>
              <td style={adminTdStyle}>
                {u.sub_status
                  ? <Pill tone={subStatusTone(u.sub_status)}>{u.sub_plan_code}: {u.sub_status}</Pill>
                  : <span style={{ color: "var(--text-muted)" }}>—</span>}
              </td>
              <td style={adminTdStyle}>
                {u.pass_plan_code
                  ? <Pill tone="info">{u.pass_plan_code} → {fmtDay(u.pass_expires_at)}</Pill>
                  : <span style={{ color: "var(--text-muted)" }}>—</span>}
              </td>
              <td style={adminTdStyle}>{fmtNum(u.sessions_30d)} / {fmtNum(u.sessions_total)}</td>
              <td style={adminTdStyle}>{u.minutes_total != null ? fmtNum(Math.round(u.minutes_total)) : "—"}</td>
              <td style={adminTdStyle}>{u.avg_minutes != null ? u.avg_minutes : "—"}</td>
              <td style={adminTdStyle}>{u.est_cost_usd != null ? `$${Number(u.est_cost_usd).toFixed(2)}` : "—"}</td>
              <td style={adminTdStyle}>{fmtDate(u.signed_up_at)}</td>
              <td style={adminTdStyle}>{fmtDate(u.last_sign_in_at)}</td>
            </tr>
          ))}
          {(!d?.rows || d.rows.length === 0) && !loading && (
            <tr><td colSpan={10} style={{ ...adminTdStyle, textAlign: "center", color: "var(--text-muted)" }}>
              No users match.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
      Click any row to see the full user detail (sessions, Lemon ids, lifecycle).
    </div>
  </div>
);

// ── Tab: Subscriptions ───────────────────────────────────────────────────────
const SubscriptionsTab = ({ d, onOpen }) => {
  const Section = ({ title, children, hint }) => (
    <div style={adminCard()}>
      <h3 style={{ marginTop: 0, marginBottom: 4, fontSize: 14, color: "var(--text-strong)" }}>{title}</h3>
      {hint && <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>{hint}</div>}
      {children}
    </div>
  );
  const EmailRow = ({ u, children }) => (
    <tr onClick={() => onOpen(u.user_id)} style={{ cursor: "pointer" }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
      <td style={adminTdStyle}>{u.email}</td>
      {children}
    </tr>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Section title="Status breakdown" hint="Active paid subscriptions only.">
        <table style={adminTableStyle}>
          <thead><tr><th style={adminThStyle}>Plan</th><th style={adminThStyle}>Status</th><th style={adminThStyle}>Users</th></tr></thead>
          <tbody>
            {(d.status_breakdown || []).map((r, i) => (
              <tr key={i}><td style={adminTdStyle}>{r.plan_code}</td>
                <td style={adminTdStyle}><Pill tone={subStatusTone(r.status)}>{r.status}</Pill></td>
                <td style={adminTdStyle}>{fmtNum(r.users)}</td></tr>
            ))}
            {(!d.status_breakdown || d.status_breakdown.length === 0) && (
              <tr><td colSpan={3} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No paid subscriptions yet.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Win-back window" hint="Cancelled but still have access. Time to send a save offer.">
        <table style={adminTableStyle}>
          <thead><tr><th style={adminThStyle}>Email</th><th style={adminThStyle}>Plan</th><th style={adminThStyle}>Ends</th><th style={adminThStyle}>Hours left</th></tr></thead>
          <tbody>
            {(d.win_back || []).map((u, i) => (
              <EmailRow key={i} u={u}>
                <td style={adminTdStyle}>{u.plan_code}</td>
                <td style={adminTdStyle}>{fmtDate(u.ends_at)}</td>
                <td style={adminTdStyle}>{u.hours_remaining}</td>
              </EmailRow>
            ))}
            {(!d.win_back || d.win_back.length === 0) && (
              <tr><td colSpan={4} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>None right now.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Renewals — next 7 days">
        <table style={adminTableStyle}>
          <thead><tr><th style={adminThStyle}>Email</th><th style={adminThStyle}>Plan</th><th style={adminThStyle}>Renews</th></tr></thead>
          <tbody>
            {(d.renewals_7d || []).map((u, i) => (
              <EmailRow key={i} u={u}>
                <td style={adminTdStyle}>{u.plan_code}</td>
                <td style={adminTdStyle}>{fmtDate(u.renews_at)}</td>
              </EmailRow>
            ))}
            {(!d.renewals_7d || d.renewals_7d.length === 0) && (
              <tr><td colSpan={3} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>None.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Past-due / paused" hint="Lemon failed to bill — nudge before auto-cancel.">
        <table style={adminTableStyle}>
          <thead><tr><th style={adminThStyle}>Email</th><th style={adminThStyle}>Plan</th><th style={adminThStyle}>Status</th><th style={adminThStyle}>Last event</th></tr></thead>
          <tbody>
            {(d.past_due_paused || []).map((u, i) => (
              <EmailRow key={i} u={u}>
                <td style={adminTdStyle}>{u.plan_code}</td>
                <td style={adminTdStyle}><Pill tone={subStatusTone(u.status)}>{u.status}</Pill></td>
                <td style={adminTdStyle}>{u.last_event_name} ({fmtDate(u.last_event_at)})</td>
              </EmailRow>
            ))}
            {(!d.past_due_paused || d.past_due_paused.length === 0) && (
              <tr><td colSpan={4} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>None.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Active passes">
        <table style={adminTableStyle}>
          <thead><tr><th style={adminThStyle}>Email</th><th style={adminThStyle}>Pass</th><th style={adminThStyle}>Purchased</th><th style={adminThStyle}>Expires</th></tr></thead>
          <tbody>
            {(d.active_passes || []).map((u, i) => (
              <EmailRow key={i} u={u}>
                <td style={adminTdStyle}>{u.pass_plan_code}</td>
                <td style={adminTdStyle}>{fmtDate(u.pass_purchased_at)}</td>
                <td style={adminTdStyle}>{fmtDate(u.pass_expires_at)}</td>
              </EmailRow>
            ))}
            {(!d.active_passes || d.active_passes.length === 0) && (
              <tr><td colSpan={4} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>None.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Passes expiring — next 14 days">
        <table style={adminTableStyle}>
          <thead><tr><th style={adminThStyle}>Email</th><th style={adminThStyle}>Pass</th><th style={adminThStyle}>Expires</th></tr></thead>
          <tbody>
            {(d.expiring_passes_14d || []).map((u, i) => (
              <EmailRow key={i} u={u}>
                <td style={adminTdStyle}>{u.pass_plan_code}</td>
                <td style={adminTdStyle}>{fmtDate(u.pass_expires_at)}</td>
              </EmailRow>
            ))}
            {(!d.expiring_passes_14d || d.expiring_passes_14d.length === 0) && (
              <tr><td colSpan={3} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>None.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Plan rules — source of truth" hint="Reflects current public.plan_rules. Bookkeeping only — pricing-card copy is in App.jsx.">
        <table style={adminTableStyle}>
          <thead><tr>
            <th style={adminThStyle}>Plan</th><th style={adminThStyle}>Group</th>
            <th style={adminThStyle}>Monthly</th><th style={adminThStyle}>Lifetime</th>
            <th style={adminThStyle}>Minutes</th><th style={adminThStyle}>Insights</th>
            <th style={adminThStyle}>PDF</th><th style={adminThStyle}>Resume</th>
            <th style={adminThStyle}>Active</th>
          </tr></thead>
          <tbody>
            {(d.plan_rules || []).map((r) => (
              <tr key={r.plan}>
                <td style={adminTdStyle}><Pill tone="info">{r.plan}</Pill></td>
                <td style={adminTdStyle}>{r.plan_group}</td>
                <td style={adminTdStyle}>{r.monthly_interviews ?? "—"}</td>
                <td style={adminTdStyle}>{r.lifetime_interviews ?? "—"}</td>
                <td style={adminTdStyle}>{r.session_minutes}</td>
                <td style={adminTdStyle}>{r.features?.insights_level || "—"}</td>
                <td style={adminTdStyle}>{r.features?.pdf_download ? "✓" : "—"}</td>
                <td style={adminTdStyle}>{r.features?.resume_upload ? "✓" : "—"}</td>
                <td style={adminTdStyle}>{r.is_active ? "✓" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
};

// ── Tab: Revenue ─────────────────────────────────────────────────────────────
const RevenueTab = ({ d, onOpen }) => {
  const Section = ({ title, hint, children }) => (
    <div style={adminCard()}>
      <h3 style={{ marginTop: 0, marginBottom: 4, fontSize: 14, color: "var(--text-strong)" }}>{title}</h3>
      {hint && <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>{hint}</div>}
      {children}
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Section title="New subscriptions — last 30 days" hint="Grouped by day and plan.">
        <table style={adminTableStyle}>
          <thead><tr><th style={adminThStyle}>Day</th><th style={adminThStyle}>Plan</th><th style={adminThStyle}>Count</th></tr></thead>
          <tbody>
            {(d.new_subs_30d || []).map((r, i) => (
              <tr key={i}><td style={adminTdStyle}>{fmtDay(r.day)}</td><td style={adminTdStyle}>{r.plan_code}</td><td style={adminTdStyle}>{fmtNum(r.count)}</td></tr>
            ))}
            {(!d.new_subs_30d || d.new_subs_30d.length === 0) && (
              <tr><td colSpan={3} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No new subs in window.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="New passes — last 30 days">
        <table style={adminTableStyle}>
          <thead><tr><th style={adminThStyle}>Day</th><th style={adminThStyle}>Pass</th><th style={adminThStyle}>Count</th></tr></thead>
          <tbody>
            {(d.new_passes_30d || []).map((r, i) => (
              <tr key={i}><td style={adminTdStyle}>{fmtDay(r.day)}</td><td style={adminTdStyle}>{r.pass_plan_code}</td><td style={adminTdStyle}>{fmtNum(r.count)}</td></tr>
            ))}
            {(!d.new_passes_30d || d.new_passes_30d.length === 0) && (
              <tr><td colSpan={3} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No new passes in window.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Conversion — last 12 weeks" hint="% of weekly signup cohort that has a paid plan now.">
        <table style={adminTableStyle}>
          <thead><tr><th style={adminThStyle}>Week of</th><th style={adminThStyle}>Signups</th><th style={adminThStyle}>Paid</th><th style={adminThStyle}>Conversion</th></tr></thead>
          <tbody>
            {(d.conversion_cohorts || []).map((r, i) => (
              <tr key={i}>
                <td style={adminTdStyle}>{fmtDay(r.cohort_week)}</td>
                <td style={adminTdStyle}>{fmtNum(r.signups)}</td>
                <td style={adminTdStyle}>{fmtNum(r.paid)}</td>
                <td style={adminTdStyle}>{r.conversion_pct ?? 0}%</td>
              </tr>
            ))}
            {(!d.conversion_cohorts || d.conversion_cohorts.length === 0) && (
              <tr><td colSpan={4} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No data.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Recent webhook events" hint="Last 50 subscription rows by event time. Click for user detail.">
        <table style={adminTableStyle}>
          <thead><tr>
            <th style={adminThStyle}>Email</th><th style={adminThStyle}>Plan</th>
            <th style={adminThStyle}>Status</th><th style={adminThStyle}>Event</th>
            <th style={adminThStyle}>When</th><th style={adminThStyle}>Lemon sub id</th>
          </tr></thead>
          <tbody>
            {(d.recent_events || []).map((u, i) => (
              <tr key={i} onClick={() => onOpen(u.user_id)} style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <td style={adminTdStyle}>{u.email}</td>
                <td style={adminTdStyle}>{u.plan_code}</td>
                <td style={adminTdStyle}><Pill tone={subStatusTone(u.status)}>{u.status || "—"}</Pill></td>
                <td style={adminTdStyle}>{u.last_event_name || "—"}</td>
                <td style={adminTdStyle}>{fmtDate(u.last_event_at)}</td>
                <td style={adminTdStyle} title={u.lemon_subscription_id}>
                  {u.lemon_subscription_id ? <code style={{ fontSize: 11 }}>{String(u.lemon_subscription_id).slice(0, 12)}…</code> : "—"}
                </td>
              </tr>
            ))}
            {(!d.recent_events || d.recent_events.length === 0) && (
              <tr><td colSpan={6} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No events yet.</td></tr>
            )}
          </tbody>
        </table>
      </Section>
    </div>
  );
};

// ── Tab: Health ──────────────────────────────────────────────────────────────
const HealthTab = ({ d }) => {
  const eng = d.engagement || {};
  const integ = d.integrity || {};
  // Each integrity counter should be 0 in a healthy DB. Highlight non-zero
  // in red so a regression is obvious without reading the number.
  const integrityRows = [
    ["Subscriptions pointing to unknown plan_code", integ.subs_unknown_plan, "Webhook routed to a missing plan_rules row."],
    ["Profiles with half-applied passes",            integ.half_applied_passes, "pass_plan_code XOR pass_expires_at."],
    ["Cancelled subs missing ends_at",               integ.cancelled_no_ends_at, "Lemon should always provide an end-of-period date."],
    ["Sessions counted but missing ended_at",        integ.sessions_missing_ended_at, "Cockpit list will hide them — see diagnose_recent_sessions.sql."],
    ["In-progress sessions older than 6 hours",      integ.stuck_in_progress_6h, "Auto-cleanup may not be running."],
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <StatCard label="DAU" value={fmtNum(eng.dau)} />
        <StatCard label="WAU" value={fmtNum(eng.wau)} />
        <StatCard label="MAU" value={fmtNum(eng.mau)} />
        <StatCard label="Unconfirmed signups (30d)" value={fmtNum(eng.unconfirmed_signups_30d)} sub="email confirmation never clicked" />
      </div>

      <div style={adminCard()}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Integrity counters</h3>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
          Each row should be 0. Non-zero is red and worth investigating.
        </div>
        <table style={adminTableStyle}>
          <tbody>
            {integrityRows.map(([label, n, hint], i) => (
              <tr key={i}>
                <td style={adminTdStyle}>
                  <div>{label}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{hint}</div>
                </td>
                <td style={{ ...adminTdStyle, textAlign: "right", fontWeight: 700 }}>
                  {n > 0
                    ? <span style={{ color: "#dc2626" }}>{fmtNum(n)}</span>
                    : <span style={{ color: "#16a34a" }}>0</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={adminCard()}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Auto-cleanup rate — last 14 days</h3>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
          Sessions ended by the abandoned-session sweep vs total. A spike usually means browser-close or AI/network flakes.
        </div>
        <table style={adminTableStyle}>
          <thead><tr>
            <th style={adminThStyle}>Day</th><th style={adminThStyle}>Total</th>
            <th style={adminThStyle}>Auto-cleanup</th><th style={adminThStyle}>%</th>
          </tr></thead>
          <tbody>
            {(d.auto_cleanup_14d || []).map((r, i) => (
              <tr key={i}>
                <td style={adminTdStyle}>{fmtDay(r.day)}</td>
                <td style={adminTdStyle}>{fmtNum(r.total)}</td>
                <td style={adminTdStyle}>{fmtNum(r.auto_cleanup)}</td>
                <td style={adminTdStyle}>{r.pct ?? 0}%</td>
              </tr>
            ))}
            {(!d.auto_cleanup_14d || d.auto_cleanup_14d.length === 0) && (
              <tr><td colSpan={4} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No sessions in window.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={adminCard()}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Average score by role — last 30 days</h3>
        <table style={adminTableStyle}>
          <thead><tr>
            <th style={adminThStyle}>Role</th><th style={adminThStyle}>Sessions</th>
            <th style={adminThStyle}>Avg score</th><th style={adminThStyle}>Avg minutes</th>
          </tr></thead>
          <tbody>
            {(d.role_scores_30d || []).map((r, i) => (
              <tr key={i}>
                <td style={adminTdStyle}>{r.role || "—"}</td>
                <td style={adminTdStyle}>{fmtNum(r.sessions)}</td>
                <td style={adminTdStyle}>{r.avg_score ?? "—"}</td>
                <td style={adminTdStyle}>{r.avg_minutes ?? "—"}</td>
              </tr>
            ))}
            {(!d.role_scores_30d || d.role_scores_30d.length === 0) && (
              <tr><td colSpan={4} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No completed sessions in window.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Tab: Usage ───────────────────────────────────────────────────────────────
// Aggregate interview-consumption analytics. Read-only dashboards driven by
// admin_usage() in supabase/admin_functions.sql. Charts intentionally absent
// — tables match the rest of the admin surface and avoid a chart-lib dep.
const UsageTab = ({ d, onOpen }) => {
  const Section = ({ title, hint, children }) => (
    <div style={adminCard()}>
      <h3 style={{ marginTop: 0, marginBottom: 4, fontSize: 14, color: "var(--text-strong)" }}>{title}</h3>
      {hint && <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>{hint}</div>}
      {children}
    </div>
  );
  const t = d.totals || {};
  const usd = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Headline totals — today / 7d / 30d, three vertical groups. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <StatCard label="Sessions today" value={fmtNum(t.sessions_today)} sub={`${fmtNum(t.minutes_today)} min · ${usd(t.est_cost_today_usd)}`} />
        <StatCard label="Sessions — 7 days" value={fmtNum(t.sessions_7d)} sub={`${fmtNum(t.minutes_7d)} min · ${usd(t.est_cost_7d_usd)}`} />
        <StatCard
          label="Sessions — 30 days"
          value={fmtNum(t.sessions_30d)}
          sub={`${fmtNum(t.minutes_30d)} min · ${usd(t.est_cost_30d_usd)}${
            t.tokens_logged_30d != null && t.sessions_30d > 0
              ? ` · ${Math.round((t.tokens_logged_30d / t.sessions_30d) * 100)}% real cost`
              : ""
          }`}
        />
      </div>

      <Section title="Daily totals — last 30 days" hint="Cap columns count sessions by the plan's session_minutes cap. ‘Other’ = free (15 min) or legacy/missing plan_at_start.">
        <div style={{ overflowX: "auto" }}>
          <table style={adminTableStyle}>
            <thead><tr>
              <th style={adminThStyle}>Day</th>
              <th style={adminThStyle}>Sessions</th>
              <th style={adminThStyle}>Minutes</th>
              <th style={adminThStyle}>Cap 30m</th>
              <th style={adminThStyle}>Cap 45m</th>
              <th style={adminThStyle}>Cap other</th>
              <th style={adminThStyle}>Est. cost</th>
            </tr></thead>
            <tbody>
              {(d.daily_30d || []).slice().reverse().map((r, i) => (
                <tr key={i}>
                  <td style={adminTdStyle}>{fmtDay(r.day)}</td>
                  <td style={adminTdStyle}>{fmtNum(r.sessions)}</td>
                  <td style={adminTdStyle}>{fmtNum(Math.round(r.minutes || 0))}</td>
                  <td style={adminTdStyle}>{fmtNum(r.cap_30min)}</td>
                  <td style={adminTdStyle}>{fmtNum(r.cap_45min)}</td>
                  <td style={adminTdStyle}>{fmtNum(r.cap_other)}</td>
                  <td style={adminTdStyle}>{usd(r.est_cost_usd)}</td>
                </tr>
              ))}
              {(!d.daily_30d || d.daily_30d.length === 0) && (
                <tr><td colSpan={7} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No sessions in window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Monthly totals — last 12 months" hint="Same shape as daily, rolled up by calendar month.">
        <div style={{ overflowX: "auto" }}>
          <table style={adminTableStyle}>
            <thead><tr>
              <th style={adminThStyle}>Month</th>
              <th style={adminThStyle}>Sessions</th>
              <th style={adminThStyle}>Minutes</th>
              <th style={adminThStyle}>Cap 30m</th>
              <th style={adminThStyle}>Cap 45m</th>
              <th style={adminThStyle}>Cap other</th>
              <th style={adminThStyle}>Est. cost</th>
            </tr></thead>
            <tbody>
              {(d.monthly_12mo || []).slice().reverse().map((r, i) => (
                <tr key={i}>
                  <td style={adminTdStyle}>{r.month}</td>
                  <td style={adminTdStyle}>{fmtNum(r.sessions)}</td>
                  <td style={adminTdStyle}>{fmtNum(Math.round(r.minutes || 0))}</td>
                  <td style={adminTdStyle}>{fmtNum(r.cap_30min)}</td>
                  <td style={adminTdStyle}>{fmtNum(r.cap_45min)}</td>
                  <td style={adminTdStyle}>{fmtNum(r.cap_other)}</td>
                  <td style={adminTdStyle}>{usd(r.est_cost_usd)}</td>
                </tr>
              ))}
              {(!d.monthly_12mo || d.monthly_12mo.length === 0) && (
                <tr><td colSpan={7} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No sessions in window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Plan mix — last 30 days" hint="How API spend splits across tiers. Watch free vs paid ratio for cost exposure.">
        <table style={adminTableStyle}>
          <thead><tr>
            <th style={adminThStyle}>Plan</th>
            <th style={adminThStyle}>Sessions</th>
            <th style={adminThStyle}>Minutes</th>
            <th style={adminThStyle}>Est. cost</th>
          </tr></thead>
          <tbody>
            {(d.plan_mix_30d || []).map((r, i) => (
              <tr key={i}>
                <td style={adminTdStyle}><Pill tone="info">{r.plan}</Pill></td>
                <td style={adminTdStyle}>{fmtNum(r.sessions)}</td>
                <td style={adminTdStyle}>{fmtNum(Math.round(r.minutes || 0))}</td>
                <td style={adminTdStyle}>{usd(r.est_cost_usd)}</td>
              </tr>
            ))}
            {(!d.plan_mix_30d || d.plan_mix_30d.length === 0) && (
              <tr><td colSpan={4} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No sessions.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Top consumers — last 30 days" hint="Sorted by minutes burned. Click for full detail. Spot abuse + identify testimonial candidates.">
        <table style={adminTableStyle}>
          <thead><tr>
            <th style={adminThStyle}>Email</th>
            <th style={adminThStyle}>Plan</th>
            <th style={adminThStyle}>Sessions</th>
            <th style={adminThStyle}>Minutes</th>
            <th style={adminThStyle}>Est. cost</th>
          </tr></thead>
          <tbody>
            {(d.top_consumers_30d || []).map((u, i) => (
              <tr key={i} onClick={() => onOpen(u.user_id)} style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <td style={adminTdStyle}>{u.email}</td>
                <td style={adminTdStyle}><Pill tone="info">{u.effective_plan}</Pill></td>
                <td style={adminTdStyle}>{fmtNum(u.sessions)}</td>
                <td style={adminTdStyle}>{fmtNum(Math.round(u.minutes || 0))}</td>
                <td style={adminTdStyle}>{usd(u.est_cost_usd)}</td>
              </tr>
            ))}
            {(!d.top_consumers_30d || d.top_consumers_30d.length === 0) && (
              <tr><td colSpan={5} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No sessions.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Duration distribution — last 30 days" hint="Are users hitting their cap or quitting early? Drives ‘is a longer cap worth selling?’.">
        <table style={adminTableStyle}>
          <thead><tr>
            <th style={adminThStyle}>Bucket</th>
            <th style={adminThStyle}>Sessions</th>
          </tr></thead>
          <tbody>
            {(d.duration_histogram_30d || []).map((r, i) => (
              <tr key={i}>
                <td style={adminTdStyle}>{r.bucket}</td>
                <td style={adminTdStyle}>{fmtNum(r.sessions)}</td>
              </tr>
            ))}
            {(!d.duration_histogram_30d || d.duration_histogram_30d.length === 0) && (
              <tr><td colSpan={2} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No sessions.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="Completion rate — last 14 days" hint="If completion drops, the flow is breaking before users complain.">
        <div style={{ overflowX: "auto" }}>
          <table style={adminTableStyle}>
            <thead><tr>
              <th style={adminThStyle}>Day</th>
              <th style={adminThStyle}>Total</th>
              <th style={adminThStyle}>Completed</th>
              <th style={adminThStyle}>Abandoned</th>
              <th style={adminThStyle}>Auto-cleanup</th>
              <th style={adminThStyle}>% completed</th>
            </tr></thead>
            <tbody>
              {(d.completion_rate_14d || []).slice().reverse().map((r, i) => (
                <tr key={i}>
                  <td style={adminTdStyle}>{fmtDay(r.day)}</td>
                  <td style={adminTdStyle}>{fmtNum(r.total)}</td>
                  <td style={adminTdStyle}>{fmtNum(r.completed)}</td>
                  <td style={adminTdStyle}>{fmtNum(r.abandoned)}</td>
                  <td style={adminTdStyle}>{fmtNum(r.auto_cleanup)}</td>
                  <td style={adminTdStyle}>{r.pct_completed != null ? `${r.pct_completed}%` : "—"}</td>
                </tr>
              ))}
              {(!d.completion_rate_14d || d.completion_rate_14d.length === 0) && (
                <tr><td colSpan={6} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No sessions in window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Role mix — last 30 days" hint="Where to focus content marketing.">
        <table style={adminTableStyle}>
          <thead><tr>
            <th style={adminThStyle}>Role</th>
            <th style={adminThStyle}>Sessions</th>
            <th style={adminThStyle}>Minutes</th>
          </tr></thead>
          <tbody>
            {(d.role_mix_30d || []).map((r, i) => (
              <tr key={i}>
                <td style={adminTdStyle}>{r.role}</td>
                <td style={adminTdStyle}>{fmtNum(r.sessions)}</td>
                <td style={adminTdStyle}>{fmtNum(Math.round(r.minutes || 0))}</td>
              </tr>
            ))}
            {(!d.role_mix_30d || d.role_mix_30d.length === 0) && (
              <tr><td colSpan={3} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No sessions.</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
        <strong>Cost source:</strong> real Claude token usage from <code>public.claude_token_usage</code> per session.
        Sessions logged before the token-logging migration fall back to the legacy estimate
        of ${(d.cost_per_minute_usd ?? 0.0167).toFixed(4)}/min (≈ $0.50 per 30-min interview).
        The "% real cost" indicator on the 30-day card shows what share of sessions in the window
        have token logs — when that approaches 100% the numbers above are essentially all real.
        Update pricing by inserting a new row into <code>public.claude_pricing</code> with a new <code>effective_from</code>;
        the fallback constant lives in <code>public.claude_session_cost_usd()</code>.
      </div>
    </div>
  );
};

// ── User detail drawer (right-side slide-in) ─────────────────────────────────
const UserDrawer = ({ d, loading, error, onClose }) => {
  const u = d?.user;
  const sessions = d?.sessions || [];
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        zIndex: 50, display: "flex", justifyContent: "flex-end",
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 100%)", height: "100%", overflowY: "auto",
          background: "var(--bg)", color: "var(--text-strong)",
          borderLeft: "1px solid var(--border-soft)", padding: 24,
        }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>User detail</h2>
          <button onClick={onClose} style={cockpitNavBtn()}>Close</button>
        </div>
        {loading && <div style={{ color: "var(--text-muted)" }}>Loading…</div>}
        {error && <div style={{ color: "#dc2626" }}>{error}</div>}
        {u && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={adminCard()}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{u.email}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                <code>{u.user_id}</code>
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Pill tone="info">plan: {u.effective_plan}</Pill>
                {u.sub_status && <Pill tone={subStatusTone(u.sub_status)}>{u.sub_plan_code}: {u.sub_status}</Pill>}
                {u.pass_plan_code && <Pill tone="info">pass: {u.pass_plan_code}</Pill>}
              </div>
            </div>

            <div style={adminCard()}>
              <h3 style={{ marginTop: 0, fontSize: 13, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Account</h3>
              <DetailRow k="Signed up" v={fmtDate(u.signed_up_at)} />
              <DetailRow k="Last sign-in" v={fmtDate(u.last_sign_in_at)} />
              <DetailRow k="Email confirmed" v={fmtDate(u.email_confirmed_at)} />
              <DetailRow k="Carried-forward credits" v={fmtNum(u.carried_forward_credits)} />
            </div>

            {(u.sub_plan_code || u.sub_status) && (
              <div style={adminCard()}>
                <h3 style={{ marginTop: 0, fontSize: 13, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Subscription</h3>
                <DetailRow k="Plan" v={u.sub_plan_code} />
                <DetailRow k="Status" v={u.sub_status} />
                <DetailRow k="Renews at" v={fmtDate(u.sub_renews_at)} />
                <DetailRow k="Ends at" v={fmtDate(u.sub_ends_at)} />
                <DetailRow k="Lemon sub id" v={u.sub_lemon_subscription_id} mono />
                <DetailRow k="Lemon customer id" v={u.sub_lemon_customer_id} mono />
                <DetailRow k="Last event" v={`${u.sub_last_event_name || "—"} (${fmtDate(u.sub_last_event_at)})`} />
              </div>
            )}

            {u.pass_plan_code && (
              <div style={adminCard()}>
                <h3 style={{ marginTop: 0, fontSize: 13, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Pass</h3>
                <DetailRow k="Pass plan" v={u.pass_plan_code} />
                <DetailRow k="Purchased" v={fmtDate(u.pass_purchased_at)} />
                <DetailRow k="Expires" v={fmtDate(u.pass_expires_at)} />
              </div>
            )}

            <div style={adminCard()}>
              <h3 style={{ marginTop: 0, fontSize: 13, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>Sessions ({sessions.length})</h3>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12, fontSize: 12, color: "var(--text-muted)" }}>
                <span><strong style={{ color: "var(--text-strong)" }}>{fmtNum(u.sessions_total)}</strong> total</span>
                <span><strong style={{ color: "var(--text-strong)" }}>{fmtNum(u.sessions_30d)}</strong> in 30d</span>
                <span><strong style={{ color: "var(--text-strong)" }}>{u.minutes_total != null ? fmtNum(Math.round(u.minutes_total)) : "—"}</strong> min burned</span>
                <span><strong style={{ color: "var(--text-strong)" }}>{u.avg_minutes != null ? u.avg_minutes : "—"}</strong> avg min</span>
                <span><strong style={{ color: "var(--text-strong)" }}>{u.est_cost_usd != null ? `$${Number(u.est_cost_usd).toFixed(2)}` : "—"}</strong> est. AI cost</span>
              </div>
              <table style={adminTableStyle}>
                <thead><tr>
                  <th style={adminThStyle}>Started</th><th style={adminThStyle}>Status</th>
                  <th style={adminThStyle}>Role</th><th style={adminThStyle}>Min</th>
                  <th style={adminThStyle}>Score</th><th style={adminThStyle}>Plan</th>
                </tr></thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id}>
                      <td style={adminTdStyle}>{fmtDate(s.started_at)}</td>
                      <td style={adminTdStyle}>{s.status}{s.ended_reason ? ` (${s.ended_reason})` : ""}</td>
                      <td style={adminTdStyle}>{s.role || "—"}</td>
                      <td style={adminTdStyle}>{s.duration_seconds != null ? Math.round(s.duration_seconds / 60) : "—"}</td>
                      <td style={adminTdStyle}>{s.overall_score ?? "—"}</td>
                      <td style={adminTdStyle}>{s.plan_at_start || "—"}</td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr><td colSpan={6} style={{ ...adminTdStyle, color: "var(--text-muted)" }}>No sessions.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DetailRow = ({ k, v, mono }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: "1px solid var(--border-soft)" }}>
    <span style={{ color: "var(--text-muted)", fontSize: 13 }}>{k}</span>
    <span style={{ fontSize: 13, textAlign: "right", wordBreak: "break-all", fontFamily: mono ? "monospace" : "inherit" }}>{v ?? "—"}</span>
  </div>
);

// ─── HeroWaves ───────────────────────────────────────────────────────────────
// Animated background for the landing hero + cockpit greeting area.
// Six sine-wave layers stroked in an orange→red gradient at varying speeds,
// frequencies, and opacities. Layer index 3 is the bright focal wave (higher
// opacity + drop-shadow glow). A soft orange radial glow sits in the
// top-left corner of the host.
//
// Constraints (per design spec):
//   - Sits behind all content via z-index: -1 inside a stacking-context
//     parent (the landing-hero <section> and cockpit <main> both create
//     one). Zero impact on layout — purely additive, absolutely positioned.
//   - pointer-events: none on every painted layer so it never blocks clicks.
//   - Pauses via IntersectionObserver when scrolled out of view, plus
//     document `visibilitychange` for hidden tabs (cancels the RAF loop;
//     resumes on next intersection).
//   - Respects `prefers-reduced-motion: reduce` — paints a single static
//     frame and never starts the RAF loop.
//   - Resizes with the host via ResizeObserver. Canvas is HiDPI-aware.
//   - Visible on both themes: orange→red is high-contrast on dark
//     (#070c18) and on light (#f8fafc). Non-focal layers stay subtle so
//     the focal wave reads as the main element.
const HeroWaves = ({ height = '100%', topGlow = true }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = canvas.getContext('2d');
    const dpr = Math.max(1, Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1));
    let w = 0, h = 0;
    let raf = 0;
    let visible = true;
    let t = 0;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      w = Math.max(1, Math.floor(rect.width));
      h = Math.max(1, Math.floor(rect.height));
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // Six layers of sine waves. Layer index 3 is the focal/center bright
    // wave (higher opacity + glow + thicker stroke). Frequencies, phases,
    // and speeds are deliberately incommensurate so the composite never
    // visually loops cleanly — keeps the motion organic over long views.
    const LAYERS = [
      { freq: 0.0070, amp: 14, speed: 0.0020, phase: 0.0, op: 0.22, glow: 0,  width: 1.2, focal: false },
      { freq: 0.0055, amp: 22, speed: 0.0014, phase: 1.1, op: 0.26, glow: 0,  width: 1.4, focal: false },
      { freq: 0.0042, amp: 30, speed: 0.0011, phase: 2.3, op: 0.32, glow: 6,  width: 1.6, focal: false },
      { freq: 0.0035, amp: 36, speed: 0.0009, phase: 0.8, op: 0.70, glow: 16, width: 2.6, focal: true  },
      { freq: 0.0050, amp: 26, speed: 0.0013, phase: 3.4, op: 0.34, glow: 6,  width: 1.5, focal: false },
      { freq: 0.0065, amp: 18, speed: 0.0017, phase: 4.6, op: 0.24, glow: 0,  width: 1.3, focal: false },
    ];

    const drawFrame = () => {
      ctx.clearRect(0, 0, w, h);
      const cy = h / 2;
      // Step size of 6px keeps the per-frame x-iteration count low even on
      // ultra-wide displays while staying smooth at the chosen amplitudes.
      const step = 6;

      for (let i = 0; i < LAYERS.length; i++) {
        const L = LAYERS[i];
        // Orange (#f97316) → mid (#ea5832) → red (#dc2626) horizontal
        // gradient stroke. Same hue band on every layer so the composite
        // reads as one unified wash, not six competing colors.
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0,    `rgba(249,115,22,${L.op})`);
        grad.addColorStop(0.5,  `rgba(234, 88, 50,${Math.min(1, L.op * 1.05)})`);
        grad.addColorStop(1,    `rgba(220, 38, 38,${L.op})`);

        ctx.beginPath();
        for (let x = 0; x <= w; x += step) {
          const y = cy + Math.sin(x * L.freq + t * L.speed + L.phase) * L.amp;
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.lineWidth = L.width;
        ctx.strokeStyle = grad;
        ctx.lineCap = 'round';
        if (L.glow > 0) {
          ctx.shadowBlur = L.glow;
          ctx.shadowColor = 'rgba(249,115,22,0.55)';
        } else {
          ctx.shadowBlur = 0;
        }
        ctx.stroke();
      }
      ctx.shadowBlur = 0;
    };

    // Delta-time progression so motion tempo stays consistent across
    // 60 / 90 / 120 Hz displays. lastTs is reset to null on every pause
    // so a paused-then-resumed loop continues from the same simulated
    // time without absorbing the elapsed real-time gap (no visible jump).
    let lastTs = null;
    const tick = (ts) => {
      if (!visible) { raf = 0; lastTs = null; return; }
      if (lastTs == null) lastTs = ts;
      // Clamp to 50ms so a backgrounded tab returning doesn't fire a
      // single huge step that visually skips the wave forward.
      const dt = Math.min(50, ts - lastTs);
      lastTs = ts;
      t += dt;
      drawFrame();
      raf = requestAnimationFrame(tick);
    };

    resize();
    drawFrame();

    // ResizeObserver covers viewport resize, orientation change, and any
    // parent-driven height change without a window resize listener.
    const ro = new ResizeObserver(() => { resize(); drawFrame(); });
    ro.observe(wrap);

    let io = null;
    let onVis = null;

    if (!reduced) {
      io = new IntersectionObserver((entries) => {
        const e = entries[0];
        const wasVisible = visible;
        visible = !!(e && e.isIntersecting);
        if (visible && !wasVisible && !raf) {
          raf = requestAnimationFrame(tick);
        }
      }, { threshold: 0 });
      io.observe(wrap);

      onVis = () => {
        // Hidden tabs already pause RAF in modern browsers, but the explicit
        // gate keeps the resume path consistent when the tab returns.
        if (document.visibilityState !== 'hidden' && visible && !raf) {
          raf = requestAnimationFrame(tick);
        }
      };
      document.addEventListener('visibilitychange', onVis);

      raf = requestAnimationFrame(tick);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      if (io) io.disconnect();
      if (onVis) document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: -1,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          pointerEvents: 'none',
          display: 'block',
        }}
      />
      {topGlow && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -140, left: -140,
            width: 520, height: 520,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(249,115,22,0.32), rgba(249,115,22,0.00) 65%)',
            filter: 'blur(6px)',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};

// ─── Promo banner ────────────────────────────────────────────────────────────
// Dismissible strip above the hero pitching the "free Starter Pass for the
// first 100 job-seekers" launch promo. Dismissal persists in localStorage
// keyed by the campaign slug, so updating the slug below resets dismissal
// for everyone (use this when running a new promo).
const PROMO_DISMISS_KEY = "nti.promo.starterPass100.dismissed.v1";
// The Lemon Squeezy discount code that drops Starter Pass to $0. Must be
// configured in Lemon with: 100% off Starter Pass variant ONLY, total
// redemption cap of 100, expiry date, one-redemption-per-customer. The
// 100-user cap is enforced server-side by Lemon — once hit, checkout shows
// "code no longer valid" and the buyer can either pay $24.99 or back out.
// DO NOT rely on this client-side constant for cap enforcement.
export const PROMO_DISCOUNT_CODE = "FRESHSTART";
export const PROMO_PLAN_CODE = "starter_pass";

// Cross-tab "pending subscribe intent" — persists the planCode (+ optional
// discountCode) that a signed-out visitor was trying to buy when they were
// bounced to sign-in/sign-up. Stored in localStorage (not a useRef) so the
// intent survives the signup → email-confirmation round-trip, where the
// confirmation link typically opens in a NEW TAB. The new tab's React
// component instance has no in-memory ref, but it does see the same
// localStorage. TTL stops a stale intent from firing days later.
const PENDING_SUBSCRIBE_KEY = "nti.subscribe.pendingIntent.v1";
const PENDING_SUBSCRIBE_TTL_MS = 60 * 60 * 1000; // 1 hour
// `intent` is either a plain plan-code string (regular "Buy" click) or
// an object { planCode, discountCode } (promo-claim flow).
function writePendingSubscribeIntent(intent) {
  if (!intent) return;
  try {
    const payload = typeof intent === "string"
      ? { planCode: intent, ts: Date.now() }
      : { ...intent, ts: Date.now() };
    localStorage.setItem(PENDING_SUBSCRIBE_KEY, JSON.stringify(payload));
  } catch {}
}
function clearPendingSubscribeIntent() {
  try { localStorage.removeItem(PENDING_SUBSCRIBE_KEY); } catch {}
}
// Read-and-consume: removes from storage as part of returning, so the
// intent only fires once per claim. Returns null if missing or stale.
function consumePendingSubscribeIntent() {
  try {
    const raw = localStorage.getItem(PENDING_SUBSCRIBE_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_SUBSCRIBE_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.planCode) return null;
    if (Date.now() - (parsed.ts || 0) > PENDING_SUBSCRIBE_TTL_MS) return null;
    return { planCode: parsed.planCode, discountCode: parsed.discountCode };
  } catch { return null; }
}

const PromoBanner = ({ user, onClaimPromo }) => {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(PROMO_DISMISS_KEY) === "1"; }
    catch { return false; }
  });
  if (dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem(PROMO_DISMISS_KEY, "1"); } catch {}
    setDismissed(true);
  };
  return (
    <div style={{
      position: "relative", zIndex: 2,
      maxWidth: 1100, margin: "20px auto 0", padding: "0 24px",
    }}>
      <div style={{
        background: "linear-gradient(135deg, rgba(249,115,22,0.14), rgba(220,38,38,0.10))",
        border: "1px solid rgba(249,115,22,0.45)",
        borderRadius: 14,
        padding: "14px 18px",
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        boxShadow: "0 6px 24px rgba(249,115,22,0.10)",
      }}>
        <span style={{
          padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
          background: "rgba(249,115,22,0.25)", color: "#f97316",
          border: "1px solid rgba(249,115,22,0.55)", letterSpacing: 0.6,
          whiteSpace: "nowrap",
        }}>BETWEEN JOBS?</span>
        <div style={{ flex: "1 1 320px", color: "var(--text-strong)", fontSize: 14, lineHeight: 1.5 }}>
          <strong style={{ fontWeight: 700 }}>3 months of Starter Pass — on us.</strong>{" "}
          <span style={{ color: "var(--text-muted)" }}>
            Recently laid off or job-hunting? We're giving away{" "}
            <strong style={{ color: "var(--text-strong)" }}>100% free access</strong> to our Starter Pass
            (5 interviews/month, 30 mins each, JD-tailored questions, full insights, PDF reports) for{" "}
            <strong style={{ color: "var(--text-strong)" }}>the first 100 job-seekers</strong>. No card, no catch.
          </span>
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-dim)" }}>
            <strong style={{ color: "var(--text-strong)" }}>No card required.</strong>{" "}
            Click <em>Claim my free 3 months</em>, sign in, then just hit{" "}
            <em>Submit order</em> on the checkout page — the 100% discount applies
            on submit, so your final charge is{" "}
            <strong style={{ color: "var(--text-strong)" }}>$0.00</strong> even
            though the cart preview still shows $24.99.
          </div>
        </div>
        <button
          onClick={onClaimPromo}
          style={{
            background: "linear-gradient(135deg,#f97316,#dc2626)",
            border: "none", color: "#fff", fontWeight: 700, fontSize: 14,
            padding: "10px 18px", borderRadius: 10, cursor: "pointer",
            boxShadow: "0 6px 16px rgba(249,115,22,0.32)",
            whiteSpace: "nowrap", fontFamily: "sans-serif",
          }}
        >Claim my free 3 months</button>
        <button
          onClick={dismiss}
          aria-label="Dismiss promotion"
          title="Dismiss"
          style={{
            background: "transparent", border: "none",
            color: "var(--text-muted)", cursor: "pointer",
            fontSize: 18, lineHeight: 1, padding: "4px 6px",
          }}
        >×</button>
      </div>
    </div>
  );
};

const LandingPage = ({ onStart, onSubscribe, onClaimPromo, theme, toggleTheme, user, onSignIn, onSignUp, onSignOut, onGoToCockpit, onViewTerms, onViewPrivacy, onViewCookies, onViewDisclaimer }) => {
  // Billing toggle state — drives which pricing array renders in the grid
  // and which subtitle line shows. "subscription" = monthly recurring,
  // "pass" = one-time 3-month bundle. Defaults to subscription so existing
  // visitors see the familiar pricing on first paint.
  const [billingMode, setBillingMode] = useState("subscription");
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportPrefill, setSupportPrefill] = useState({});
  // (The previous PROMO_CLAIM_INTENT_KEY → auto-open-support useEffect
  // was removed when the promo flow was rewired to go straight to Lemon
  // checkout with the FRESHSTART discount auto-applied.)
  const displayName =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    (user?.email ? user.email.split('@')[0] : '');
  const navBtn = (label, opts = {}) => {
    const variant = opts.primary ? "primary" : opts.accent ? "accent" : "default";
    const variantStyles = {
      primary: {
        background: "linear-gradient(135deg,#f97316,#dc2626)",
        border: "none", color: "#fff",
        boxShadow: "0 6px 16px rgba(249,115,22,0.32)",
      },
      accent: {
        background: "rgba(249,115,22,0.08)",
        border: "1px solid rgba(249,115,22,0.55)",
        color: "#f97316",
      },
      default: {
        background: "transparent",
        border: "1px solid var(--border-soft)",
        color: "var(--text-muted)",
      },
    };
    return (
      <button
        onClick={opts.onClick}
        disabled={opts.disabled}
        title={opts.title || ""}
        style={{
          ...variantStyles[variant],
          padding: "8px 14px", borderRadius: 10, fontSize: 13, fontWeight: 700,
          cursor: opts.disabled ? "not-allowed" : "pointer", fontFamily: "sans-serif",
          opacity: opts.disabled ? 0.55 : 1,
          transition: "transform 0.12s ease",
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.97)")}
        onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >{label}</button>
    );
  };

  return (
    <div style={{
      minHeight: "100vh", background: "var(--bg)", color: "var(--text-strong)",
      fontFamily: "'Lora', serif", position: "relative", overflow: "hidden",
    }}>
      <GlobalStyles />

      {/* Ambient glow lives on body::after now (single source, fixed
          behind the headline). Removed local absolute glow to keep the
          page to one focal point. */}

      {/* Top nav */}
      <nav style={{
        position: "relative", zIndex: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px clamp(12px, 4vw, 28px)", maxWidth: 1180, margin: "0 auto",
        flexWrap: "wrap", gap: 10,
      }}>
        <Wordmark logoSize={56} fontSize={19} gap={6} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <a href="#how" style={{ color: "var(--text-dim)", fontSize: 13, fontFamily: "sans-serif", textDecoration: "none", padding: "8px 10px" }} className="nti-nav-link">How it works</a>
          <a href="#pricing" style={{ color: "var(--text-dim)", fontSize: 13, fontFamily: "sans-serif", textDecoration: "none", padding: "8px 10px" }} className="nti-nav-link">Pricing</a>
          {user ? (
            <>
              <span
                title={user.email}
                style={{
                  color: "var(--text-muted)",
                  fontSize: 13,
                  fontFamily: "sans-serif",
                  padding: "6px 10px",
                  maxWidth: 180,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Hi, <strong style={{ color: "var(--text-strong)" }}>{displayName}</strong>
              </span>
              {onGoToCockpit && navBtn("My cockpit", { accent: true, onClick: onGoToCockpit })}
              {navBtn("Sign out", { onClick: onSignOut })}
            </>
          ) : (
            <>
              {navBtn("Sign in", { accent: true, onClick: onSignIn })}
              {navBtn("Sign up", { primary: true, onClick: onSignUp })}
            </>
          )}
          <button
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-label="Toggle theme"
            style={{
              width: 36, height: 36, borderRadius: "50%", marginLeft: 4,
              background: "var(--surface-2)", border: "1px solid var(--border-soft)",
              color: "var(--text-muted)", fontSize: 15, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1,
            }}
          >{theme === "dark" ? "☀️" : "🌙"}</button>
        </div>
      </nav>

      {/* ── Promo banner: free Starter Pass for first 100 job-seekers ──
          Dismissal is remembered in localStorage so a returning visitor
          isn't pestered. The CTA routes signed-in users straight to Lemon
          checkout with the FRESHSTART discount auto-applied (or, for
          signed-out visitors, to sign-in first and then on to checkout).
          The 100-redemption cap is enforced server-side in Lemon Squeezy. */}
      <PromoBanner
        user={user}
        onClaimPromo={onClaimPromo}
      />

      {/* Hero */}
      <section style={{
        position: "relative", zIndex: 1,
        maxWidth: 920, margin: "0 auto", padding: "48px 24px 40px", textAlign: "center",
        // isolation: isolate confines the HeroWaves' z-index: -1 to this
        // section's stacking context (zIndex: 1 above already establishes
        // one — isolation is belt-and-braces against future refactors that
        // might remove the explicit zIndex).
        isolation: "isolate",
      }}>
        <HeroWaves height="100%" topGlow={true} />
        <style>{`
          @keyframes ntiHeroFadeUp {
            from { opacity: 0; transform: translateY(10px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes ntiPushPulse {
            0%, 100% { filter: brightness(1) drop-shadow(0 0 0 rgba(249,115,22,0)); }
            50%      { filter: brightness(1.18) drop-shadow(0 0 16px rgba(249,115,22,0.45)); }
          }
          @keyframes ntiCursorBlink {
            0%, 49% { opacity: 1; }
            50%, 100% { opacity: 0; }
          }
          @keyframes ntiTypeIn {
            0%   { -webkit-clip-path: inset(0 100% 0 0); clip-path: inset(0 100% 0 0); }
            100% { -webkit-clip-path: inset(0 0%   0 0); clip-path: inset(0 0%   0 0); }
          }
          .nti-hero-headline { animation: ntiHeroFadeUp 0.5s ease-out both; }
          .nti-hero-pushback {
            background: linear-gradient(135deg,#f97316,#dc2626);
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            display: inline-block;
            animation: ntiPushPulse 4.2s ease-in-out infinite 1.2s;
          }
          /* Punchline container is always visible — the typing happens
             inside .nti-hero-typed so the layout slot is reserved from
             the start (no jump when text appears). */
          .nti-hero-typed {
            display: inline-block;
            clip-path: inset(0 100% 0 0);
            animation: ntiTypeIn 2.1s steps(27, end) 1.2s both;
          }
          .nti-hero-cursor {
            display: inline-block;
            width: 3px; height: 0.95em;
            background: #f97316;
            margin-left: 4px;
            vertical-align: -2px;
            opacity: 0;
            /* Cursor only starts blinking after typing finishes
               (1.2s delay + 2.1s typing ≈ 3.3s). */
            animation: ntiCursorBlink 0.85s steps(1) infinite 3.3s;
          }
          .nti-hero-sub {
            animation: ntiHeroFadeUp 0.55s ease-out 3.6s both;
          }
          /* Respect reduced-motion preferences — show everything statically. */
          @media (prefers-reduced-motion: reduce) {
            .nti-hero-headline,
            .nti-hero-pushback,
            .nti-hero-typed,
            .nti-hero-cursor,
            .nti-hero-sub {
              animation: none !important;
              -webkit-clip-path: none !important;
              clip-path: none !important;
              opacity: 1 !important;
              filter: none !important;
            }
          }
        `}</style>
        <h1 className="nti-hero-headline" style={{
          fontSize: "clamp(36px, 6vw, 64px)", lineHeight: 1.05,
          margin: "0 0 14px", fontWeight: 800, letterSpacing: -1.4,
          color: "var(--text-strong)",
        }}>
          Practice interviews that{" "}
          <span className="nti-hero-pushback">actually push back.</span>
        </h1>
        <p className="nti-hero-punchline" style={{
          color: "var(--text-muted)",
          fontSize: "clamp(17px, 2vw, 22px)",
          fontStyle: "italic",
          letterSpacing: -0.2,
          margin: "0 0 26px", lineHeight: 1.4,
          fontFamily: "'Lora', serif",
        }}>
          <span className="nti-hero-typed">Because the real ones will.</span><span className="nti-hero-cursor" aria-hidden="true" />
        </p>
        <p className="nti-hero-sub" style={{
          color: "var(--text-dim)", fontSize: "clamp(14px, 1.4vw, 16px)",
          maxWidth: 600, margin: "0 auto 30px", lineHeight: 1.55, fontFamily: "sans-serif",
        }}>
          Your AI interviewer probes weak spots, asks for specifics, and drills
          into your reasoning — so by interview day, nothing catches you off guard.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={onStart}
            style={{
              background: "linear-gradient(135deg,#f97316,#dc2626)",
              border: "none", color: "#fff",
              padding: "14px 26px", borderRadius: 12, fontSize: 15, fontWeight: 700,
              cursor: "pointer", fontFamily: "sans-serif",
              boxShadow: "0 10px 30px rgba(249,115,22,0.35)",
              transition: "transform 0.15s ease",
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >Start a free mock interview →</button>
          <a
            href="#how"
            style={{
              background: "var(--surface-2)", border: "1px solid var(--border-soft)",
              color: "var(--text-muted)", textDecoration: "none",
              padding: "14px 22px", borderRadius: 12, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: "sans-serif",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >See how it works</a>
        </div>
        <div style={{
          marginTop: 18, color: "var(--text-dim)", fontSize: 13, fontWeight: 500,
          fontFamily: "sans-serif", letterSpacing: 0.1,
        }}>
          Free to start · 12-dimension coaching report · Voice-first
        </div>
      </section>

      {/* How it works */}
      <section id="how" style={{
        position: "relative", zIndex: 1,
        maxWidth: 920, margin: "60px auto 0", padding: "0 24px",
      }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.6, color: "var(--text-strong)", textAlign: "center", margin: "0 0 8px" }}>
          From cold start to confident in 3 steps
        </h2>
        <p style={{ color: "var(--text-dim)", fontSize: 14, textAlign: "center", margin: "0 0 36px", fontFamily: "sans-serif" }}>
          Three steps. No accounts. No friction.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 28 }}>
          <HowStep n={1} title="Pick your role" body="Choose your job title, experience level, and the focus areas you want to be tested on. We tailor the questions instantly." />
          <HowStep n={2} title="Have a real conversation" body="Speak your answers out loud. The AI follows up like a real interviewer — clarifying, probing, going deeper when it matters." />
          <HowStep n={3} title="Get scored & download" body="At the end, see a breakdown across communication, depth, structure and more — then download a printable PDF report." />
        </div>
      </section>

      {/* Interview Demo */}
      <section id="demo-interview" style={{
        position: "relative", zIndex: 1,
        maxWidth: 1080, margin: "70px auto 0", padding: "0 24px",
      }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.6, color: "var(--text-strong)", textAlign: "center", margin: "0 0 8px" }}>
          See it in action
        </h2>
        <p style={{ color: "var(--text-dim)", fontSize: 14, textAlign: "center", margin: "0 0 36px", fontFamily: "sans-serif" }}>
          A real, voice-first conversation that pushes back like a hiring manager.
        </p>
        <div className="nti-demo-row" style={{
          display: "grid", gridTemplateColumns: "1fr", gap: 36, alignItems: "center",
        }}>
          <div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 18 }}>
              <DemoPoint icon="🎙" title="Listens to your real voice" body="Speak naturally — the AI transcribes what you said and responds out loud in seconds, just like a phone screen." />
              <DemoPoint icon="🔄" title="Follows up like a hiring manager" body="Probes weak spots, asks for specifics, and drills into the reasoning behind your decisions." />
              <DemoPoint icon="📄" title="Tailored to your role and resume" body="Every question is framed around the job you're targeting and the real experience on your resume." />
            </ul>
          </div>
          <InterviewDemo />
        </div>
      </section>

      {/* Feedback Demo */}
      <section id="demo-feedback" style={{
        position: "relative", zIndex: 1,
        maxWidth: 1080, margin: "70px auto 0", padding: "0 24px",
      }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.6, color: "var(--text-strong)", textAlign: "center", margin: "0 0 8px" }}>
          Get a coach-grade scorecard
        </h2>
        <p style={{ color: "var(--text-dim)", fontSize: 14, textAlign: "center", margin: "0 0 36px", fontFamily: "sans-serif" }}>
          Twelve dimensions, four categories, with written feedback for every single one.
        </p>
        <div className="nti-demo-row-reverse" style={{
          display: "grid", gridTemplateColumns: "1fr", gap: 36, alignItems: "center",
        }}>
          <div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 18 }}>
              <DemoPoint icon="📊" title="Numbers and written analysis" body="Every dimension gets a 0–10 score plus a paragraph explaining exactly why — no vague feedback." />
              <DemoPoint icon="🎯" title="Top 3 priorities for next time" body="The ranked list of what to fix before your next real interview, with concrete examples from your own answers." />
              <DemoPoint icon="🏆" title="A clear readiness verdict" body="Walk away knowing the level of role you're ready to interview for today, and what's still standing in the way." />
            </ul>
          </div>
          <FeedbackDemo />
        </div>
      </section>

      {/* Demo video — lightweight click-to-play YouTube embed.
          Placed right after the FeedbackDemo so visitors can see the product
          in motion before scrolling into features and pricing. Heading is
          intentionally distinct from the FeedbackDemo's "See it in action"
          so the two sections don't look like duplicates. */}
      <section id="demo-video" style={{
        position: "relative", zIndex: 1,
        maxWidth: 1080, margin: "70px auto 0", padding: "0 24px",
      }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.6, color: "var(--text-strong)", textAlign: "center", margin: "0 0 8px" }}>
          Watch a quick walkthrough
        </h2>
        <p style={{ color: "var(--text-dim)", fontSize: 14, textAlign: "center", margin: "0 0 24px", fontFamily: "sans-serif" }}>
          A quick look at the AI interviewer, the scoring, and the feedback you walk away with.
        </p>
        <div className="nti-video-row" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 28 }}>
          <div>
            <DemoVideo videoId="-WqS9G73RVY" title="Nail That Interview — product demo" />
            <p style={{ color: "var(--text-dim)", fontSize: 13, textAlign: "center", margin: "12px 0 0", fontFamily: "sans-serif", fontWeight: 600 }}>
              Product walkthrough
            </p>
          </div>
          <div>
            <DemoVideo videoId="L5irhUhFz0o" title="Nail That Interview — full interview demo and insights deep-dive" />
            <p style={{ color: "var(--text-dim)", fontSize: 13, textAlign: "center", margin: "12px 0 0", fontFamily: "sans-serif", fontWeight: 600 }}>
              Interview Demo
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{
        position: "relative", zIndex: 1,
        maxWidth: 1180, margin: "80px auto 0", padding: "0 24px",
      }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.6, color: "var(--text-strong)", textAlign: "center", margin: "0 0 8px" }}>
          Pricing
        </h2>
        <p style={{ color: "var(--text-dim)", fontSize: 14, textAlign: "center", margin: "0 0 18px", fontFamily: "sans-serif" }}>
          {billingMode === "subscription"
            ? "Start free. Upgrade anytime. Cancel anytime."
            : "One-time payment. No auto-renew. 3 months of access."}
        </p>

        {/* Billing toggle (segmented control). Subscription ↔ 3-Month Pass —
            single grid swaps in place. Pill-shaped to match the brand's
            rounded language; active segment uses the orange gradient so the
            current selection reads as primary. */}
        <div role="tablist" aria-label="Billing type" style={{
          display: "flex", justifyContent: "center", margin: "0 0 28px",
        }}>
          <div style={{
            display: "inline-flex",
            background: "var(--surface)",
            border: "1px solid var(--border-soft)",
            borderRadius: 99,
            padding: 4,
            gap: 4,
          }}>
            {(() => {
              const tabs = [
                { id: "subscription", label: "Subscription" },
                { id: "pass", label: "3-Month Pass" },
              ];
              // Standard WAI-ARIA tab keyboard nav: ←/→ moves between tabs,
              // Home/End jump to the first/last. Activation is automatic on
              // focus change (matches the "automatic activation" pattern,
              // since switching tabs has no side effects beyond rendering
              // the other pricing array).
              const handleTabKey = (e) => {
                const idx = tabs.findIndex((t) => t.id === billingMode);
                if (idx < 0) return;
                let next = null;
                if (e.key === "ArrowRight") next = (idx + 1) % tabs.length;
                else if (e.key === "ArrowLeft") next = (idx - 1 + tabs.length) % tabs.length;
                else if (e.key === "Home") next = 0;
                else if (e.key === "End") next = tabs.length - 1;
                if (next !== null) {
                  e.preventDefault();
                  setBillingMode(tabs[next].id);
                  // Move focus to the newly active tab so the user sees a
                  // visible focus ring on it.
                  const el = document.getElementById(`billing-tab-${tabs[next].id}`);
                  if (el) el.focus();
                }
              };
              return tabs.map((opt) => {
                const active = billingMode === opt.id;
                return (
                <button
                  key={opt.id}
                  id={`billing-tab-${opt.id}`}
                  role="tab"
                  aria-selected={active}
                  tabIndex={active ? 0 : -1}
                  onKeyDown={handleTabKey}
                  onClick={() => setBillingMode(opt.id)}
                  style={{
                    padding: "9px 22px",
                    borderRadius: 99,
                    border: "none",
                    fontSize: 13.5, fontWeight: 700, fontFamily: "sans-serif",
                    cursor: "pointer",
                    background: active
                      ? "linear-gradient(135deg,#f97316,#dc2626)"
                      : "transparent",
                    color: active ? "#fff" : "var(--text-dim)",
                    boxShadow: active ? "0 6px 18px rgba(249,115,22,0.30)" : "none",
                    transition: "background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
                  }}
                >
                  {opt.label}
                </button>
              );
            });
            })()}
          </div>
        </div>

        <p style={{ color: "var(--text-dimmer)", fontSize: 12.5, textAlign: "center", margin: "0 0 44px", fontFamily: "sans-serif" }}>
          7-day refund if you've used 2 or fewer interviews —{" "}
          <button
            onClick={onViewTerms}
            style={{
              background: "none", border: "none", padding: 0,
              color: "var(--text-dim)", textDecoration: "underline", cursor: "pointer",
              font: "inherit",
            }}
          >see refund terms</button>.
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(250px, 100%), 1fr))",
          gap: 22,
          alignItems: "stretch",
        }}>
          {(billingMode === "subscription" ? PRICING_TIERS : PASS_TIERS).map((tier) => (
            <PriceCard key={tier.name} tier={tier} onStart={onStart} onSubscribe={onSubscribe} />
          ))}
        </div>
      </section>

      {/* "Why not a free AI chatbot?" — addresses the most common
          objection (DIY with a general-purpose chatbot) without naming
          competitors. Sits right after pricing because that's where the
          "is this worth $9.99?" doubt peaks. */}
      <section id="vs-chatbot" style={{
        position: "relative", zIndex: 1,
        maxWidth: 1080, margin: "90px auto 0", padding: "0 24px",
      }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <p style={{
            color: "#f97316", fontSize: 12, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 2.2,
            margin: "0 0 10px", fontFamily: "sans-serif",
          }}>
            The honest comparison
          </p>
          <h2 style={{
            fontSize: 30, fontWeight: 800, color: "var(--text-strong)",
            margin: "0 0 10px", letterSpacing: -0.6, fontFamily: "sans-serif",
          }}>
            Why not just use a free AI chatbot?
          </h2>
          <p style={{
            color: "var(--text-dim)", fontSize: 15, margin: "0 auto", maxWidth: 640,
            fontFamily: "sans-serif", lineHeight: 1.55,
          }}>
            You can absolutely prompt a general AI to roleplay an interviewer.
            Most people try it once and quietly stop. Here's what actually
            changes when you use a tool built for this.
          </p>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))",
          gap: 18,
        }}>
          {/* Generic chatbot column */}
          <div style={{
            background: "var(--card)",
            border: "1px solid var(--border-soft)",
            borderRadius: 18, padding: "26px 24px",
            backdropFilter: "blur(10px)",
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "var(--text-dim)",
              textTransform: "uppercase", letterSpacing: 1.6,
              marginBottom: 6, fontFamily: "sans-serif",
            }}>
              A general AI chatbot
            </div>
            <h3 style={{
              fontSize: 19, fontWeight: 700, color: "var(--text-muted)",
              margin: "0 0 18px", fontFamily: "sans-serif",
            }}>
              Free, but unstructured
            </h3>
            <ul style={{
              listStyle: "none", padding: 0, margin: 0,
              fontFamily: "sans-serif", fontSize: 14, color: "var(--text-dim)",
              lineHeight: 1.6,
            }}>
              {[
                "You write the prompt. Quality depends on your prompt-engineering skill.",
                "No structured scoring — feedback is a paragraph, easy to forget by tomorrow.",
                "No sense of time. The conversation drifts; doesn't pace like a real 30-minute interview.",
                "Forgets you between chats. No trends, no progress tracking across sessions.",
                "You have to manually paste the job description every single time.",
                "No downloadable report to review before the real interview.",
                "Tends to be encouraging by default. Often won't push back hard enough.",
              ].map((line, i) => (
                <li key={i} style={{
                  display: "flex", gap: 10, marginBottom: 10,
                  alignItems: "flex-start",
                }}>
                  <span aria-hidden style={{
                    flexShrink: 0, marginTop: 7,
                    width: 5, height: 5, borderRadius: "50%",
                    background: "var(--text-darker)",
                  }} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Nail That Interview column — accent border to draw the eye */}
          <div style={{
            background: "linear-gradient(160deg, rgba(249,115,22,0.10), var(--card))",
            border: "1px solid rgba(249,115,22,0.40)",
            borderRadius: 18, padding: "26px 24px",
            backdropFilter: "blur(10px)",
            boxShadow: "0 14px 40px rgba(249,115,22,0.10)",
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#f97316",
              textTransform: "uppercase", letterSpacing: 1.6,
              marginBottom: 6, fontFamily: "sans-serif",
            }}>
              Nail That Interview
            </div>
            <h3 style={{
              fontSize: 19, fontWeight: 700, color: "var(--text-strong)",
              margin: "0 0 18px", fontFamily: "sans-serif",
            }}>
              Built for one job: getting you ready
            </h3>
            <ul style={{
              listStyle: "none", padding: 0, margin: 0,
              fontFamily: "sans-serif", fontSize: 14, color: "var(--text-muted)",
              lineHeight: 1.6,
            }}>
              {[
                "Interviewer persona, follow-ups, and difficulty are tuned by us — no prompt skill required.",
                "Every answer scored across 12 dimensions, with side-by-side comments you can act on.",
                "The AI knows the session length and paces itself to wrap up like a real interview.",
                "Insights dashboard tracks your scores, strengths and gaps across every session.",
                "Paste a job description (and on Power, your resume) once — questions tailor automatically.",
                "Downloadable PDF report on every tier, including Free.",
                "Designed to push back. Vague answers get probed; weak ones get called out — kindly.",
              ].map((line, i) => (
                <li key={i} style={{
                  display: "flex", gap: 10, marginBottom: 10,
                  alignItems: "flex-start",
                }}>
                  <span aria-hidden style={{
                    flexShrink: 0, marginTop: 6,
                    width: 7, height: 7, borderRadius: "50%",
                    background: "#f97316",
                    boxShadow: "0 0 0 3px rgba(249,115,22,0.18)",
                  }} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p style={{
          textAlign: "center", marginTop: 24, fontSize: 13,
          color: "var(--text-dim)", fontFamily: "sans-serif", fontStyle: "italic",
        }}>
          Free tier is genuinely free. Try it before you decide whether
          a chatbot would have been enough.
        </p>
      </section>

      {/* FAQ — accordion using native <details>/<summary> for zero-JS,
          fully accessible expand/collapse. Themed via the .nti-faq-* CSS
          rules in the page-level <style> block below. */}
      <section id="faq" style={{
        position: "relative", zIndex: 1,
        maxWidth: 820, margin: "90px auto 0", padding: "0 24px",
      }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <p style={{
            color: "#f97316", fontSize: 12, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 2.2,
            margin: "0 0 10px", fontFamily: "sans-serif",
          }}>
            Questions, answered
          </p>
          <h2 style={{
            fontSize: 30, fontWeight: 800, color: "var(--text-strong)",
            margin: "0 0 10px", letterSpacing: -0.6, fontFamily: "sans-serif",
          }}>
            Frequently asked questions
          </h2>
          <p style={{
            color: "var(--text-dim)", fontSize: 15, margin: 0,
            fontFamily: "sans-serif",
          }}>
            Tap any question to expand the answer.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            {
              q: "Is the AI feedback actually useful, or is it generic?",
              a: "Every answer is graded across 12 dimensions — clarity, structure, specificity, ownership, impact, and so on — with comments tied to the actual words you said. We run multiple evaluation passes in parallel so the feedback isn't a single AI's first impression. You'll see exactly which dimensions cost you points and what would have raised them.",
            },
            {
              q: "Can this really replace a human interview coach?",
              a: "No, and we won't pretend otherwise. A human coach who has worked at your target company will always read between the lines better than any AI. What we replace is the ten reps you can't afford to do with that coach — practice at 2 AM, the night before, in pyjamas, with a fresh interviewer every time.",
            },
            {
              q: "What kinds of interviews can I practice?",
              a: "Behavioral, situational, leadership, and role-specific interviews across product, engineering, design, sales, marketing, operations, and more. We're not built for live whiteboard coding or system-design diagrams — for those, pair us with a tool designed for that workflow.",
            },
            {
              q: "How long does a session take?",
              a: "Free sessions are 15 minutes. Starter and Pro run 30 minutes. Power runs 45 minutes — matching the length of a real interview round. The AI paces itself based on the time remaining and wraps up naturally rather than cutting off mid-question.",
            },
            {
              q: "Do I need to upload a resume or job description?",
              a: "Both are optional and you'll still get a useful session without them. If you paste a job description, questions tailor to that role. On the Power tier you can also upload a resume so questions probe your actual experience. Without either, you get well-crafted general practice for the role type you pick.",
            },
            {
              q: "What's in the feedback report?",
              a: "Twelve dimension scores, a per-dimension comment explaining the score, your full transcript, and an overall readiness score. You can download the report as a PDF on every tier — including Free — to review later or share with a mentor.",
            },
            {
              q: "What happens to my interview transcripts and resume?",
              a: "Your data is stored in your account so you can revisit past sessions, and is never used to train AI models. To delete specific sessions or your entire account, send us a note through the support form and we'll process the request within a few business days. The session replays we capture for debugging mask all text before transmission, so transcripts and resume contents aren't included in those replays. Full details are in our Privacy Policy.",
            },
            {
              q: "Why is there a Free tier — what's the catch?",
              a: "No catch. You get two 15-minute sessions per calendar month with the same AI interviewer everyone else uses, plus the PDF report and a Snapshot view of your insights. It's enough to honestly evaluate whether the product helps you. If you're prepping seriously, two short sessions a month won't be enough — that's the upgrade pressure, not crippled features.",
            },
            {
              q: "Can I cancel anytime?",
              a: "Yes. Subscriptions cancel from your account in two clicks and stay active until the end of the period you've already paid for. Passes are one-time purchases that simply expire when the period ends — nothing to cancel. Refunds are available within 7 days of purchase if you've used 2 or fewer interviews — see the Terms page for the full policy.",
            },
            {
              q: "I had a bad session — was it the AI or was it me?",
              a: "Honestly, sometimes both. AI interviewers occasionally drift or ask an awkward follow-up; that's worth flagging through the Contact link so we can investigate. But also, a hard interview that exposes weak areas is the whole point — that's data you wanted before the real one. Read the dimension scores; the lowest one is usually where the truth is.",
            },
          ].map((item, i) => (
            <details key={i} className="nti-faq-item">
              <summary className="nti-faq-summary">
                <span style={{
                  fontSize: 15, fontWeight: 600, color: "var(--text-strong)",
                  fontFamily: "sans-serif", lineHeight: 1.4,
                  flex: 1, paddingRight: 14,
                }}>
                  {item.q}
                </span>
                <span aria-hidden className="nti-faq-chevron">+</span>
              </summary>
              <div style={{
                padding: "0 22px 20px",
                color: "var(--text-muted)", fontSize: 14, lineHeight: 1.65,
                fontFamily: "sans-serif",
              }}>
                {item.a}
              </div>
            </details>
          ))}
        </div>

        <p style={{
          textAlign: "center", marginTop: 26, fontSize: 13,
          color: "var(--text-dim)", fontFamily: "sans-serif",
        }}>
          Still have a question?{" "}
          <button
            onClick={() => setSupportOpen(true)}
            style={{
              background: "none", border: "none", padding: 0,
              color: "#f97316", textDecoration: "underline", cursor: "pointer",
              font: "inherit", fontWeight: 600,
            }}
          >Send us a note</button>{" "}
          — a real human reads them.
        </p>
      </section>

      {/* Final CTA */}
      <section style={{
        position: "relative", zIndex: 1,
        maxWidth: 720, margin: "80px auto 40px", padding: "40px 30px", textAlign: "center",
        background: "linear-gradient(160deg, rgba(249,115,22,0.10), var(--card))",
        border: "1px solid rgba(249,115,22,0.30)",
        borderRadius: 22, backdropFilter: "blur(14px)",
      }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: "var(--text-strong)", margin: "0 0 8px", letterSpacing: -0.4 }}>
          Your next interview deserves a rehearsal.
        </h2>
        <p style={{ color: "var(--text-dim)", fontSize: 14, margin: "0 0 22px", fontFamily: "sans-serif" }}>
          Start a free mock session right now — no credit card needed.
        </p>
        <button
          onClick={onStart}
          style={{
            background: "linear-gradient(135deg,#f97316,#dc2626)", border: "none", color: "#fff",
            padding: "13px 26px", borderRadius: 12, fontSize: 15, fontWeight: 700,
            cursor: "pointer", fontFamily: "sans-serif",
            boxShadow: "0 10px 30px rgba(249,115,22,0.35)",
          }}
        >Start your mock interview →</button>
      </section>

      {/* Footer */}
      <footer style={{
        position: "relative", zIndex: 1,
        borderTop: "1px solid var(--border-soft)",
        padding: "26px 24px 18px", color: "var(--text-darker)", fontSize: 12, fontFamily: "sans-serif",
        maxWidth: 1180, margin: "0 auto",
      }}>
        <div style={{
          display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12,
        }}>
          <span>© {new Date().getFullYear()} Nail That Interview · AI-powered mock interviews</span>
          <span style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
            <button
              onClick={onViewPrivacy}
              style={{
                background: "none", border: "none", padding: 0,
                color: "inherit", font: "inherit", cursor: "pointer",
                textDecoration: "underline", opacity: 0.85,
              }}
            >Privacy</button>
            <button
              onClick={onViewTerms}
              style={{
                background: "none", border: "none", padding: 0,
                color: "inherit", font: "inherit", cursor: "pointer",
                textDecoration: "underline", opacity: 0.85,
              }}
            >Terms</button>
            <button
              onClick={onViewCookies}
              style={{
                background: "none", border: "none", padding: 0,
                color: "inherit", font: "inherit", cursor: "pointer",
                textDecoration: "underline", opacity: 0.85,
              }}
            >Cookies</button>
            <button
              onClick={onViewDisclaimer}
              style={{
                background: "none", border: "none", padding: 0,
                color: "inherit", font: "inherit", cursor: "pointer",
                textDecoration: "underline", opacity: 0.85,
              }}
            >Disclaimer</button>
            <button
              onClick={() => setSupportOpen(true)}
              style={{
                background: "none", border: "none", padding: 0,
                color: "inherit", font: "inherit", cursor: "pointer",
                textDecoration: "underline", opacity: 0.85,
              }}
            >Contact</button>
          </span>
        </div>
        <div style={{
          marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-soft)",
          fontSize: 11, color: "var(--text-darker)", opacity: 0.75, lineHeight: 1.55,
          textAlign: "center", fontStyle: "italic",
        }}>
          Nail That Interview is an AI practice tool. We don't guarantee job offers or specific career outcomes — your results depend on your effort and many factors outside our control.
        </div>
      </footer>

      <style>{`
        /* FAQ accordion — themed via CSS vars so light/dark just works */
        .nti-faq-item {
          background: var(--card);
          border: 1px solid var(--border-soft);
          border-radius: 14px;
          backdrop-filter: blur(10px);
          overflow: hidden;
          transition: border-color 0.18s ease, box-shadow 0.18s ease;
        }
        .nti-faq-item[open] {
          border-color: rgba(249,115,22,0.40);
          box-shadow: 0 10px 30px rgba(249,115,22,0.08);
        }
        .nti-faq-summary {
          list-style: none;
          cursor: pointer;
          padding: 18px 22px;
          display: flex;
          align-items: center;
          gap: 12px;
          user-select: none;
        }
        .nti-faq-summary::-webkit-details-marker { display: none; }
        .nti-faq-summary:focus-visible {
          outline: 2px solid #f97316;
          outline-offset: -2px;
          border-radius: 14px;
        }
        .nti-faq-chevron {
          flex-shrink: 0;
          width: 26px; height: 26px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 20px; font-weight: 400; line-height: 1;
          color: #f97316;
          border: 1px solid rgba(249,115,22,0.35);
          border-radius: 50%;
          transition: transform 0.22s ease, background 0.18s ease;
        }
        .nti-faq-item[open] .nti-faq-chevron {
          transform: rotate(45deg);
          background: rgba(249,115,22,0.12);
        }
        @media (min-width: 880px) {
          .nti-demo-row,
          .nti-demo-row-reverse { grid-template-columns: 1fr 1fr !important; gap: 56px !important; }
          .nti-demo-row-reverse > *:first-child { order: 2; }
          .nti-video-row { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
        }
        @keyframes nti-bubble-in {
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes nti-fade-in {
          to { opacity: 1; }
        }
        @keyframes nti-fade-out {
          to { opacity: 0; }
        }
        @keyframes nti-wave {
          from { height: 6px; }
          to { height: 20px; }
        }
        @keyframes nti-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes nti-pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.35); }
        }
        @keyframes nti-bar-fill {
          to { transform: scaleX(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="nti-bubble-in"],
          [style*="nti-fade-in"],
          [style*="nti-fade-out"],
          [style*="nti-wave"],
          [style*="nti-bounce"],
          [style*="nti-pulse"],
          [style*="nti-bar-fill"] {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>
      <SupportModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        user={user}
        defaultCategory={supportPrefill.category || ""}
        defaultSubject={supportPrefill.subject || ""}
        defaultMessage={supportPrefill.message || ""}
      />
    </div>
  );
};

// ─── Support / Contact modal ─────────────────────────────────────────────────
// Used by the landing-page footer "Contact" link, the Terms page, and the
// cockpit header. Posts to /api/support which fans out to the team inbox via
// Resend and sends an auto-acknowledgment to the user. Falls back to a
// mailto: link if the request fails so users are never stranded.
// Support categories. Keep in sync with the allow-list in api/support.js.
// The order here is the order shown in the dropdown — most-frequent /
// most-actionable buckets first, "Other" always last.
const SUPPORT_CATEGORIES = [
  { value: "discount", label: "Discount / promo code request" },
  { value: "refund",   label: "Refund request" },
  { value: "billing",  label: "Billing or subscription issue" },
  { value: "bug",      label: "Something's broken / bug report" },
  { value: "account",  label: "Account or login help" },
  { value: "feature",  label: "Feature request or suggestion" },
  { value: "feedback", label: "General feedback" },
  { value: "other",    label: "Something else" },
];

const SupportModal = ({
  open,
  onClose,
  user,
  // Optional prefill — used by entry points that already know the
  // category/subject/message (e.g. the "Request refund" button on the
  // pass-holder cockpit). Empty defaults preserve the original blank-form
  // behaviour for everyone else.
  defaultCategory = "",
  defaultSubject = "",
  defaultMessage = "",
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  // Turnstile token. The widget produces a fresh token on mount/render and
  // again any time we explicitly reset it (after submit or error). Tokens
  // are single-use and expire ~5 min after issue, so we always re-prompt
  // before the next submission.
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  // When the user clicks "Send" before the Turnstile widget has produced
  // a token (common with `appearance: interaction-only` because the
  // challenge is invisible until needed), we don't error — we set this
  // flag and a useEffect below auto-fires the actual submit the instant
  // the token arrives. Avoids the previous "Please wait a moment for the
  // verification challenge to finish" dead-end.
  const [pendingSubmit, setPendingSubmit] = useState(false);
  // Captures any error code Cloudflare Turnstile fires via onError
  // (e.g. domain not whitelisted, network failure, expired challenge).
  // Surfaced to the user immediately so they don't sit through the
  // 20s safety timeout waiting for a token that will never arrive.
  const [turnstileError, setTurnstileError] = useState("");
  // Ref-wrapped submit body so the auto-fire useEffect can call the
  // latest closure (with the freshly-arrived token) without listing
  // doSubmit in its deps and re-running every render.
  const doSubmitRef = useRef(null);

  // Pre-fill identity fields whenever the modal opens for a signed-in user.
  // Also clear any stale Turnstile token from a previous open — tokens
  // expire ~5 min after issue and are single-use, so re-using one across
  // open/close cycles risks a 400 from the server. The widget re-issues
  // a fresh token on remount.
  useEffect(() => {
    if (!open) return;
    setStatus("idle");
    setErrorMsg("");
    setTurnstileToken("");
    setPendingSubmit(false);
    setTurnstileError("");
    turnstileRef.current?.reset();
    const displayName =
      user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      (user?.email ? user.email.split("@")[0] : "");
    setName((prev) => prev || displayName || "");
    setEmail((prev) => prev || user?.email || "");
    // Apply prefill on every open — unconditionally, including when the
    // defaults are empty strings. This is the explicit reset path used
    // when a caller (e.g. the cockpit nav 'Support' button) wants a
    // blank form after a previous open had populated it (e.g. via the
    // pass-holder 'Request refund' button). The user can still edit
    // anything before sending.
    setCategory(defaultCategory || "");
    setSubject(defaultSubject || "");
    setMessage(defaultMessage || "");
  }, [open, user, defaultCategory, defaultSubject, defaultMessage]);

  // Auto-fire the queued submit once Turnstile delivers a token, with a
  // 12s safety timeout so a broken/blocked widget doesn't hang the
  // button forever. Gated on `open` so a queued submit cannot fire (and
  // the timeout cannot surface a hidden "verification didn't complete"
  // error) after the user has already closed the modal — the cleanup
  // will clear any pending timeout when `open` flips false. The prefill
  // effect resets pendingSubmit/status the next time the modal opens.
  // MUST live above the `if (!open) return null` early return below —
  // otherwise the hook count differs between closed and open renders
  // and React tears the whole tree down (caught by the top-level Sentry
  // error boundary as a black "Something went wrong" page).
  useEffect(() => {
    if (!open || !pendingSubmit) return;
    if (turnstileToken) {
      setPendingSubmit(false);
      doSubmitRef.current?.();
      return;
    }
    // If the widget reported an error before the token arrived, abort
    // the wait immediately rather than burning the safety timeout.
    if (turnstileError) {
      setPendingSubmit(false);
      setStatus("error");
      setErrorMsg(
        `Verification couldn't load (code: ${turnstileError}). Please refresh and try again, or email support directly.`
      );
      return;
    }
    const t = setTimeout(() => {
      setPendingSubmit(false);
      setStatus("error");
      setErrorMsg("Verification didn't complete. Please try again, or email support directly.");
    }, 20000);
    return () => clearTimeout(t);
  }, [open, pendingSubmit, turnstileToken, turnstileError]);

  if (!open) return null;

  // The actual network call. Kept on a ref so the auto-fire useEffect
  // below can invoke the latest closure (with the just-arrived token)
  // without having to list it as a dep and re-run on every render.
  doSubmitRef.current = async () => {
    setStatus("sending");
    setErrorMsg("");
    try {
      const apiBase = process.env.REACT_APP_API_URL || "";
      const r = await fetch(`${apiBase}/api/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, email, category, subject, message,
          userId: user?.id || null,
          turnstileToken,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setStatus("error");
        setErrorMsg(data?.error || "Something went wrong. Please try again.");
        // The token we sent is now spent (or rejected) — force a fresh one
        // so the next submission attempt has a valid challenge ready.
        turnstileRef.current?.reset();
        setTurnstileToken("");
        return;
      }
      setStatus("sent");
      setCategory("");
      setSubject("");
      setMessage("");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } catch (err) {
      console.error("[support] submit failed", err);
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    }
  };

  // Click handler. If Turnstile hasn't issued a token yet, queue the
  // submit and let the auto-fire useEffect run it the moment the token
  // arrives — much better UX than the previous error-and-bail flow.
  const submit = (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setErrorMsg("");
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      // Widget is in a known-bad state — don't bother queueing.
      if (turnstileError) {
        setStatus("error");
        setErrorMsg(
          `Verification couldn't load (code: ${turnstileError}). Please refresh and try again, or email support directly.`
        );
        return;
      }
      setStatus("sending");
      setPendingSubmit(true);
      return;
    }
    doSubmitRef.current?.();
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    background: "var(--surface)", color: "var(--text)",
    border: "1px solid var(--border-soft)", borderRadius: 10,
    padding: "10px 12px", fontSize: 14, fontFamily: "sans-serif",
    outline: "none",
  };
  const labelStyle = {
    display: "block", fontSize: 12, fontWeight: 600,
    color: "var(--text-muted)", marginBottom: 6, fontFamily: "sans-serif",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-modal-title"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg)", color: "var(--text)",
          border: "1px solid var(--border-soft)", borderRadius: 16,
          width: "100%", maxWidth: 520, padding: 24,
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          fontFamily: "sans-serif",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 id="support-modal-title" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "var(--text-strong)" }}>
            Contact support
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent", border: "none", color: "var(--text-muted)",
              fontSize: 22, cursor: "pointer", padding: 4, lineHeight: 1,
            }}
          >×</button>
        </div>
        <p style={{ margin: "0 0 18px", fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.55 }}>
          Questions, refund requests, or anything else — drop us a note and we'll get back to you within 1 business day.
        </p>

        {status === "sent" ? (
          <div style={{ padding: 18, borderRadius: 12, background: "var(--surface-2)", border: "1px solid var(--border-soft)" }}>
            <p style={{ margin: "0 0 6px", fontWeight: 700, color: "var(--text-strong)" }}>
              Message sent.
            </p>
            <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.55 }}>
              Check your inbox for a confirmation. We typically reply within 1 business day.
            </p>
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={onClose}
                style={{
                  background: "#f97316", color: "#fff", border: "none",
                  padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                  cursor: "pointer",
                }}
              >Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <label style={labelStyle} htmlFor="support-name">Your name</label>
                <input
                  id="support-name" type="text" value={name}
                  onChange={(e) => setName(e.target.value)}
                  required maxLength={120} style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="support-email">Email</label>
                <input
                  id="support-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="support-category">Topic</label>
                <select
                  id="support-category" value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                  style={{
                    ...inputStyle,
                    appearance: "none",
                    cursor: "pointer",
                    // Inline SVG caret so the dropdown stays themed without a global CSS change.
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%2394a3b8' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M1 1.5l5 5 5-5'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    paddingRight: 32,
                  }}
                >
                  <option value="" disabled>Select a topic…</option>
                  {SUPPORT_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle} htmlFor="support-subject">Subject</label>
                <input
                  id="support-subject" type="text" value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required minLength={2} maxLength={200}
                  placeholder="Briefly describe the issue"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="support-message">Message</label>
                <textarea
                  id="support-message" value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required minLength={10} maxLength={8000} rows={6}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 120, fontFamily: "inherit" }}
                />
              </div>
            </div>

            {TURNSTILE_SITE_KEY && (
              <div style={{ marginTop: 14 }}>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => {
                    setTurnstileToken(token);
                    setTurnstileError("");
                  }}
                  onError={(code) => {
                    // Most common codes: 110200 (domain not allowed),
                    // 300010/300020 (challenge failed), 600010 (network).
                    // Surfaced to the user so they can act on it instead
                    // of silently sitting through the safety timeout.
                    console.warn("[turnstile] error", code);
                    setTurnstileToken("");
                    setTurnstileError(String(code || "unknown"));
                  }}
                  onExpire={() => setTurnstileToken("")}
                  options={{
                    theme: "dark",
                    size: "flexible",
                    appearance: "interaction-only",
                  }}
                />
              </div>
            )}

            {status === "error" && (
              <p style={{ marginTop: 12, marginBottom: 0, fontSize: 13, color: "#ef4444" }}>
                {errorMsg}{" "}
                <a
                  href={`mailto:support@nailthatinterview.app?subject=${encodeURIComponent(subject || "Support request")}`}
                  style={{ color: "#ef4444", textDecoration: "underline" }}
                >Email directly instead</a>.
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18, gap: 12, flexWrap: "wrap" }}>
              <a
                href="mailto:support@nailthatinterview.app"
                style={{ fontSize: 12.5, color: "var(--text-dim)", textDecoration: "underline" }}
              >Or email support@nailthatinterview.app</a>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button" onClick={onClose}
                  style={{
                    background: "transparent", color: "var(--text-muted)",
                    border: "1px solid var(--border-soft)", padding: "9px 16px",
                    borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}
                >Cancel</button>
                <button
                  type="submit" disabled={status === "sending"}
                  style={{
                    background: "#f97316", color: "#fff", border: "none",
                    padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                    cursor: status === "sending" ? "wait" : "pointer",
                    opacity: status === "sending" ? 0.7 : 1,
                  }}
                >{status === "sending" ? (pendingSubmit ? "Verifying…" : "Sending…") : "Send message"}</button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Terms & Conditions page ─────────────────────────────────────────────────
// Public legal page with the refund policy as its centerpiece. Reachable from
// the landing-page footer and the small refund note above the pricing cards.
// Refunds are processed manually via the Lemon Squeezy dashboard after a user
// emails support@nailthatinterview.app — no in-app refund button (avoids the
// fraud surface of self-serve refunds and keeps Lemon as the single source of
// truth for billing state).
const TermsPage = ({ theme, toggleTheme, onBack, user }) => {
  const [supportOpen, setSupportOpen] = useState(false);
  const sectionStyle = { marginTop: 28 };
  const h2Style = {
    fontSize: 18, fontWeight: 800, color: "var(--text-strong)",
    margin: "0 0 8px", letterSpacing: -0.2,
  };
  const pStyle = {
    fontSize: 14.5, lineHeight: 1.65, color: "var(--text-muted)",
    margin: "0 0 10px", fontFamily: "sans-serif",
  };
  const liStyle = {
    fontSize: 14.5, lineHeight: 1.65, color: "var(--text-muted)",
    fontFamily: "sans-serif", marginBottom: 6,
  };
  const mailHref = "mailto:support@nailthatinterview.app";
  const lastUpdated = "May 3, 2026";
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)", position: "relative" }}>
      <GlobalStyles />

      {/* Top bar: brand + back + theme toggle */}
      <header style={{
        position: "relative", zIndex: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 28px", maxWidth: 980, margin: "0 auto",
        gap: 12, flexWrap: "wrap",
      }}>
        <Wordmark logoSize={56} fontSize={19} gap={6} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={onBack} style={{
            background: "transparent", border: "1px solid var(--border-soft)",
            color: "var(--text-muted)", padding: "8px 14px", borderRadius: 10,
            fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "sans-serif",
          }}>← Back</button>
          <button
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-label="Toggle theme"
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "var(--surface-2)", border: "1px solid var(--border-soft)",
              color: "var(--text-muted)", fontSize: 15, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, lineHeight: 1,
            }}
          >{theme === "dark" ? "☀️" : "🌙"}</button>
        </div>
      </header>

      <main style={{
        maxWidth: 760, margin: "0 auto", padding: "20px 28px 80px",
        fontFamily: "'Lora', serif",
      }}>
        <h1 style={{
          fontSize: 32, fontWeight: 800, letterSpacing: -0.6,
          color: "var(--text-strong)", margin: "8px 0 6px",
        }}>Terms & Conditions</h1>
        <p style={{ color: "var(--text-dimmer)", fontSize: 13, margin: "0 0 4px", fontFamily: "sans-serif" }}>
          Last updated: {lastUpdated}
        </p>

        <section style={sectionStyle}>
          <p style={pStyle}>
            Welcome to Nail That Interview ("we", "us", "our"). By creating an account or
            using our service at nailthatinterview.app, you agree to these Terms.
            If you do not agree, please do not use the service.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>1. What we provide</h2>
          <p style={pStyle}>
            Nail That Interview is an AI-powered mock interview practice tool. You can
            run simulated interviews, receive structured feedback, and track your
            progress over time. The service is for practice only — we do not guarantee
            job offers, interview outcomes, or any specific career result.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>2. Eligibility & accounts</h2>
          <p style={pStyle}>
            You must be at least 16 years old to use the service. You are responsible
            for the security of your account credentials and for any activity under
            your account. Please do not share your account.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>3. Payments & billing</h2>
          <p style={pStyle}>
            Paid plans are billed in U.S. dollars by Lemon Squeezy, which acts as our
            merchant of record. By subscribing, you authorize Lemon Squeezy to charge
            your payment method on a recurring basis (monthly) until you cancel.
            Applicable taxes are handled by Lemon Squeezy and shown at checkout.
          </p>
        </section>

        {/* Refund policy — the section users will read most carefully. */}
        <section style={{ ...sectionStyle, padding: 20, borderRadius: 14, background: "var(--surface-2)", border: "1px solid var(--border-soft)" }}>
          <h2 style={{ ...h2Style, fontSize: 20, color: "var(--text-strong)" }}>4. Refund policy</h2>
          <p style={pStyle}>
            We want you to feel confident trying a paid plan. We offer refunds under
            the following clear, simple terms:
          </p>
          <ul style={{ margin: "0 0 10px 22px", padding: 0 }}>
            <li style={liStyle}>
              <strong>7-day refund window.</strong> You may request a full refund
              within 7 calendar days of your first paid charge.
            </li>
            <li style={liStyle}>
              <strong>2-interview limit.</strong> You must have used 2 or fewer
              interviews on the paid plan during that period. Any interview that
              decremented your quota counts, whether or not you finished it.
            </li>
            <li style={liStyle}>
              <strong>How to request.</strong> Use the{" "}
              <button
                onClick={() => setSupportOpen(true)}
                style={{
                  background: "none", border: "none", padding: 0,
                  color: "#f97316", textDecoration: "underline", cursor: "pointer",
                  font: "inherit",
                }}
              >in-app contact form</button>{" "}
              (or email{" "}
              <a href={mailHref} style={{ color: "#f97316", textDecoration: "underline" }}>
                support@nailthatinterview.app
              </a>) from the address tied to your account, with the subject
              "Refund request". We will confirm and process eligible refunds
              within 5 business days. The refund is issued to the original
              payment method by Lemon Squeezy.
            </li>
            <li style={liStyle}>
              <strong>After 7 days, or after 3 or more interviews used,</strong>{" "}
              we are unable to issue a refund — you can still cancel your
              subscription at any time to stop future renewals (see Section 5).
            </li>
            <li style={liStyle}>
              <strong>Renewal charges</strong> (the second month onward) are not
              refundable. To avoid being billed for the next period, cancel before
              your renewal date.
            </li>
          </ul>
          <p style={{ ...pStyle, marginTop: 8, fontSize: 13.5, color: "var(--text-dim)" }}>
            Refunds are issued at our discretion in accordance with this policy.
            Repeated refund requests, fraudulent activity, or terms-of-service
            violations may forfeit eligibility.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>5. Cancellation</h2>
          <p style={pStyle}>
            You can cancel your subscription at any time from the customer portal
            link in any Lemon Squeezy receipt email, or by contacting us. Cancelling
            stops future renewals; it does not on its own trigger a refund of past
            charges (refund eligibility is governed by Section 4). You retain access
            to the paid features until the end of the current billing period.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>6. Acceptable use</h2>
          <p style={pStyle}>
            You agree not to misuse the service: no scraping or reverse-engineering
            the AI components, no attempts to exceed plan quotas through technical
            means, no abusive or unlawful content in your inputs, and no use that
            violates the rights of others. We may suspend or terminate accounts that
            breach these terms.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>7. Your content</h2>
          <p style={pStyle}>
            You retain ownership of resumes, answers, and any content you submit.
            You grant us a limited license to process that content solely to provide
            the service to you (running interviews, generating feedback, storing your
            session history). We do not sell your content, and we do not use it to
            train third-party models without your explicit consent.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>8. Disclaimers</h2>
          <p style={pStyle}>
            The service is provided "as is" without warranties of any kind. AI
            outputs may contain errors or be inappropriate for a given situation —
            you are responsible for evaluating their applicability. Practice
            performance is not a predictor of real interview outcomes.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>9. Limitation of liability</h2>
          <p style={pStyle}>
            To the maximum extent permitted by law, our total liability to you for
            any claim arising out of or relating to the service is limited to the
            amount you paid us in the 12 months preceding the claim. We are not
            liable for indirect, incidental, or consequential damages, including
            lost opportunities or lost income.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>10. Changes to these terms</h2>
          <p style={pStyle}>
            We may update these terms from time to time. Material changes will be
            announced via email or an in-app notice with reasonable notice before
            taking effect. Continued use of the service after the effective date
            constitutes acceptance of the updated terms.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>11. Contact</h2>
          <p style={pStyle}>
            Questions, refund requests, and support inquiries —{" "}
            <button
              onClick={() => setSupportOpen(true)}
              style={{
                background: "none", border: "none", padding: 0,
                color: "#f97316", textDecoration: "underline", cursor: "pointer",
                font: "inherit",
              }}
            >open the contact form</button>{" "}
            or email{" "}
            <a href={mailHref} style={{ color: "#f97316", textDecoration: "underline" }}>
              support@nailthatinterview.app
            </a>.
          </p>
        </section>
      </main>
      <SupportModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        user={user}
      />
    </div>
  );
};

// ─── Generic policy page ─────────────────────────────────────────────────────
// Renders Privacy, Cookie, and Disclaimer policies inline on our own domain.
// The HTML for each policy is committed to the repo at
// public/policies/<slug>.html — first authored in Termly, then pasted in.
// This means runtime has zero dependency on Termly being up, and we can drop
// the Termly subscription after launch without breaking these pages. To
// refresh a policy, edit it in Termly, copy the rendered HTML, and overwrite
// the corresponding file. The page reuses the same header / chrome as
// <TermsPage/> so the look stays consistent across all four legal surfaces.
const PolicyPage = ({ slug, title, theme, toggleTheme, onBack }) => {
  const [state, setState] = useState({ status: "loading", html: "", error: "" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", html: "", error: "" });
    fetch(`/policies/${encodeURIComponent(slug)}.html`, { cache: "no-cache" })
      .then(async (r) => {
        if (cancelled) return;
        if (!r.ok) {
          setState({ status: "error", html: "", error: "This policy is not yet available. Please check back shortly." });
          return;
        }
        const text = await r.text();
        if (cancelled) return;
        // Guard against the dev server falling back to index.html (SPA shell)
        // when the static file is missing — we'd otherwise inject our own app
        // markup into the policy slot.
        if (/<!doctype html>/i.test(text) || /<div id="root">/i.test(text)) {
          setState({ status: "error", html: "", error: "This policy is not yet available. Please check back shortly." });
          return;
        }
        setState({ status: "ready", html: text, error: "" });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[policy] fetch failed", err);
        setState({ status: "error", html: "", error: "Could not load this policy. Please check your connection and try again." });
      });
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)", position: "relative" }}>
      <GlobalStyles />
      <header style={{
        position: "relative", zIndex: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 28px", maxWidth: 980, margin: "0 auto",
        gap: 12, flexWrap: "wrap",
      }}>
        <Wordmark logoSize={56} fontSize={19} gap={6} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={onBack} style={{
            background: "transparent", border: "1px solid var(--border-soft)",
            color: "var(--text-muted)", padding: "8px 14px", borderRadius: 10,
            fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "sans-serif",
          }}>← Back</button>
          <button
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-label="Toggle theme"
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "var(--surface-2)", border: "1px solid var(--border-soft)",
              color: "var(--text-muted)", fontSize: 15, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, lineHeight: 1,
            }}
          >{theme === "dark" ? "☀️" : "🌙"}</button>
        </div>
      </header>

      <main style={{
        maxWidth: 760, margin: "0 auto", padding: "20px 28px 80px",
        fontFamily: "'Lora', serif",
      }}>
        <h1 style={{
          fontSize: 32, fontWeight: 800, letterSpacing: -0.6,
          color: "var(--text-strong)", margin: "8px 0 18px",
        }}>{title}</h1>

        {state.status === "loading" && (
          <p style={{ color: "var(--text-muted)", fontSize: 14, fontFamily: "sans-serif" }}>
            Loading policy…
          </p>
        )}
        {state.status === "error" && (
          <div style={{
            padding: 16, borderRadius: 12,
            border: "1px solid rgba(248,113,113,0.4)",
            background: "rgba(248,113,113,0.10)",
            color: "#fca5a5", fontSize: 14, fontFamily: "sans-serif",
          }}>
            {state.error || "Could not load this policy."}
          </div>
        )}
        {state.status === "ready" && (
          <div
            className="nti-policy-content"
            style={{
              color: "var(--text-muted)", fontSize: 14.5, lineHeight: 1.65,
              fontFamily: "sans-serif",
            }}
            dangerouslySetInnerHTML={{ __html: state.html }}
          />
        )}
      </main>

      {/* Light theming for the injected policy markup so it matches our
          own pages instead of Termly's default off-white viewer skin. */}
      <style>{`
        .nti-policy-content h1,
        .nti-policy-content h2,
        .nti-policy-content h3,
        .nti-policy-content h4 {
          color: var(--text-strong);
          font-family: 'Lora', serif;
          letter-spacing: -0.2px;
          margin: 26px 0 10px;
          line-height: 1.3;
        }
        .nti-policy-content h1 { font-size: 26px; }
        .nti-policy-content h2 { font-size: 20px; }
        .nti-policy-content h3 { font-size: 17px; }
        .nti-policy-content h4 { font-size: 15px; }
        .nti-policy-content p,
        .nti-policy-content li {
          color: var(--text-muted);
          font-size: 14.5px;
          line-height: 1.65;
          margin: 0 0 10px;
        }
        .nti-policy-content ul,
        .nti-policy-content ol { margin: 0 0 14px 22px; padding: 0; }
        .nti-policy-content li { margin-bottom: 6px; }
        .nti-policy-content a {
          color: #f97316;
          text-decoration: underline;
        }
        .nti-policy-content strong { color: var(--text-strong); }
        .nti-policy-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 14px 0;
          font-size: 13.5px;
        }
        .nti-policy-content th,
        .nti-policy-content td {
          border: 1px solid var(--border-soft);
          padding: 8px 10px;
          text-align: left;
          vertical-align: top;
        }
        .nti-policy-content th { background: var(--surface-2); color: var(--text-strong); }
      `}</style>
    </div>
  );
};

// ─── Limit-reached modal ──────────────────────────────────────────────────────

const LimitReachedModal = ({ type, usage, onClose, onSeePlans }) => {
  const titleMap = {
    free: "You've used your free interviews this month",
    monthly: "You've hit your monthly limit",
    plan: "Your plan isn't active",
  };
  const bodyMap = {
    free: "The Free plan includes 2 interviews per month, resetting on the 1st of each month. Upgrade to Starter, Pro, or Power for more — see the plans below.",
    monthly: `You've used all ${usage?.monthly_interviews || 0} interviews on your ${usage?.display_name || ''} plan this month. They reset at the start of your next billing cycle.`,
    plan: "Your current plan is unavailable. Please contact support so we can sort this out.",
  };
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(7,12,24,0.7)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, fontFamily: "'Lora', serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 460,
          background: "var(--card)", border: "1px solid var(--border-soft)",
          borderRadius: 18, padding: 28, position: "relative",
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        }}
      >
        <div style={{ fontSize: 30, marginBottom: 8 }}>🚦</div>
        <h2 style={{ margin: 0, color: "var(--text-strong)", fontSize: 20, fontWeight: 700 }}>
          {titleMap[type] || "Interview limit reached"}
        </h2>
        <p style={{ marginTop: 12, color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55, fontFamily: "sans-serif" }}>
          {bodyMap[type] || ""}
        </p>
        <div style={{ marginTop: 22 }}>
          <button
            onClick={() => { onSeePlans?.(); onClose(); }}
            style={{
              width: "100%", padding: "11px 14px", borderRadius: 10,
              background: "linear-gradient(135deg,#f97316,#dc2626)",
              border: "none", color: "#fff", fontSize: 13, fontWeight: 700,
              cursor: "pointer", fontFamily: "sans-serif",
              boxShadow: "0 6px 18px rgba(249,115,22,0.35)",
            }}
          >See plans</button>
        </div>
      </div>
    </div>
  );
};

// Small helper: format mm:ss for the in-interview countdown.
const fmtMMSS = (totalSeconds) => {
  if (totalSeconds == null || !isFinite(totalSeconds)) return "";
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function NailThatInterview() {
  // Auth state (Supabase)
  const [user, setUser] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [authView, setAuthView] = useState(null) // null | 'login' | 'signup'

  // Tier / usage state (loaded once user is signed in)
  const [usage, setUsage] = useState(null)
  const [usageLoading, setUsageLoading] = useState(false)
  const [usageError, setUsageError] = useState(null)

  // The resume-upload step (step 2) is gated to the Power plan only. Non-Power
  // users skip straight from tone (step 1) to the interview, so the progress
  // dots should reflect the actual number of steps they'll see. Declared here
  // (after the `usage` useState) to avoid a TDZ — production builds turn TDZ
  // refs into a hard runtime error. See gotcha in replit.md.
  //
  // `usageReady` distinguishes "we haven't loaded the plan yet" from "loaded
  // and the user is on Free". Used to disable the wizard's Next button on
  // step 1 so a Power user can't accidentally skip the resume upload by
  // clicking before their plan resolves.
  const usageReady = !!usage || !user;
  const planCodeForFlow = String(usage?.plan_code || 'free').toLowerCase();
  // Resume upload is the premium "quality" lever — Power sub and Power
  // Pass only. Server-side: plan_rules.features.resume_upload = true on
  // both `power` and `power_pass` rows.
  const canUploadResume = planCodeForFlow === 'power' || planCodeForFlow === 'power_pass';
  const TOTAL_STEPS = canUploadResume ? 3 : 2;
  const [currentSessionId, setCurrentSessionId] = useState(null)
  const [sessionStartedAt, setSessionStartedAt] = useState(null)
  const [secondsRemaining, setSecondsRemaining] = useState(null)
  // Mirror of secondsRemaining in a ref so submitAnswer can read the live
  // value when constructing the per-turn time-pacing instruction without
  // adding the state to its dependency closure.
  const secondsRemainingRef = useRef(null)
  useEffect(() => { secondsRemainingRef.current = secondsRemaining }, [secondsRemaining])
  const [limitModal, setLimitModal] = useState(null) // null | 'free' | 'monthly' | 'plan'
  const pendingStartRef = useRef(false) // user clicked "Start" while signed-out
  // The "Subscribe" pending intent is now persisted in localStorage instead
  // of a useRef so it survives the signup → email-confirmation new-tab
  // round-trip. See writePendingSubscribeIntent / consumePendingSubscribeIntent.
  // Single-flight guards — prevent double-clicks / timer races from causing
  // duplicate start_interview / end_interview RPC calls.
  const startingRef = useRef(false)
  const endingRef = useRef(false)
  // Live refs so abandonment helpers always see the latest IDs even when
  // called from stale event handlers (e.g. the Home button).
  const currentSessionIdRef = useRef(null)
  const sessionStartedAtRef = useRef(null)
  useEffect(() => { currentSessionIdRef.current = currentSessionId }, [currentSessionId])
  useEffect(() => { sessionStartedAtRef.current = sessionStartedAt }, [sessionStartedAt])

  // Track the current phase and the last-seen user id in refs so the auth
  // listener (registered once on mount) can read live values without being
  // re-created. We use these to distinguish a *real* login transition from a
  // background re-emit of SIGNED_IN that supabase fires when the tab regains
  // focus — the latter must NOT yank the user out of whatever screen they're
  // on (e.g. mid-way through the setup wizard).
  // NOTE: we hardcode the initial value to "landing" rather than reading the
  // `phase` state directly, because `phase` is declared lower in this
  // component and reading it here would be a temporal dead zone reference.
  // The mirror-effect below keeps the ref in sync from the first commit.
  const phaseRef = useRef("landing")
  const lastUserIdRef = useRef(null)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setAuthLoading(false)
      lastUserIdRef.current = session?.user?.id ?? null
      // If a user is already signed in on first load (refresh), drop them
      // on the cockpit instead of the marketing landing page.
      if (session?.user) setPhase("cockpit")
    })
    // Listen for auth changes — single source of truth for login + logout.
    // We ONLY react to SIGNED_IN / SIGNED_OUT (and the initial replay) so
    // background TOKEN_REFRESHED / USER_UPDATED events don't yank the user
    // out of whatever screen they're currently on.
    const { data: { subscription } } =
      supabase.auth.onAuthStateChange((event, session) => {
        setUser(session?.user ?? null)
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          if (!session?.user) return
          const newUserId = session.user.id
          // Detect a *real* login transition vs supabase rehydrating the same
          // session on tab focus. Only the former should ever change phase.
          const isActualLogin = lastUserIdRef.current !== newUserId
          lastUserIdRef.current = newUserId
          if (!isActualLogin) {
            // Same user as before → this is a refocus / rehydrate. Leave the
            // user on whatever screen they were on; no UI side-effects.
            return
          }
          identify(newUserId)
          if (event === 'SIGNED_IN') {
            track('user_signed_in')
          }
          // Auto-close the auth screen after successful login/signup
          setAuthView(null)
          // If they clicked "Subscribe" before signing in, send them straight
          // to Lemon checkout. If they clicked "Start", take them into the
          // setup wizard. Otherwise: only override the current phase if they
          // were on the public landing page. Anyone already mid-flow stays
          // put (defensive guard against any future rehydrate-as-SIGNED_IN
          // edge case slipping past the userId check above).
          const pendingSubscribe = consumePendingSubscribeIntent()
          if (pendingSubscribe) {
            // Route through attemptSubscribe so a paid user who got signed
            // out, clicked Subscribe on the landing page, then signed back
            // in still hits the double-subscribe guard. attemptSubscribe
            // is async (it may fetch usage) so kick it off and let it
            // handle the redirect itself; meanwhile drop them on the
            // cockpit so they're not staring at the auth screen.
            setPhase("cockpit")
            attemptSubscribe(pendingSubscribe.planCode, session.user, {
              discountCode: pendingSubscribe.discountCode,
            })
            return
          } else if (pendingStartRef.current) {
            pendingStartRef.current = false
            setStep(0)
            setPhase("setup")
          } else if (phaseRef.current === "landing" || !phaseRef.current) {
            setPhase("cockpit")
          }
        } else if (event === 'SIGNED_OUT') {
          lastUserIdRef.current = null
          resetUser()
          // On sign-out (manual or session expiry) return to landing and
          // clear any pending intents so the next sign-in goes to the
          // cockpit cleanly.
          pendingStartRef.current = false
          clearPendingSubscribeIntent()
          setAuthView(null)
          setPhase("landing")
          setStep(0)
          setUsage(null)
          setCurrentSessionId(null)
          setSessionStartedAt(null)
        }
        // For TOKEN_REFRESHED / USER_UPDATED / PASSWORD_RECOVERY etc. we
        // intentionally do nothing — keep the user where they are.
      })
    return () => subscription.unsubscribe()
  }, [])

  // Load tier + usage info whenever the user changes (login / refresh).
  useEffect(() => {
    if (!user) { setUsage(null); setUsageError(null); return }
    let cancelled = false
    setUsageLoading(true)
    setUsageError(null)
    loadUsageInfo()
      .then((u) => {
        if (cancelled) return
        console.log('[usage] initial load:', u)
        setUsage(u)
        if (!u) setUsageError('get_usage_info returned null/empty')
      })
      .catch((e) => {
        console.error('[usage] FAILED to load:', e)
        if (!cancelled) setUsageError(e?.message || String(e))
      })
      .finally(() => { if (!cancelled) setUsageLoading(false) })
    return () => { cancelled = true }
  }, [user])

  // Setup state
  const [step, setStep] = useState(0);

  // If the plan changes mid-flow (downgrade, refresh, etc.) and the current
  // step no longer exists for this user (e.g. they were on the resume step
  // as a Power user and just got downgraded), pull them back to the last
  // valid step so they don't see an empty card.
  useEffect(() => {
    if (step >= TOTAL_STEPS) setStep(TOTAL_STEPS - 1);
  }, [TOTAL_STEPS, step]);
  const [apiKey] = useState(""); // key lives on server now — not needed client-side
  const [role, setRole] = useState("");
  const [experience, setExperience] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [tone, setTone] = useState("professional");
  const [interviewer, setInterviewer] = useState("alex");
  const [skills, setSkills] = useState([]);
  const [pendingAnswer, setPendingAnswer] = useState("");
  const [micDebug, setMicDebug] = useState("");
  const [reviewBeforeSend, setReviewBeforeSend] = useState(() => {
    try {
      const v = localStorage.getItem("nti-review-before-send");
      return v === null ? true : v === "true";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("nti-review-before-send", String(reviewBeforeSend)); } catch {}
  }, [reviewBeforeSend]);
  const reviewBeforeSendRef = useRef(reviewBeforeSend);
  useEffect(() => { reviewBeforeSendRef.current = reviewBeforeSend; }, [reviewBeforeSend]);
  // isMobile drives the compact two-row interview header layout (phone-sized
  // viewports can't fit the timer + interview-count + edit/auto + theme +
  // quit + sign-out controls in a single row without overlapping).
  const isMobile = useIsMobile(640);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("nti-theme") || "dark"; } catch { return "dark"; }
  });

  useEffect(() => {
    try {
      document.documentElement.setAttribute("data-theme", theme);
      localStorage.setItem("nti-theme", theme);
      const meta = document.getElementById("meta-theme-color");
      if (meta) meta.setAttribute("content", theme === "light" ? "#f8fafc" : "#070c18");
    } catch {}
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // ─── Subscription banner ───────────────────────────────────────────────────
  // When buyers come back from a successful Lemon Squeezy checkout, the URL
  // carries `?checkout=success`. Show a one-time confirmation, then strip the
  // param so a refresh doesn't re-show it. The actual plan flip happens
  // asynchronously via the webhook — `loadUsageInfo` may need a moment to
  // catch up, so we re-fetch usage 4 seconds after the banner appears.
  const [subscriptionBanner, setSubscriptionBanner] = useState(null); // null | 'success'
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("checkout") === "success") {
        setSubscriptionBanner("success");
        params.delete("checkout");
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
        window.history.replaceState({}, "", newUrl);
        // Give the webhook a few seconds, then refresh the cockpit's usage.
        const t = setTimeout(() => {
          loadUsageInfo().then((u) => setUsage(u)).catch(() => {});
        }, 4000);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  // Shared subscribe attempt. Used by both the landing-page "Subscribe →"
  // buttons AND the post-signup auto-redirect path so the double-subscribe
  // guard runs in both flows. Async because we need to fetch usage on
  // demand if it hasn't been loaded yet (the landing page doesn't load
  // usage on mount, and even on the cockpit there's a window between
  // mount and first `loadUsageInfo` resolution).
  const attemptSubscribe = async (planCode, userObj, opts = {}) => {
    const targetPlan = String(planCode || '').toLowerCase();
    if (!userObj || !targetPlan) return;
    // If we don't already know the user's current plan, fetch it now.
    // Without this, a paid user who clicks Subscribe before usage loads
    // would slip through the guard (currentPlan would coerce to 'free').
    let info = usage;
    if (!info?.plan_code) {
      try {
        info = await loadUsageInfo();
        if (info) setUsage(info);
      } catch (e) {
        // Fail open: if we genuinely can't read the plan, fall through
        // to checkout rather than block a legitimate purchase.
        console.warn('[attemptSubscribe] usage fetch failed, allowing checkout:', e);
      }
    }
    const currentPlan = String(info?.plan_code || 'free').toLowerCase();
    // Strip the _pass suffix when comparing tiers so we can detect cross-type
    // collisions (e.g. an active `starter` subscriber clicking "Buy Starter
    // Pass" — same feature set, would result in double-billing).
    const currentBase = currentPlan.replace(/_pass$/, '');
    const targetBase  = String(targetPlan || '').toLowerCase().replace(/_pass$/, '');
    const targetIsPass  = String(targetPlan || '').endsWith('_pass');
    const currentIsPass = currentPlan.endsWith('_pass');
    if (currentPlan !== 'free' && currentPlan === targetPlan) {
      alert(`You're already on the ${currentPlan} plan.`);
      return;
    }
    // Same tier, different billing type — block to prevent paying twice for
    // the same feature set. Tell the user what to do instead of just walling
    // them off.
    if (currentPlan !== 'free' && currentBase === targetBase) {
      alert(
        targetIsPass
          ? `You're already subscribed to the ${currentBase} plan, so a ${currentBase} 3-month pass would double-bill you for the same features. Cancel your subscription via Manage Subscription first if you'd prefer the one-time pass.`
          : `You already own a ${currentBase} 3-month pass — subscribing now would double-bill you. Wait until your pass expires, or contact support for help switching to a subscription early.`
      );
      return;
    }
    if (currentPlan !== 'free' && !currentIsPass && !targetIsPass) {
      const ok = window.confirm(
        `You're already subscribed to the ${currentPlan} plan. To switch ` +
        `to ${targetPlan}, use Manage Subscription — Lemon will swap your ` +
        `plan in one step and prorate the difference. Subscribing again ` +
        `here would charge you for both plans at the same time.\n\n` +
        `Open the customer portal now?`
      );
      if (ok) handleManageSubscription();
      return;
    }
    const url = buildLemonCheckoutUrl(targetPlan, userObj, { discountCode: opts.discountCode });
    if (url) {
      track('upgrade_clicked', { plan: targetPlan });
      window.location.href = url;
    } else if (typeof targetPlan === "string" && targetPlan.endsWith("_pass")) {
      // Pass UUIDs aren't configured in Lemon Squeezy yet — let buyers know
      // it's coming rather than silently failing on the click.
      alert("3-month passes are launching soon. Check back in a few days!");
    }
  };

  // Open the Lemon Squeezy customer portal in a new tab. The server
  // generates a one-time URL scoped to the signed-in user's customer record;
  // we send it to a fresh tab so they don't lose their place in the cockpit.
  const handleManageSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        alert("Please sign in again to manage your subscription.");
        return;
      }
      const apiBase = process.env.REACT_APP_API_URL || "";
      const resp = await fetch(`${apiBase}/api/lemon-portal`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.url) {
        alert(json?.error || "Could not open billing portal. Please try again.");
        return;
      }
      window.open(json.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("[manage-subscription] failed:", e);
      alert("Could not open billing portal. Please try again.");
    }
  };

  const [resumeFile, setResumeFile] = useState(null);
  const [resumeName, setResumeName] = useState("");
  const [resumeText, setResumeText] = useState("");
  const [resumeInsights, setResumeInsights] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");

  // Interview state
  const [phase, setPhase] = useState("landing");

  // Tracks where the user came from when opening the Terms page so the back
  // button returns them to the right screen (landing vs cockpit).
  const [termsReturnPhase, setTermsReturnPhase] = useState(null);
  const openTerms = () => {
    setTermsReturnPhase(phase);
    setPhase("terms");
  };
  // The Privacy / Cookies / Disclaimer policy pages share the same
  // return-phase mechanism as Terms — when the user clicks "Back" we send
  // them to wherever they came from (cockpit if signed in, landing if not,
  // or the screen they were already on). policy-* phase names keep the
  // top-level switch obvious.
  const openPolicy = (slug) => {
    setTermsReturnPhase(phase === "terms" || phase?.startsWith?.("policy-") ? termsReturnPhase : phase);
    setPhase(slug === "terms" ? "terms" : `policy-${slug}`);
  };
  // Mirror `phase` into the ref declared near the top of this component so
  // long-lived listeners (auth, focus, etc.) can read the current phase
  // without re-subscribing on every render.
  useEffect(() => { phaseRef.current = phase }, [phase]);
  const [messages, setMessages] = useState([]);
  const [newMsgIdx, setNewMsgIdx] = useState(-1);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");
  const [exchangeCount, setExchangeCount] = useState(0);
  const [evaluation, setEvaluation] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  // Rotating progress message shown while feedback is being generated, so the
  // wait feels active even though the actual work is happening on the server.
  const [evaluatingStage, setEvaluatingStage] = useState("Reviewing the conversation…");
  // Bumped after each end_interview so the cockpit refetches its session list
  // and any user landing back on the cockpit sees the just-finished session.
  const [sessionsRefreshKey, setSessionsRefreshKey] = useState(0);

  // Cycle the "Please wait…" status line every ~2.4s while feedback is being
  // generated, so the user gets continuous visual confirmation that work is
  // happening even when no other UI changes for a few seconds. Declared AFTER
  // the isEvaluating useState above so the dependency array doesn't read a
  // const that hasn't been initialised yet (TDZ trap in production builds).
  useEffect(() => {
    if (!isEvaluating) return;
    const stages = [
      "Reviewing the conversation…",
      "Scoring communication and answer quality…",
      "Looking for specific moments to highlight…",
      "Measuring critical thinking and structure…",
      "Identifying what to focus on next time…",
      "Wrapping up your personalised report…",
    ];
    let i = 0;
    setEvaluatingStage(stages[0]);
    const id = setInterval(() => {
      i = (i + 1) % stages.length;
      setEvaluatingStage(stages[i]);
    }, 2400);
    return () => clearInterval(id);
  }, [isEvaluating]);

  const recognitionRef = useRef(null);
  const transcriptRef = useRef("");
  const finalChunksRef = useRef("");
  const vocabRef = useRef([]);
  const messagesRef = useRef([]);
  const stoppingRef = useRef(false);
  const stopTimerRef = useRef(null);
  const submittingRef = useRef(false);
  const wantsRecordingRef = useRef(false);
  // Synchronous single-flight guard for startRecording. The `isRecording`
  // state check at function entry isn't enough because setIsRecording is
  // async and we await getUserMedia before flipping it — two fast taps can
  // both pass the React-state guard and spin up overlapping recognition
  // instances. This ref flips synchronously and is cleared in every exit
  // path (success, error, unsupported, perm-denied, double-flight skip).
  const startingRecordingRef = useRef(false);
  // Flips true whenever the page goes hidden (screen lock, tab switch,
  // app switch on mobile). startRecording reads it on iOS to decide
  // whether to run a prophylactic `warmupAudioStack()` BEFORE
  // getUserMedia — without that pre-warmup, the post-lock OS audio
  // session returns a `track.muted=true` stream that needs the 1.5s
  // onunmute wait AND leaves Apple's SR engine stuck delivering zero
  // frames. Cleared once consumed by startRecording.
  const pageWasHiddenRef = useRef(false);
  // Holds the getUserMedia MediaStream for the SR session lifetime. Keeping
  // it alive (instead of stopping tracks right after the permission probe)
  // signals to the OS that the page is actively using audio — without this,
  // after a screen-lock + unlock the audio track returns `muted=true` and
  // SR.start() silently produces zero audio frames until ANOTHER lock-unlock
  // cycle resets the device. We stop the tracks only on real teardown
  // (stopRecording / forceStopRecognition / fatal onerror).
  const micStreamRef = useRef(null);
  // Web Audio context held alongside the mic stream. Two purposes:
  // (1) On Android Chrome, the act of routing the captured stream through
  //     an AudioContext forces the OS audio stack to actually deliver
  //     frames — without this, SpeechRecognition can "start" successfully
  //     after a screen lock and yet receive zero audio frames until the
  //     next lock/unlock cycle resets the device.
  // (2) Keeping the context alive prevents the OS from putting the audio
  //     session back to sleep mid-utterance.
  const audioCtxRef = useRef(null);
  // Preallocated 100ms-silence HTMLAudioElement used to flip the iOS audio
  // session category back to "inactive" after speechSynthesis ends. iOS's
  // window.speechSynthesis leaves the OS session pinned in "playback-only"
  // mode for 1-2 minutes after TTS — synth.cancel() does NOT release it,
  // and the next getUserMedia call returns a muted/dead track. The
  // documented WebKit workaround is to play a real <audio> element with a
  // non-speech source: that "captures" the session away from speech-synth
  // and releases it cleanly on `ended`. Element is allocated + "unlocked"
  // (play+pause) inside a user gesture by unlockSilenceAudio so the later
  // programmatic .play() in resetAudioSessionAfterTTS — which fires from
  // a speechSynthesis onend callback OUTSIDE any user gesture — is not
  // rejected by iOS autoplay policy.
  const silenceAudioRef = useRef(null);
  // Watchdog flag: createRecognition sets this true on first onaudiostart
  // / onsoundstart / onresult. startRecording's post-start watchdog reads
  // it to detect the "silent-stuck" SR case and force a retry with a
  // fresh stream + context. Ref (not state) so the watchdog timer sees
  // the current value without a re-render.
  const gotAudioRef = useRef(false);
  const systemPromptRef = useRef("");
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, transcript]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => {
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    // Restore previously saved resume from this browser, if any.
    try {
      const saved = localStorage.getItem("interview-coach-resume-v1");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.text) {
          setResumeName(parsed.name || "Saved resume");
          setResumeText(parsed.text);
          setResumeInsights(parsed.insights || "");
          setUploadStatus("✓ Using your saved resume");
        }
      }
    } catch {}
    return () => {
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    };
  }, []);

  const MAX_RESUME_BYTES = 5 * 1024 * 1024; // 5 MB

  const handleResumeUpload = async (file) => {
    setUploadStatus("");
    if (!file) return;

    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (!isPdf) {
      setUploadStatus("⚠ Only PDF files are accepted.");
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      setUploadStatus(`⚠ Resume is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 5 MB.`);
      return;
    }

    setResumeFile(file);
    setResumeName(file.name);
    setUploadStatus("Reading your resume…");
    try {
      const base64 = await readFileAsBase64(file);
      const resumeParseUserId = await getCurrentUserIdForCostAttribution();
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: "You are a resume parser. Extract text and provide insights.",
          // Resume parsing happens before a session exists — session_id stays
          // null so the call still gets logged against the user for cost
          // attribution. call_kind lets the dashboard slice it out separately.
          session_id: null,
          user_id: resumeParseUserId,
          call_kind: 'resume_parse',
          messages: [{
            role: "user",
            content: [
              { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
              { type: "text", text: "Extract all text from this resume. Then on a new line starting with 'INSIGHTS:' write 1–2 sentences summarising: years of experience, main skills, notable employers/projects. Return only the text and the INSIGHTS line." },
            ],
          }],
        }),
      });
      const data = await res.json();
      const full = data.content?.map((b) => b.text || "").join("") || "";
      const insightMatch = full.match(/INSIGHTS:(.*)/s);
      const insights = insightMatch ? insightMatch[1].trim() : "";
      const text = full.replace(/INSIGHTS:.*/s, "").trim();
      setResumeInsights(insights);
      setResumeText(text);
      setUploadStatus("✓ Resume analysed and saved to this browser");
      try {
        localStorage.setItem(
          "interview-coach-resume-v1",
          JSON.stringify({ name: file.name, text, insights, savedAt: Date.now() })
        );
      } catch {
        // Storage may be full or disabled (private mode) — resume still works for this session.
      }
    } catch {
      setUploadStatus("⚠ Couldn't read resume — will use general questions");
    }
  };

  const clearSavedResume = () => {
    try { localStorage.removeItem("interview-coach-resume-v1"); } catch {}
    setResumeFile(null);
    setResumeName("");
    setResumeText("");
    setResumeInsights("");
    setUploadStatus("Saved resume removed.");
  };

  const startInterview = async () => {
    if (startingRef.current) return; // single-flight: ignore double-clicks
    startingRef.current = true;
    unlockSpeech();
    unlockSilenceAudio();
    setError("");

    // ── Tier enforcement: ask the DB to atomically check limits and either
    //    return a fresh session row, an in-progress one to resume, or throw.
    setIsThinking(true);
    let session;
    try {
      session = await startInterviewSession({ jobDescription, interviewer });
    } catch (e) {
      setIsThinking(false);
      startingRef.current = false;
      if (e.message === 'NOT_AUTHENTICATED') { setAuthView('login'); return; }
      if (e.message === 'FREE_LIMIT_REACHED') { setLimitModal('free'); return; }
      if (e.message === 'MONTHLY_LIMIT_REACHED') { setLimitModal('monthly'); return; }
      if (e.message === 'PLAN_UNAVAILABLE') { setLimitModal('plan'); return; }
      setError("Couldn't start interview: " + e.message);
      return;
    }

    setCurrentSessionId(session.id);
    setSessionStartedAt(new Date(session.started_at).getTime());
    endingRef.current = false; // a fresh session — re-enable the end guard
    track('interview_started', { role, experience, interviewer });
    // Refresh usage so the in-app banner reflects this new session.
    loadUsageInfo().then(setUsage).catch(() => {});

    const interviewerObj = INTERVIEWERS.find((i) => i.id === interviewer) || INTERVIEWERS[0];
    // Defense-in-depth: only Power-tier users can upload a resume in the
    // wizard, but `resumeText` may be hydrated from a previous session's
    // localStorage even after a downgrade. Strip it here so the system
    // prompt never references a resume the user no longer has access to.
    const includeResume = canUploadResume;
    systemPromptRef.current = buildSystemPrompt({
      role, experience, tone,
      resumeText:     includeResume ? resumeText     : "",
      resumeInsights: includeResume ? resumeInsights : "",
      interviewerName: interviewerObj.name, skills, jobDescription,
    });
    setPhase("interview");
    try {
      const initialMessages = [{
        role: "user",
        content: `Please begin the mock interview now. Introduce yourself as ${interviewerObj.name} and ask the first interview question.`,
      }];
      // Pass the stable system prompt as cacheableSystem on the very first
      // chat call too — Anthropic writes the cache on turn 1 (small write
      // penalty) so turns 2+ get the discounted cache-read price. Saves
      // one full-price chat call vs. only caching from turn 2 onward.
      const reply = await callClaude(
        initialMessages,
        "",
        undefined,
        { sessionId: session.id, callKind: 'chat', cacheableSystem: systemPromptRef.current }
      );
      setMessages([{ role: "assistant", content: reply }]);
      setNewMsgIdx(0);
      setIsThinking(false);
      setIsSpeaking(true);
      speak(reply, () => {
        setIsSpeaking(false);
        resetAudioSessionAfterTTS();
      }, interviewer);
    } catch (e) {
      // The RPC already created an "active" session row before the LLM
      // bootstrap failed. If we don't close it out here it'll sit active
      // forever and the user will be told they have a session to resume.
      setError("Connection failed: " + e.message);
      setIsThinking(false);
      setPhase("setup");
      try { await abandonInterviewIfActive(); } catch {}
    } finally {
      startingRef.current = false;
    }
  };

  // Abandons the active session in the DB without running an evaluation.
  // Used when the user navigates Home / signs out / resets mid-interview so
  // their interview row doesn't sit "active" forever (and so quota math is
  // accurate). Safe to call from any phase — no-op if no active session.
  // Transactional: state is only cleared AFTER the RPC succeeds, so a
  // network failure leaves the handle intact for retry.
  const abandonInterviewIfActive = async () => {
    const sessionId = currentSessionIdRef.current;
    if (!sessionId || endingRef.current) return;
    endingRef.current = true;
    const startedAt = sessionStartedAtRef.current;
    const durationSeconds = startedAt
      ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
      : 0;
    try {
      await endInterviewSession({
        sessionId,
        durationSeconds,
        endedReason: 'abandoned',
        role, experience, tone,
        modelUsed: 'sonnet',
      });
      // Only on success do we relinquish the handle and stop the timer.
      setCurrentSessionId(null);
      setSessionStartedAt(null);
      setSecondsRemaining(null);
      loadUsageInfo().then(setUsage).catch(() => {});
    } catch (e) {
      // RPC failed — keep the session handle so the user (or the next
      // navigation) can retry closure. Release the guard so retries aren't
      // permanently blocked.
      console.warn('end_interview (abandon) failed:', e?.message || e);
      endingRef.current = false;
    }
  };

  // ── Defensive mic shutdown on phase change ─────────────────────────────────
  // Belt-and-braces: any time we navigate away from the interview phase
  // (cockpit, ended, setup, anywhere), force-stop any live recognition. This
  // catches code paths that forgot to call forceStopRecognition themselves.
  // Inlined (rather than calling forceStopRecognition) to avoid a forward
  // reference and to keep the effect's dep list trivially correct.
  useEffect(() => {
    if (phase === "interview") return;
    // Tear down if ANY of: SR is live, the user wants recording, OR we're
    // still holding the mic stream / AudioContext. The last two matter on
    // iOS specifically — the visibilitychange=hidden handler intentionally
    // keeps micStreamRef + audioCtxRef alive across a lock (so foregrounding
    // can rewarm them), but if the user navigates away (cockpit, sign-out,
    // ended) before tapping Record again, nothing else releases them and
    // the OS mic indicator would stay on until page reload.
    if (
      !recognitionRef.current &&
      !wantsRecordingRef.current &&
      !micStreamRef.current &&
      !audioCtxRef.current
    ) return;
    wantsRecordingRef.current = false;
    stoppingRef.current = true;
    if (stopTimerRef.current) {
      try { clearTimeout(stopTimerRef.current); } catch {}
      stopTimerRef.current = null;
    }
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.onresult = null; rec.onerror = null; rec.onend = null; } catch {}
      try { rec.abort(); } catch {}
      try { rec.stop(); } catch {}
    }
    recognitionRef.current = null;
    // Also release the held getUserMedia stream + AudioContext — without
    // this, leaving phase=interview via sign-out (which bypasses reset())
    // or any other phase transition would leave the OS-level mic indicator
    // on and the audio device grabbed until the page reloaded.
    releaseHeldAudio();
    // Drop transcript buffers — leaving phase=interview means whatever was
    // mid-utterance is gone, and we don't want it to seed the next session.
    transcriptRef.current = "";
    finalChunksRef.current = "";
    setIsRecording(false);
  }, [phase]);

  // ── Per-session countdown timer ─────────────────────────────────────────────
  // Uses the user's plan-specific session_minutes (15 for Free, 30 for paid,
  // configurable per-tier via plan_rules). Fail-CLOSED: if usage hasn't loaded
  // we use the strictest possible cap (15 min) so a network glitch can never
  // give a Free user a 30-min session.
  useEffect(() => {
    if (phase !== "interview" || !sessionStartedAt) {
      setSecondsRemaining(null);
      return;
    }
    const sessionMinutes = Number(usage?.session_minutes) || 15;
    const totalMs = sessionMinutes * 60 * 1000;
    const tick = () => {
      const elapsed = Date.now() - sessionStartedAt;
      const remaining = Math.max(0, Math.floor((totalMs - elapsed) / 1000));
      setSecondsRemaining(remaining);
      if (remaining === 0) {
        endSessionAndEvaluate('time_cap');
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase, sessionStartedAt, usage?.session_minutes]);

  const submitAnswer = async (userText) => {
    if (submittingRef.current) return;
    // If a late onresult fires after the session already ended (e.g. user
    // hits End or the timer expires while their last utterance is still
    // being finalised), drop it on the floor — sending it to Claude would
    // cause a stray "thinking" spinner and pointless reply on the
    // already-frozen feedback screen.
    if (phaseRef.current !== "interview" || endingRef.current) return;
    const cleanedText = userText.trim();
    if (!cleanedText) {
      setError("I couldn't hear an answer. Please try recording again.");
      setTranscript("");
      transcriptRef.current = "";
      stoppingRef.current = false;
      setIsRecording(false);
      return;
    }
    submittingRef.current = true;
    const userMsg = { role: "user", content: cleanedText };
    const updated = [...messagesRef.current, userMsg];
    setMessages(updated);
    setNewMsgIdx(updated.length - 1);
    setTranscript("");
    transcriptRef.current = "";
    // Clear the per-utterance finalized buffer too — otherwise the previous
    // answer's text leaks into the NEXT turn's startRecording (which used to
    // wipe finalChunksRef defensively, but doing so destroyed any screen-lock
    // rescued draft). Lifecycle ownership: submit + stop clear, start preserves.
    finalChunksRef.current = "";
    setIsThinking(true);
    try {
      // Append a live time-pacing instruction to the system prompt for THIS
      // turn only. The base prompt is unchanged, so the model's persona stays
      // intact; the appended note just tells it how much runway is left so
      // it can decide whether to ask another question, ask a small one, or
      // give a short closing instead. Empty string for early in the session.
      const pacingNote = buildTimeAwarenessNote(secondsRemainingRef.current);
      // Split system into stable prefix (cacheable) + volatile pacing tail.
      // systemPromptRef.current is built once at session start and never
      // mutated, so it's a perfect cache key. pacingNote changes every turn
      // and stays uncached. Anthropic caches the prefix on first call and
      // reads it back at 1/10th the price on subsequent turns (5-min TTL,
      // refreshed by each cache hit — interview turn cadence keeps it hot).
      const reply = await callClaude(
        updated.map((m) => ({ role: m.role, content: m.content })),
        pacingNote,
        undefined,
        {
          sessionId: currentSessionIdRef.current,
          callKind: 'chat',
          cacheableSystem: systemPromptRef.current,
        }
      );
      const withAI = [...updated, { role: "assistant", content: reply }];
      setMessages(withAI);
      setNewMsgIdx(withAI.length - 1);
      setExchangeCount((c) => c + 1);
      setIsThinking(false);
      setIsSpeaking(true);
      speak(reply, () => {
        setIsSpeaking(false);
        resetAudioSessionAfterTTS();
      }, interviewer);
    } catch (e) {
      setError("Something went wrong: " + e.message);
      setIsThinking(false);
    } finally {
      submittingRef.current = false;
    }
  };

  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
  // iOS detection covers iPhone / iPad / iPod plus modern iPadOS (which
  // reports as "MacIntel" with touch points). PWA install adds no UA
  // change but the audio-session behavior is identical to Safari, so a
  // single isIOS flag is enough to switch our screen-lock recovery
  // strategy. iOS SpeechRecognition uses Apple's native dictation engine
  // and does NOT consume our getUserMedia stream / AudioContext — so the
  // Android double-getUserMedia warmup is useless here, and the analyser
  // pump can detect audio that SR can't see. The watchdog and recovery
  // paths branch on this to do the right thing per platform.
  const isIOS = typeof navigator !== "undefined" && (
    /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1)
  );

  // Keep the speech-vocab in sync with the user's role + selected skills so the
  // post-processor can fix mishearings of those exact terms.
  useEffect(() => {
    vocabRef.current = buildSpeechVocab(role, skills);
  }, [role, skills]);

  // Screen-lock / tab-hidden survival guard. When the user's phone screen
  // locks (or they switch tabs), mobile browsers throttle JS and kill the
  // SpeechRecognition engine — `onend` fires without a final result, and
  // any in-flight interim text would be wiped by the next instance unless
  // we explicitly snapshot it into finalChunksRef. On resume, re-arm the
  // engine if the user was still recording so they can keep talking
  // without tapping Stop/Start again.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        // Remember the lock/app-switch — used by the next startRecording
        // to decide whether the held audio (if any) is still trustworthy.
        pageWasHiddenRef.current = true;
        // Preserve interim transcript before the engine dies, regardless
        // of platform — both iOS and Android can drop the last utterance
        // when the page hides mid-recognition.
        if (
          transcriptRef.current &&
          transcriptRef.current.trim().length > finalChunksRef.current.trim().length
        ) {
          finalChunksRef.current = transcriptRef.current.trim();
        }
        // iOS PWA: proactively stop recording AND release the held mic +
        // AudioContext on lock. The user returns to a "Start Recording"
        // button with their transcript preserved; tapping it runs the
        // standard startRecording flow with a fresh getUserMedia against
        // a clean OS audio session.
        //
        // Earlier iterations kept the held stream alive across the lock
        // (commit b4a0672) as a latency optimization — but real-device
        // testing in May 2026 showed that the held stream is exactly what
        // keeps iOS from releasing the audio session, which forced a 20s
        // passive-poll recovery on every subsequent Record tap. Releasing
        // here lets iOS fully reset the session immediately, so the next
        // Record (post-unlock) gets a working mic in ~200-500ms with no
        // poll. Android still auto-restarts on visibility=visible because
        // it actually works there.
        if (isIOS && wantsRecordingRef.current) {
          const rec = recognitionRef.current;
          if (rec) {
            try { rec.onresult = null; rec.onerror = null; rec.onend = null; } catch {}
            try { rec.abort(); } catch {}
          }
          recognitionRef.current = null;
          try { releaseHeldAudio(); } catch {}
          wantsRecordingRef.current = false;
          stoppingRef.current = false;
          setIsRecording(false);
          // Surface the preserved text in the review/submit box so the
          // user, on unlock, can submit immediately without having to
          // tap Record again. Without this, the rescued transcript
          // sits in finalChunksRef invisibly and the user thinks
          // their answer was lost. (May 2026 UX fix: tapping Record
          // again is now optional — only needed if they want to add
          // more spoken content.)
          const rescued = (transcriptRef.current || finalChunksRef.current || "").trim();
          if (rescued) {
            setPendingAnswer(rescued);
            setMicDebug("");
            setError("⏸ Paused while screen was locked — your answer is preserved below. Submit it, edit it, or tap Record to continue speaking.");
          } else {
            setMicDebug("⏸ Paused while screen was locked — tap Start Recording to resume");
          }
          // Deliberately do NOT touch transcriptRef / finalChunksRef —
          // they hold the user's in-progress answer and the next
          // startRecording will build on them if the user chooses to
          // continue speaking instead of submitting what they have.
        }
      } else if (document.visibilityState === "visible") {
        if (
          wantsRecordingRef.current &&
          !stoppingRef.current &&
          !recognitionRef.current &&
          // Don't race with an in-flight manual startRecording — it may be
          // mid-getUserMedia or mid-onunmute-wait, with recognitionRef
          // legitimately null. Restarting here would spawn a competing SR
          // instance that fights the manual one for the audio device.
          !startingRecordingRef.current
        ) {
          // Note: on iOS the hidden-branch above cleared
          // wantsRecordingRef, so this auto-restart only ever fires on
          // Android (which is what we want).
          try { restartRecognition(); } catch {}
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const restartRecognition = () => {
    if (!wantsRecordingRef.current || stoppingRef.current) return;
    // If a manual startRecording is in flight (mid-getUserMedia or
    // mid-onunmute-wait), defer to it — otherwise we'd spawn a competing
    // SR instance that fights the manual one for the audio device.
    if (startingRecordingRef.current) return;
    try {
      const next = createRecognition();
      recognitionRef.current = next;
      next.start();
    } catch {
      // First attempt threw — null the ref so the delayed retry's
      // `if (recognitionRef.current) return;` guard doesn't see the failed
      // instance and skip the retry entirely. Detach handlers first so the
      // failed instance can't fire onend → re-entering restartRecognition.
      const failed = recognitionRef.current;
      if (failed) {
        try { failed.onresult = null; failed.onerror = null; failed.onend = null; } catch {}
        try { failed.abort(); } catch {}
      }
      recognitionRef.current = null;
      // Try once more after a short delay (Android sometimes needs the audio
      // resource to be released before a new instance can start).
      setTimeout(() => {
        if (!wantsRecordingRef.current || stoppingRef.current) return;
        // Same in-flight guard as the first attempt: a manual startRecording
        // may have begun during the 350ms wait, in which case it owns
        // recognitionRef. Also bail if someone else already populated it.
        if (startingRecordingRef.current) return;
        if (recognitionRef.current) return;
        try {
          const next2 = createRecognition();
          recognitionRef.current = next2;
          next2.start();
        } catch {
          // Give up restarting but DO NOT auto-submit — keep UI in recording
          // state so the user can tap Stop & Send when ready.
          recognitionRef.current = null;
        }
      }, 350);
    }
  };

  const createRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;
    const rec = new SR();
    // On Android Chrome, continuous mode emits duplicates — use single-utterance
    // mode and rely on auto-restart in onend.
    rec.continuous = !isAndroid;
    rec.interimResults = true;
    rec.maxAlternatives = 4; // request alternatives so we can pick the one that best matches the user's vocab
    rec.lang = "en-US";

    rec.onstart = () => {
      console.log("[mic] onstart");
      setMicDebug("🎙 Listening — start speaking…");
    };

    rec.onaudiostart = () => {
      console.log("[mic] audio start");
      // INTENTIONALLY does NOT set gotAudioRef. On Android Chrome this
      // event fires when SR *opens* its audio source — not when frames
      // actually arrive. Trusting it defeats the silent-stuck watchdog:
      // SR happily "starts", flips this flag, and then receives zero
      // frames forever until the OS audio session is reset (lock/unlock).
      // gotAudioRef is only flipped by onsoundstart / onspeechstart /
      // onresult AND by the AnalyserNode pump in primeAudioContext —
      // all signals that require REAL audio frames to be flowing.
    };
    rec.onsoundstart = () => {
      console.log("[mic] sound detected");
      gotAudioRef.current = true;
      setMicDebug("🔊 Sound detected — keep going…");
    };
    rec.onspeechstart = () => {
      console.log("[mic] speech start");
      gotAudioRef.current = true;
      setMicDebug("🗣 Speech detected — capturing…");
    };

    rec.onresult = (e) => {
      const vocab = vocabRef.current;
      let sessionFinal = "";
      let interim = "";
      for (let i = 0; i < e.results.length; i++) {
        const result = e.results[i];
        if (result.isFinal) {
          // Pick alternative with most vocab matches; tie → highest confidence (index 0).
          let bestT = result[0].transcript || "";
          let bestScore = scoreVocabMatches(bestT, vocab);
          for (let k = 1; k < result.length; k++) {
            const t = result[k].transcript || "";
            const s = scoreVocabMatches(t, vocab);
            if (s > bestScore) { bestT = t; bestScore = s; }
          }
          sessionFinal += correctTranscript(bestT, vocab) + " ";
        } else {
          interim += result[0].transcript || "";
        }
      }
      rec._sessionFinal = sessionFinal.trim();
      // Watchdog signal — any onresult, even an interim, means real audio
      // frames reached the SR engine.
      gotAudioRef.current = true;
      const combined = `${finalChunksRef.current} ${sessionFinal} ${interim}`
        .replace(/\s+/g, " ")
        .trim();
      transcriptRef.current = combined;
      setTranscript(combined);
      const wc = combined ? combined.split(/\s+/).length : 0;
      setMicDebug(`✏️ Captured ${wc} word${wc === 1 ? "" : "s"}`);
      console.log("[mic] result", { sessionFinal, interim, combined });
    };

    rec.onerror = (event) => {
      console.log("[mic] error:", event.error, event.message);
      const code = event.error || "unknown";
      if (code === "not-allowed" || code === "service-not-allowed") {
        wantsRecordingRef.current = false;
        setIsRecording(false);
        // Fully tear down — otherwise the orphaned SR instance keeps the
        // audio device half-grabbed and the next start() captures nothing.
        try { rec.onresult = null; rec.onerror = null; rec.onend = null; } catch {}
        try { rec.abort(); } catch {}
        try { rec.stop(); } catch {}
        recognitionRef.current = null;
        releaseHeldAudio();
        setError("🎤 Microphone permission denied. Tap the lock icon in the address bar and allow microphone access, then reload.");
        setMicDebug(`❌ Permission denied (${code})`);
        return;
      }
      if (code === "no-speech") {
        setMicDebug("🤫 No speech heard yet — speak louder, closer to the mic");
        return;
      }
      if (code === "aborted" || code === "network") {
        setMicDebug(`⚠ ${code} — restarting…`);
        return;
      }
      // Screen-lock / app-switch path: the browser kills the audio source
      // and fires `audio-capture` (or `audio-busy`). The previous code
      // nulled recognitionRef but never aborted the instance, leaving the
      // OS-level audio device half-held — the user's next Start tap created
      // a fresh SR that started but received zero audio frames, producing
      // the "I couldn't hear an answer" red banner until the browser
      // eventually garbage-collected the orphan (~minutes).
      //
      // Fix: fully tear the failed instance down (detach handlers + abort +
      // stop) BEFORE nulling, so the device is released cleanly and the
      // next startRecording can grab it immediately.
      if (stoppingRef.current) {
        try { rec.onresult = null; rec.onerror = null; rec.onend = null; } catch {}
        try { rec.abort(); } catch {}
        return;
      }
      try { rec.onresult = null; rec.onerror = null; rec.onend = null; } catch {}
      try { rec.abort(); } catch {}
      try { rec.stop(); } catch {}
      wantsRecordingRef.current = false;
      setIsRecording(false);
      recognitionRef.current = null;
      // For the screen-lock audio-capture case, show a recoverable message
      // — the user just needs to tap Start again, no permission to fix.
      releaseHeldAudio();
      if (code === "audio-capture" || code === "audio-busy") {
        setError("Microphone dropped after the screen locked. Tap Start Recording again to resume — your answer so far is preserved.");
      } else {
        setError(`Microphone error: ${code}. ` + (event.message || "Check browser permissions."));
      }
      setMicDebug(`❌ ${code}`);
    };

    rec.onend = () => {
      // Save finalized text from this session before the instance is gone.
      if (rec._sessionFinal) {
        finalChunksRef.current = `${finalChunksRef.current} ${rec._sessionFinal}`
          .replace(/\s+/g, " ")
          .trim();
        transcriptRef.current = finalChunksRef.current;
        setTranscript(finalChunksRef.current);
      } else if (
        transcriptRef.current &&
        transcriptRef.current.trim().length > finalChunksRef.current.trim().length
      ) {
        // No final result was emitted this session, but the live transcript
        // (interim + earlier finals) is longer than what we've committed.
        // This is the screen-lock / tab-hidden / mobile-pause scenario:
        // the browser killed the engine mid-utterance before it could emit
        // a final result. Promote the live interim text to finalChunksRef
        // so the auto-restart below doesn't wipe the user's answer when the
        // next onresult callback rebuilds `combined` from finalChunksRef.
        // Less perfectly punctuated than a true final, but vastly better
        // than losing the entire answer and forcing the user to start over.
        finalChunksRef.current = transcriptRef.current.trim();
      }
      if (wantsRecordingRef.current && !stoppingRef.current) {
        restartRecognition();
        return;
      }
      recognitionRef.current = null;
    };

    return rec;
  };

  // Bullet-proof recognition kill — used both by the user-facing Stop button
  // (indirectly, via stopRecording's gentle path) and by anything that needs
  // to FORCE the mic off no matter what state it's in: the session-end timer,
  // the abandon-session helper, the back-to-cockpit button, etc.
  //
  // Critically: we set `wantsRecordingRef.current = false` and
  // `stoppingRef.current = true` BEFORE calling abort(). The recognition's
  // own `onend` handler checks these flags to decide whether to auto-restart;
  // without this ordering, abort() triggers onend which sees "still wants to
  // record" and resurrects a fresh recognition instance — that's the bug
  // that caused the mic to keep listening after the 30-min timer fired.
  // Called from every user-gesture entry point (Start Interview, Record,
  // Stop, Replay). Preallocates the silent-MP3 HTMLAudioElement and
  // "unlocks" it via a synchronous play()+pause() inside the user gesture
  // so that the later programmatic play() in resetAudioSessionAfterTTS —
  // which fires from an async speechSynthesis onend callback, OUTSIDE
  // any user gesture — is not rejected by iOS autoplay policy.
  // Idempotent: only the first call actually allocates + unlocks.
  // No-op on Android.
  const unlockSilenceAudio = () => {
    if (!isIOS) return;
    if (silenceAudioRef.current) return;
    try {
      const a = new Audio('/silence.mp3');
      a.playsInline = true;
      a.preload = 'auto';
      a.volume = 0;
      silenceAudioRef.current = a;
      const p = a.play();
      const settle = () => {
        try { a.pause(); a.currentTime = 0; } catch {}
      };
      if (p && typeof p.then === 'function') {
        p.then(settle).catch(() => {});
      } else {
        settle();
      }
    } catch {}
  };

  // iOS-only: invoked at the end of every TTS playback (speak() onend
  // callback in startInterview / submitAnswer / replayLastReply).
  //
  // Background: iOS's window.speechSynthesis leaves the OS audio
  // session pinned in "playback-only" mode for 1-2 minutes after TTS
  // ends. synth.cancel() does NOT release it. In that state the next
  // getUserMedia returns a muted/dead track and Apple's SpeechRecognition
  // engine starts but receives zero audio frames — which presents to the
  // user as "tap Record on Q2, nothing happens, no transcript appears."
  //
  // The documented WebKit workaround is to play a real <audio> element
  // with a non-speech source: it "captures" the OS session away from
  // speech-synth and releases it cleanly on `ended`. 100ms of silence
  // at volume=0 is enough. Used by ElevenLabs / Speechify / similar iOS
  // web players.
  //
  // NOTE (May 2026, iteration 8b): this is intentionally NOT coupled to
  // releasing the held mic stream — iteration 8 already releases on
  // stopRecording AND visibilitychange=hidden, so by the time this
  // callback fires there's nothing held. The earlier version of this
  // helper also called releaseHeldAudio(); that's now redundant and
  // would null out the stream of an in-progress recording in the rare
  // case the user tapped Record DURING the silent clip.
  const resetAudioSessionAfterTTS = () => {
    if (!isIOS) return;
    // If the user has already tapped Record (or one is starting), let
    // their flow proceed — don't fire a programmatic .play() that
    // would race the gUM call.
    if (
      wantsRecordingRef.current ||
      recognitionRef.current ||
      startingRecordingRef.current
    ) return;
    try {
      const a = silenceAudioRef.current || new Audio('/silence.mp3');
      a.playsInline = true;
      a.volume = 0;
      try { a.currentTime = 0; } catch {}
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        try { a.onended = null; a.onerror = null; } catch {}
        try { a.pause(); a.currentTime = 0; } catch {}
      };
      a.onended = done;
      a.onerror = done;
      const p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => done());
      }
      // Safety net: even if onended never fires, force settle at 500ms.
      setTimeout(done, 500);
    } catch {}
  };

  // Centralized mic-stream + AudioContext teardown so every exit path
  // (stop, force-stop, fatal error, phase change, retry) releases both
  // resources together. Keeping them in lock-step is what prevents the
  // "OS mic indicator stays on after sign-out" and "AudioContext leaks
  // across sessions" classes of bug.
  const releaseHeldAudio = () => {
    if (micStreamRef.current) {
      try { micStreamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
      micStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      const held = audioCtxRef.current;
      audioCtxRef.current = null;
      if (held.intervalId) { try { clearInterval(held.intervalId); } catch {} }
      if (held.source) { try { held.source.disconnect(); } catch {} }
      if (held.analyser) { try { held.analyser.disconnect(); } catch {} }
      if (held.ctx) { try { held.ctx.close(); } catch {} }
    }
  };

  // Route the live mic stream through an AudioContext + AnalyserNode and
  // keep pulling samples on an interval. Two jobs:
  //   (1) The periodic analyser pull forces the OS audio stack to actually
  //       deliver frames — without an ongoing pump, getUserMedia returns
  //       a "live" track but SR.start() receives zero frames until the
  //       next OS audio-session reset (i.e. a screen lock cycle).
  //   (2) The pump is also our SOURCE-OF-TRUTH audio-flow detector: any
  //       non-zero bin means real frames are arriving. SR's onaudiostart
  //       event lies on Android (fires even when no frames flow), so we
  //       cannot trust it — the analyser is independent of SR and never
  //       lies. The held ref exposes `seenAudio()` and `lastNonZeroAt`
  //       for the pre-flight check and the runtime watchdog.
  //
  // We never connect to destination (would echo the user's voice back at
  // them) — the analyser pull alone is sufficient.
  const primeAudioContext = async (stream) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      if (ctx.state === "suspended") {
        try { await ctx.resume(); } catch {}
      }
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const held = {
        ctx,
        source,
        analyser,
        intervalId: null,
        anyAudioSeen: false,
        lastNonZeroAt: 0,
        lastSpeechAt: 0,
        // Sticky "ever saw audio in this context's lifetime" — used by the
        // pre-flight check which runs immediately after a fresh prime, so
        // the flag is freshly false at that point. "Audio" here means
        // ANY non-zero frequency data (mic alive); does NOT imply speech.
        seenAudio() { return this.anyAudioSeen; },
        // Recency-based check — required by the post-start watchdog,
        // which can't use the sticky flag because pre-flight likely
        // already flipped it true. If frames aren't flowing RIGHT NOW
        // we want to know, regardless of past history.
        seenRecentAudio(windowMs) {
          return this.lastNonZeroAt > 0 && (Date.now() - this.lastNonZeroAt) < windowMs;
        },
        // Speech-level recency — distinct from `seenRecentAudio` because
        // a silent room still produces low non-zero values from the mic
        // noise floor. This requires multiple bins with amplitude well
        // above noise floor before counting as "user is speaking",
        // which is what the watchdog needs to avoid false-firing during
        // a genuine pause.
        seenRecentSpeech(windowMs) {
          return this.lastSpeechAt > 0 && (Date.now() - this.lastSpeechAt) < windowMs;
        },
      };
      // Thresholds picked empirically: noise floor on a typical phone
      // mic gives a few bins <10/255 and almost never multiple bins
      // >=25. Human speech easily lights up >=6 bins in the 100-4000Hz
      // range above 25/255. Two-condition AND keeps false positives
      // low even in a slightly noisy environment.
      const SPEECH_BIN_THRESH = 25;
      const SPEECH_BIN_COUNT = 6;
      const tick = () => {
        try {
          analyser.getByteFrequencyData(buf);
          // Mic-alive check: any non-zero bin proves the stream is
          // delivering frames (a starved stream returns exact zeros).
          let anyNonZero = false;
          let loudBins = 0;
          for (let i = 0; i < buf.length; i++) {
            if (buf[i] > 0) anyNonZero = true;
            if (buf[i] >= SPEECH_BIN_THRESH) loudBins++;
          }
          const now = Date.now();
          if (anyNonZero) {
            held.anyAudioSeen = true;
            held.lastNonZeroAt = now;
          }
          if (loudBins >= SPEECH_BIN_COUNT) {
            held.lastSpeechAt = now;
          }
          // IMPORTANT: do NOT flip gotAudioRef from the analyser pump.
          // gotAudioRef is the SR-side audio-flow signal (set only by
          // SR's own sound/speech/result events). The analyser is the
          // mic-side signal (independent of SR). Keeping them separate
          // is what lets the watchdog detect iOS's broken state where
          // the mic is delivering audio fine but SR's own audio path
          // is starved — if we conflated the two flags here, the
          // watchdog would think SR was alive and never reset it.
        } catch {}
      };
      tick();
      held.intervalId = setInterval(tick, 100);
      audioCtxRef.current = held;
    } catch {
      // AudioContext priming is a best-effort wake-up; if it fails (e.g.
      // browser denies a new context), fall through and let SR try on
      // its own. Don't block the user from recording.
    }
  };

  // Audio-session warmup workaround. Both Android Chrome and iOS Safari
  // can return a "live" getUserMedia track whose frames never flow after
  // a screen lock — the OS audio session is half-asleep. The documented
  // fix is to acquire-then-stop a throwaway stream first; that forces the
  // OS session to re-initialize for the real call that follows.
  //
  // Android: one cycle with a 150ms gap is enough — Chrome's audio session
  //   recovers quickly post-lock.
  // iOS: a single cycle is often NOT enough. Field reports show users
  //   needing 15-20 seconds of passive wait after unlock before SR works.
  //   Empirically, multiple warmup cycles with wider gaps actively kick
  //   the session awake much faster than waiting (each cycle gives iOS a
  //   chance to release-then-re-init). 3 cycles × ~600ms ≈ ~1.8s of
  //   warmup is the sweet spot we've found between recovery reliability
  //   and added latency on the happy path (only runs after pageWasHidden).
  const warmupAudioStack = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;
    const cycles = isIOS ? 3 : 1;
    const gapMs  = isIOS ? 450 : 150;
    for (let i = 0; i < cycles; i++) {
      try {
        const dummy = await navigator.mediaDevices.getUserMedia({ audio: true });
        try { dummy.getTracks().forEach((t) => t.stop()); } catch {}
      } catch {
        // Permission denied or device busy — caller will surface a real
        // error on the next acquisition attempt. Don't break the loop;
        // the next cycle may succeed once iOS finishes waking up.
      }
      await new Promise((r) => setTimeout(r, gapMs));
    }
  };

  const forceStopRecognition = () => {
    wantsRecordingRef.current = false;
    stoppingRef.current = true;
    if (stopTimerRef.current) {
      try { clearTimeout(stopTimerRef.current); } catch {}
      stopTimerRef.current = null;
    }
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.onresult = null; rec.onerror = null; rec.onend = null; } catch {}
      try { rec.abort(); } catch {}
      try { rec.stop(); } catch {}
    }
    recognitionRef.current = null;
    releaseHeldAudio();
    // Clear transcript buffers too — force-stop is the "abandon, don't
    // resume" path (time cap, end session, navigate away). Without this,
    // a stale finalChunksRef would survive into the NEXT interview's first
    // turn and be re-seeded by startRecording's rescue logic.
    transcriptRef.current = "";
    finalChunksRef.current = "";
    setIsRecording(false);
    setMicDebug("");
  };

  const startRecording = async () => {
    if (startingRecordingRef.current) return;
    startingRecordingRef.current = true;
    // Outer try/finally ABSOLUTELY guarantees the single-flight guard is
    // cleared, even if any sync setup throws (speechSynthesis is patchy
    // across mobile browsers and has historically thrown in WebViews).
    try {
    try { unlockSpeech(); } catch {}
    try { unlockSilenceAudio(); } catch {}
    if (isThinking || isRecording) return;
    try { window.speechSynthesis?.cancel?.(); } catch {}
    setIsSpeaking(false);
    // ── Screen-lock rescue: preserve finalChunksRef across a re-start ──────
    // After a phone lock / app-switch, the recognition engine dies with an
    // `audio-capture: source is muted` error, which sets wantsRecordingRef=
    // false (so the visibilitychange auto-restart no-ops). The user sees
    // their answer text frozen on screen and taps Start Recording to keep
    // going. We MUST NOT wipe finalChunksRef here — the visibility-hidden
    // handler snapshotted the interim text into it precisely so this new
    // recording session could continue building on it (onresult re-renders
    // `combined = finalChunksRef + sessionFinal + interim`).
    //
    // submitAnswer and stopRecording own clearing finalChunksRef for the
    // happy path; by the time the user starts a fresh turn it's already "".
    // If it's non-empty here, it's a rescue draft — keep it and seed the
    // visible transcript so the UI never flickers to empty.
    const rescued = finalChunksRef.current ? finalChunksRef.current.trim() : "";
    setTranscript(rescued);
    transcriptRef.current = rescued;
    stoppingRef.current = false;
    submittingRef.current = false;
    wantsRecordingRef.current = true;
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    setError("");
    setMicDebug("🎙 Requesting microphone…");

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setMicDebug("");
      setError("Speech recognition is not supported in this browser. Please use Chrome on desktop or Android. (iOS Safari does not support live speech recognition.)");
      return;
    }

    // Defensive: tear down ANY lingering SR instance from a prior turn or a
    // screen-lock error path. Without this, a half-grabbed audio device from
    // an orphaned instance starves the new one and the user sees "I couldn't
    // hear an answer" until the browser GC's the orphan (minutes on Android).
    const stale = recognitionRef.current;
    if (stale) {
      try { stale.onresult = null; stale.onerror = null; stale.onend = null; } catch {}
      try { stale.abort(); } catch {}
      try { stale.stop(); } catch {}
      recognitionRef.current = null;
      // Tiny yield so the browser's audio subsystem actually releases the
      // device before SR.start() asks for it again (mainly an Android race).
      await new Promise((r) => setTimeout(r, 120));
    }

    // Drop any prior held stream + AudioContext before requesting a new one.
    // Stale streams can themselves go into `muted=true` after a screen lock
    // and need to be torn down so the fresh getUserMedia call returns a
    // live track. A stale AudioContext bound to a dead stream is also
    // useless and would block the priming step from waking the OS audio
    // stack, leaving SR to start silent.
    //
    // iOS optimization: if we still have a healthy held stream from the
    // previous turn (stopRecording kept it alive specifically so the OS
    // audio session doesn't go back to sleep between answers), reuse it
    // verbatim — re-acquiring a fresh stream after just releasing one is
    // the exact failure mode causing "tap Record → 2-3s lag → silent SR"
    // on iOS PWA between answers in the same interview. We only release
    // if the held stream/track is actually dead or if the page was
    // hidden (lock cycle), both of which clear micStreamRef elsewhere.
    // Reuse the previous turn's held stream only if (a) iOS, (b) the page
    // hasn't been hidden since (lock cycles always force re-acquire), (c)
    // the track flags say it's healthy, AND (d) the analyser has seen
    // actual audio frames in the last 2 seconds. (d) is critical: after
    // a broken post-lock recording, the held track can keep reporting
    // readyState='live' && !muted while the OS audio session is dead
    // and zero frames are flowing. Without the analyser check, the fast
    // path silently picks up the dead stream and SR launches against it,
    // producing "next record after submit doesn't capture audio either."
    const reusableMicStream =
      isIOS &&
      !pageWasHiddenRef.current &&
      micStreamRef.current &&
      audioCtxRef.current &&
      micStreamRef.current.getAudioTracks().some(
        (t) => t.readyState === "live" && !t.muted
      ) &&
      audioCtxRef.current.seenRecentAudio(2000);
    if (!reusableMicStream) {
      releaseHeldAudio();
    }

    // Helper: acquire a fresh mic stream + prime AudioContext + wait briefly
    // if the OS returned a muted track. Returns true on success, false on
    // unrecoverable failure (caller has already shown the right error).
    //
    // IMPORTANT PLATFORM BRANCH:
    //   - iOS: hold the stream + AudioContext alive for the SR session.
    //     Apple's SR engine has its own audio source independent of
    //     getUserMedia, so they don't compete, and the held stream
    //     keeps the OS audio session warm for the duration of THIS
    //     recording. (Iteration 8, May 2026: we no longer hold across
    //     turns / screen locks — those releases happen in
    //     stopRecording and the visibilitychange=hidden handler so
    //     iOS can fully reset the audio session category and avoid
    //     the loud-TTS / 20s-wake bugs. Within a single recording
    //     turn the hold is still correct.)
    //   - Android: SpeechRecognition acquires its OWN mic via Google's
    //     speech service. Holding a getUserMedia stream open at the
    //     same time competes for the mic — SR starts but receives ZERO
    //     audio frames, onsoundstart/onresult never fire, no transcript
    //     ever appears. The historically-working Android flow is to
    //     acquire the stream purely as a permission probe, IMMEDIATELY
    //     release it, and let SR grab the mic on its own.
    const acquireAndPrimeStream = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return true;
      // Fast-path: we kept the previous turn's stream + AudioContext alive
      // and they're still healthy. Skip the full re-acquire (which on iOS
      // produces a muted-track + stuck SR engine). Caller-side guard above
      // already verified the track is live + unmuted.
      if (reusableMicStream) return true;
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        wantsRecordingRef.current = false;
        setMicDebug(`❌ Mic blocked: ${err.name || "error"}`);
        setError("🎤 Microphone access was blocked. In your browser, tap the lock/info icon in the address bar, allow Microphone, then reload the page.");
        return false;
      }
      if (isAndroid) {
        // Permission probe only — release immediately so SR can grab the
        // mic. Do NOT prime an AudioContext or hold the stream.
        try { stream.getTracks().forEach((t) => t.stop()); } catch {}
        return true;
      }
      micStreamRef.current = stream;
      const track = stream.getAudioTracks()[0];
      if (track && track.muted) {
        setMicDebug("⏳ Waking microphone…");
        // Capped at 600ms: if Apple is going to fire onunmute it does so
        // within ~200-400ms on a healthy device. The previous 1500ms
        // budget was the main source of the 2-3s perceived lag after a
        // lock/unlock. A track that's still muted after 600ms almost
        // always stays muted until the audio session is reset, which we
        // already handle via the pre-warmup branch in startRecording.
        await new Promise((resolve) => {
          let done = false;
          const finish = () => { if (done) return; done = true; try { track.onunmute = null; } catch {} resolve(); };
          try { track.onunmute = finish; } catch {}
          setTimeout(finish, 600);
        });
      }
      // Prime AudioContext to force the OS audio stack to actually deliver
      // frames — the missing piece that was letting SR.start() "succeed"
      // with zero audio after a screen lock on iOS PWA.
      await primeAudioContext(stream);
      return true;
    };

    // ── PRE-FLIGHT AUDIO DETECTION ─────────────────────────────────────
    // The fundamental Android Chrome bug: after a screen lock, even a
    // brand-new getUserMedia + brand-new AudioContext can deliver a stream
    // whose audio frames never flow. SR.start() then "succeeds" but is
    // starved of frames forever — until the OS audio session is reset
    // (which a screen lock cycle happens to do).
    //
    // We detect this BEFORE starting SR by sampling the AnalyserNode for
    // ~700ms. A working mic in even a "silent" room produces non-zero
    // frequency bins from the electronic noise floor + ambient sound.
    // Exact zeros across all bins for the entire window means the OS
    // audio session is stuck — and on the retry we use the documented
    // double-getUserMedia warmup trick to force-reset it without needing
    // a screen lock.
    const PREFLIGHT_MS = 700;
    const PREFLIGHT_MS_RETRY = 900;

    const waitForLiveAudio = (ms) => new Promise((resolve) => {
      const t0 = Date.now();
      const tick = () => {
        if (audioCtxRef.current && audioCtxRef.current.seenAudio()) return resolve(true);
        if (Date.now() - t0 >= ms) return resolve(!!(audioCtxRef.current && audioCtxRef.current.seenAudio()));
        setTimeout(tick, 50);
      };
      tick();
    });

    // Cancellation guard: the page can be hidden (screen-lock on iOS
    // proactively clears wantsRecordingRef in the visibilitychange
    // handler) at any await point below. If that happens mid-flight,
    // bail BEFORE creating SR / flipping isRecording true — otherwise
    // we'd re-establish recording state the user explicitly didn't
    // want and strand a held mic stream.
    const cancelled = () =>
      !wantsRecordingRef.current ||
      stoppingRef.current ||
      (typeof document !== "undefined" && document.visibilityState === "hidden");
    const abortStart = () => {
      // Don't release the held mic stream on iOS while the page is hidden
      // (screen-lock). The visibility-hidden handler deliberately keeps
      // micStreamRef + audioCtxRef alive so iOS can rewarm them on
      // foreground — releasing here would re-create the dead-session
      // problem we just fixed. The phase-change teardown / next
      // startRecording / forceStopRecognition will release when safe.
      const isHidden =
        typeof document !== "undefined" && document.visibilityState === "hidden";
      if (!(isIOS && isHidden)) {
        releaseHeldAudio();
      }
      wantsRecordingRef.current = false;
      setIsRecording(false);
    };

    // Unified pre-flight (post iteration-8 May 2026).
    //
    // The earlier iOS "fragile path" (post-lock 20s passive poll on the
    // held stream) was deleted because we now release the held stream
    // on both stopRecording AND visibility-hidden. So by the time we
    // reach here on iOS post-unlock, there is no held stream to poll —
    // we just do a fresh acquire + single-retry preflight, same as the
    // first-record case. This is the original happy path, untouched.
    pageWasHiddenRef.current = false;
    if (!(await acquireAndPrimeStream())) return;
    if (cancelled()) { abortStart(); return; }
    if (!isAndroid) {
      let live = await waitForLiveAudio(PREFLIGHT_MS);
      if (cancelled()) { abortStart(); return; }
      if (!live && wantsRecordingRef.current && !stoppingRef.current) {
        setMicDebug("⏳ Waking microphone…");
        releaseHeldAudio();
        await warmupAudioStack();
        if (cancelled()) { abortStart(); return; }
        if (!(await acquireAndPrimeStream())) return;
        if (cancelled()) { abortStart(); return; }
        live = await waitForLiveAudio(PREFLIGHT_MS_RETRY);
        if (cancelled()) { abortStart(); return; }
        if (!live) {
          setMicDebug("⚠ Mic slow to wake — trying anyway…");
        }
      }
    }

    // Helper: spin up a fresh SR instance and start it.
    const launchRecognition = () => {
      const rec = createRecognition();
      if (!rec) return null;
      recognitionRef.current = rec;
      // ALWAYS reset gotAudioRef before each rec.start() — it is now an
      // SR-only signal (set by SR.onsoundstart/onspeechstart/onresult)
      // and must measure THIS SR session, not the previous one. A stale
      // `true` from a prior turn would let waitForAudio() resolve
      // immediately and silently disable the watchdog — the exact bug
      // the iOS fix is trying to address.
      gotAudioRef.current = false;
      try {
        rec.start();
      } catch (err) {
        recognitionRef.current = null;
        setMicDebug(`❌ start() failed: ${err.name || ""}`);
        return null;
      }
      return rec;
    };

    const waitForAudio = (ms) => new Promise((resolve) => {
      const t0 = Date.now();
      const tick = () => {
        if (gotAudioRef.current) return resolve(true);
        if (Date.now() - t0 >= ms) return resolve(false);
        setTimeout(tick, 100);
      };
      tick();
    });

    if (cancelled()) { abortStart(); return; }
    let rec = launchRecognition();
    if (!rec) {
      releaseHeldAudio();
      wantsRecordingRef.current = false;
      setError("Recording could not start. Please try again.");
      return;
    }
    // Final cancellation check: if the page hid between launchRecognition
    // (which started SR) and this point, tear the just-launched SR down
    // before it has a chance to emit any state changes.
    if (cancelled()) {
      try { rec.onresult = null; rec.onerror = null; rec.onend = null; } catch {}
      try { rec.abort(); } catch {}
      recognitionRef.current = null;
      abortStart();
      return;
    }
    setIsRecording(true);
    // Identity token: bind the watchdog to the specific recognition
    // instance it launched. If anything else replaces recognitionRef
    // during the wait (e.g. rec.onend's auto-restart from
    // restartRecognition), the watchdog's teardown branch must NOT
    // clobber the replacement instance — it should silently bail.
    const myRec = rec;

    // ── POST-START WATCHDOG (cross-platform, two failure modes) ───────
    //
    // Failure mode A — Android: SR's audio path is starved AND the mic
    // itself isn't delivering frames. Both gotAudioRef (SR signal) and
    // the analyser go cold. Recovery = full stream + AudioContext reset
    // via the documented double-getUserMedia warmup trick.
    //
    // Failure mode B — iOS: SR uses Apple's native dictation engine,
    // which has its OWN audio path independent of getUserMedia. After
    // a screen lock that engine can get stuck delivering zero frames to
    // SR even though our held getUserMedia stream is healthy and the
    // analyser pump is happily detecting the user's voice. Recovery on
    // iOS is JUST to recreate the SR instance — touching the mic stream
    // or AudioContext doesn't help (they're already fine).
    //
    // Detection has to handle both. The signal we trust is:
    //   "SR has been silent for 3.5s AND the analyser has seen the user
    //    speak recently (within 1s)."
    // If the analyser is also silent, the user just isn't speaking yet —
    // no SR problem to fix. If the analyser sees audio but SR doesn't,
    // SR is the broken party regardless of platform.
    const SR_SILENT_MS = 3500;
    const RECENT_AUDIO_MS = 1000;

    const recoverSr = async (badRec) => {
      // Tear down the silent SR instance.
      try { badRec.onresult = null; badRec.onerror = null; badRec.onend = null; } catch {}
      try { badRec.abort(); } catch {}
      recognitionRef.current = null;
      // PLATFORM SPLIT (May 2026, post-real-device-testing):
      //
      // Android: the active double-getUserMedia warmup is what un-sticks
      //   Chrome's SR engine. Keep the original release + warmup + acquire
      //   + preflight flow.
      //
      // iOS: real-device testing showed that active warmup/acquire cycles
      //   while iOS is recovering an audio session actively prolong the
      //   dead window (iOS resets its internal recovery timer on every
      //   gUM call). When SR has gone silent mid-turn the right move is
      //   passive: try resume() on the held AudioContext, then poll the
      //   analyser, and only re-launch SR once we see live frames (or
      //   the budget ends). No fresh gUM unless the held stream is gone.
      //   NOTE: this recovery path only runs DURING a recording turn,
      //   when acquireAndPrimeStream has just held a fresh stream for
      //   us. Iteration 8 (May 2026) removed the between-turns held
      //   stream, but within a turn we still hold and this path is
      //   still the right recovery strategy.
      setMicDebug("⏳ Mic stuck — recovering audio…");
      if (isIOS) {
        try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch {}
        if (!micStreamRef.current || !audioCtxRef.current) {
          if (!(await acquireAndPrimeStream())) { setIsRecording(false); return null; }
          if (cancelled()) { abortStart(); return null; }
        } else {
          try {
            const ctx = audioCtxRef.current.ctx;
            if (ctx && ctx.state === "suspended") await ctx.resume();
          } catch {}
        }
        const MAX_WAIT_MS = 15000;
        const t0 = Date.now();
        let live = false;
        while (Date.now() - t0 < MAX_WAIT_MS) {
          if (
            audioCtxRef.current &&
            (audioCtxRef.current.seenRecentAudio(500) ||
              audioCtxRef.current.seenAudio())
          ) { live = true; break; }
          await new Promise((r) => setTimeout(r, 250));
          if (cancelled()) { abortStart(); return null; }
          const remaining = Math.ceil((MAX_WAIT_MS - (Date.now() - t0)) / 1000);
          setMicDebug(`⏳ Recovering microphone… ${remaining}s remaining`);
        }
        setMicDebug(live ? "" : "⚠ Mic still waking — trying anyway, speak now");
      } else {
        // Android path unchanged.
        releaseHeldAudio();
        await warmupAudioStack();
        if (cancelled()) { abortStart(); return null; }
        if (!(await acquireAndPrimeStream())) {
          setIsRecording(false);
          return null;
        }
        if (cancelled()) { abortStart(); return null; }
      }
      const fresh = launchRecognition();
      if (!fresh) {
        // Always release the held stream + AudioContext when we abandon
        // the recording attempt — otherwise on iOS the mic indicator
        // stays on and the AudioContext/stream leak across the rest of
        // the session even though wantsRecording is now false.
        releaseHeldAudio();
        wantsRecordingRef.current = false;
        setIsRecording(false);
        setError("Microphone is still waking up. Wait a moment and tap Start Recording again — your answer so far is preserved.");
      }
      return fresh;
    };

    const ok = await waitForAudio(SR_SILENT_MS);
    // Use seenRecentSpeech (loud-bins threshold), NOT seenRecentAudio
    // (any non-zero) — noise floor alone would otherwise count as "user
    // spoke" and reset SR during genuine pauses.
    const userSpoke = !!(audioCtxRef.current && audioCtxRef.current.seenRecentSpeech(RECENT_AUDIO_MS));
    if (
      !ok &&
      userSpoke &&
      wantsRecordingRef.current &&
      !stoppingRef.current &&
      recognitionRef.current === myRec
    ) {
      setMicDebug(isIOS ? "⏳ Restarting speech engine…" : "⏳ Mic stuck silent — resetting audio…");
      rec = await recoverSr(myRec);
      if (!rec) return;
      const myRec2 = rec;
      const ok2 = await waitForAudio(SR_SILENT_MS);
      const userSpoke2 = !!(audioCtxRef.current && audioCtxRef.current.seenRecentSpeech(RECENT_AUDIO_MS));
      if (
        !ok2 &&
        userSpoke2 &&
        wantsRecordingRef.current &&
        !stoppingRef.current &&
        recognitionRef.current === myRec2
      ) {
        // Second attempt also silent while user is speaking — surface a
        // platform-appropriate recoverable message and leave the engine
        // running so any late-arriving frames still land. On iOS this
        // genuinely sometimes requires a screen lock/unlock; the
        // message tells the user that explicitly instead of leaving
        // them to discover it.
        setMicDebug("⚠ Speech engine silent — try again");
        setError(
          isIOS
            ? "Your iPhone's speech engine got stuck after the screen lock. Tap Stop, then Record again. If that still doesn't work, briefly lock and unlock your phone — your answer so far is preserved."
            : "Microphone is still waking up. Wait a moment and tap Start Recording again — your answer so far is preserved."
        );
      }
    }
    } finally {
      startingRecordingRef.current = false;
    }
  };

  const stopRecording = () => {
    unlockSpeech();
    unlockSilenceAudio();
    if (submittingRef.current) return;
    stoppingRef.current = true;
    wantsRecordingRef.current = false;
    setIsRecording(false);
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    const rec = recognitionRef.current;

    // Gentle stop first — this lets the engine emit any final result it had
    // buffered. We do NOT null the handlers yet so onresult / onend can run.
    if (rec) {
      try { rec.stop(); } catch {}
    }

    // Wait briefly for the engine to flush, then capture and finalize.
    setTimeout(() => {
      // Now hard-kill the recognition instance.
      if (rec) {
        try { rec.onresult = null; rec.onerror = null; rec.onend = null; } catch {}
        try { rec.abort(); } catch {}
      }
      recognitionRef.current = null;

      // Release held mic + AudioContext on every stop, every platform
      // (iteration 8, May 2026). On iOS this is critical for two
      // reasons: (1) the held stream pins the OS audio session in the
      // loud PlayAndRecord category, making subsequent TTS unbearably
      // loud from Q2 onward; (2) the held stream prevents iOS from
      // releasing the session, forcing a 20s passive-poll recovery on
      // every Record tap after a lock. Releasing here lets the session
      // revert to the quiet playback category before the next TTS, AND
      // lets iOS fully reset on lock so the next Record acquires
      // cleanly. Trade-off: +200-500ms latency on every Record tap.
      releaseHeldAudio();
      const captured = (transcriptRef.current || finalChunksRef.current || "").trim();
      setTranscript("");
      transcriptRef.current = "";
      // Clear the finalized buffer so the next turn starts clean. Without
      // this, a partial finalChunksRef from this turn would seed the next
      // turn's recording. (Mirror of the same clear in submitAnswer.)
      finalChunksRef.current = "";
      stoppingRef.current = false;

      setMicDebug("");

      // If the user has disabled the review step AND we captured something,
      // submit immediately for a faster, conversational flow.
      if (!reviewBeforeSendRef.current && captured) {
        submitAnswer(captured);
        return;
      }

      // Otherwise open the review box so the user can edit / type a fallback.
      setPendingAnswer(captured);
      if (!captured) {
        setError("I couldn't hear an answer — type it below or re-record.");
      }
    }, 500);
  };

  const sendPendingAnswer = () => {
    const text = pendingAnswer.trim();
    if (!text) return;
    setPendingAnswer("");
    submitAnswer(text);
  };

  const discardPendingAnswer = () => {
    setPendingAnswer("");
    setError("");
    setMicDebug("");
  };

  const replayLastReply = () => {
    unlockSpeech();
    unlockSilenceAudio();
    const lastAssistant = [...messagesRef.current].reverse().find((m) => m.role === "assistant");
    if (!lastAssistant) return;
    setIsSpeaking(true);
    speak(lastAssistant.content, () => {
      setIsSpeaking(false);
      resetAudioSessionAfterTTS();
    }, interviewer);
  };

  const endSessionAndEvaluate = async (endedReason = 'user_ended') => {
    // Sync guard: ref check beats useState here (state updates are async,
    // so isEvaluating can be stale when the timer fires alongside a click).
    if (endingRef.current) return;
    endingRef.current = true;
    if (isEvaluating) return;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    // Hard-kill the mic regardless of `isRecording` — the React state can lag
    // behind the actual SpeechRecognition lifecycle, and the previous
    // implementation only aborted when isRecording was true. That left the
    // recognition's onend handler free to auto-restart the mic after the
    // 30-min timer fired, which is exactly the bug the user reported.
    forceStopRecognition();

    // Capture the session ID + duration up front; we may end early on timeout.
    const sessionId = currentSessionId;
    const durationSeconds = sessionStartedAt
      ? Math.max(0, Math.floor((Date.now() - sessionStartedAt) / 1000))
      : 0;

    if (messagesRef.current.length === 0) {
      setPhase("ended");
      // Empty session: still close it out so it doesn't sit "active" forever.
      if (sessionId) {
        try {
          await endInterviewSession({
            sessionId,
            durationSeconds,
            endedReason: 'abandoned',
            role, experience, tone,
            modelUsed: 'sonnet',
          });
          loadUsageInfo().then(setUsage).catch(() => {});
        } catch (e) { console.warn('end_interview failed:', e?.message || e); }
      }
      return;
    }
    setIsEvaluating(true);
    setError("");
    setEvaluatingStage("Reviewing the conversation…");
    setPhase("ended");

    let parsed = null;
    try {
      const interviewerObj = INTERVIEWERS.find((i) => i.id === interviewer) || INTERVIEWERS[0];
      const transcriptForEval = messagesRef.current
        .map((m) => `${m.role === "assistant" ? interviewerObj.name : "Candidate"}: ${m.content}`)
        .join("\n\n");
      const userMessage = [{ role: "user", content: `Here is the full interview transcript:\n\n${transcriptForEval}\n\nNow produce the JSON exactly as instructed.` }];

      // Five parallel calls: one per category + one meta-analysis. Wall-clock
      // becomes ~max(callTime), not sum, and each call generates a much
      // smaller JSON than the old single mega-call. Promise.allSettled lets
      // us recover gracefully if one call fails — we still show whatever the
      // other four returned rather than failing the entire evaluation.
      const settled = await Promise.allSettled([
        callClaude(userMessage, buildMetaEvalPrompt({ role, experience, interviewerName: interviewerObj.name, durationSeconds }), 1800, { sessionId, callKind: 'eval_meta' }),
        ...CATEGORIES_FOR_EVAL.map(cat =>
          callClaude(userMessage, buildCategoryEvalPrompt(cat, role, experience, interviewerObj.name, durationSeconds), 1500, { sessionId, callKind: 'eval_category' })
        ),
      ]);

      const safeParseJson = (txt) => {
        if (!txt) return null;
        const m = String(txt).match(/\{[\s\S]*\}/);
        if (!m) return null;
        try { return JSON.parse(m[0]); } catch { return null; }
      };

      const meta = settled[0].status === 'fulfilled' ? safeParseJson(settled[0].value) : null;
      const cats = CATEGORIES_FOR_EVAL.map((cat, i) => {
        const r = settled[i + 1];
        if (r.status !== 'fulfilled') return { name: cat.name, dimensions: [] };
        const p = safeParseJson(r.value);
        if (!p || !Array.isArray(p.dimensions)) return { name: cat.name, dimensions: [] };
        return p;
      });

      const totalDims = cats.reduce((acc, c) => acc + c.dimensions.length, 0);
      if (!meta && totalDims === 0) {
        throw new Error("Couldn't generate evaluation. Please try ending again.");
      }

      // Belt-and-braces: even though the eval prompt enforces the duration
      // cap, clamp every dimension client-side too so a model drift can never
      // produce a 9.5/10 on a 60-second session.
      const durationBand = getDurationBand(durationSeconds);
      if (durationBand.cap != null) {
        for (const cat of cats) {
          for (const d of cat.dimensions) {
            const s = typeof d.score === 'number' ? d.score
              : (typeof d.score === 'string' ? parseFloat(d.score) : NaN);
            if (isFinite(s) && s > durationBand.cap) {
              d.score = durationBand.cap;
            }
          }
        }
      }

      // Weighted overall score — Thinking and Role Specific Performance count
      // ~20% more than Communication and Presence for technical roles, per
      // the original scoring rubric.
      let weightedSum = 0, totalWeight = 0;
      for (const cat of cats) {
        const w = (CATEGORIES_FOR_EVAL.find(c => c.name === cat.name)?.weight) ?? 1.0;
        for (const d of cat.dimensions) {
          const s = typeof d.score === 'number' ? d.score
            : (typeof d.score === 'string' ? parseFloat(d.score) : NaN);
          if (isFinite(s)) { weightedSum += s * w; totalWeight += w; }
        }
      }
      const overallScore = totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 10) / 10 : null;
      const readiness = overallScore == null ? "Developing"
        : overallScore <= 3.0 ? "Beginner"
        : overallScore <= 5.5 ? "Developing"
        : overallScore <= 7.9 ? "Interview Ready"
        : "Strong Candidate";

      parsed = {
        overallScore,
        readiness,
        headline: meta?.headline || "Thanks for the session — here's how it went.",
        categories: cats,
        fillerWords: meta?.fillerWords || null,
        bestAnswer: meta?.bestAnswer || null,
        weakestAnswer: meta?.weakestAnswer || null,
        topPriorities: Array.isArray(meta?.topPriorities) ? meta.topPriorities : [],
        readinessVerdict: meta?.readinessVerdict || null,
        // Duration metadata so the cockpit, history, and FeedbackReport can
        // render a "Practice depth" badge without recomputing the band.
        durationSeconds,
        depthBand: { key: durationBand.key, label: durationBand.label, cap: durationBand.cap },
      };
      setEvaluation(parsed);
    } catch (e) {
      setError("Couldn't generate evaluation: " + e.message);
    } finally {
      setIsEvaluating(false);
    }

    // Persist the session (with whatever scores we got, if any) to Supabase.
    if (sessionId) {
      try {
        const overall = parsed
          ? (typeof parsed.overallScore === 'number' ? parsed.overallScore
              : typeof parsed.score === 'number' ? parsed.score : null)
          : null;
        console.log('[end_interview] calling RPC', { sessionId, durationSeconds, endedReason });
        const result = await endInterviewSession({
          sessionId,
          durationSeconds,
          endedReason,
          overallScore: overall,
          feedback: parsed,
          transcript: messagesRef.current,
          dimensionScores: parsed ? extractDimensionScores(parsed) : null,
          fillerWordCount: parsed?.fillerWords?.total ?? null,
          role, experience, tone,
          modelUsed: 'sonnet',
        });
        console.log('[end_interview] RPC result:', result);
        track('interview_completed', {
          duration_seconds: durationSeconds,
          ended_reason: endedReason,
          overall_score: overall,
          role, experience,
        });
        loadUsageInfo().then((u) => { console.log('[usage] reloaded:', u); setUsage(u); }).catch(() => {});
        // Tell the cockpit to refetch its session list so the row that just
        // closed out (and any score we just computed) shows up immediately
        // when the user navigates back, without a manual refresh.
        setSessionsRefreshKey(k => k + 1);
      } catch (e) {
        console.error('[end_interview] FAILED:', e);
        // Surface the failure so we can see WHY the row isn't closing.
        setError("Couldn't close out the session: " + (e?.message || e) + " — your interview may still be counted. Please refresh.");
      }
    } else {
      console.warn('[end_interview] no sessionId, nothing to close out');
    }

    // Clear active session refs so the timer doesn't keep ticking — but ONLY
    // if the user hasn't already started another interview while this eval was
    // running in the background. Without this guard, the tail of an old eval
    // (after the user clicked "Back to cockpit" and started a fresh session)
    // would clobber the new session's currentSessionId/timer state.
    if (!sessionId || currentSessionIdRef.current === sessionId) {
      setCurrentSessionId(null);
      setSessionStartedAt(null);
      setSecondsRemaining(null);
    } else {
      console.log('[end_interview] skipping cleanup — a newer session is active', {
        finishedSessionId: sessionId,
        currentSessionId: currentSessionIdRef.current,
      });
    }
  };

  const downloadFeedbackPDF = () => {
    if (!evaluation) return;
    const dateStr = new Date().toLocaleString();
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const list = (items) => (items || []).map((it) => `<li>${esc(it)}</li>`).join("");
    const overall = evaluation.overallScore ?? evaluation.score ?? 0;
    const readiness = evaluation.readiness || "";
    // Tolerant score parser — handles plain numbers, strings, and "8.5/10" hallucinations
    const parseScore = (s) => {
      if (typeof s === "number" && isFinite(s)) return s;
      const m = String(s ?? "").match(/-?\d+(\.\d+)?/);
      return m ? parseFloat(m[0]) : NaN;
    };
    const fmtScore = (s) => {
      const n = parseScore(s);
      if (!isFinite(n)) return "—";
      return Number.isInteger(n) ? String(n) : n.toFixed(1);
    };
    const categories = Array.isArray(evaluation.categories)
      ? evaluation.categories
      : (Array.isArray(evaluation.breakdown)
          ? [{ name: "Skill Breakdown", dimensions: evaluation.breakdown.map((b) => ({
              name: b.name, score: b.score,
              feedback: [...(b.wentWell || []), ...(b.improve || [])].join(" • ")
            })) }]
          : []);
    const fillerWords = evaluation.fillerWords || null;
    const bestAnswer = evaluation.bestAnswer || null;
    const weakestAnswer = evaluation.weakestAnswer || null;
    const topPriorities = Array.isArray(evaluation.topPriorities) ? evaluation.topPriorities : [];
    const verdict = evaluation.readinessVerdict || null;

    const categoriesHTML = categories.map((cat) => {
      const dims = Array.isArray(cat.dimensions) ? cat.dimensions : [];
      const dimsHTML = dims.map((d) => {
        const n = parseScore(d.score);
        const pct = Math.max(0, Math.min(100, ((isFinite(n) ? n : 0) / 10) * 100));
        return `
          <div class="dim">
            <div class="dim-head">
              <span class="dim-name">${esc(d.name)}</span>
              <span class="dim-score">${esc(fmtScore(d.score))}</span>
            </div>
            <div class="bar"><div class="bar-fill" style="width:${pct}%"></div></div>
            ${d.feedback ? `<p class="dim-feedback">${esc(d.feedback)}</p>` : ""}
          </div>`;
      }).join("");
      return `
        <div class="cat">
          <h3>${esc(cat.name)}</h3>
          ${dimsHTML}
        </div>`;
    }).join("");

    const fillerHTML = fillerWords ? `
      <h2>Filler Word Count</h2>
      <div class="insight">
        <div class="filler-total"><strong>${esc(fillerWords.total ?? 0)}</strong> total filler words</div>
        ${Array.isArray(fillerWords.breakdown) && fillerWords.breakdown.length > 0
          ? `<div class="filler-grid">${fillerWords.breakdown.map((f) =>
              `<span class="chip">"${esc(f.word)}" × <strong>${esc(f.count ?? 0)}</strong></span>`
            ).join("")}</div>`
          : ""}
        ${fillerWords.comment ? `<p class="insight-text">${esc(fillerWords.comment)}</p>` : ""}
      </div>` : "";

    const bestWeakHTML = (bestAnswer || weakestAnswer) ? `
      <h2>Best & Weakest Answer</h2>
      <div class="bw-grid">
        ${bestAnswer ? `
          <div class="insight insight-good">
            <div class="insight-label good">★ BEST ANSWER</div>
            ${bestAnswer.questionSummary ? `<div class="insight-q">${esc(bestAnswer.questionSummary)}</div>` : ""}
            ${bestAnswer.why ? `<p class="insight-text">${esc(bestAnswer.why)}</p>` : ""}
          </div>` : ""}
        ${weakestAnswer ? `
          <div class="insight insight-warn">
            <div class="insight-label warn">⚠ WEAKEST ANSWER</div>
            ${weakestAnswer.questionSummary ? `<div class="insight-q">${esc(weakestAnswer.questionSummary)}</div>` : ""}
            ${weakestAnswer.why ? `<p class="insight-text">${esc(weakestAnswer.why)}</p>` : ""}
          </div>` : ""}
      </div>` : "";

    const prioritiesHTML = topPriorities.length > 0 ? `
      <h2>Top 3 Priorities Before Next Interview</h2>
      <div class="insight">
        ${topPriorities.slice(0, 3).map((p, i) => `
          <div class="priority">
            <span class="priority-num">${i + 1}</span>
            <span class="priority-text">${esc(p)}</span>
          </div>
        `).join("")}
      </div>` : "";

    const verdictHTML = verdict && (verdict.readyFor || verdict.notReadyFor || verdict.keyBlocker) ? `
      <h2>Interview Readiness Verdict</h2>
      <div class="insight">
        ${verdict.readyFor ? `<div class="verdict-row"><div class="verdict-label good">✓ READY FOR</div><p class="insight-text">${esc(verdict.readyFor)}</p></div>` : ""}
        ${verdict.notReadyFor ? `<div class="verdict-row"><div class="verdict-label bad">✕ NOT YET READY FOR</div><p class="insight-text">${esc(verdict.notReadyFor)}</p></div>` : ""}
        ${verdict.keyBlocker ? `<div class="verdict-row"><div class="verdict-label warn">🔑 KEY BLOCKER</div><p class="insight-text">${esc(verdict.keyBlocker)}</p></div>` : ""}
      </div>` : "";

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Nail That Interview — Feedback</title>
<style>
  @page { margin: 16mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #111; line-height: 1.5; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .meta { color: #555; font-size: 12px; margin-bottom: 14px; }
  .scale-note { color: #888; font-size: 11px; font-style: italic; margin-bottom: 18px; }
  .top { display: flex; align-items: center; gap: 18px; margin: 14px 0 18px; page-break-inside: avoid; }
  .score { width: 92px; height: 92px; border-radius: 50%; background: linear-gradient(135deg,#f97316,#dc2626); color: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; flex: none; }
  .score b { font-size: 24px; font-weight: 800; line-height: 1; }
  .score span { font-size: 9px; opacity: 0.85; margin-top: 3px; letter-spacing: 0.5px; }
  .headline { font-size: 14.5px; color: #222; }
  .badge { display: inline-block; padding: 4px 11px; border-radius: 99px; background: #fef3c7; color: #92400e; font-size: 12px; font-weight: 700; margin-top: 6px; }
  h2 { font-size: 13px; margin: 22px 0 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #b91c1c; border-bottom: 1px solid #eee; padding-bottom: 4px; }
  .cat { margin-bottom: 14px; padding: 10px 12px; border: 1px solid #eee; border-radius: 8px; page-break-inside: avoid; }
  .cat h3 { font-size: 13px; color: #b45309; margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid #f1f1f1; text-transform: uppercase; letter-spacing: 0.4px; }
  .dim { padding: 6px 0 8px; }
  .dim + .dim { border-top: 1px dashed #f1f1f1; margin-top: 6px; }
  .dim-head { display: flex; justify-content: space-between; align-items: baseline; font-size: 13px; font-weight: 700; }
  .dim-name { color: #222; }
  .dim-score { color: #b91c1c; font-size: 14px; font-weight: 800; }
  .bar { height: 6px; background: #f1f1f1; border-radius: 99px; overflow: hidden; margin: 5px 0 6px; }
  .bar-fill { height: 100%; background: linear-gradient(90deg,#f97316,#dc2626); }
  .dim-feedback { font-size: 12px; color: #444; margin: 4px 0 0; line-height: 1.5; }
  .insight { padding: 10px 12px; border: 1px solid #eee; border-radius: 8px; page-break-inside: avoid; }
  .insight-text { font-size: 12px; color: #333; margin: 6px 0 0; line-height: 1.5; }
  .insight-good { border-color: #bbf7d0; background: #f0fdf4; }
  .insight-warn { border-color: #fde68a; background: #fffbeb; }
  .insight-label { font-size: 10.5px; font-weight: 800; letter-spacing: 0.6px; margin-bottom: 4px; }
  .insight-label.good { color: #166534; }
  .insight-label.warn { color: #92400e; }
  .insight-q { font-size: 12.5px; font-weight: 700; color: #222; margin-bottom: 4px; }
  .filler-total { font-size: 13px; margin-bottom: 8px; }
  .filler-total strong { color: #b91c1c; font-size: 18px; }
  .filler-grid { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
  .chip { display: inline-block; padding: 3px 9px; border-radius: 99px; background: #f3f4f6; border: 1px solid #e5e7eb; font-size: 11.5px; color: #444; }
  .bw-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .priority { display: flex; gap: 10px; align-items: flex-start; padding: 8px 0; }
  .priority + .priority { border-top: 1px solid #f1f1f1; }
  .priority-num { flex: none; width: 22px; height: 22px; border-radius: 50%; background: linear-gradient(135deg,#f97316,#dc2626); color: #fff; font-size: 11px; font-weight: 800; display: flex; align-items: center; justify-content: center; }
  .priority-text { font-size: 12.5px; color: #222; line-height: 1.5; flex: 1; }
  .verdict-row { padding: 6px 0; }
  .verdict-row + .verdict-row { border-top: 1px solid #f1f1f1; }
  .verdict-label { font-size: 10.5px; font-weight: 800; letter-spacing: 0.5px; margin-bottom: 3px; }
  .verdict-label.good { color: #166534; }
  .verdict-label.bad { color: #991b1b; }
  .verdict-label.warn { color: #92400e; }
  .footer { margin-top: 24px; font-size: 11px; color: #999; text-align: center; }
  .disclaimer { margin-top: 14px; padding: 10px 14px; border-top: 1px solid #eee; font-size: 10.5px; color: #888; text-align: center; line-height: 1.5; font-style: italic; }
</style></head><body>
  <h1>Nail That Interview — Session Feedback</h1>
  <div class="meta">${esc(role)} · ${esc(experience)} · ${esc(dateStr)}</div>
  <div class="scale-note">All scores are out of 10.</div>
  <div class="top">
    <div class="score"><b>${esc(fmtScore(overall))}</b><span>OVERALL</span></div>
    <div>
      <div class="headline">${esc(evaluation.headline)}</div>
      ${readiness ? `<div class="badge">Readiness: ${esc(readiness)}</div>` : ""}
    </div>
  </div>
  <h2>Multi-Dimensional Breakdown</h2>
  ${categoriesHTML}
  ${fillerHTML}
  ${bestWeakHTML}
  ${prioritiesHTML}
  ${verdictHTML}
  <div class="footer">Generated by Nail That Interview · ${esc(dateStr)}</div>
  <div class="disclaimer">This is an AI tool for practice. We do not guarantee job offers and are not responsible for career outcomes.</div>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Please allow pop-ups to download the PDF."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  const reset = () => {
    stopAllSpeech();
    // Use the full force-stop helper so the held MediaStream is also
    // released — otherwise the OS-level mic indicator (and audio device
    // grab) lingers after abandon / Home / signout flows.
    forceStopRecognition();
    // If we're abandoning a live session, close it out in the DB so the
    // row doesn't sit "active" and the quota is counted correctly.
    abandonInterviewIfActive();
    // Wipe transcript buffers — Home / new-session must NOT inherit a
    // mid-utterance draft from the abandoned session via startRecording's
    // screen-lock rescue path. (forceStopRecognition already clears these
    // but we keep the explicit writes for readability + defense.)
    transcriptRef.current = "";
    finalChunksRef.current = "";
    wantsRecordingRef.current = false;
    setPhase("setup"); setStep(0); setMessages([]);
    setTranscript(""); setError("");
    setIsThinking(false); setIsSpeaking(false); setIsRecording(false);
    setExchangeCount(0); setRole(""); setExperience(""); setSkills([]); setJobDescription("");
    setEvaluation(null); setIsEvaluating(false); setPendingAnswer("");
    // Note: resume is intentionally preserved across sessions.
    // Use the "Remove saved resume" button on the upload step to clear it.
  };

  const toneLabel = TONE_OPTIONS.find((t) => t.id === tone)?.label || "";
  const interviewerObj = INTERVIEWERS.find((i) => i.id === interviewer) || INTERVIEWERS[0];

  const signOut = async () => {
    // Close out any live session first so quota counting stays accurate.
    await abandonInterviewIfActive()
    // The onAuthStateChange listener handles the rest of the state cleanup.
    await supabase.auth.signOut()
  }

  // ── AUTH LOADING (initial session check only) ───────────────────────────────
  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#070c18',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#f1f5f9',
        fontSize: 16,
        fontFamily: 'sans-serif',
        gap: 12
      }}>
        <div style={{
          width: 20, height: 20,
          border: '2px solid #f97316',
          borderTop: '2px solid transparent',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        Loading...
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  // ── AUTH SCREEN (only when user explicitly clicks Sign in / Sign up) ────────
  if (authView) {
    return (
      <>
        <GlobalStyles />
        <AuthScreen
          initialView={authView}
          onViewPolicy={(slug) => {
            // Opening a policy from the auth screen closes auth (without
            // discarding pending Start/Subscribe intents — the user is
            // briefly stepping away to read a legal doc, not abandoning
            // their flow). The policy page's "Back" button returns them
            // to landing/cockpit, from which they can re-open signup.
            setAuthView(null);
            openPolicy(slug);
          }}
          onClose={() => {
            // Cancelling the auth screen also cancels any pending "Start" or
            // "Subscribe" action, otherwise a later sign-in (e.g. via the nav
            // button) would unexpectedly jump them into the setup wizard or
            // redirect them to a Lemon Squeezy checkout for a plan they no
            // longer want.
            pendingStartRef.current = false
            clearPendingSubscribeIntent()
            setAuthView(null)
          }}
        />
      </>
    )
  }

  // ── ADMIN DASHBOARD (internal-only, gated client+server) ────────────────────
  // Server-side check in api/_lib/admin.js is the real gate; this client
  // check just avoids rendering the page (and its data fetches) for non-
  // admins who arrive here via stale state.
  if (phase === "admin") {
    if (!user || !isAdminEmail(user?.email)) {
      // Bounce non-admins back to wherever makes sense.
      setPhase(user ? "cockpit" : "landing");
      return null;
    }
    return (
      <>
        <GlobalStyles />
        <AdminPage
          theme={theme}
          toggleTheme={toggleTheme}
          user={user}
          onBack={() => setPhase("cockpit")}
        />
      </>
    );
  }

  // ── COCKPIT (logged-in home) ────────────────────────────────────────────────
  if (phase === "cockpit" && user) {
    return (
      <Cockpit
        user={user}
        usage={usage}
        theme={theme}
        toggleTheme={toggleTheme}
        sessionsRefreshKey={sessionsRefreshKey}
        onStart={() => { setStep(0); setPhase("setup"); }}
        onSeePlans={() => {
          setPhase("landing");
          setTimeout(() => {
            const el = document.getElementById('pricing');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }, 80);
        }}
        onViewLanding={() => setPhase("landing")}
        onSignOut={signOut}
        onOpenAdmin={() => setPhase("admin")}
        onManageSubscription={handleManageSubscription}
        subscriptionBanner={subscriptionBanner}
        onDismissSubscriptionBanner={() => setSubscriptionBanner(null)}
        onRepeatRole={(s) => {
          if (s?.role) setRole(s.role);
          if (s?.experience) setExperience(s.experience);
          if (s?.job_description) setJobDescription(s.job_description); else setJobDescription("");
          if (s?.interviewer) setInterviewer(s.interviewer);
          if (s?.tone) setTone(s.tone);
          // We don't persist per-session selected skills, so reset to a clean
          // slate; the role-based suggestions will repopulate from the lookup.
          setSkills([]);
          setStep(0);
          setPhase("setup");
        }}
      />
    );
  }

  // ── LANDING SCREEN (public homepage) ────────────────────────────────────────
  if (phase === "landing") {
    return (
      <LandingPage
        onStart={() => {
          if (!user) {
            // Defer the start until after they finish signing up.
            pendingStartRef.current = true;
            setAuthView('signup');
            return;
          }
          setStep(0);
          setPhase("setup");
        }}
        onSubscribe={(planCode) => {
          if (!user) {
            // Need an account so the webhook can attach the subscription
            // to a real user_id. Stash the plan and resume after sign-up
            // (localStorage so the intent survives the email-confirmation
            // new-tab round-trip — see writePendingSubscribeIntent).
            writePendingSubscribeIntent(planCode);
            setAuthView('signup');
            return;
          }
          // Double-subscribe guard lives inside attemptSubscribe so the
          // same logic runs from the post-signup auto-redirect too.
          attemptSubscribe(planCode, user);
        }}
        onClaimPromo={() => {
          // Promo banner CTA. Routes to Starter Pass checkout with the
          // FRESHSTART discount code auto-applied (drops total to $0
          // when the code is still within Lemon's redemption cap).
          // Signed-out visitors get bounced to sign-up first; the
          // post-sign-in handler consumes the intent and runs the same
          // attemptSubscribe call. Stored in localStorage so the discount
          // code survives the email-confirmation new-tab hop.
          const intent = { planCode: PROMO_PLAN_CODE, discountCode: PROMO_DISCOUNT_CODE };
          if (!user) {
            writePendingSubscribeIntent(intent);
            setAuthView('signup');
            return;
          }
          attemptSubscribe(intent.planCode, user, { discountCode: intent.discountCode });
        }}
        theme={theme}
        toggleTheme={toggleTheme}
        user={user}
        onSignIn={() => setAuthView('login')}
        onSignUp={() => setAuthView('signup')}
        onSignOut={signOut}
        onGoToCockpit={user ? () => setPhase("cockpit") : undefined}
        onViewTerms={openTerms}
        onViewPrivacy={() => openPolicy("privacy")}
        onViewCookies={() => openPolicy("cookies")}
        onViewDisclaimer={() => openPolicy("disclaimer")}
      />
    );
  }

  // ── TERMS & CONDITIONS ──────────────────────────────────────────────────────
  if (phase === "terms") {
    return (
      <TermsPage
        theme={theme}
        toggleTheme={toggleTheme}
        user={user}
        onBack={() => setPhase(termsReturnPhase || (user ? "cockpit" : "landing"))}
      />
    );
  }

  // ── PRIVACY / COOKIES / DISCLAIMER (Termly-backed) ──────────────────────────
  // Each policy is rendered inline through the /api/termly-policy proxy so
  // the URL stays on our domain and the look matches the rest of the app.
  if (phase === "policy-privacy" || phase === "policy-cookies" || phase === "policy-disclaimer") {
    const policyMeta = {
      "policy-privacy":    { slug: "privacy",    title: "Privacy Policy" },
      "policy-cookies":    { slug: "cookies",    title: "Cookie Policy"  },
      "policy-disclaimer": { slug: "disclaimer", title: "Disclaimer"     },
    }[phase];
    return (
      <PolicyPage
        slug={policyMeta.slug}
        title={policyMeta.title}
        theme={theme}
        toggleTheme={toggleTheme}
        onBack={() => setPhase(termsReturnPhase || (user ? "cockpit" : "landing"))}
      />
    );
  }

  // ── SETUP SCREEN ────────────────────────────────────────────────────────────
  if (phase === "setup") {
    return (
      <div style={{
        minHeight: "100vh", background: "var(--bg)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: 24, fontFamily: "'Lora', serif",
        position: "relative",
      }}>
        <GlobalStyles />

        {/* Top bar: Home (left) + Theme toggle (right) */}
        <HomeButton
          onClick={() => {
            // If a session was created but never started (e.g. LLM bootstrap
            // failed and bumped us back to setup), close it out so the row
            // doesn't sit "active" and skew the quota.
            abandonInterviewIfActive();
            setPhase(user ? "cockpit" : "landing");
          }}
          style={{ position: "absolute", top: 16, left: 16 }}
        />

        <button
          onClick={toggleTheme}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          aria-label="Toggle theme"
          style={{
            position: "absolute", top: 16, right: 16,
            width: 38, height: 38, borderRadius: "50%",
            background: "var(--surface-2)", border: "1px solid var(--border-soft)",
            color: "var(--text-muted)", fontSize: 16, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 0, lineHeight: 1,
          }}
        >{theme === "dark" ? "☀️" : "🌙"}</button>

        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <Wordmark logoSize={88} fontSize={28} gap={8} />
          <p style={{ color: "var(--text-dimmer)", fontSize: 13, margin: "10px 0 0", fontStyle: "italic" }}>
            AI-powered · Resume-aware · Voice-first
          </p>
        </div>

        {/* Usage badge — shows current plan + interviews left */}
        {(() => {
          const u = describeUsage(usage);
          if (!u) return null;
          const tone = u.atLimit
            ? { bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.35)", color: "#f87171" }
            : { bg: "rgba(249,115,22,0.10)", border: "rgba(249,115,22,0.30)", color: "#f97316" };
          return (
            <div style={{
              width: "100%", maxWidth: 500, marginBottom: 14,
              padding: "10px 14px", borderRadius: 12,
              background: tone.bg, border: `1px solid ${tone.border}`,
              color: tone.color, fontSize: 12.5, fontFamily: "sans-serif",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 10,
            }}>
              <span>
                <strong style={{ fontWeight: 700 }}>{u.planLabel} plan</strong>
                {" · "}{u.label}
              </span>
              <span style={{ opacity: 0.85 }}>
                {u.sessionMinutes} min / interview
              </span>
            </div>
          );
        })()}

        {/* Wizard card */}
        <div style={{
          width: "100%", maxWidth: 500,
          background: "var(--card)", border: "1px solid var(--border-soft)",
          borderRadius: 22, padding: 30, backdropFilter: "blur(16px)",
        }}>
          <div style={{ marginBottom: 26 }}>
            <ProgressDots current={step} total={TOTAL_STEPS} />
          </div>

          {step === 0 && <StepRole role={role} setRole={setRole} experience={experience} setExperience={setExperience} skills={skills} setSkills={setSkills} jobDescription={jobDescription} setJobDescription={setJobDescription} onNext={() => setStep(1)} onBack={() => setPhase(user ? "cockpit" : "landing")} />}
          {step === 1 && (
            <StepTone
              tone={tone} setTone={setTone}
              interviewer={interviewer} setInterviewer={setInterviewer}
              onNext={() => {
                // Wait for the plan to load before deciding whether to go
                // to the resume step or straight into the interview — a
                // Power user clicking before usage resolves would otherwise
                // skip step 2 entirely.
                if (!usageReady) return;
                if (canUploadResume) setStep(2); else startInterview();
              }}
              onBack={() => setStep(0)}
            />
          )}
          {step === 2 && canUploadResume && (
            <StepResume
              resumeName={resumeName}
              hasResume={!!resumeText}
              uploadStatus={uploadStatus}
              resumeInsights={resumeInsights}
              onUpload={handleResumeUpload}
              onClear={clearSavedResume}
              onNext={startInterview}
              onBack={() => setStep(1)}
            />
          )}

          {error && (
            <p style={{ color: "#f87171", fontSize: 13, marginTop: 14, textAlign: "center", fontFamily: "sans-serif" }}>
              {error}
            </p>
          )}
        </div>

        <p style={{ color: "var(--text-darker)", fontSize: 11, marginTop: 14, fontFamily: "sans-serif" }}>
          Free to start · AI-powered
        </p>
        <p style={{
          color: "var(--text-darker)", fontSize: 11, marginTop: 6,
          fontFamily: "sans-serif", fontStyle: "italic", opacity: 0.85,
          maxWidth: 480, lineHeight: 1.5,
        }}>
          For practice and skill-building only. AI feedback is a guide, not a verdict.
        </p>

        {limitModal && (
          <LimitReachedModal
            type={limitModal}
            usage={usage}
            onClose={() => setLimitModal(null)}
            onSeePlans={() => {
              setPhase("landing");
              setTimeout(() => {
                const el = document.getElementById('pricing');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }, 80);
            }}
          />
        )}
      </div>
    );
  }

  // ── INTERVIEW SCREEN ────────────────────────────────────────────────────────
  return (
    <div style={{
      // Use dynamic viewport height (100dvh) so iOS Safari's collapsing
      // address bar doesn't break the sticky header / internal-scroll
      // contract. `minHeight: 100vh` is the fallback for browsers without
      // dvh support; modern browsers prefer the latter. `overflow: hidden`
      // on the wrapper guarantees the chat panel's `overflow-y: auto` is
      // the ONLY scroll on the page — without this, message-list growth
      // would scroll the whole document and drag the header off-screen.
      minHeight: "100vh",
      height: "100dvh",
      overflow: "hidden",
      background: "var(--bg)",
      display: "flex", flexDirection: "column",
      fontFamily: "'Lora', serif",
    }}>
      <GlobalStyles />

      {/* Header — sticky to the top of the flex column. On mobile (<=640px)
          the right-side cluster wraps onto its own row via flexWrap so the
          timer / interview-count / edit-mode / theme / quit controls each
          get an independent touch target instead of overlapping. */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", rowGap: 8, columnGap: 8,
        padding: isMobile ? "8px 12px" : "11px 18px",
        borderBottom: "1px solid var(--border-soft)",
        background: "var(--header-bg)", backdropFilter: "blur(12px)",
        position: "sticky", top: 0, zIndex: 10,
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: isMobile ? 8 : 12,
          minWidth: 0, flex: isMobile ? "1 1 100%" : "0 1 auto",
        }}>
          <HomeButton
            onClick={() => {
              window.speechSynthesis.cancel();
              // Going Home mid-interview = abandonment. Close the DB row so
              // the user's quota is counted and the row doesn't sit "active".
              abandonInterviewIfActive();
              setPhase(user ? "cockpit" : "landing");
            }}
            circleSize={isMobile ? 22 : 26}
            fontSize={isMobile ? 11 : 12}
          />
          <div style={{
            width: isMobile ? 30 : 36, height: isMobile ? 30 : 36, borderRadius: "50%",
            background: "linear-gradient(135deg,#f97316,#dc2626)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: isMobile ? 16 : 20, color: "#fff",
            boxShadow: "0 0 16px rgba(249,115,22,0.4)",
            flexShrink: 0,
          }}>{interviewerObj.avatar}</div>
          <div style={{ minWidth: 0, overflow: "hidden" }}>
            <div style={{
              color: "var(--text-strong)", fontSize: 13, fontWeight: 600,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {isMobile ? interviewerObj.name : `${interviewerObj.name} · AI Interviewer`}
            </div>
            {!isMobile && (
              <div style={{
                color: "var(--text-dimmer)", fontSize: 11, fontFamily: "sans-serif",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {role} · {experience} · {toneLabel}
              </div>
            )}
          </div>
        </div>
        <div style={{
          display: "flex", alignItems: "center",
          gap: isMobile ? 6 : 8,
          flexWrap: "wrap",
          justifyContent: isMobile ? "flex-end" : "flex-end",
          flex: isMobile ? "1 1 100%" : "0 1 auto",
        }}>
          {secondsRemaining != null && (() => {
            const warn = secondsRemaining <= 60;
            const danger = secondsRemaining <= 15;
            const palette = danger
              ? { bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.45)", color: "#ef4444" }
              : warn
                ? { bg: "rgba(234,179,8,0.15)", border: "rgba(234,179,8,0.45)", color: "#eab308" }
                : { bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.32)", color: "#22c55e" };
            return (
              <div
                title={`Session time remaining (${Number(usage?.session_minutes || 0)} min cap)`}
                style={{
                  padding: "3px 10px", borderRadius: 99, fontSize: 11, fontFamily: "sans-serif",
                  background: palette.bg, border: `1px solid ${palette.border}`,
                  color: palette.color, display: "inline-flex", alignItems: "center", gap: 4,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                ⏱ {fmtMMSS(secondsRemaining)}
              </div>
            );
          })()}
          {(() => {
            // Interview-number badge: shows "Interview X of Y" using the
            // user's plan allowance. Hidden until usage info loads.
            //
            // During an active interview the row hasn't been counted yet
            // (end_interview RPC fires when the user clicks End), so we add
            // 1 to make the badge read "you're on interview #N right now".
            // Once the interview ends, end_interview + loadUsageInfo bumps
            // `used` to include that session, so on the feedback screen we
            // must NOT add the +1 again — otherwise a user who just finished
            // their first session sees "Interview 2 of 15" instead of "1 of 15".
            const u = describeUsage(usage);
            if (!u || !u.total) return null;
            const offset = phase === "interview" ? 1 : 0;
            const num = Math.min(Math.max(1, u.used + offset), u.total);
            return (
              <div style={{
                padding: "3px 10px", borderRadius: 99, fontSize: 11, fontFamily: "sans-serif",
                background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.22)",
                color: "#f97316",
              }}>
                Interview {num} of {u.total}
              </div>
            );
          })()}
          <button
            onClick={() => setReviewBeforeSend((v) => !v)}
            title={reviewBeforeSend
              ? "Edit ON — tap to auto-send transcripts the moment you stop speaking"
              : "Auto-send ON — tap to edit transcripts before sending"}
            aria-label="Toggle edit-before-send"
            style={{
              height: 30, padding: "0 10px", borderRadius: 99,
              background: reviewBeforeSend ? "var(--surface-2)" : "rgba(34,197,94,0.15)",
              border: `1px solid ${reviewBeforeSend ? "var(--border-soft)" : "rgba(34,197,94,0.4)"}`,
              color: reviewBeforeSend ? "var(--text-muted)" : "#22c55e",
              fontSize: 11, cursor: "pointer", fontFamily: "sans-serif",
              display: "flex", alignItems: "center", gap: 5, lineHeight: 1,
            }}
          >
            <span style={{ fontSize: 13 }}>{reviewBeforeSend ? "✏️" : "⚡"}</span>
            <span>{reviewBeforeSend ? "Edit" : "Auto"}</span>
          </button>
          <button
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            aria-label="Toggle theme"
            style={{
              width: 30, height: 30, borderRadius: "50%",
              background: "var(--surface-2)", border: "1px solid var(--border-soft)",
              color: "var(--text-muted)", fontSize: 14, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, lineHeight: 1,
            }}
          >{theme === "dark" ? "☀️" : "🌙"}</button>
          {phase === "interview" && !isEvaluating && (
            <button
              onClick={() => {
                if (window.confirm('Quit this interview without getting feedback? This still counts toward your interview limit.')) {
                  reset();
                }
              }}
              title="Quit without generating feedback (still counts toward your limit)"
              style={{
                background: "var(--surface-2)", border: "1px solid var(--border-soft)",
                borderRadius: 8, color: "var(--text-dim)", padding: "5px 12px", fontSize: 12,
                cursor: "pointer", fontFamily: "sans-serif",
              }}
            >Quit</button>
          )}
          {!isMobile && (
            // Sign out lives in the cockpit nav on mobile — keeping it in
            // the interview header on phones forces overlap with Quit and
            // makes both buttons unhittable. Quit already exits the
            // interview cleanly; if the user truly wants to sign out
            // mid-interview, Quit → cockpit → Sign-out is one extra tap.
            <button
              onClick={signOut}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8,
                color: '#64748b',
                padding: '5px 10px',
                fontSize: 11,
                cursor: 'pointer',
                fontFamily: 'inherit',
                marginLeft: 6
              }}
            >
              Sign out
            </button>
          )}
        </div>
      </div>

      {/* Status strip */}
      <div style={{
        textAlign: "center", padding: "7px 20px",
        background: "rgba(249,115,22,0.03)", borderBottom: "1px solid rgba(249,115,22,0.07)",
      }}>
        <span style={{ color: "var(--text-dimmer)", fontSize: 12, fontFamily: "sans-serif" }}>
          {isSpeaking ? `🔊 ${interviewerObj.name} is speaking…`
            : isThinking ? `⏳ ${interviewerObj.name} is thinking…`
            : pendingAnswer ? "✏️ Review what was captured — fix any wrong words, then send"
            : isRecording ? (micDebug || "🔴 Listening — click stop when finished")
            : "🎤 Click Start Recording, speak, then click Stop"}
        </span>
      </div>

      {/* Chat */}
      <div style={{
        flex: 1, overflowY: "auto", padding: "22px 18px",
        maxWidth: 720, width: "100%", margin: "0 auto",
      }}>
        {messages.map((m, i) => <Bubble key={i} msg={m} isNew={i === newMsgIdx} avatar={interviewerObj.avatar} />)}

        {isThinking && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 38, height: 38, borderRadius: "50%",
              background: "linear-gradient(135deg,#f97316,#dc2626)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, color: "#fff",
            }}>{interviewerObj.avatar}</div>
            <div style={{ display: "flex", gap: 5 }}>
              {[0, 200, 400].map((d) => (
                <div key={d} style={{
                  width: 9, height: 9, borderRadius: "50%", background: "#f97316",
                  animation: `blink 1.2s ${d}ms infinite`,
                }} />
              ))}
            </div>
          </div>
        )}

        {transcript && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <div style={{
              maxWidth: "74%", padding: "12px 16px", borderRadius: "20px 4px 20px 20px",
              background: "rgba(249,115,22,0.09)", border: "1px solid rgba(249,115,22,0.22)",
              color: "#fb923c", fontSize: 14, fontStyle: "italic", fontFamily: "sans-serif",
            }}>{transcript}</div>
          </div>
        )}

        {pendingAnswer && (
          <div style={{
            background: "rgba(249,115,22,0.06)",
            border: "1px solid rgba(249,115,22,0.28)",
            borderRadius: 14, padding: 14, marginBottom: 12,
          }}>
            <div style={{
              color: "#fdba74", fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
              textTransform: "uppercase", marginBottom: 8, fontFamily: "sans-serif",
            }}>
              ✏️ Review your answer (edit if any words were misheard)
            </div>
            <textarea
              value={pendingAnswer}
              onChange={(e) => setPendingAnswer(e.target.value)}
              autoFocus
              placeholder="Type your answer here…"
              rows={Math.min(8, Math.max(3, pendingAnswer.split("\n").length + Math.floor(pendingAnswer.length / 70)))}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                background: "var(--input-bg)", border: "1px solid var(--surface-strong)",
                color: "var(--text-strong)", fontSize: 14, lineHeight: 1.55, resize: "vertical",
                fontFamily: "sans-serif", outline: "none", boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                onClick={discardPendingAnswer}
                style={{
                  padding: "8px 14px", borderRadius: 99, fontSize: 12.5,
                  background: "var(--surface)", color: "var(--text-faint)",
                  border: "1px solid var(--surface-strong)", cursor: "pointer",
                  fontFamily: "sans-serif",
                }}
              >🔁 Discard & re-record</button>
              <button
                onClick={() => { setReviewBeforeSend(false); sendPendingAnswer(); }}
                disabled={!pendingAnswer.trim()}
                title="Switches to Auto mode: sends this answer and won't show this review box again. You can switch back anytime with the Edit/Auto toggle at the top."
                style={{
                  padding: "8px 14px", borderRadius: 99, fontSize: 12.5, fontWeight: 600,
                  background: "rgba(34,197,94,0.12)", color: "#22c55e",
                  border: "1px solid rgba(34,197,94,0.4)",
                  cursor: pendingAnswer.trim() ? "pointer" : "not-allowed",
                  opacity: pendingAnswer.trim() ? 1 : 0.5,
                  fontFamily: "sans-serif",
                }}
              >⚡ Submit & don't ask again</button>
              <button
                onClick={sendPendingAnswer}
                disabled={!pendingAnswer.trim()}
                style={{
                  padding: "8px 16px", borderRadius: 99, fontSize: 12.5, fontWeight: 700,
                  background: "linear-gradient(135deg,#f97316,#dc2626)", color: "#fff",
                  border: "none", cursor: pendingAnswer.trim() ? "pointer" : "not-allowed",
                  opacity: pendingAnswer.trim() ? 1 : 0.5,
                  fontFamily: "sans-serif",
                }}
              >📤 Submit answer</button>
            </div>
          </div>
        )}

        {phase === "ended" && (
          <div style={{
            padding: 28, borderRadius: 20, marginTop: 16,
            background: "var(--surface)", border: "1px solid var(--border-soft)",
            animation: "fadeUp 0.5s ease",
          }}>
            {isEvaluating && (
              <div style={{ textAlign: "center", padding: "8px 0" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>📝</div>
                <p style={{ color: "var(--text-strong)", fontSize: 17, margin: 0, fontFamily: "sans-serif", fontWeight: 700 }}>
                  Please wait…
                </p>
                <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "6px 0 0", fontFamily: "sans-serif", lineHeight: 1.5 }}>
                  Your feedback is being generated.
                </p>
                <p style={{ color: "var(--text-faint)", fontSize: 12.5, margin: "10px 0 0", fontFamily: "sans-serif", minHeight: 18, transition: "opacity 0.3s" }}>
                  {evaluatingStage}
                </p>
                <p style={{ color: "var(--text-faint)", fontSize: 11.5, margin: "14px 0 0", fontFamily: "sans-serif" }}>
                  Scoring 12 parameters across 4 categories — usually 10–15 seconds.
                </p>
                <button
                  onClick={() => setPhase("cockpit")}
                  title="Continue to your cockpit while we finish generating feedback in the background"
                  style={{
                    marginTop: 18, padding: "9px 18px", borderRadius: 10,
                    background: "transparent", color: "var(--text-muted)",
                    border: "1px solid var(--border-soft)",
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                    fontFamily: "sans-serif",
                  }}
                >← Back to cockpit</button>
              </div>
            )}

            {evaluation && <FeedbackReport evaluation={evaluation} />}

            <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center", marginTop: 22 }}>
              {evaluation && (() => {
                const planCode = String(usage?.plan_code || 'free').toLowerCase();
                // PDF download is enabled for EVERY tier including Free
                // (May 2026): originally a Free→paid lever, but the
                // conversion model shifted from "lock & promise" to
                // "show & nudge" — Free's 2-interviews/month cap is the
                // primary upgrade pressure now, not feature gating.
                // Server-side mirror: plan_rules.features.pdf_download
                // = true on every row including `free` (set by
                // free_pdf_insights_may2026.sql). Variable kept
                // (instead of inlining true) so a future re-gate is a
                // one-line change.
                const canDownloadPdf = true;
                void planCode;
                if (canDownloadPdf) {
                  return (
                    <button onClick={downloadFeedbackPDF} style={{
                      padding: "11px 22px", borderRadius: 12,
                      background: "var(--surface-2)", color: "var(--text-strong)",
                      border: "1px solid rgba(249,115,22,0.4)",
                      fontSize: 13.5, fontWeight: 600, cursor: "pointer",
                      fontFamily: "sans-serif",
                    }}>⬇ Download feedback as PDF</button>
                  );
                }
                return (
                  <div style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
                    padding: "14px 18px", borderRadius: 12,
                    background: "var(--surface-2)",
                    border: "1px dashed rgba(249,115,22,0.4)",
                    maxWidth: 420,
                  }}>
                    <button
                      disabled
                      title="Downloadable PDF reports are available on every paid plan"
                      style={{
                        padding: "11px 22px", borderRadius: 12,
                        background: "transparent", color: "var(--text-faint)",
                        border: "1px solid var(--border-soft)",
                        fontSize: 13.5, fontWeight: 600, cursor: "not-allowed",
                        fontFamily: "sans-serif", opacity: 0.6,
                      }}>🔒 Download feedback as PDF</button>
                    <p style={{
                      color: "var(--text-faint)", fontSize: 12.5, lineHeight: 1.5,
                      margin: 0, textAlign: "center", fontFamily: "sans-serif",
                    }}>
                      Downloadable feedback reports are available on every paid plan. Upgrade to take your feedback with you, share it with a mentor, or revisit it before your next interview.
                    </p>
                    <button
                      onClick={() => {
                        setPhase("landing");
                        setTimeout(() => {
                          const el = document.getElementById('pricing');
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }, 80);
                      }}
                      style={{
                        padding: "9px 18px", borderRadius: 10,
                        background: "linear-gradient(135deg,#f97316,#dc2626)",
                        color: "#fff", border: "none",
                        fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                        fontFamily: "sans-serif",
                        boxShadow: "0 6px 16px rgba(249,115,22,0.32)",
                      }}>See plans →</button>
                  </div>
                );
              })()}
              {evaluation && (
                <div style={{ textAlign: "center", marginTop: 4 }}>
                  <p style={{ color: "var(--text-faint)", fontSize: 12.5, margin: "0 0 8px", fontFamily: "sans-serif", lineHeight: 1.5 }}>
                    We're in early access and building this with our users — your feedback directly shapes what comes next
                  </p>
                  <a
                    href="https://forms.gle/9o7LtBMYq7QJF9Wr6"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-block",
                      padding: "10px 20px", borderRadius: 12,
                      background: "var(--surface)", color: "#fdba74",
                      border: "1px solid rgba(253,186,116,0.4)",
                      fontSize: 13, fontWeight: 600, textDecoration: "none",
                      fontFamily: "sans-serif",
                    }}
                  >💬 Give Feedback</a>
                </div>
              )}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
                <button onClick={() => setPhase("cockpit")} style={{
                  padding: "12px 22px", borderRadius: 12,
                  background: "var(--surface-2)", color: "var(--text-strong)",
                  border: "1px solid var(--border-soft)",
                  fontSize: 13.5, fontWeight: 600, cursor: "pointer",
                  fontFamily: "sans-serif",
                }}>← Back to cockpit</button>
                <button onClick={reset} style={{
                  padding: "12px 28px", borderRadius: 12, border: "none",
                  background: "linear-gradient(135deg,#f97316,#dc2626)",
                  color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
                  fontFamily: "sans-serif",
                }}>Start New Session</button>
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Controls */}
      {phase === "interview" && (
        <div style={{
          borderTop: "1px solid var(--border-soft)", padding: "16px 24px 28px",
          background: "var(--header-bg)", backdropFilter: "blur(14px)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
        }}>
          <Waveform isActive={isRecording} />

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <div style={{ position: "relative", width: 74, height: 74 }}>
              {isRecording && (
                <>
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2px solid rgba(239,68,68,0.45)", animation: "ripple 1.4s infinite", pointerEvents: "none", touchAction: "none", zIndex: 0 }} />
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "1px solid rgba(239,68,68,0.22)", animation: "ripple 1.4s 0.5s infinite", pointerEvents: "none", touchAction: "none", zIndex: 0 }} />
                </>
              )}
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isThinking || !!pendingAnswer}
                style={{
                  position: "relative", zIndex: 2,
                  width: 74, height: 74, borderRadius: "50%", border: "none",
                  background: isRecording
                    ? "linear-gradient(135deg,#ef4444,#dc2626)"
                    : (isThinking || pendingAnswer)
                    ? "var(--surface-2)"
                    : "linear-gradient(135deg,#f97316,#dc2626)",
                  color: "#fff", fontSize: 28,
                  cursor: (isThinking || pendingAnswer) ? "not-allowed" : "pointer",
                  boxShadow: isRecording
                    ? "0 0 44px rgba(239,68,68,0.7)"
                    : "0 0 30px rgba(249,115,22,0.5)",
                  transition: "all 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: pendingAnswer ? 0.45 : 1,
                }}
              >
                {isRecording ? "⏹" : isThinking ? "⏳" : "🎤"}
              </button>
            </div>
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isThinking || !!pendingAnswer}
              style={{
                padding: "12px 22px", borderRadius: 999, minWidth: 220,
                border: isRecording ? "1px solid rgba(248,113,113,0.5)" : "1px solid rgba(249,115,22,0.35)",
                background: isRecording ? "rgba(239,68,68,0.18)" : "rgba(249,115,22,0.14)",
                color: isRecording ? "#fecaca" : "#fed7aa",
                fontSize: 14, fontWeight: 700, cursor: (isThinking || pendingAnswer) ? "not-allowed" : "pointer",
                fontFamily: "sans-serif",
                opacity: pendingAnswer ? 0.5 : 1,
              }}
            >
              {isRecording ? "Stop & Review" : "Start Recording"}
            </button>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            <button
              type="button"
              onClick={replayLastReply}
              disabled={isThinking || !messages.some((m) => m.role === "assistant")}
              style={{
                background: "var(--surface)", border: "1px solid var(--surface-3)",
                borderRadius: 999, color: "var(--text-faint)", padding: "8px 14px", fontSize: 12,
                cursor: isThinking ? "not-allowed" : "pointer", fontFamily: "sans-serif",
              }}
            >
              🔊 Replay {interviewerObj.name}
            </button>
            <button
              type="button"
              onClick={() => endSessionAndEvaluate('user_ended')}
              disabled={isThinking || isEvaluating || messages.length === 0}
              style={{
                background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)",
                borderRadius: 999, color: "#86efac", padding: "8px 16px", fontSize: 12, fontWeight: 700,
                cursor: isThinking || isEvaluating ? "not-allowed" : "pointer", fontFamily: "sans-serif",
              }}
            >
              🏁 End & Get Feedback
            </button>
          </div>

          {error && (
            <p style={{ color: "#f87171", fontSize: 12, margin: 0, textAlign: "center", fontFamily: "sans-serif" }}>
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
